// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService, { getOwnerByBoatId } from './authService';
import AdminDashboard from './AdminDashboard';
import { codeMatches, textMatches } from './utils/searchUtils';
import { latinId, normName, idKeys, samePersonStrict, sameIdentity, unifyCrew } from './utils/identity';
import { formatDateEU } from './utils/dateFormat';
// 🔥 FIX 6 & 7: Import API functions for charter sync and vessels
// 🔥 FIX 16: Added API loading functions for multi-device sync
import { saveBooking, renameBooking, getVessels, getBookingsByVessel, deleteBooking, updateCharterPayments, updateCharterStatus, getBooking, getAllBookings, getTasksByVessel, saveTask, deleteTask, migrateTasksFromLocalStorage, getInvoicesByVessel, saveInvoice, deleteInvoice, migrateInvoicesFromLocalStorage, checkDuplicateCharterCode, checkDateOverlap, savePage1DataHybrid, loadChartererData } from './services/apiService';
// 🔥 FIX 23: Charter Party DOCX generation
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
// 🔥 Auto-refresh now handled globally in App.tsx (every 3 minutes)
// import { useAutoRefresh } from './hooks/useAutoRefresh';
// 🔥 FIX 35: Professional PDF generation service
import { generateOwnerCharterPDF } from './services/pdfTemplateService';
import { nationalityToIso3 } from './data/nationalityAliases';
import CountrySelect from './components/CountrySelect';
import { companyChartererPatch, crewWithoutCharterer, isCompanyCharterer, INDIVIDUAL_KEYS } from './utils/chartererType';
// 🔥 FIX 36: Professional HTML email service (no PDF attachment)
import { sendOwnerCharterEmail } from './services/emailTemplateService';

// =====================================================
// FLEET MANAGEMENT - PROFESSIONAL VERSION WITH AUTH
// PART 1/4: Base + Components + Login + Admin
// =====================================================

// --- Utility Functions ---
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const mid = () => "m_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

/* ΣΚΙΠΕΡ στην κάρτα/pop-up του Booking plan: crew list role 'skipper' → πεδία
   skipper* → ναυλωτής αν sameAsSkipper → «». Badge «⛵ …»/«⛵ —». */
const skipperName = (b: any): string => {
  const bd = b?.booking_data ?? b?.bookingData ?? b ?? {};
  const crew = Array.isArray(bd.crewMembers) ? bd.crewMembers : [];
  const nm = (m: any) => (m?.name || '').trim() || [m?.firstName, m?.lastName].filter(Boolean).join(' ').trim();
  const sk = crew.find((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
  if (nm(sk)) return nm(sk);
  const sf = [bd.skipperFirstName, bd.skipperLastName].filter(Boolean).join(' ').trim() || (bd.skipperName || '').trim();
  if (sf) return sf;
  if (bd.sameAsSkipper || String(bd.skipperMode || '').toLowerCase() === 'me') {
    const ch = (bd.chartererCompanyName || '').trim()
      || [bd.chartererFirstName, bd.chartererLastName].filter(Boolean).join(' ').trim()
      || (bd.clientName || bd.charterer || '').trim();
    if (ch) return ch;
  }
  return '';
};
const skipperBadge = (b: any): string => `⛵ ${skipperName(b) || '—'}`;

// --- Company Information ---
const COMPANY_INFO = {
  name: "TAILWIND YACHTING",
  address: "Leukosias 37, Alimos",
  phone: "+30 6978196009",
  emails: {
    info: "info@tailwindyachting.com",
    accounting: "accounting@tailwindyachting.com",
    charter: "charter@tailwindyachting.com"
  }
};

// --- Brokers / Agencies List ---
const BROKERS = [
  'Sunsail Greece',
  'Moorings Charter',
  'Dream Yacht',
  'Navigare Yachting',
  'Click&Boat',
  'Yacht Charter Fleet',
  'Zizoo',
  'Sailo',
  'GetMyBoat',
  'Boatbound',
  'Direct (Απευθείας)'
];

// --- Shared Fleet Service ---
const FLEET_STORAGE_KEY = 'app_fleet_vessels';

// 🔥 FIX 5: INITIAL_FLEET with numeric IDs matching API format
const INITIAL_FLEET = [
  { id: 8, name: "Bob", type: "Catamaran", model: "Lagoon 42" },
  { id: 7, name: "Perla", type: "Catamaran", model: "Lagoon 46" },
  { id: 6, name: "Infinity", type: "Catamaran", model: "Bali 4.2" },
  { id: 1, name: "Maria 1", type: "Monohull", model: "Jeanneau Sun Odyssey 449" },
  { id: 2, name: "Maria 2", type: "Monohull", model: "Jeanneau yacht 54" },
  { id: 4, name: "Bar Bar", type: "Monohull", model: "Beneteau Oceanis 46.1" },
  { id: 5, name: "Kalispera", type: "Monohull", model: "Bavaria c42 Cruiser" },
  { id: 3, name: "Valesia", type: "Monohull", model: "Bavaria c42 Cruiser" }
];

const FleetService = {
  initialize() {
    const stored = localStorage.getItem(FLEET_STORAGE_KEY);
    if (!stored) {
      localStorage.setItem(FLEET_STORAGE_KEY, JSON.stringify(INITIAL_FLEET));
      console.log('✅ Fleet initialized with 8 boats');
    }
  },

  getAllBoats() {
    try {
      const stored = localStorage.getItem(FLEET_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      return INITIAL_FLEET;
    } catch (error) {
      console.error('Error loading fleet:', error);
      return INITIAL_FLEET;
    }
  },

  addBoat(boat) {
    try {
      const boats = this.getAllBoats();

      if (boats.find(b => b.id.toUpperCase() === boat.id.toUpperCase())) {
        throw new Error(`Boat with ID "${boat.id}" already exists`);
      }

      // 🔥 FIX 37: Include owner details when adding boat
      boats.push({
        id: boat.id.toUpperCase(),
        name: boat.name,
        type: boat.type,
        model: boat.model || '',
        ownerCode: boat.ownerCode || '',
        ownerName: boat.ownerName || '',
        ownerEmail: boat.ownerEmail || '',
        ownerCompany: boat.ownerCompany || '',
        ownerTaxId: boat.ownerTaxId || '',
        ownerPhone: boat.ownerPhone || '',
        ownerAddress: boat.ownerAddress || '',
        createdAt: new Date().toISOString()
      });

      boats.sort((a, b) => a.id.localeCompare(b.id));
      localStorage.setItem(FLEET_STORAGE_KEY, JSON.stringify(boats));
      console.log('✅ Boat added:', boat.id);

      authService.logActivity('add_boat', boat.id);

      return true;
    } catch (error) {
      console.error('Error adding boat:', error);
      throw error;
    }
  },

  removeBoat(boatId) {
    try {
      const boats = this.getAllBoats();
      const filtered = boats.filter(b => b.id !== boatId);
      localStorage.setItem(FLEET_STORAGE_KEY, JSON.stringify(filtered));
      console.log('✅ Boat removed:', boatId);
      
      authService.logActivity('remove_boat', boatId);
      
      return true;
    } catch (error) {
      console.error('Error removing boat:', error);
      return false;
    }
  }
};

FleetService.initialize();

// --- Icons (SVG) ---
const icons = {
  home: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>),
  message: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>),
  logout: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>),
  media: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>),
  tasks: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>),
  charter: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 9v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9"></path><path d="M20 9h-4V4a2 2 0 0 0-2-2H9.96A2 2 0 0 0 8 4v5H4"></path><path d="M12 9v11"></path></svg>),
  financials: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0 4h18"></path><circle cx="7" cy="17" r="2"></circle></svg>),
  bookingSheet: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><line x1="10" y1="14" x2="21" y2="14"></line><line x1="10" y1="18" x2="21" y2="18"></line><line x1="3" y1="14" x2="7" y2="14"></line><line x1="3" y1="18" x2="7" y2="18"></line></svg>),
  email: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>),
  fileText: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>),
  calendar: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>),
  chevronLeft: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>),
  returnArrow: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 10 4 15 9 20"></polyline><path d="M20 4v7a4 4 0 0 1-4 4H4"></path></svg>),
  chevronRight: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>),
  plus: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>),
  x: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>),
  download: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>),
  send: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>),
  upload: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>),
  check: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>),
  checkCircle: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>),
  xCircle: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>),
  helpCircle: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>),
  // 🔥 FIX 37: User icon for owner details
  user: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>),
  lock: (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>),
  edit: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>),
  shield: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>),
  eye: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>)
};

let globalShowMessage = (text, type) => console.log(text, type);

// =====================================================
// PAYMENT STATUS HELPER - Supports both English and Greek values
// =====================================================
const getPaymentStatusInfo = (paymentStatus: string | undefined) => {
  // Handle both English and Greek payment status values
  switch (paymentStatus) {
    case 'Paid':
    case 'ΕΞΟΦΛΗΜΕΝΟ':
      return {
        text: 'ΕΞΟΦΛΗΜΕΝΟ',
        color: 'text-green-400',
        showLight: false,
        lightBlink: false,
        needsPayment: false
      };
    case 'Partial':
    case 'ΜΕΡΙΚΗ ΠΛΗΡΩΜΗ':
      return {
        text: 'ΜΕΡΙΚΗ ΠΛΗΡΩΜΗ',
        color: 'text-orange-400',
        showLight: true,
        lightBlink: false,
        needsPayment: true
      };
    case 'Pending':
    case 'ΑΝΕΞΟΦΛΗΤΟ':
    default:
      return {
        text: 'ΑΝΕΞΟΦΛΗΤΟ',
        color: 'text-red-400',
        showLight: true,
        lightBlink: true,
        needsPayment: true
      };
  }
};

// =====================================================
// HELPER: Check if charter needs urgent payment attention
// Returns true if charter is unpaid/partial AND within 30 days of start
// =====================================================
const needsUrgentPaymentAttention = (charter: any) => {
  if (!charter?.startDate) return false;

  const paymentInfo = getPaymentStatusInfo(charter.paymentStatus);
  if (!paymentInfo.needsPayment) return false; // Already paid

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(charter.startDate);
  startDate.setHours(0, 0, 0, 0);

  const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  // Needs attention if within 30 days (including past dates that haven't been paid)
  return daysUntilStart <= 30;
};

// =====================================================
// END OF PART 1 - Continue with Part 2
// =====================================================
// =====================================================
// PART 2/4: PDF Generator + Main Component + Login + Admin
// =====================================================

const generateSpecimenPdf = (charter, boatData, companyInfo = COMPANY_INFO) => {
  try {
    const { jsPDF } = window.jspdf;
    if (!jsPDF) {
      alert('PDF library not loaded. Please refresh the page.');
      return;
    }
    
    authService.logActivity('download_specimen', charter.code);
    
    const doc = new jsPDF();
    
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.src = 'https://drive.google.com/uc?export=download&id=1DOmo-MO9ZsfmZm7ac4olSF8nl3dJAAer';
    
    let logoLoaded = false;
    
    logoImg.onload = () => {
      logoLoaded = true;
      generatePDF(true);
    };
    
    logoImg.onerror = () => {
      console.warn('Logo failed to load, using text fallback');
      generatePDF(false);
    };
    
    setTimeout(() => {
      if (!logoLoaded) {
        console.warn('Logo timeout, using text fallback');
        generatePDF(false);
      }
    }, 2000);
    
    const generatePDF = (hasLogo) => {
      try {
        if (hasLogo) {
          doc.addImage(logoImg, 'PNG', 20, 10, 40, 18);
        } else {
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('LOGO', 40, 20);
        }
      } catch (e) {
        console.error('Error adding logo:', e);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('LOGO', 40, 20);
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('TAILWIND YACHTING', 190, 15, { align: 'right' });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Leukosias 37, Alimos', 190, 20, { align: 'right' });
      doc.text('Tel: +30 6978196009', 190, 25, { align: 'right' });
      doc.text('info@tailwindyachting.com', 190, 30, { align: 'right' });
      doc.text('accounting@tailwindyachting.com', 190, 34, { align: 'right' });
      doc.text('charter@tailwindyachting.com', 190, 38, { align: 'right' });
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`CHARTERING INFORMATION - ${charter.status === 'Confirmed' ? 'RESERVATION' : 'OPTION'} ${charter.code}`, 105, 55, { align: 'center' });
      
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(20, 60, 190, 60);
      
      doc.setFillColor(240, 240, 240);
      doc.rect(20, 70, 170, 40, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      
      let yPos = 78;
      
      doc.text('YACHT:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(boatData.name || boatData.id, 70, yPos);
      
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.text('FROM:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(charter.startDate || '', 70, yPos);
      
      doc.setFont('helvetica', 'bold');
      doc.text('TO:', 110, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(charter.endDate || '', 130, yPos);
      
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.text('DEPARTURE:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(charter.departure || 'ALIMOS MARINA', 70, yPos);
      
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.text('ARRIVAL:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(charter.arrival || 'ALIMOS MARINA', 70, yPos);
      
      yPos = 125;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('FINANCIAL TERMS:', 20, yPos);
      
      yPos += 10;
      doc.setFontSize(10);
      
      doc.setFillColor(245, 245, 245);
      doc.rect(20, yPos - 5, 170, 45, 'F');
      
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.rect(20, yPos - 5, 170, 45);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 128, 0);
      doc.text('Charter Fee (Income):', 25, yPos);
      doc.text(`${(charter.amount || 0).toFixed(2)}€`, 185, yPos, { align: 'right' });
      
      yPos += 10;
      doc.setTextColor(255, 0, 0);
      doc.text('Commission (Expense):', 25, yPos);
      doc.text(`-${(charter.commission || 0).toFixed(2)}€`, 185, yPos, { align: 'right' });
      
      yPos += 10;
      doc.text('VAT on Commission (24%) (Expense):', 25, yPos);
      doc.text(`-${(charter.vat_on_commission || 0).toFixed(2)}€`, 185, yPos, { align: 'right' });
      
      yPos += 10;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      const netIncome = (charter.amount || 0) - (charter.commission || 0) - (charter.vat_on_commission || 0);
      doc.text('NET INCOME:', 25, yPos);
      doc.text(`${netIncome.toFixed(2)}€`, 185, yPos, { align: 'right' });
      
      yPos += 25;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Please advise regarding the acceptance of the charter.', 105, yPos, { align: 'center' });
      
      yPos += 8;
      doc.text('Thank you,', 105, yPos, { align: 'center' });
      
      doc.save(`specimen_${charter.code}.pdf`);
      
      if (globalShowMessage) {
        globalShowMessage('✅ Specimen PDF κατέβηκε επιτυχώς!', 'success');
      }
    };
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Σφάλμα: ' + error.message);
  }
};

// Convert number to English words (for charter party document)
const numberToWords = (num: number): string => {
  if (num === 0) return 'Zero Euro';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertGroup = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertGroup(n % 100) : '');
  };

  const euros = Math.floor(Math.abs(num));
  const cents = Math.round((Math.abs(num) - euros) * 100);

  let result = '';
  if (euros >= 1000000) {
    result += convertGroup(Math.floor(euros / 1000000)) + ' Million ';
    const remainder = euros % 1000000;
    if (remainder >= 1000) {
      result += convertGroup(Math.floor(remainder / 1000)) + ' Thousand ';
      if (remainder % 1000) result += (remainder % 1000 < 100 ? 'and ' : '') + convertGroup(remainder % 1000);
    } else if (remainder > 0) {
      result += 'and ' + convertGroup(remainder);
    }
  } else if (euros >= 1000) {
    result += convertGroup(Math.floor(euros / 1000)) + ' Thousand ';
    if (euros % 1000) result += (euros % 1000 < 100 ? 'and ' : '') + convertGroup(euros % 1000);
  } else {
    result = convertGroup(euros);
  }

  result = result.trim() + ' Euro';
  if (cents > 0) {
    result += ' and ' + convertGroup(cents) + ' Cents';
  }
  return result;
};

// 🔥 FIX 23: Generate Charter Party DOCX with auto-fill
const generateCharterParty = async (charter, boat, showMessage?) => {
  console.log('🚀 Charter Party button clicked!');
  console.log('🚀 Charter:', charter);
  console.log('🚀 Boat:', boat);

  try {
    console.log('📄 Step 1: Fetching template...');

    // Load template from public folder
    const templateUrl = '/templates/FINAL-Charter-Party-Tailwind-2026.docx';
    console.log('📄 Template URL:', templateUrl);

    const response = await fetch(templateUrl);
    console.log('📄 Response status:', response.status, response.statusText);

    if (!response.ok) {
      console.error('❌ Template not found at:', templateUrl);
      console.error('❌ Response:', response.status, response.statusText);
      alert(`❌ Template file not found!\n\nPlease place the template at:\npublic/templates/FINAL-Charter-Party-Tailwind-2026.docx`);
      return;
    }

    console.log('📄 Step 2: Converting to ArrayBuffer...');
    const templateBuffer = await response.arrayBuffer();
    console.log('📄 Template loaded, size:', templateBuffer.byteLength, 'bytes');

    // Fetch owner data from API
    let ownerApiData: any = null;
    try {
      const vesselName = boat?.name || '';
      const ownerResponse = await fetch(`https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(vesselName)}`);
      if (ownerResponse.ok) {
        const ownerResult = await ownerResponse.json();
        ownerApiData = ownerResult?.data || ownerResult;
        if (ownerApiData && !ownerApiData.vessel_name) ownerApiData = null;
      }
    } catch (e) {
      console.log('Could not fetch owner data:', e);
    }

    // Calculate financial values - amount entered is GROSS (includes 12% VAT)
    const grossAmount = charter.amount || 0;
    const charterAmount = grossAmount / 1.12; // NET = gross / 1.12
    const vatAmount = grossAmount - charterAmount; // VAT = gross - NET
    const totalWithVat = grossAmount; // Total = gross (amount entered by user)

    // Phase 3: Load online charterer data (includes signature)
    const onlineDataRaw: any = await loadChartererData(charter.code).catch(() => null);
    let onlineData: any = onlineDataRaw;
    try {
      const invR = await fetch('/api/crew-invitations.php?action=list&booking_number=' + encodeURIComponent(charter.code || ''));
      const invJ = await invR.json();
      if (invJ && invJ.success && Array.isArray(invJ.data)) {
        const skInv = invJ.data.find((x: any) => x.role === 'skipper' && x.status === 'submitted');
        if (skInv) {
          onlineData = onlineData || {};
          const fullName = ((skInv.submitted_first_name || '') + ' ' + (skInv.submitted_last_name || '')).trim();
          if (!onlineData.skipperName) onlineData.skipperName = fullName;
          if (!onlineData.skipperFirstName) onlineData.skipperFirstName = skInv.submitted_first_name || '';
          if (!onlineData.skipperLastName) onlineData.skipperLastName = skInv.submitted_last_name || '';
          if (!onlineData.skipperPassport) onlineData.skipperPassport = skInv.submitted_passport || '';
          if (!onlineData.skipperPhone) onlineData.skipperPhone = skInv.submitted_phone || '';
          if (!onlineData.skipperEmail) onlineData.skipperEmail = skInv.submitted_email || skInv.invite_email || '';
          if (!onlineData.skipperAddress) onlineData.skipperAddress = skInv.submitted_address || '';
          if (!onlineData.skipperNationality) onlineData.skipperNationality = skInv.submitted_nationality || '';
          if (!onlineData.skipperDob) onlineData.skipperDob = skInv.submitted_date_of_birth || '';
          onlineData.skipperSignature = skInv.signature_data || onlineData.skipperSignature || '';
        }
      }
    } catch (eInv) { console.log('invitation enrich skipped:', eInv); }
    console.log('Online charterer data (enriched):', onlineData);

    // Prepare data for auto-fill - matches template placeholders
    const data = {
      // Vessel Info
      VESSEL_NAME: boat?.name || charter.vesselName || charter.boatName || '',
      VESSEL_TYPE: boat?.type || '',
      REGISTER_NUMBER: '',

      // Date
      DAY: new Date().getDate().toString(),
      MONTH: (new Date().getMonth() + 1).toString(),
      YEAR: new Date().getFullYear().toString(),

      // Owner Info - AUTO-FILL from API data
      OWNER_NAME: ownerApiData?.owner_first_name && ownerApiData?.owner_last_name
        ? ownerApiData.owner_first_name + ' ' + ownerApiData.owner_last_name
        : ownerApiData?.company_name || '',
      OWNER_ADDRESS: [ownerApiData?.street, ownerApiData?.street_number, ownerApiData?.postal_code, ownerApiData?.city].filter(Boolean).join(', ') || '',
      OWNER_ID: ownerApiData?.id_passport_number || '',
      OWNER_PASSPORT: ownerApiData?.id_passport_number || '',
      OWNER_TAX: ownerApiData?.vat_number || '',
      OWNER_TAX_OFFICE: ownerApiData?.tax_office || '',
      OWNER_PHONE: ownerApiData?.phone || '',
      OWNER_EMAIL: ownerApiData?.owner_email || ownerApiData?.company_email || '',

      // Broker Info - only fill when a foreign broker is selected
      BROKER2_NAME: charter.hasForeignBroker && charter.broker?.trim() ? (charter.broker || '') : '',
      BROKER2_ADDRESS: charter.hasForeignBroker && charter.broker?.trim() ? (charter.brokerAddress || '') : '',
      BROKER2_TAX: charter.hasForeignBroker && charter.broker?.trim() ? (charter.brokerAfm || charter.brokerTax || '') : '',
      BROKER2_TAX_OFFICE: charter.hasForeignBroker && charter.broker?.trim() ? (charter.brokerDoy || charter.brokerTaxOffice || '') : '',
      BROKER2_PHONE: charter.hasForeignBroker && charter.broker?.trim() ? (charter.brokerPhone || '') : '',
      BROKER2_EMAIL: charter.hasForeignBroker && charter.broker?.trim() ? (charter.brokerEmail || '') : '',

      // Charterer Info - AUTO-FILL from charter data
      /* ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ: στο ναυλοσύμφωνο μπαίνει η ΕΠΩΝΥΜΙΑ και τα εταιρικά
         (ΑΦΜ/VAT, ΔΟΥ, διεύθυνση, χώρα, επικοινωνία). Ταυτότητα/διαβατήριο
         μένουν ΚΕΝΑ — νομικό πρόσωπο δεν έχει. */
      CHARTERER_NAME: isCompanyCharterer(charter)
        ? (charter.chartererCompanyName || charter.chartererLastName || '')
        : charter.chartererFirstName && charter.chartererLastName
        ? `${charter.chartererFirstName} ${charter.chartererLastName}`
        : charter.skipperFirstName && charter.skipperLastName
          ? `${charter.skipperFirstName} ${charter.skipperLastName}`
          : charter.clientName || charter.charterer || '',
      CHARTERER_COMPANY: isCompanyCharterer(charter)
        ? (charter.chartererCompanyName || charter.chartererLastName || '') : '',
      CHARTERER_COUNTRY: charter.chartererCountry || (charter.chartererData || {}).country || '',
      CHARTERER_TYPE: isCompanyCharterer(charter) ? 'Εταιρεία / Company' : 'Φυσικό πρόσωπο / Individual',
      CHARTERER_FIRST_NAME: isCompanyCharterer(charter) ? '' : (onlineData?.firstName || charter.chartererFirstName || charter.skipperFirstName || ''),
      CHARTERER_LAST_NAME: isCompanyCharterer(charter)
        ? (charter.chartererCompanyName || charter.chartererLastName || '')
        : (onlineData?.lastName || charter.chartererLastName || charter.skipperLastName || ''),
      CHARTERER_ADDRESS: onlineData?.address || charter.chartererAddress || charter.skipperAddress || '',
      CHARTERER_ID: isCompanyCharterer(charter) ? '' : ((onlineData?.idType === 'id' ? onlineData?.idNumber : '') || charter.chartererIdNumber || ''),
      CHARTERER_PASSPORT: isCompanyCharterer(charter) ? '' : ((onlineData?.idType === 'passport' ? onlineData?.idNumber : '') || charter.chartererPassport || ''),
      CHARTERER_TAX: onlineData?.taxNumber || charter.chartererTax || charter.chartererAfm || '',
      CHARTERER_TAX_OFFICE: onlineData?.taxOffice || charter.chartererTaxOffice || charter.chartererDoy || '',
      CHARTERER_PHONE: onlineData?.tel || charter.chartererPhone || charter.skipperPhone || '',
      CHARTERER_EMAIL: onlineData?.email || charter.chartererEmail || charter.skipperEmail || '',

      // Charter Dates - AUTO-FILL
      CHECKIN_DATE: charter.startDate || '',
      CHECKOUT_DATE: charter.endDate || '',
      DEPARTURE_PORT: charter.departure || 'ALIMOS MARINA',
      ARRIVAL_PORT: charter.arrival || 'ALIMOS MARINA',

      // Financial - AUTO-FILL
      NET_CHARTER_FEE: charterAmount.toFixed(2),
      VAT_AMOUNT: vatAmount.toFixed(2),
      TOTAL_CHARTER_PRICE: totalWithVat.toFixed(2),
      TOTAL_IN_WORDS: numberToWords(grossAmount),
      CHARTER_AMOUNT: charterAmount.toFixed(2),

      // Additional fields - AUTO-FILL from charter data + owner custom_fields
      PROFESSIONAL_LICENSE: '',
      AMEPA: '',
      CALL_SIGN: '',
      SECURITY_DEPOSIT: '',
      DAMAGE_WAIVER: '',
      APA_AMOUNT: charter.apa || charter.apaAmount || '',

      // Reference
      CHARTER_CODE: charter.charterCode || charter.code || charter.bookingCode || '',
      BOOKING_CODE: charter.code || '',

      // Signatures (IIFE - copied 1:1 from CharterAgreementPage)
      CHARTERER_SIGNATURE: onlineData?.signature ? '[object ArrayBuffer]' : '',
      '%CHARTERER_SIGNATURE': onlineData?.signature ? '[object ArrayBuffer]' : '',
      '%SKIPPER_SIGNATURE': (onlineData?.skipperSignature || (onlineData?.isAlsoSkipper && onlineData?.signature)) ? '[object ArrayBuffer]' : '',
      SKIPPER_SIGNATURE: onlineData?.skipperSignature ? '[object ArrayBuffer]' : '',

      // Signature dates (DD/MM/YYYY). Fallback σε submittedAt για legacy.
      CHARTERER_SIGNED_AT: (d => d ? new Date(d).toLocaleDateString('en-GB') : '')(
        onlineData?.chartererSignedAt || onlineData?.submittedAt || ''
      ),
      SKIPPER_SIGNED_AT: (d => d ? new Date(d).toLocaleDateString('en-GB') : '')(
        (onlineData?.isAlsoSkipper
          ? (onlineData?.skipperSignedAt || onlineData?.chartererSignedAt)
          : onlineData?.skipperSignedAt) || onlineData?.submittedAt || ''
      )
    };

    // Fill template placeholders from owner custom_fields
    if (ownerApiData?.custom_fields) {
      try {
        const cf = typeof ownerApiData.custom_fields === 'string'
          ? JSON.parse(ownerApiData.custom_fields)
          : ownerApiData.custom_fields;
        const cfMap: Record<string, string> = {
          'register_no': 'REGISTER_NUMBER',
          'professional_license': 'PROFESSIONAL_LICENSE',
          'amepa': 'AMEPA',
          'call_sign': 'CALL_SIGN',
          'security_deposit': 'SECURITY_DEPOSIT'
        };
        for (const [cfKey, placeholder] of Object.entries(cfMap)) {
          if (cf[cfKey] && !data[placeholder]) {
            data[placeholder] = cf[cfKey];
          }
        }
        // Fallback: if SECURITY_DEPOSIT still empty, try charter data
        if (!data['SECURITY_DEPOSIT']) {
          data['SECURITY_DEPOSIT'] = charter.securityDeposit || charter.deposit || '';
        }
      } catch (e) {
        console.log('Error parsing owner custom_fields:', e);
      }
    }

    console.log('📋 Step 4: Auto-fill data prepared:', data);

    // Generate document with docxtemplater
    console.log('📄 Step 5: Creating PizZip...');
    const zip = new PizZip(templateBuffer);
    console.log('📄 Step 6: PizZip created successfully');

    console.log('📄 Step 7: Creating Docxtemplater...');
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '{{', end: '}}' },
      nullGetter: () => ''
    });
    console.log('📄 Step 8: Docxtemplater created successfully');

    // Render with data
    console.log('📄 Step 9: Rendering document...');
    doc.render(data);
    console.log('📄 Step 10: Document rendered successfully');

    // Generate blob
    console.log('📄 Step 11: Generating blob...');
    const blob = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    console.log('📄 Step 12: Blob generated, size:', blob.size, 'bytes');

    // Download file
    const filename = `Charter-Party-${charter.code || 'document'}.docx`;
    console.log('📄 Step 13: Saving file as:', filename);
    
    // === SIG_INJECT_MARKER: server-side signature injection ===
    let finalBlob = blob;
    try {
      const sig = onlineData?.signature || '';
      if (sig && blob && blob.size > 0) {
        console.log('Injecting signature via server...');
        const fd = new FormData();
        fd.append('docx', blob, 'input.docx');
        fd.append('charterer_signature', sig);
        fd.append('filename', filename);
        const resp = await fetch('/api/inject-signature.php', { method: 'POST', body: fd });
        if (resp.ok) {
          const signedBlob = await resp.blob();
          if (signedBlob && signedBlob.size > 5000) {
            finalBlob = signedBlob;
            console.log('Signature injected, size:', signedBlob.size);
          }
        } else {
          console.warn('Inject endpoint HTTP:', resp.status);
        }
      }
    } catch (sigErr) {
      console.error('Signature injection failed (non-fatal):', sigErr);
    }
    saveAs(finalBlob, filename);

    console.log('✅ Charter Party generated and downloaded successfully!');
    if (showMessage) {
      showMessage('✅ Charter Party DOCX κατέβηκε επιτυχώς!', 'success');
    }

  } catch (error: any) {
    console.error('❌ Error generating Charter Party:', error);
    console.error('❌ Error stack:', error.stack);
    console.error('❌ Error message:', error.message);
    if (showMessage) {
      showMessage('❌ Σφάλμα: ' + error.message, 'error');
    } else {
      alert('❌ Error: ' + error.message);
    }
  }
};

// ΜΙΑ κοινή σύνθεση «πλήρες πλήρωμα»: ο skipper (Πλοίαρχος) υπάρχει ΠΑΝΤΑ, ακόμη κι
// όταν είναι ο ναυλωτής (skipperMode='me') και δεν βρίσκεται στα crewMembers.
// Επιστρέφει το derived skipper αντικείμενο (role='skipper', __derived) ή null όταν
// το πλήρωμα έχει ήδη μέλος με role=skipper (π.χ. skipperMode='crew').
const deriveSkipper = (charter: any, rawCrew: any[]): any => {
  const list = Array.isArray(rawCrew) ? rawCrew : [];
  if (list.some((m: any) => String(m?.role || '').toLowerCase() === 'skipper')) return null;
  const c: any = charter || {};
  const mode = String(c.skipperMode || '').toLowerCase();
  const isMe = mode === 'me'
    || c.isAlsoSkipper === true || c.isAlsoSkipper === 'true'
    || c.sameAsSkipper === true || c.sameAsSkipper === 'true';
  if (mode === 'invite') return null; // skipper θα σταλεί invitation — δεν έχει συμπληρωθεί ακόμη
  // ΤΑ ΠΛΗΡΗ ΣΤΟΙΧΕΙΑ ζουν στο nested chartererData (booking_data->chartererData) — τα
  // flat charterer* πεδία είναι συχνά μισοάδεια. Τραβάμε από τη ΣΩΣΤΗ πηγή κατά mode.
  const cd: any = c.chartererData || {};
  let sk: any = null;
  if (isMe) {
    // mode 'me' → στοιχεία ΝΑΥΛΩΤΗ (chartererData)
    const fn = cd.firstName || c.chartererFirstName || c.firstName || '';
    const ln = cd.lastName || c.chartererLastName || c.lastName || '';
    const nm = `${fn} ${ln}`.trim() || c.chartererName || c.clientName || '';
    if (nm) sk = {
      name: nm,
      passport: cd.idNumber || cd.passport || c.chartererIdNumber || c.chartererPassport || '',
      dateOfBirth: cd.dob || cd.dateOfBirth || c.chartererDob || c.chartererDateOfBirth || '',
      gender: cd.gender || c.chartererGender || '',
      nationality: cd.nationality || c.chartererNationality || '',
      email: cd.email || c.chartererEmail || '',
      phone: cd.tel || cd.phone || c.chartererPhone || '',
      role: 'skipper', __derived: true,
    };
  } else {
    // mode 'other' → skipper πεδία (chartererData.skipper* ή flat)
    const fn = cd.skipperFirstName || c.skipperFirstName || '';
    const ln = cd.skipperLastName || c.skipperLastName || '';
    const nm = `${fn} ${ln}`.trim() || cd.skipperName || c.skipperName || '';
    if (nm) sk = {
      name: nm,
      passport: cd.skipperIdNumber || cd.skipperPassport || c.skipperPassport || c.skipperIdNumber || '',
      dateOfBirth: cd.skipperDob || c.skipperDob || c.skipperDateOfBirth || '',
      gender: cd.skipperGen || cd.skipperGender || c.skipperGender || '',
      nationality: cd.skipperNationality || c.skipperNationality || '',
      email: cd.skipperEmail || c.skipperEmail || '',
      phone: cd.skipperPhone || c.skipperPhone || '',
      role: 'skipper', __derived: true,
    };
  }
  return sk;
};

// 🔥 FIX 27: Generate Crew List DOCX with auto-fill
const generateCrewList = async (charter, boat, boatDetails?, showMessage?) => {
  console.log('👥 generateCrewList called');
  console.log('👥 charter:', JSON.stringify(charter, null, 2));
  console.log('👥 boat:', JSON.stringify(boat, null, 2));
  console.log('👥 boatDetails (from caller):', JSON.stringify(boatDetails, null, 2));

  try {
    // Step 1: Fetch vessel custom fields from API (don't rely on boatDetails arg - it's often empty)
    let cf: Record<string, string> = {};
    try {
      const vesselName = boat?.name || charter.vesselName || charter.boatName || '';
      const ownerResponse = await fetch(`https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(vesselName)}`);
      if (ownerResponse.ok) {
        const ownerResult = await ownerResponse.json();
        const ownerData = ownerResult?.data || ownerResult;
        cf = typeof ownerData?.custom_fields === 'string'
          ? JSON.parse(ownerData.custom_fields)
          : (ownerData?.custom_fields || {});
        console.log('👥 Vessel custom_fields from API:', JSON.stringify(cf, null, 2));
      }
    } catch (e) {
      console.warn('👥 Could not fetch vessel data from API:', e);
    }

    console.log('📄 Fetching Crew List template...');

    // Load template from public folder
    const templateUrl = `/templates/CrewList2026.docx?t=${Date.now()}`;
    console.log('📄 Template URL:', templateUrl);

    const response = await fetch(templateUrl, { cache: 'no-store' });
    console.log('📄 Response status:', response.status, response.statusText);

    if (!response.ok) {
      console.error('❌ Template not found at:', templateUrl);
      alert(`❌ Template file not found!\n\nPlease place the template at:\npublic/templates/Crew-List.docx`);
      return;
    }

    const templateBuffer = await response.arrayBuffer();
    console.log('📄 Template loaded, size:', templateBuffer.byteLength, 'bytes');

    // Passengers array — try all possible field names on charter
    // ΕΞΑΙΡΕΣΗ skipper: η γραμμή CREW1 γεμίζει ξεχωριστά από skipperFirstName/LastName,
    // οπότε αν ο skipper βρίσκεται και στο crewMembers (π.χ. από Excel import) θα διπλοεγγραφόταν.
    // v6.471: ο 'co-skipper' ΔΕΝ εξαιρείται πλέον — είναι ξεχωριστό άτομο, δεν γράφεται
    // πουθενά αλλού στο DOCX και έλειπε τελείως από το Crew List (ναύλος 669: 9 αντί 11 άτομα,
    // ενώ το ΑΑΔΕ export τον μετράει κανονικά). Χωρητικότητα: CREW2-12 = 11 θέσεις, max 10 σήμερα.
    const crewSourceAll: any[] = (charter.crewMembers || charter.passengers || charter.crew || charter.crewList || []);
    let crewMembers: any[] = crewSourceAll
      .filter((m: any) => String(m?.role || '').toLowerCase() !== 'skipper');
    console.log('👥 Passengers found (skipper excluded, co-skipper included):', crewMembers.length, crewMembers);

    // Format dates helper
    const formatDate = (dateStr: string): string => {
      if (!dateStr) return '';
      try {
        // Already formatted dd/mm/yyyy — return as-is
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-GB');
      } catch {
        return dateStr || '';
      }
    };

    // Safe passenger field getter — never returns undefined/null
    const pax = (idx: number): any => crewMembers[idx] || {};
    const paxName = (p: any): string =>
      (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || '');
    const paxStr = (v: any): string => (v == null ? '' : String(v));

    // -------------------------------------------------------
    // Skipper data (from charter fields — check both top-level
    // and charter.bookingData which some saves use)
    // -------------------------------------------------------
    const bd: any = charter.bookingData || {};
      let onlineDataCL: any = await loadChartererData(charter.code).catch(() => null);
    try {
      const invR2 = await fetch('/api/crew-invitations.php?action=list&booking_number=' + encodeURIComponent(charter.code || ''));
      const invJ2 = await invR2.json();
      if (invJ2 && invJ2.success && Array.isArray(invJ2.data)) {
        const skInv2 = invJ2.data.find((x: any) => x.role === 'skipper' && x.status === 'submitted');
        if (skInv2) {
          onlineDataCL = onlineDataCL || {};
          if (!onlineDataCL.skipperFirstName) onlineDataCL.skipperFirstName = skInv2.submitted_first_name || '';
          if (!onlineDataCL.skipperLastName) onlineDataCL.skipperLastName = skInv2.submitted_last_name || '';
          if (!onlineDataCL.skipperPassport) onlineDataCL.skipperPassport = skInv2.submitted_passport || '';
          if (!onlineDataCL.skipperPhone) onlineDataCL.skipperPhone = skInv2.submitted_phone || '';
          if (!onlineDataCL.skipperEmail) onlineDataCL.skipperEmail = skInv2.submitted_email || skInv2.invite_email || '';
          if (!onlineDataCL.skipperNationality) onlineDataCL.skipperNationality = skInv2.submitted_nationality || '';
          if (!onlineDataCL.skipperDob) onlineDataCL.skipperDob = skInv2.submitted_date_of_birth || '';
          onlineDataCL.skipperSignature = skInv2.signature_data || onlineDataCL.skipperSignature || '';
          if (!onlineDataCL.skipperName) onlineDataCL.skipperName = ((skInv2.submitted_first_name || '') + ' ' + (skInv2.submitted_last_name || '')).trim();
        }
        const crewInvs = invJ2.data.filter((x: any) => x.status === 'submitted' && x.role !== 'skipper');
        if (crewInvs.length > 0) {
          (charter as any).crewMembers = (charter as any).crewMembers || [];
          const existing = (charter as any).crewMembers;
          const byPass = new Map();
          existing.forEach((m: any) => { if (m.passport) byPass.set(m.passport, m); });
          crewInvs.forEach((ci: any) => {
            const passport = ci.submitted_passport || '';
            const entry = {
              name: ((ci.submitted_first_name || '') + ' ' + (ci.submitted_last_name || '')).trim(),
              firstName: ci.submitted_first_name || '',
              lastName: ci.submitted_last_name || '',
              passport: passport,
              dateOfBirth: ci.submitted_date_of_birth || '',
              nationality: ci.submitted_nationality || '',
              email: ci.submitted_email || ci.invite_email || '',
              phone: ci.submitted_phone || '',
              address: ci.submitted_address || '',
              role: ci.role || 'crew',
              signature: ci.signature_data || '',
              fromInvitation: true,
            };
            byPass.set(passport || ('__' + ci.token), entry);
          });
          (charter as any).crewMembers = Array.from(byPass.values());
        }
      }
    } catch (e3) { console.log('crew list invitation enrich skipped:', e3); }

        // skipperMode='me' → ο ναυλωτής ΕΙΝΑΙ ο skipper· τα πλήρη στοιχεία ζουν στα
    // charterer πεδία του chartererData (idNumber/dob/gender/nationality/tel/email),
    // όχι στα skipper* (που για 'me' μένουν μισοάδεια). Fallback σε αυτά.
    const clMode = String(onlineDataCL?.skipperMode || charter.skipperMode || bd.skipperMode || '').toLowerCase();
    const clMe = clMode === 'me' || onlineDataCL?.isAlsoSkipper === true || bd.sameAsSkipper === true || bd.sameAsSkipper === 'true';
    let skipperFirstName = onlineDataCL?.skipperFirstName || charter.skipperFirstName || bd.skipperFirstName || (clMe ? (onlineDataCL?.firstName || charter.chartererFirstName || '') : '');
    let skipperLastName  = onlineDataCL?.skipperLastName || charter.skipperLastName  || bd.skipperLastName  || (clMe ? (onlineDataCL?.lastName || charter.chartererLastName || '') : '');
    let skipperName = skipperFirstName && skipperLastName
      ? `${skipperFirstName} ${skipperLastName}`
      : (onlineDataCL?.isAlsoSkipper ? (onlineDataCL?.skipperName || `${onlineDataCL?.firstName||''} ${onlineDataCL?.lastName||''}`.trim()) : '') || charter.skipperName || bd.skipperName || charter.clientName || '';
    let skipperPhone       = onlineDataCL?.skipperPhone || charter.skipperPhone       || bd.skipperPhone       || (clMe ? (onlineDataCL?.tel || charter.chartererPhone || '') : '');
    let skipperEmail       = onlineDataCL?.skipperEmail || charter.skipperEmail       || bd.skipperEmail       || (clMe ? (onlineDataCL?.email || charter.chartererEmail || '') : '');
    let skipperPassport    = onlineDataCL?.skipperPassport || onlineDataCL?.skipperIdNumber || charter.skipperPassport || bd.skipperPassport || (clMe ? (onlineDataCL?.idNumber || charter.chartererIdNumber || '') : '');
    let skipperNationality = onlineDataCL?.skipperNationality || charter.skipperNationality || bd.skipperNationality || (clMe ? (onlineDataCL?.nationality || charter.chartererNationality || '') : '');
    let skipperGender      = onlineDataCL?.skipperGen || onlineDataCL?.skipperGender || charter.skipperGender || bd.skipperGender || (clMe ? (onlineDataCL?.gender || charter.chartererGender || '') : '');
    let skipperDob         = formatDate(onlineDataCL?.skipperDob || charter.skipperDob || charter.skipperDateOfBirth || bd.skipperDob || bd.skipperDateOfBirth || (clMe ? (onlineDataCL?.dob || charter.chartererDob || '') : ''));

    // ── CREW1: ΤΕΛΕΥΤΑΙΑ ΠΗΓΗ = η γραμμή crewMembers[role='skipper'] ──────────────
    // Όταν ο σκίπερ υπάρχει ΜΟΝΟ ως γραμμή πληρώματος (import Excel/PDF, π.χ. ναύλος
    // 698) τα skipper* πεδία είναι κενά: το CREW1 έβγαινε άδειο ΚΑΙ η γραμμή του
    // κοβόταν από τους επιβάτες (φίλτρο role='skipper') → ο άνθρωπος έλειπε ΤΕΛΕΙΩΣ
    // από το Crew List. Συμπληρώνει ΜΟΝΟ κενά πεδία — καμία υπάρχουσα τιμή δεν χάνεται.
    const skRowForList: any = crewSourceAll.find((m: any) => String(m?.role || '').toLowerCase() === 'skipper') || null;
    if (skRowForList) {
      const rowName = (skRowForList.name || `${skRowForList.firstName || ''} ${skRowForList.lastName || ''}`.trim() || '');
      if (!skipperName) skipperName = rowName;
      if (!skipperFirstName) skipperFirstName = skRowForList.firstName || '';
      if (!skipperLastName) skipperLastName = skRowForList.lastName || '';
      if (!skipperPassport) skipperPassport = skRowForList.passport || '';
      if (!skipperDob) skipperDob = formatDate(skRowForList.dateOfBirth || '');
      if (!skipperGender) skipperGender = skRowForList.gender || '';
      if (!skipperNationality) skipperNationality = skRowForList.nationality || '';
      if (!skipperPhone) skipperPhone = skRowForList.phone || '';
      if (!skipperEmail) skipperEmail = skRowForList.email || '';
    }

    // ── ΙΔΙΟΣ ΑΝΘΡΩΠΟΣ ΜΙΑ ΦΟΡΑ ────────────────────────────────────────────────────
    // Ο σκίπερ γράφεται στο CREW1. Αν ο ΙΔΙΟΣ άνθρωπος υπάρχει και ως γραμμή πληρώματος
    // (τυπικά ο ναυλωτής που είναι και σκίπερ, δηλωμένος ως «άλλο πρόσωπο»), κόβεται από
    // τους επιβάτες — αλλιώς μπαίνει δύο φορές και η αρίθμηση ξεκινά από Passenger 2.
    // ΚΑΝΟΝΑΣ: ταιριάζει ΜΟΝΟ όνομα **ΚΑΙ** ταυτότητα μαζί. ΠΟΤΕ μόνο όνομα — δύο
    // συνονόματοι με άλλο ID είναι διαφορετικοί άνθρωποι. Φίλτρο ΕΞΟΔΟΥ: τα δεδομένα
    // του χρήστη δεν αλλάζουν πουθενά.
    {
      const skN = normName(skipperName) || normName(skipperFirstName, skipperLastName);
      const skIds = idKeys(skipperPassport);
      // ΑΠΑΙΤΕΙΤΑΙ ταυτότητα ΕΔΩ: το dedup του Crew List συγκρίνει τον σκίπερ με ΓΡΑΜΜΕΣ
      // ΠΛΗΡΩΜΑΤΟΣ, όχι με τον ναυλωτή. Χωρίς ID θα μπορούσε να κόψει συνονόματο επιβάτη.
      if (skN && skIds.length) {
        const before = crewMembers.length;
        crewMembers = crewMembers.filter((m: any) => !samePersonStrict(
          skN, skIds,
          normName(m?.name) || normName(m?.firstName, m?.lastName),
          idKeys(m?.passport, m?.idNumber)
        ));
        if (crewMembers.length !== before) {
          console.log('👥 Crew List: ο σκίπερ ήταν και γραμμή πληρώματος — γράφεται μία φορά (CREW1)');
        }
      }
    }

    console.log('👥 Skipper resolved:', { skipperName, skipperPhone, skipperEmail, skipperPassport, skipperNationality, skipperGender, skipperDob });

    // -------------------------------------------------------
    // Vessel / registration fields
    // cf = custom_fields from API (keys: register_no, call_sign, professional_license)
    // -------------------------------------------------------
    const charterPartyNoFull = charter.code || charter.charterCode || charter.bookingCode || charter.bookingNumber || ''; const charterPartyNo = charterPartyNoFull.replace(/CHARTER PARTY N[O0]s*/i, '').replace(/CHARER PARTY N[O0]s*/i, '').replace(/CHARTER PART N[O0]s*/i, '').trim();
    const registrationNumber = cf['register_no']         || boatDetails?.['Register No / Αριθμός Νηολογίου'] || '';
    const callSign           = cf['call_sign']            || boatDetails?.['CALL SIGN']                        || '';
    const eMitroo            = cf['professional_license'] || boatDetails?.['Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο'] || '';
    const flag               = cf['flag']                 || boatDetails?.['Flag']                             || 'Greek';
    const registryPort       = cf['port_of_registry']     || boatDetails?.['Port of Registry']                 || 'Piraeus';

    console.log('👥 Vessel fields resolved:', { charterPartyNo, registrationNumber, callSign, eMitroo, flag, registryPort });

    // -------------------------------------------------------
    // Build the data object — only keys that exist as
    // {{placeholders}} in CrewList2026.docx are included.
    //
    // Template structure:
    //   CREW1 row = Skipper
    //   CREW2–CREW12 rows = Passengers 1–11
    //   Footer: SKIPPER_NAME (×3), SKIPPER_MOBILE, CHARTERER_MOBILE
    //   No ARRIVAL_TIME placeholder exists (it is static text).
    // -------------------------------------------------------
    const data: Record<string, string> = {
      // Header / vessel block
      CHARTER_PARTY_NO:    charterPartyNo,
      YACHT_NAME:          boat?.name || charter.vesselName || charter.boatName || '',
      YACHT_TYPE:          boat?.type || cf['yacht_type'] || '',
      FLAG:                flag,
      REGISTRY_PORT:       registryPort,
      REGISTRATION_NUMBER: registrationNumber,
      CALL_SIGN:           callSign,
      E_MITROO:            eMitroo,

      // Dates
      EMBARKATION_DATE:     formatDate(charter.startDate  || ''),
      EMBARKATION_PLACE:    charter.departure || 'ALIMOS MARINA',
      DISEMBARKATION_DATE:  formatDate(charter.endDate    || ''),
      DISEMBARKATION_PLACE: charter.arrival   || 'ALIMOS MARINA',

      // Footer skipper block (only these 3 placeholders exist in the template)
      SKIPPER_NAME:     skipperName,
      SKIPPER_MOBILE:   skipperPhone,
      CHARTERER_MOBILE: skipperPhone,

      // Skipper signature date (DD/MM/YYYY). Fallback σε submittedAt για legacy.
      // ΠΡΟΣΟΧΗ: σε generateCrewList η μεταβλητή είναι `onlineDataCL` (όχι onlineData).
      SKIPPER_SIGNED_AT: formatDate(
        (onlineDataCL?.isAlsoSkipper
          ? (onlineDataCL?.skipperSignedAt || onlineDataCL?.chartererSignedAt)
          : onlineDataCL?.skipperSignedAt) || onlineDataCL?.submittedAt || ''
      ),

      // ---- CREW1 = Skipper row ----
      CREW1_NAME:        skipperName,
      CREW1_DOB:         skipperDob,
      CREW1_PASSPORT:    skipperPassport,
      CREW1_GENDER:      skipperGender,
      CREW1_NATIONALITY: skipperNationality,
      CREW1_PHONE:       skipperPhone,
      CREW1_EMAIL:       skipperEmail,

      // ---- CREW2–CREW12 = Passengers 1–11 (crewMembers[0]–[10]) ----
      CREW2_NAME:        paxStr(paxName(pax(0))),
      CREW2_DOB:         formatDate(paxStr(pax(0).dateOfBirth)),
      CREW2_PASSPORT:    paxStr(pax(0).passport),
      CREW2_GENDER:      paxStr(pax(0).gender),
      CREW2_NATIONALITY: paxStr(pax(0).nationality),
      CREW2_PHONE:       paxStr(pax(0).phone),
      CREW2_EMAIL:       paxStr(pax(0).email),

      CREW3_NAME:        paxStr(paxName(pax(1))),
      CREW3_DOB:         formatDate(paxStr(pax(1).dateOfBirth)),
      CREW3_PASSPORT:    paxStr(pax(1).passport),
      CREW3_GENDER:      paxStr(pax(1).gender),
      CREW3_NATIONALITY: paxStr(pax(1).nationality),
      CREW3_PHONE:       paxStr(pax(1).phone),
      CREW3_EMAIL:       paxStr(pax(1).email),

      CREW4_NAME:        paxStr(paxName(pax(2))),
      CREW4_DOB:         formatDate(paxStr(pax(2).dateOfBirth)),
      CREW4_PASSPORT:    paxStr(pax(2).passport),
      CREW4_GENDER:      paxStr(pax(2).gender),
      CREW4_NATIONALITY: paxStr(pax(2).nationality),
      CREW4_PHONE:       paxStr(pax(2).phone),
      CREW4_EMAIL:       paxStr(pax(2).email),

      CREW5_NAME:        paxStr(paxName(pax(3))),
      CREW5_DOB:         formatDate(paxStr(pax(3).dateOfBirth)),
      CREW5_PASSPORT:    paxStr(pax(3).passport),
      CREW5_GENDER:      paxStr(pax(3).gender),
      CREW5_NATIONALITY: paxStr(pax(3).nationality),
      CREW5_PHONE:       paxStr(pax(3).phone),
      CREW5_EMAIL:       paxStr(pax(3).email),

      CREW6_NAME:        paxStr(paxName(pax(4))),
      CREW6_DOB:         formatDate(paxStr(pax(4).dateOfBirth)),
      CREW6_PASSPORT:    paxStr(pax(4).passport),
      CREW6_GENDER:      paxStr(pax(4).gender),
      CREW6_NATIONALITY: paxStr(pax(4).nationality),
      CREW6_PHONE:       paxStr(pax(4).phone),
      CREW6_EMAIL:       paxStr(pax(4).email),

      CREW7_NAME:        paxStr(paxName(pax(5))),
      CREW7_DOB:         formatDate(paxStr(pax(5).dateOfBirth)),
      CREW7_PASSPORT:    paxStr(pax(5).passport),
      CREW7_GENDER:      paxStr(pax(5).gender),
      CREW7_NATIONALITY: paxStr(pax(5).nationality),
      CREW7_PHONE:       paxStr(pax(5).phone),
      CREW7_EMAIL:       paxStr(pax(5).email),

      CREW8_NAME:        paxStr(paxName(pax(6))),
      CREW8_DOB:         formatDate(paxStr(pax(6).dateOfBirth)),
      CREW8_PASSPORT:    paxStr(pax(6).passport),
      CREW8_GENDER:      paxStr(pax(6).gender),
      CREW8_NATIONALITY: paxStr(pax(6).nationality),
      CREW8_PHONE:       paxStr(pax(6).phone),
      CREW8_EMAIL:       paxStr(pax(6).email),

      CREW9_NAME:        paxStr(paxName(pax(7))),
      CREW9_DOB:         formatDate(paxStr(pax(7).dateOfBirth)),
      CREW9_PASSPORT:    paxStr(pax(7).passport),
      CREW9_GENDER:      paxStr(pax(7).gender),
      CREW9_NATIONALITY: paxStr(pax(7).nationality),
      CREW9_PHONE:       paxStr(pax(7).phone),
      CREW9_EMAIL:       paxStr(pax(7).email),

      CREW10_NAME:        paxStr(paxName(pax(8))),
      CREW10_DOB:         formatDate(paxStr(pax(8).dateOfBirth)),
      CREW10_PASSPORT:    paxStr(pax(8).passport),
      CREW10_GENDER:      paxStr(pax(8).gender),
      CREW10_NATIONALITY: paxStr(pax(8).nationality),
      CREW10_PHONE:       paxStr(pax(8).phone),
      CREW10_EMAIL:       paxStr(pax(8).email),

      CREW11_NAME:        paxStr(paxName(pax(9))),
      CREW11_DOB:         formatDate(paxStr(pax(9).dateOfBirth)),
      CREW11_PASSPORT:    paxStr(pax(9).passport),
      CREW11_GENDER:      paxStr(pax(9).gender),
      CREW11_NATIONALITY: paxStr(pax(9).nationality),
      CREW11_PHONE:       paxStr(pax(9).phone),
      CREW11_EMAIL:       paxStr(pax(9).email),

      CREW12_NAME:        paxStr(paxName(pax(10))),
      CREW12_DOB:         formatDate(paxStr(pax(10).dateOfBirth)),
      CREW12_PASSPORT:    paxStr(pax(10).passport),
      CREW12_GENDER:      paxStr(pax(10).gender),
      CREW12_NATIONALITY: paxStr(pax(10).nationality),
      CREW12_PHONE:       paxStr(pax(10).phone),
      CREW12_EMAIL:       paxStr(pax(10).email),
    };

    console.log('📋 Step 4: Auto-fill data prepared:', data);

    // Generate document with docxtemplater
    console.log('📄 Step 5: Creating PizZip...');
    const zip = new PizZip(templateBuffer);
    console.log('📄 Step 6: PizZip created successfully');

    console.log('📄 Step 7: Creating Docxtemplater...');
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '{{', end: '}}' },
      nullGetter: () => ''
    });
    console.log('📄 Step 8: Docxtemplater created successfully');

    // Render with data
    console.log('📄 Step 9: Rendering document...');
    doc.render(data);
    console.log('📄 Step 10: Document rendered successfully');

    // -------------------------------------------------------
    // Post-render: inject arrival time into static text.
    // The template has NO {{ARRIVAL_TIME}} placeholder — the
    // line reads "Arrival time in the marina: ___________".
    // We replace the underscores with the actual time value.
    // -------------------------------------------------------
    const arrivalTime: string = charter.startTime || charter.checkInTime
      || (charter.bookingData as any)?.startTime
      || (charter.bookingData as any)?.checkInTime
      || (charter.bookingData as any)?.checkInHour
      || '';
    console.log('👥 arrivalTime for injection:', arrivalTime,
      { startTime: charter.startTime, checkInTime: charter.checkInTime,
        bdStartTime: (charter.bookingData as any)?.startTime,
        bdCheckInTime: (charter.bookingData as any)?.checkInTime,
        bdCheckInHour: (charter.bookingData as any)?.checkInHour });

    const renderedZip = doc.getZip();
    if (arrivalTime) {
      const xmlFile = 'word/document.xml';
      let xml: string = renderedZip.files[xmlFile].asText();
      // Replace the underscore placeholder that follows "marina: "
      xml = xml.replace(/Arrival time in the marina:\s*_{3,}/g,
        `Arrival time in the marina: ${arrivalTime}`);
      renderedZip.file(xmlFile, xml);
      console.log('👥 Arrival time injected:', arrivalTime);
    }

    // Generate blob
    console.log('📄 Step 11: Generating blob...');
    const blob = renderedZip.generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    console.log('📄 Step 12: Blob generated, size:', blob.size, 'bytes');

    // Download file
    const filename = `Crew-List-${charter.code || 'document'}.docx`;
    console.log('📄 Step 13: Saving file as:', filename);
    
    // === CREW_SIG_INJECT_MARKER: inject skipper signature for Crew List ===
    let finalBlob = blob;
    try {

      // Το booking-level sameAsSkipper (booking_data) είναι authoritative —
      // ο χρήστης το θέτει στο booking. Το archive chartererData.isAlsoSkipper
      // μπορεί να είναι stale (false) ενώ ο online check-in κατέγραψε ΚΕΝΟ
      // (100% διάφανο) skipperSignature canvas → αόρατη υπογραφή. Όταν
      // skipper == charterer χρησιμοποιούμε την πραγματική charterer signature·
      // αλλιώς skipperSignature με fallback στη charterer signature.
      const sameSkipper =
        onlineDataCL?.isAlsoSkipper === true ||
        bd?.sameAsSkipper === true || bd?.sameAsSkipper === 'true' ||
        (charter as any)?.sameAsSkipper === true;
      const skipperSig = sameSkipper
        ? (onlineDataCL?.signature || onlineDataCL?.skipperSignature || '')
        : (onlineDataCL?.skipperSignature || onlineDataCL?.signature || '');
      if (skipperSig && blob && blob.size > 0) {
        console.log('Injecting Crew List skipper signature via server...');
        const fd = new FormData();
        fd.append('docx', blob, 'crew.docx');
        fd.append('skipper_signature', skipperSig);
        fd.append('mode', 'crew_list');
        fd.append('filename', filename);
        const resp = await fetch('/api/inject-signature.php', { method: 'POST', body: fd });
        if (resp.ok) {
          const signed = await resp.blob();
          if (signed && signed.size > 5000) {
            finalBlob = signed;
            console.log('Crew skipper signature injected, size:', signed.size);
          }
        } else {
          console.warn('Crew inject endpoint HTTP:', resp.status);
        }
      }
    } catch (sigErr) {
      console.error('Crew signature injection failed (non-fatal):', sigErr);
    }
    saveAs(finalBlob, filename);

    console.log('✅ Crew List generated and downloaded successfully!');
    if (showMessage) {
      showMessage('✅ Crew List DOCX κατέβηκε επιτυχώς!', 'success');
    }

  } catch (error: any) {
    console.error('❌ Error generating Crew List:', error);
    console.error('❌ Error stack:', error.stack);
    console.error('❌ Error message:', error.message);
    if (showMessage) {
      showMessage('❌ Σφάλμα: ' + error.message, 'error');
    } else {
      alert('❌ Error: ' + error.message);
    }
  }
};

// 🔥 FIX 36: Enhanced email function using professional HTML template service
// 🔥 FIX 38: Updated to use getOwnerByBoatId for owner details from AdminPanel
const sendCharterEmail = async (charter, boat, action) => {
  const boatName = typeof boat === 'string' ? boat : (boat.name || boat.id);
  const boatId = typeof boat === 'string' ? boat : boat.id;

  console.log('📧 sendCharterEmail CALLED (using HTML template service)');
  console.log('📧 Charter:', charter.code);
  console.log('📧 Boat:', boatName, '(ID:', boatId, ')');
  console.log('📧 Action:', action);

  try {
    authService.logActivity(`send_charter_${action}`, charter.code);

    // Map action to status for the email template
    let emailStatus = action;
    if (action === 'option_accepted') emailStatus = 'Option Accepted';
    else if (action === 'pending_final_confirmation') emailStatus = 'Pending Final Confirmation';
    else if (action === 'confirmed') emailStatus = 'Confirmed';
    else if (action === 'cancelled') emailStatus = 'Cancelled';
    else if (action === 'expired') emailStatus = 'Expired';
    else if (action === 'new_charter') emailStatus = 'Option';

    // 🔥 FIX 38: Fetch owner details from AdminPanel-managed data
    const ownerFromAdmin = getOwnerByBoatId(boatId);
    console.log('📧 Owner from AdminPanel:', ownerFromAdmin);

    // Create boat object for email service
    const boatData = {
      name: boatName,
      id: boatId,
      ownerCompany: ownerFromAdmin?.ownerCompany || 'OWNER'
    };

    // Create owner object for email service with full details from AdminPanel
    const ownerData = {
      company: ownerFromAdmin?.ownerCompany || '',
      name: ownerFromAdmin?.ownerName || '',
      email: ownerFromAdmin?.ownerEmail || '',
      phone: ownerFromAdmin?.ownerPhone || '',
      taxId: ownerFromAdmin?.ownerTaxId || '',
      address: ownerFromAdmin?.ownerAddress || ''
    };

    console.log('📧 Owner email will be sent to:', ownerData.email || '(not set - admin notifications only)');

    // 🔥 FIX 36: Use professional HTML email service (NO PDF attachment)
    const success = await sendOwnerCharterEmail(charter, boatData, ownerData, emailStatus);

    if (success) {
      console.log('📧 ✅ Professional HTML email sent successfully!');
    } else {
      console.error('📧 ❌ Failed to send HTML email');
    }

    return success;
  } catch (error) {
    console.error('📧 Email send error:', error);
    return false;
  }
};

// 🔥 FIX 2: Function to determine initial page based on user type
const getInitialPage = (state: any): string => {
  if (state?.userType === 'OWNER' && state?.boatId) {
    return 'dashboard';
  }
  if (state?.userType === 'OWNER' && state?.showSummary) {
    return 'fleetSummary';
  }
  return 'adminDashboard';
};

export default function FleetManagement() {
  const location = useLocation();
  const navigate = useNavigate();

  // 🔥 FIX 2: Use getInitialPage for initial state
  const [page, setPage] = useState(() => getInitialPage(location.state));
  const [currentUser] = useState({ uid: 'admin-user' });
  const [loading, setLoading] = useState(false);
  const [boatData, setBoatData] = useState(null);
  const [searchCharter, setSearchCharter] = useState(null);
  const [pendingEditCharter, setPendingEditCharter] = useState<any>(null);
  const [allBoats, setAllBoats] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  
  const [showFleetSummary, setShowFleetSummary] = useState(false);
  const [fleetBoatIds, setFleetBoatIds] = useState([]);
  const [ownerCode, setOwnerCode] = useState('');
  
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const isAdmin = authService.isAdmin();
  const isOwner = authService.isOwner();
  const isTechnical = authService.isTechnical();
  const isBooking = authService.isBooking();
  const isAccounting = authService.isAccounting();

  // Modal state for AdminDashboard
  const [showAddBoat, setShowAddBoat] = useState(false);
  const [showEmployeeManagement, setShowEmployeeManagement] = useState(false);
  const [showDataManagement, setShowDataManagement] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showFinancials, setShowFinancials] = useState(false);
  const [financialsData, setFinancialsData] = useState({ boats: [], totals: { income: 0, expenses: 0, net: 0 } });

  // 🔥 NEW: State for Page 1 bookings notification
  const [page1BookingsNeedingAmount, setPage1BookingsNeedingAmount] = useState<{
    count: number;
    firstBooking: any | null;
    boatId: string | null;
  }>({ count: 0, firstBooking: null, boatId: null });

  useEffect(() => {
    const user = authService.getCurrentUser();
    setAuthenticatedUser(user);

    if (!user && !location.state) {
      console.log('⚠️ No user logged in, redirecting to homepage');
      navigate('/');
      return;
    }
  }, []);
  
  useEffect(() => {
    const state = location.state;
    
    if (state?.userType === 'OWNER' && state?.ownerCode) {
      const ownerUser = {
        code: state.ownerCode,
        name: `Owner ${state.ownerCode}`,
        role: 'OWNER',
        permissions: null,
        boatIds: state.boatIds || [state.boatId],
        loginTime: new Date().toISOString()
      };
      // 🔥 FIX: Use sessionStorage to match authService
      sessionStorage.setItem('auth_current_user', JSON.stringify(ownerUser));
      setAuthenticatedUser(ownerUser);
      console.log('✅ Owner auto-logged in:', state.ownerCode);
    }
    
    if (state?.userType === 'OWNER' && state?.showSummary && state?.boatIds) {
      const boats = FleetService.getAllBoats();
      const ownerBoats = boats.filter(b => state.boatIds.includes(b.id));
      
      setFleetBoatIds(state.boatIds);
      setOwnerCode(state.ownerCode);
      setShowFleetSummary(true);
      setPage('fleetSummary');
      
      console.log('✅ Owner accessing Fleet Summary for boats:', state.boatIds);
      return;
    }
    
    if (state?.userType === 'OWNER' && state?.boatId) {
      // 🔥 FIX: Use INITIAL_FLEET as fallback if FleetService is empty
      let boats = FleetService.getAllBoats();
      if (boats.length === 0) {
        console.log('⚠️ FleetService empty, using INITIAL_FLEET');
        boats = INITIAL_FLEET;
      }

      const boat = boats.find(b => b.id === state.boatId);

      if (boat) {
        setBoatData(boat);
        setOwnerCode(state.ownerCode);
        setShowFleetSummary(false);
        setPage('dashboard');

        console.log('✅ Owner viewing boat dashboard:', boat.id, boat);
      } else {
        console.error('❌ Boat not found:', state.boatId, 'Available boats:', boats.map(b => b.id));
      }
      return;
    }
    
    if (state?.userType === 'COMPANY' && state?.isAdmin) {
      setShowFleetSummary(false);
      setPage('adminDashboard');
      loadBoats();
      
      console.log('✅ Admin logged in automatically');
      return;
    }
  }, [location]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handleOpenCharterByCode = async (code) => {
    try {
      const r = await fetch(`/api/bookings.php?action=search_by_code&code=${encodeURIComponent(code)}`);
      const j = await r.json();
      if (j && j.booking && j.booking.booking_data) {
        const bd = j.booking.booking_data;
        setSearchCharter({ ...bd, code: bd.code || j.booking.booking_number, id: bd.id || j.booking.booking_number });
      } else { showMessage('Δεν βρέθηκε ναύλος: ' + code, 'info'); }
    } catch (e) { showMessage('Σφάλμα αναζήτησης ναύλου', 'error'); }
  };

  // Search-path payments save (mirrors handleUpdatePayments without local items/boat scope)
  const handleSearchUpdatePayments = async (newPayments) => {
    try {
      const totalPaid = (newPayments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
      const amount = parseFloat(searchCharter?.amount || searchCharter?.totalPrice || '0') || 0;
      let status = 'ΑΝΕΞΟΦΛΗΤΟ';
      if (totalPaid >= amount - 0.05 && amount > 0) status = 'ΕΞΟΦΛΗΜΕΝΟ';
      else if (totalPaid > 0) status = 'ΜΕΡΙΚΗ ΠΛΗΡΩΜΗ';
      await updateCharterPayments(searchCharter.code, newPayments, status);
      setSearchCharter(prev => ({ ...prev, payments: newPayments, paymentStatus: status }));
      showMessage('✅ Οι πληρωμές αποθηκεύτηκαν!', 'success');
    } catch (e) {
      showMessage('❌ Σφάλμα αποθήκευσης πληρωμών', 'error');
    }
  };

  useEffect(() => {
    globalShowMessage = showMessage;
  }, []);

  useEffect(() => {
    loadBoats();
  }, []);

  // 🔥 FIX: Load boat data when allBoats is populated (handles owner navigation)
  useEffect(() => {
    const state = location.state;
    if (!boatData && state?.userType === 'OWNER' && state?.boatId) {
      // Use allBoats if available, otherwise use INITIAL_FLEET
      let boats = allBoats.length > 0 ? allBoats : INITIAL_FLEET;

      const boat = boats.find(b => b.id === state.boatId);
      if (boat) {
        setBoatData(boat);
        setOwnerCode(state.ownerCode);
        setPage('dashboard');
        console.log('✅ Boat data loaded for owner:', boat.id, boat);
      } else {
        console.error('❌ Boat not found in allBoats/INITIAL_FLEET:', state.boatId);
      }
    }
  }, [allBoats, boatData, location.state]);

  useEffect(() => {
    if (page === 'adminDashboard') {
      loadBoats();
    }
  }, [page]);

  useEffect(() => {
    if (showFinancials && allBoats.length > 0) {
      loadFinancialsData();
    }
  }, [showFinancials, allBoats]);

  // 🔥 NEW: Scan for Page 1 bookings needing financial details
  useEffect(() => {
    const scanForPage1Bookings = async () => {
      let totalCount = 0;
      let firstBookingFound: any = null;
      let firstBoatId: string | null = null;

      // Get all boats
      const boats = FleetService.getAllBoats();

      // 🔥 API-only: Get all bookings from API
      try {
        const allBookings = await getAllBookings();

        for (const boat of boats) {
          // Filter bookings for this boat
          const charters = allBookings.filter((b: any) =>
            b.vesselId === boat.id ||
            b.vesselName?.toLowerCase() === boat.name?.toLowerCase()
          );

          // Find Page 1 bookings without amount
          const page1BookingsNeedingDetails = charters.filter((c: any) =>
            (c.source === 'page1' || c.status === 'Draft') &&
            (!c.amount || c.amount === 0)
          );

          if (page1BookingsNeedingDetails.length > 0) {
            totalCount += page1BookingsNeedingDetails.length;

            // Save first booking found for quick navigation
            if (!firstBookingFound) {
              firstBookingFound = page1BookingsNeedingDetails[0];
              firstBoatId = boat.id;
            }

            console.log(`📋 Boat ${boat.name}: ${page1BookingsNeedingDetails.length} Page 1 booking(s) need financial details`);
          }
        }
      } catch (e) {
        console.warn('Error scanning for Page 1 bookings:', e);
      }

      setPage1BookingsNeedingAmount({
        count: totalCount,
        firstBooking: firstBookingFound,
        boatId: firstBoatId
      });

      if (totalCount > 0) {
        console.log(`🔔 NOTIFICATION: ${totalCount} Page 1 booking(s) need financial details`);
      }
    };

    // Scan on mount and when page changes
    scanForPage1Bookings();

    // Re-scan every 30 seconds for new bookings
    const interval = setInterval(scanForPage1Bookings, 30000);
    return () => clearInterval(interval);
  }, [page, allBoats]);

  // 🔥 Handler to navigate to first Page 1 booking needing details
  const handleNotificationClick = () => {
    if (page1BookingsNeedingAmount.boatId && page1BookingsNeedingAmount.firstBooking) {
      const boats = FleetService.getAllBoats();
      const boat = boats.find(b => b.id === page1BookingsNeedingAmount.boatId);

      if (boat) {
        setBoatData(boat);
        setSelectedCategory('ΝΑΥΛΑ');  // 🔥 FIX: Use string not object
        setPage('details');
        showMessage(`📝 Μεταφορά στο ${boat.name} - Ναύλος: ${page1BookingsNeedingAmount.firstBooking.code}`, 'info');
      }
    }
  };

  const loadBoats = () => {
    try {
      const boats = FleetService.getAllBoats();
      setAllBoats(boats);
    } catch (e) {
      console.error('Error loading boats:', e);
    }
  };

  const handleBoatAdded = () => {
    loadBoats();
  };

  // 🔥 FIX 16: Load financials from API first, merge with localStorage
  const loadFinancialsData = async () => {
    let totalIncome = 0;
    let totalExpenses = 0;
    const boatsData: any[] = [];

    console.log('🔍 FleetManagement: Scanning for Page 1 bookings...');
    console.log('📦 Available boats:', allBoats.map(b => `${b.id}:${b.name}`));

    // Load all boats in parallel for better performance
    await Promise.all(allBoats.map(async (boat: any) => {
      // Load charters from API ONLY
      let charters: any[] = [];
      try {
        charters = await getBookingsByVessel(boat.id);
        console.log(`📂 ${boat.name}: ${charters.length} charters from API`);
      } catch (e) {
        console.error(`❌ ${boat.name}: API failed, no charters loaded`);
        charters = [];
      }

      // Load invoices (localStorage only for now)
      const invoicesKey = `fleet_${boat.id}_ΤΙΜΟΛΟΓΙΑ`;
      const invoicesStored = localStorage.getItem(invoicesKey);
      const invoices = invoicesStored ? JSON.parse(invoicesStored) : [];

      const boatIncome = charters.reduce((sum: number, c: any) => sum + (c.amount || 0), 0);
      const charterExpenses = charters.reduce((sum: number, c: any) => sum + (c.commission || 0) + (c.vat_on_commission || 0), 0);
      const invoiceExpenses = invoices.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
      const boatExpenses = charterExpenses + invoiceExpenses;
      const boatNet = boatIncome - boatExpenses;

      // 🔥 Count Page 1 bookings (Draft status or source=page1)
      const draftCount = charters.filter((c: any) => c.status === 'Draft' || c.source === 'page1').length;

      // 🔥 DEBUG: Log Page 1 bookings per boat
      if (draftCount > 0) {
        console.log(`📋 BOAT ${boat.name}: ${draftCount} Page 1 booking(s)`,
          charters.filter((c: any) => c.status === 'Draft' || c.source === 'page1').map((c: any) => c.code)
        );
      }

      totalIncome += boatIncome;
      totalExpenses += boatExpenses;

      boatsData.push({
        id: boat.id,
        name: boat.name,
        income: boatIncome,
        expenses: boatExpenses,
        net: boatNet,
        chartersCount: charters.length,
        invoicesCount: invoices.length,
        draftCount: draftCount  // 🔥 Page 1 booking count
      });
    }));

    setFinancialsData({
      boats: boatsData,
      totals: {
        income: totalIncome,
        expenses: totalExpenses,
        net: totalIncome - totalExpenses
      }
    });
    console.log('✅ FleetManagement: Financials loaded from API');
  };

  const handleLogout = () => {
    authService.logActivity('logout_fleet_management');
    authService.logout();
    navigate('/');
  };

  const handleAdminSelectBoat = (boat) => {
    setBoatData(boat);
    setShowFleetSummary(false);
    setPage('dashboard');
    
    authService.logActivity('select_boat', boat.id);
  };

  const navigatePage = (pageName) => {
    // Πίσω στο boat dashboard (από εσωτερικές σελίδες όπως ΕΡΓΑΣΙΕΣ, BOOKING SHEET κλπ)
    if (pageName === 'boatDashboard') {
      setPage('dashboard');
      return;
    }
    
    // Admin πατάει σπιτάκι -> adminDashboard
    if (isAdmin && pageName === 'dashboard') {
      setPage('adminDashboard');
      setBoatData(null);
      setShowFleetSummary(false);
      return;
    }
    
    // Owner με fleet summary πάει στο owner-dashboard
    if (isOwner && showFleetSummary && pageName === 'dashboard') {
      navigate('/owner-dashboard', { state: { ownerCode: ownerCode } });
      return;
    }
    
    // Owner με ownerCode πάει στο owner-dashboard
    if (isOwner && ownerCode && pageName === 'dashboard') {
      navigate('/owner-dashboard', { state: { ownerCode: ownerCode } });
      return;
    }
    
    // TECHNICAL, BOOKING, ACCOUNTING - πάνε στο adminDashboard (boat list)
    if ((isTechnical || isBooking || isAccounting) && pageName === 'dashboard') {
      setPage('adminDashboard');
      setBoatData(null);
      return;
    }
    
    // Messages page
    if (pageName === 'messages' && boatData) {
      setPage('messages');
      return;
    }
    
    if (pageName === 'details' || pageName === 'bookingSheet' || pageName === 'email' || pageName === 'documents') return;
    setSelectedCategory('');
    setPage(pageName);
  };

  const selectCategory = (categoryName) => {
    setSelectedCategory(categoryName);

    authService.logActivity('select_category', categoryName);

    if (categoryName === 'ΟΙΚΟΝΟΜΙΚΑ') {
      setPage('financials');
    } else if (categoryName === 'ΑΠΟΣΤΟΛΗ E-MAIL') {
      setPage('email');
    } else if (categoryName === 'BOOKING SHEET') {
      setPage('bookingSheet');
    } else if (categoryName === 'ΕΓΓΡΑΦΑ & ΣΤΟΙΧΕΙΑ') {
      setPage('documents');
    } else if (categoryName === 'ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ') {
      // 🔥 FIX 37: Navigate to owner details page
      setPage('ownerDetails');
    } else {
      setPage('details');
    }
  };

  const renderPage = () => {
    if (loading) return <FullScreenLoader />;

    switch (page) {
      case 'adminDashboard':
        return <AdminDashboard
          boats={allBoats}
          onSelectBoat={handleAdminSelectBoat}
          onOpenCharterByCode={handleOpenCharterByCode}
          onHome={handleLogout}
          navigate={setPage}
          loadBoats={loadBoats}
          showAddBoat={showAddBoat}
          setShowAddBoat={setShowAddBoat}
          showEmployeeManagement={showEmployeeManagement}
          setShowEmployeeManagement={setShowEmployeeManagement}
          showDataManagement={showDataManagement}
          setShowDataManagement={setShowDataManagement}
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          showFinancials={showFinancials}
          setShowFinancials={setShowFinancials}
        />;
      case 'fleetSummary':
        return <FleetSummaryPage boatIds={fleetBoatIds} ownerCode={ownerCode} navigate={navigatePage} showMessage={showMessage} />;
      case 'dashboard':
        return <DashboardPage boat={boatData} onSelectCategory={selectCategory} navigate={navigatePage} ownerCode={ownerCode} />;
      case 'details':
        return <DetailsPage boat={boatData} category={selectedCategory} navigate={navigatePage} showMessage={showMessage} initialEditCharter={pendingEditCharter} onConsumedEdit={() => setPendingEditCharter(null)} />;
      case 'financials':
        return <FinancialsPage boat={boatData} navigate={navigatePage} setPage={setPage} setSelectedCategory={setSelectedCategory} showMessage={showMessage} />;
      case 'bookingSheet':
        return <BookingSheetPage boat={boatData} navigate={navigatePage} showMessage={showMessage} />;
      case 'messages':
        return <MessagesPage boat={boatData} currentUser={currentUser} navigate={navigatePage} showMessage={showMessage} />;
      case 'email':
        return <EmailPage boat={boatData} navigate={navigatePage} />;
      case 'documents':
        return <DocumentsAndDetailsPage boat={boatData} navigate={navigatePage} showMessage={showMessage} />;
      case 'ownerDetails':
        // 🔥 FIX 37: Owner details page
        return <OwnerDetailsPage boat={boatData} navigate={navigatePage} showMessage={showMessage} />;
      case 'fleetBookingPlan':
        return <FleetBookingPlanPage navigate={setPage} showMessage={showMessage} />;
      default:
        return <p>Unknown page</p>;
    }
  };

  return (
    <div className="h-screen w-screen bg-[#f3f4f6] text-[#374151] font-sans">
      <MessageDisplay message={message} />

      {/* 🔥 NEW: Page 1 Bookings Notification Banner */}
      {page1BookingsNeedingAmount.count > 0 && (
        <div
          onClick={handleNotificationClick}
          className="fixed top-0 left-0 right-0 z-50 bg-[#1e40af] text-white py-3 px-4 cursor-pointer hover:bg-blue-800 transition-all shadow-lg border-b border-[#d1d5db]"
        >
          <div className="w-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl animate-bounce">🔔</span>
              <div>
                <p className="font-bold text-sm">
                  {page1BookingsNeedingAmount.count} νέα ναύλα από Check-in
                </p>
                <p className="text-xs text-blue-200">
                  Χρειάζονται συμπλήρωση τιμής - Πατήστε για επεξεργασία
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                {page1BookingsNeedingAmount.count}
              </span>
              <span className="text-xl">→</span>
            </div>
          </div>
        </div>
      )}

      <div className={`min-h-screen w-full bg-[#f3f4f6] overflow-hidden ${page1BookingsNeedingAmount.count > 0 ? 'pt-16' : ''}`}>
        {renderPage()}
      </div>

      {/* Admin Dashboard Modals */}
      {showAddBoat && (
        <AddBoatModal
          onClose={() => setShowAddBoat(false)}
          onBoatAdded={handleBoatAdded}
        />
      )}

      {showEmployeeManagement && (
        <EmployeeManagementModal
          onClose={() => setShowEmployeeManagement(false)}
        />
      )}

      {showDataManagement && (
        <DataManagementModal
          onClose={() => setShowDataManagement(false)}
          boats={allBoats}
          onDataCleared={() => { loadBoats(); loadFinancialsData(); }}
        />
      )}

      {showActivityLog && (
        <ActivityLogModal
          onClose={() => setShowActivityLog(false)}
        />
      )}

      {showFinancials && (
        <FinancialsSummaryModal
          onClose={() => setShowFinancials(false)}
          financialsData={financialsData}
          boats={allBoats}
        />
      )}

      {searchCharter && (
        <CharterDetailModal
          key={searchCharter.id || searchCharter.code}
          charter={searchCharter}
          boat={(FleetService.getAllBoats ? FleetService.getAllBoats() : allBoats || []).find(b => b.id === searchCharter.vesselId || b.name === searchCharter.vesselName) || { id: searchCharter.vesselId || '', name: searchCharter.vesselName || '' }}
          canViewFinancials={authService.canViewFinancials ? authService.canViewFinancials() || isOwner : isOwner}
          canEditCharters={isAdmin || isBooking}
          canAcceptCharter={false}
          isOwnerUser={authService.isOwner ? authService.isOwner() : isOwner}
          onClose={() => setSearchCharter(null)}
          onDelete={() => {}}
          onUpdateStatus={() => {}}
          onUpdatePayments={handleSearchUpdatePayments}
          showMessage={showMessage}
          onEdit={() => {
              const _b = (FleetService.getAllBoats ? FleetService.getAllBoats() : allBoats || []).find(b => b.id === searchCharter.vesselId || b.name === searchCharter.vesselName);
              const _charter = searchCharter;
              setSearchCharter(null);
              if (_b) { setBoatData(_b); setShowFleetSummary(false); setSelectedCategory('ΝΑΥΛΑ'); setPage('details'); setPendingEditCharter(_charter); }
              else { showMessage('Δεν βρέθηκε το σκάφος του ναύλου.', 'error'); }
            }}
        />
      )}
    </div>
  );
}

function MessageDisplay({ message }) {
  if (!message.text) return null;
  const bgColor = message.type === 'error' ? 'bg-red-600' : 'bg-green-600';
  return (
    <div className={`fixed top-5 left-1/2 -translate-x-1/2 p-3 rounded-lg text-white font-semibold shadow-lg z-50 ${bgColor} animate-pulse`}>
      {message.text}
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="flex items-center justify-center h-full bg-[#f3f4f6]">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1e40af]"></div>
    </div>
  );
}

function Header({ title, onBack, onHome = null, lightTheme = false, subtitle = null }) {
  const user = authService.getCurrentUser();

  // Light theme colors
  const headerBg = lightTheme ? 'bg-[#1e40af]' : 'bg-white';
  const headerBorder = lightTheme ? 'border-[#d1d5db]' : 'border-[#d1d5db]';
  const titleColor = lightTheme ? 'text-white' : 'text-[#374151]';
  const userColor = lightTheme ? 'text-blue-200' : 'text-[#1e40af]';
  const subtitleColor = lightTheme ? 'text-blue-100' : 'text-[#6b7280]';
  const buttonHover = lightTheme ? 'hover:bg-blue-700' : 'hover:bg-[#f9fafb]';
  const backButtonColor = lightTheme ? 'text-white' : 'text-[#1e40af]';

  return (
    <div className={`${headerBg} p-4 shadow-md flex items-center justify-between sticky top-0 z-20 w-full border-b ${headerBorder}`}>
      {/* LEFT: Home button - styled with house icon */}
      {onHome && (
        <button
          onClick={onHome}
          className="bg-[#1e40af] hover:bg-blue-700 border border-blue-400 rounded-lg px-3 py-2 transition-colors flex flex-col items-center min-w-[60px]"
          title="Αρχική"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span className="text-[10px] text-white mt-1 font-medium">Αρχική</span>
        </button>
      )}
      {!onHome && <div className="w-16"></div>}

      <div className="flex-grow text-center">
        <h1 className={`text-xl font-bold ${titleColor} truncate px-2`}>{title}</h1>
        {user && (
          <div className="flex items-center justify-center gap-2 mt-1">
            {user.role === 'OWNER' ? icons.eye : icons.shield}
            <span className={`text-xs ${userColor} font-semibold`}>
              {user.role === 'OWNER' ? `${user.name} (View Only)` : user.name}
            </span>
          </div>
        )}
        {subtitle && (
          <div className="text-xl font-semibold text-[#1e40af] mt-1">
            {subtitle}
          </div>
        )}
      </div>

      {/* RIGHT: Return arrow - just navigate back, NO logout */}
      {onBack && (
        <button
          onClick={onBack}
          className={`${backButtonColor} p-2 -mr-2 ${buttonHover} rounded-lg transition-colors flex flex-col items-center`}
          title="Πίσω (παραμένετε συνδεδεμένοι)"
        >
          {icons.returnArrow}
          <span className="text-[10px] mt-0.5">Return</span>
        </button>
      )}
      {!onBack && <div className="w-12"></div>}
    </div>
  );
}

function BottomNav({ activePage, onNavigate, lightTheme = false }) {
  const items = [
    { name: 'dashboard', label: 'Αρχική', icon: icons.home },
    { name: 'messages', label: 'Μηνύματα', icon: icons.message },
  ];

  // Light theme colors - white background with blue/gray icons
  const navBg = 'bg-white';
  const navBorder = 'border-[#d1d5db]';
  const activeColor = 'text-[#1e40af] bg-[#f3f4f6]';
  const inactiveColor = 'text-[#6b7280]';
  const hoverColor = 'hover:text-[#1e40af]';

  return (
    <div className={`${navBg} p-2 shadow-inner-top flex justify-around sticky bottom-0 z-20 w-full border-t ${navBorder}`}>
      {items.map((item) => (
        <button key={item.name} onClick={() => onNavigate(item.name)} className={`flex flex-col items-center justify-center p-2 rounded-lg w-24 ${activePage === item.name ? activeColor : inactiveColor} ${hoverColor} transition-colors`}>
          {item.icon}
          <span className="text-xs mt-1">{item.label}</span>
        </button>
      ))}
    </div>
  );
}

function AddBoatModal({ onClose, onBoatAdded }) {
  const [newBoat, setNewBoat] = useState({
    id: '',
    name: '',
    type: 'Monohull',
    model: '',
    // 🔥 FIX 37: Owner details fields
    ownerCode: '',
    ownerName: '',
    ownerEmail: '',
    ownerCompany: '',
    ownerTaxId: '',
    ownerPhone: '',
    ownerAddress: ''
  });
  const [error, setError] = useState('');
  const [existingOwners, setExistingOwners] = useState<any[]>([]);

  // 🔥 FIX 37: Load existing owners for auto-fill
  useEffect(() => {
    const owners = authService.getAllOwnerCodes();
    setExistingOwners(owners);
  }, []);

  // 🔥 FIX 37: Auto-fill owner details when owner code is selected
  const handleOwnerCodeChange = (code: string) => {
    setNewBoat(prev => ({ ...prev, ownerCode: code }));

    if (code) {
      // Find existing owner with this code
      const existingOwner = existingOwners.find(o => codeMatches(o.code, code));
      if (existingOwner) {
        setNewBoat(prev => ({
          ...prev,
          ownerCode: code,
          ownerName: existingOwner.ownerName || '',
          ownerEmail: existingOwner.ownerEmail || '',
          ownerCompany: existingOwner.ownerCompany || '',
          ownerTaxId: existingOwner.ownerTaxId || '',
          ownerPhone: existingOwner.ownerPhone || '',
          ownerAddress: existingOwner.ownerAddress || ''
        }));
      }
    }
  };

  if (!authService.canManageFleet()) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-[#374151] mb-6">You don't have permission to add boats.</p>
          <button onClick={onClose} className="px-6 py-3 bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold rounded-lg">
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!newBoat.id.trim() || !newBoat.name.trim()) {
      setError('ID και Όνομα είναι υποχρεωτικά!');
      return;
    }

    // 🔥 FIX 37: Validate email if provided
    if (newBoat.ownerEmail && !newBoat.ownerEmail.includes('@')) {
      setError('Παρακαλώ εισάγετε έγκυρο email!');
      return;
    }

    try {
      // 🔥 FIX 37: Add boat with owner details
      FleetService.addBoat({
        id: newBoat.id.trim(),
        name: newBoat.name.trim(),
        type: newBoat.type,
        model: newBoat.model.trim(),
        ownerCode: newBoat.ownerCode.trim(),
        ownerName: newBoat.ownerName.trim(),
        ownerEmail: newBoat.ownerEmail.trim(),
        ownerCompany: newBoat.ownerCompany.trim(),
        ownerTaxId: newBoat.ownerTaxId.trim(),
        ownerPhone: newBoat.ownerPhone.trim(),
        ownerAddress: newBoat.ownerAddress.trim()
      });

      globalShowMessage(`Το σκάφος ${newBoat.id} προστέθηκε επιτυχώς!`, 'success');
      onBoatAdded();
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 my-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#1e40af]">Προσθήκη Νέου Σκάφους</h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-white">{icons.x}</button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 border border-red-600 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">
              ID Σκάφους <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newBoat.id}
              onChange={(e) => setNewBoat({ ...newBoat, id: e.target.value.toUpperCase() })}
              placeholder="π.χ. APOLLO"
              className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
            />
            <p className="text-xs text-[#6b7280] mt-1">Μοναδικός κωδικός για σύνδεση</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">
              Πλήρες Όνομα <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newBoat.name}
              onChange={(e) => setNewBoat({ ...newBoat, name: e.target.value })}
              placeholder="π.χ. Bavaria 50-APOLLO"
              className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">
              Τύπος <span className="text-red-500">*</span>
            </label>
            <select
              value={newBoat.type}
              onChange={(e) => setNewBoat({ ...newBoat, type: e.target.value })}
              className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
            >
              <option value="Catamaran">Catamaran</option>
              <option value="Monohull">Monohull</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-2">
              Μοντέλο (προαιρετικό)
            </label>
            <input
              type="text"
              value={newBoat.model}
              onChange={(e) => setNewBoat({ ...newBoat, model: e.target.value })}
              placeholder="π.χ. Bavaria 50"
              className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
            />
          </div>

          {/* 🔥 FIX 37: Owner Details Section */}
          <div className="border-t border-[#1e40af] pt-4 mt-4">
            <h3 className="text-lg font-bold text-[#1e40af] mb-3">👤 Στοιχεία Ιδιοκτήτη</h3>

            {/* Owner Code Selection with Auto-fill */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-[#374151] mb-2">
                Κωδικός Ιδιοκτήτη (υπάρχων)
              </label>
              <select
                value={newBoat.ownerCode}
                onChange={(e) => handleOwnerCodeChange(e.target.value)}
                className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
              >
                <option value="">-- Επιλέξτε ή εισάγετε νέο --</option>
                {existingOwners.map(owner => (
                  <option key={owner.code} value={owner.code}>
                    {owner.code} {owner.ownerName ? `(${owner.ownerName})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[#6b7280] mt-1">Επιλέξτε για αυτόματη συμπλήρωση στοιχείων</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Όνομα Ιδιοκτήτη
                </label>
                <input
                  type="text"
                  value={newBoat.ownerName}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerName: e.target.value })}
                  placeholder="Ονοματεπώνυμο"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1e40af] mb-2">
                  Email Ιδιοκτήτη ⭐
                </label>
                <input
                  type="email"
                  value={newBoat.ownerEmail}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerEmail: e.target.value })}
                  placeholder="owner@email.com"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border-2 border-[#1e40af] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Εταιρεία
                </label>
                <input
                  type="text"
                  value={newBoat.ownerCompany}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerCompany: e.target.value })}
                  placeholder="Εταιρεία ΕΠΕ"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  ΑΦΜ
                </label>
                <input
                  type="text"
                  value={newBoat.ownerTaxId}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerTaxId: e.target.value })}
                  placeholder="123456789"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Τηλέφωνο
                </label>
                <input
                  type="tel"
                  value={newBoat.ownerPhone}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerPhone: e.target.value })}
                  placeholder="+30 697 1234567"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Διεύθυνση
                </label>
                <input
                  type="text"
                  value={newBoat.ownerAddress}
                  onChange={(e) => setNewBoat({ ...newBoat, ownerAddress: e.target.value })}
                  placeholder="Οδός, Πόλη, ΤΚ"
                  className="w-full px-4 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold rounded-lg transition duration-200"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#1e40af] hover:bg-blue-800 text-white font-bold rounded-lg transition duration-200"
            >
              Προσθήκη
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================
// EMPLOYEE MANAGEMENT MODAL
// =====================================================
function EmployeeManagementModal({ onClose }) {
  const [employees, setEmployees] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    code: '',
    name: '',
    role: 'TECHNICAL',
    canEdit: true,
    canDelete: false,
    canManageFleet: false,
    canClearData: false,
    canManageCodes: false,
    canViewFinancials: false,
    canEditFinancials: false,
    canDoCheckInOut: true,
    canManageTasks: true
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    const codes = await authService.fetchEmployeesFromAPI().catch(() => authService.getAllEmployeeCodes());
    setEmployees(codes);
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'ADMIN': return 'bg-red-600';
      case 'TECHNICAL': return 'bg-blue-600';
      case 'BOOKING': return 'bg-green-600';
      case 'ACCOUNTING': return 'bg-yellow-600';
      case 'CLEANING': return 'bg-teal-600';
      default: return 'bg-gray-600';
    }
  };

  const getRolePermissions = (role) => {
    switch(role) {
      case 'ADMIN':
        return {
          canEdit: true, canDelete: true, canManageFleet: true, canClearData: true,
          canManageCodes: true, canViewFinancials: true, canEditFinancials: true,
          canDoCheckInOut: true, canManageTasks: true
        };
      case 'TECHNICAL':
        return {
          canEdit: true, canDelete: false, canManageFleet: false, canClearData: false,
          canManageCodes: false, canViewFinancials: false, canEditFinancials: false,
          canDoCheckInOut: true, canManageTasks: true
        };
      case 'BOOKING':
        return {
          canEdit: true, canDelete: false, canManageFleet: false, canClearData: false,
          canManageCodes: false, canViewFinancials: true, canEditFinancials: false,
          canDoCheckInOut: true, canManageTasks: false
        };
      case 'ACCOUNTING':
        return {
          canEdit: true, canDelete: false, canManageFleet: false, canClearData: false,
          canManageCodes: false, canViewFinancials: true, canEditFinancials: true,
          canDoCheckInOut: false, canManageTasks: false
        };
      case 'CLEANING':
        return {
          canEdit: false, canDelete: false, canManageFleet: false, canClearData: false,
          canManageCodes: false, canViewFinancials: false, canEditFinancials: false,
          canDoCheckInOut: false, canManageTasks: false, canViewCleaning: true
        };
      default:
        return {};
    }
  };

  const handleRoleChange = (role) => {
    const permissions = getRolePermissions(role);
    setNewEmployee(prev => ({ ...prev, role, ...permissions }));
  };

  const generateCode = () => {
    const prefixes = { ADMIN: 'ADM', TECHNICAL: 'TEC', BOOKING: 'BOOK', ACCOUNTING: 'ACC', CLEANING: 'CLEAN' };
    const prefix = prefixes[newEmployee.role] || 'EMP';
    const num = String(employees.filter(e => e.role === newEmployee.role).length + 1).padStart(3, '0');
    const name = newEmployee.name.toUpperCase().replace(/\s/g, '');
    return `${prefix}${num}!${name}`;
  };

  const handleChangeCode = async (oldCode: string, currentName: string) => {
    const newCode = prompt(`Νέος κωδικός για "${currentName}":`, oldCode);
    if (!newCode || !newCode.trim() || newCode.trim() === oldCode) return;
    const success = await authService.updateEmployeeCode(oldCode, { code: newCode.trim() });
    if (success) {
      loadEmployees();
      globalShowMessage(`Κωδικός άλλαξε: ${oldCode} → ${newCode.trim()}`, 'success');
    } else {
      globalShowMessage('❌ Σφάλμα αλλαγής κωδικού!', 'error');
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name.trim()) {
      globalShowMessage('❌ Παρακαλώ εισάγετε όνομα!', 'error');
      return;
    }

    const code = newEmployee.code || generateCode();

    const success = await authService.addEmployeeCode({
      code: code,
      name: newEmployee.name.trim(),
      role: newEmployee.role,
      canEdit: newEmployee.canEdit,
      canDelete: newEmployee.canDelete,
      canManageFleet: newEmployee.canManageFleet,
      canClearData: newEmployee.canClearData,
      canManageCodes: newEmployee.canManageCodes,
      canViewFinancials: newEmployee.canViewFinancials,
      canEditFinancials: newEmployee.canEditFinancials,
      canDoCheckInOut: newEmployee.canDoCheckInOut,
      canManageTasks: newEmployee.canManageTasks
    });

    if (success) {
      globalShowMessage(`✅ Υπάλληλος ${newEmployee.name} προστέθηκε!`, 'success');
      loadEmployees();
      setShowAddForm(false);
      setNewEmployee({
        code: '', name: '', role: 'TECHNICAL',
        canEdit: true, canDelete: false, canManageFleet: false, canClearData: false,
        canManageCodes: false, canViewFinancials: false, canEditFinancials: false,
        canDoCheckInOut: true, canManageTasks: true
      });
    } else {
      globalShowMessage('❌ Σφάλμα προσθήκης!', 'error');
    }
  };

  const handleToggleEmployee = async (code) => {
    await authService.toggleEmployeeCode(code);
    loadEmployees();
    globalShowMessage('✅ Κατάσταση άλλαξε!', 'success');
  };

  const handleDeleteEmployee = async (code, name) => {
    if (code === 'ADMIN2025') {
      globalShowMessage('❌ Δεν μπορείτε να διαγράψετε τον κύριο Admin!', 'error');
      return;
    }

    if (window.confirm(`Διαγραφή υπαλλήλου ${name};`)) {
      await authService.deleteEmployeeCode(code);
      loadEmployees();
      globalShowMessage('✅ Υπάλληλος διαγράφηκε!', 'success');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-[#d1d5db]">
          <h2 className="text-2xl font-bold text-purple-400 flex items-center gap-2">
            {icons.shield}
            <span>Διαχείριση Υπαλλήλων</span>
          </h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-white p-2">{icons.x}</button>
        </div>

        <div className="p-4 border-b border-[#d1d5db]">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
          >
            {icons.plus}
            <span>{showAddForm ? 'Ακύρωση' : 'Προσθήκη Νέου Υπαλλήλου'}</span>
          </button>

          {showAddForm && (
            <div className="mt-4 p-4 bg-[#f3f4f6] rounded-lg space-y-4 border border-[#d1d5db]">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Όνομα *</label>
                <input
                  type="text"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="π.χ. Γιάννης"
                  className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Κωδικός (προαιρετικό)</label>
                <input
                  type="text"
                  value={newEmployee.code}
                  onChange={(e) => setNewEmployee((prev: any) => ({ ...prev, code: e.target.value }))}
                  placeholder="Αυτόματος αν μείνει κενό"
                  className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Ρόλος *</label>
                <select
                  value={newEmployee.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="TECHNICAL">🔧 TECHNICAL - Τεχνικός</option>
                  <option value="BOOKING">📅 BOOKING - Κρατήσεις</option>
                  <option value="ACCOUNTING">💰 ACCOUNTING - Λογιστήριο</option>
                  <option value="CLEANING">🧽 CLEANING - Συνεργείο Καθαρισμού</option>
                  <option value="ADMIN">👑 ADMIN - Διαχειριστής</option>
                </select>
              </div>

              <div className="p-3 bg-white rounded-lg border border-[#d1d5db]">
                <p className="text-sm font-semibold text-[#374151] mb-2">Permissions για {newEmployee.role}:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={newEmployee.canDoCheckInOut ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canDoCheckInOut ? '✅' : '❌'} Check-in/out
                  </div>
                  <div className={newEmployee.canManageTasks ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canManageTasks ? '✅' : '❌'} Εργασίες
                  </div>
                  <div className={newEmployee.canViewFinancials ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canViewFinancials ? '✅' : '❌'} Οικονομικά (view)
                  </div>
                  <div className={newEmployee.canEditFinancials ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canEditFinancials ? '✅' : '❌'} Οικονομικά (edit)
                  </div>
                  <div className={newEmployee.canManageFleet ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canManageFleet ? '✅' : '❌'} Fleet Management
                  </div>
                  <div className={newEmployee.canManageCodes ? 'text-green-400' : 'text-red-400'}>
                    {newEmployee.canManageCodes ? '✅' : '❌'} Διαχ. Κωδικών
                  </div>
                </div>
              </div>

              <div className="p-3 bg-purple-900 rounded-lg border border-purple-700">
                <p className="text-sm text-purple-200">
                  <span className="font-bold">Κωδικός:</span> {generateCode() || 'θα δημιουργηθεί αυτόματα'}
                </p>
              </div>

              <button
                onClick={handleAddEmployee}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg"
              >
                💾 Αποθήκευση Υπαλλήλου
              </button>
            </div>
          )}
        </div>

        <div className="flex-grow overflow-y-auto p-4">
          <h3 className="text-lg font-semibold text-[#374151] mb-3">
            Υπάλληλοι ({employees.length})
          </h3>
          
          <div className="space-y-2">
            {employees.map(emp => (
              <div 
                key={emp.code} 
                className={`p-4 rounded-lg border ${emp.enabled ? 'bg-[#f3f4f6] border-[#d1d5db]' : 'bg-gray-100 border-red-900 opacity-60'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`px-2 py-1 rounded text-xs font-bold text-white ${getRoleColor(emp.role)}`}>
                      {emp.role}
                    </div>
                    <div>
                      <p className="font-bold text-white">{emp.name}</p>
                      <p className="text-xs text-[#6b7280] font-mono">
                        {emp.code}
                        <button
                          onClick={() => handleChangeCode(emp.code, emp.name)}
                          className="ml-2 text-purple-400 hover:text-purple-300 text-xs"
                          title="Αλλαγή κωδικού"
                        >
                          ✏️
                        </button>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleEmployee(emp.code)}
                      className={`px-3 py-1 rounded text-sm font-bold ${emp.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                    >
                      {emp.enabled ? '✅ ON' : '❌ OFF'}
                    </button>
                    
                    {emp.code !== 'ADMIN2025' && (
                      <button
                        onClick={() => handleDeleteEmployee(emp.code, emp.name)}
                        className="p-2 bg-red-900 hover:bg-red-800 text-red-400 rounded"
                      >
                        {icons.x}
                      </button>
                    )}
                  </div>
                </div>
                
                {!emp.enabled && (
                  <div className="mt-2 text-xs text-red-400">
                    ⚠️ Απενεργοποιημένος - δεν μπορεί να συνδεθεί
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-[#d1d5db]">
          <button
            onClick={onClose}
            className="w-full bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold py-3 px-4 rounded-lg"
          >
            Κλείσιμο
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// DATA MANAGEMENT MODAL - Διαγραφή Δεδομένων
// =====================================================
function DataManagementModal({ onClose, boats, onDataCleared }) {
  const [selectedItems, setSelectedItems] = useState({
    charters: { enabled: false, mode: 'all', boats: [] },
    page1Charters: { enabled: false, mode: 'all', bookings: [] }, // 🔥 NEW: Page 1 bookings
    invoices: { enabled: false, mode: 'all', boats: [] },
    tasks: { enabled: false, mode: 'all', boats: [] },
    messages: { enabled: false, mode: 'all', boats: [] },
    documents: { enabled: false, mode: 'all', boats: [] },
    boatDetails: { enabled: false, mode: 'all', boats: [] },
    activityLogs: { enabled: false }
  });
  const [step, setStep] = useState(1);
  const [adminCode, setAdminCode] = useState('');
  const [error, setError] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);

  // 🔥 NEW: State for fetched bookings from API
  const [apiBookings, setApiBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // 🔥 NEW: Fetch bookings from API when page1Charters is expanded
  const fetchBookingsFromAPI = async () => {
    setLoadingBookings(true);
    try {
      const response = await fetch('/api/bookings.php');
      const data = await response.json();
      if (data.success && data.bookings) {
        const bookingsList = Array.isArray(data.bookings) ? data.bookings : Object.values(data.bookings);
        setApiBookings(bookingsList);
        console.log('✅ Fetched', bookingsList.length, 'bookings from API');
      } else {
        setApiBookings([]);
      }
    } catch (error) {
      console.error('❌ Error fetching bookings:', error);
      setApiBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const dataTypes = [
    { key: 'charters', label: 'Ναύλοι (localStorage)', icon: '⚓', storageKey: 'ΝΑΥΛΑ', hasBoats: true },
    { key: 'page1Charters', label: 'Ναύλοι Page 1 (Database)', icon: '📝', hasBoats: false, hasBookings: true }, // 🔥 NEW
    { key: 'invoices', label: 'Τιμολόγια/Έξοδα', icon: '📄', storageKey: 'ΤΙΜΟΛΟΓΙΑ', hasBoats: true },
    { key: 'tasks', label: 'Εργασίες', icon: '📋', storageKey: 'ΕΡΓΑΣΙΕΣ', hasBoats: true },
    { key: 'messages', label: 'Μηνύματα', icon: '💬', storageKey: 'messages', hasBoats: true },
    { key: 'documents', label: 'Έγγραφα', icon: '📁', storageKey: 'documents', hasBoats: true },
    { key: 'boatDetails', label: 'Στοιχεία Σκαφών', icon: '🚤', storageKey: 'details', hasBoats: true },
    { key: 'activityLogs', label: 'Activity Logs', icon: '📊', hasBoats: false },
  ];

  const toggleItem = (key) => {
    setSelectedItems(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  const setMode = (key, mode) => {
    setSelectedItems(prev => ({
      ...prev,
      [key]: { ...prev[key], mode, boats: mode === 'all' ? [] : prev[key].boats }
    }));
  };

  const toggleBoatForItem = (key, boatId) => {
    setSelectedItems(prev => {
      const currentBoats = prev[key].boats || [];
      const newBoats = currentBoats.includes(boatId)
        ? currentBoats.filter(id => id !== boatId)
        : [...currentBoats, boatId];
      return {
        ...prev,
        [key]: { ...prev[key], boats: newBoats }
      };
    });
  };

  const selectAllBoatsForItem = (key) => {
    setSelectedItems(prev => {
      const currentBoats = prev[key].boats || [];
      const newBoats = currentBoats.length === boats.length ? [] : boats.map(b => b.id);
      return {
        ...prev,
        [key]: { ...prev[key], boats: newBoats }
      };
    });
  };

  // 🔥 NEW: Toggle individual booking selection for page1Charters
  const toggleBookingSelection = (bookingCode: string) => {
    setSelectedItems(prev => {
      const currentBookings = prev.page1Charters.bookings || [];
      const newBookings = currentBookings.includes(bookingCode)
        ? currentBookings.filter(code => code !== bookingCode)
        : [...currentBookings, bookingCode];
      return {
        ...prev,
        page1Charters: { ...prev.page1Charters, bookings: newBookings }
      };
    });
  };

  // 🔥 NEW: Select/deselect all bookings
  const selectAllBookings = () => {
    setSelectedItems(prev => {
      const currentBookings = prev.page1Charters.bookings || [];
      const newBookings = currentBookings.length === apiBookings.length
        ? []
        : apiBookings.map(b => b.code || b.bookingCode || b.id);
      return {
        ...prev,
        page1Charters: { ...prev.page1Charters, bookings: newBookings }
      };
    });
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(item => item.enabled).length;
  };

  const isValidSelection = () => {
    for (const [key, value] of Object.entries(selectedItems)) {
      const item = value as any;
      // 🔥 Handle page1Charters separately (uses bookings instead of boats)
      if (key === 'page1Charters') {
        if (item.enabled && item.mode === 'selective' && (!item.bookings || item.bookings.length === 0)) {
          return false;
        }
      } else if (item.enabled && item.mode === 'selective' && item.boats?.length === 0) {
        return false;
      }
    }
    return getSelectedCount() > 0;
  };

  const handleProceed = () => {
    if (!isValidSelection()) {
      setError('Επιλέξτε τουλάχιστον ένα στοιχείο και σκάφη για επιλεκτική διαγραφή!');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleConfirmDelete = async () => {
    const employee = authService.getEmployeeByCode(adminCode);
    if (!employee || employee.role !== 'ADMIN') {
      setError('❌ Λάθος κωδικός Admin! Η διαγραφή ακυρώθηκε.');
      return;
    }

    let deletedCount = 0;
    const apiErrors: string[] = [];

    // Helper function to delete from database API
    const deleteFromDatabase = async (type: string): Promise<number> => {
      try {
        const response = await fetch('/api/delete-all.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type })
        });
        const result = await response.json();
        if (result.success) {
          console.log(`✅ Database delete (${type}):`, result);
          return result.deleted_count || 0;
        } else {
          console.error(`❌ Database delete (${type}) failed:`, result.error);
          apiErrors.push(`${type}: ${result.error}`);
          return 0;
        }
      } catch (error) {
        console.error(`❌ Database delete (${type}) error:`, error);
        apiErrors.push(`${type}: Network error`);
        return 0;
      }
    };

    // Process each data type
    for (const dataType of dataTypes) {
      const item = selectedItems[dataType.key];
      if (!item.enabled) continue;

      if (dataType.key === 'activityLogs') {
        authService.clearActivityLogs();
        deletedCount++;
        continue;
      }

      // 🔥 NEW: Handle page1Charters separately (deletes from database)
      if (dataType.key === 'page1Charters') {
        const page1Item = selectedItems.page1Charters;

        if (page1Item.mode === 'all') {
          // Delete ALL bookings from database
          const dbCount = await deleteFromDatabase('bookings');
          deletedCount += dbCount;
          console.log('🧹 Cleared all Page 1 bookings from database');
        } else if (page1Item.mode === 'selective' && page1Item.bookings?.length > 0) {
          // Delete selected bookings one by one from API
          for (const bookingCode of page1Item.bookings) {
            try {
              const response = await fetch(`/api/bookings.php?booking_number=${encodeURIComponent(bookingCode)}`, {
                method: 'DELETE'
              });
              const result = await response.json();
              if (result.success) {
                console.log(`✅ Deleted booking: ${bookingCode}`);
                deletedCount++;
              } else {
                console.error(`❌ Failed to delete ${bookingCode}:`, result.error);
                apiErrors.push(`${bookingCode}: ${result.error}`);
              }
            } catch (error) {
              console.error(`❌ Error deleting ${bookingCode}:`, error);
              apiErrors.push(`${bookingCode}: Network error`);
            }
          }
          console.log(`🧹 Deleted ${page1Item.bookings.length} selected bookings`);
        }
        continue;
      }

      // Skip page1Charters in regular processing (handled above)
      if (!dataType.storageKey) continue;

      // Delete from localStorage
      const boatsToDelete = item.mode === 'all' ? boats : boats.filter(b => item.boats.includes(b.id));

      boatsToDelete.forEach(boat => {
        const storageKey = `fleet_${boat.id}_${dataType.storageKey}`;
        localStorage.removeItem(storageKey);
        deletedCount++;
      });

      // Delete from database API (only for types that have database tables)
      if (item.mode === 'all') {
        if (dataType.key === 'charters') {
          // Delete ALL bookings from database
          const dbCount = await deleteFromDatabase('bookings');
          deletedCount += dbCount;
        } else if (dataType.key === 'messages') {
          // Delete ALL chats and messages from database
          const dbCount = await deleteFromDatabase('chats');
          deletedCount += dbCount;
        }
      }
    }

    authService.logActivity('clear_data', `Deleted ${deletedCount} data items`);

    // Data deleted from API - UI will refresh from API on next load

    if (apiErrors.length > 0) {
      globalShowMessage(`⚠️ Διαγράφηκαν ${deletedCount} στοιχεία (με σφάλματα: ${apiErrors.join(', ')})`, 'warning');
    } else {
      globalShowMessage(`✅ Διαγράφηκαν ${deletedCount} στοιχεία δεδομένων!`, 'success');
    }
    onDataCleared();
    onClose();
  };

  const getSummary = () => {
    const summary = [];
    dataTypes.forEach(dataType => {
      const item = selectedItems[dataType.key];
      if (!item.enabled) return;

      if (dataType.key === 'activityLogs') {
        summary.push({ icon: dataType.icon, label: dataType.label, detail: 'Όλα' });
      } else if (dataType.key === 'page1Charters') {
        // Handle page1Charters separately (uses bookings)
        if (item.mode === 'all') {
          summary.push({ icon: dataType.icon, label: dataType.label, detail: 'Όλες οι κρατήσεις' });
        } else {
          const count = selectedItems.page1Charters.bookings?.length || 0;
          summary.push({ icon: dataType.icon, label: dataType.label, detail: `${count} κρατήσεις` });
        }
      } else if (item.mode === 'all') {
        summary.push({ icon: dataType.icon, label: dataType.label, detail: 'Όλα τα σκάφη' });
      } else {
        summary.push({ icon: dataType.icon, label: dataType.label, detail: item.boats.join(', ') });
      }
    });
    return summary;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-[#d1d5db] bg-red-900">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            ⚠️
            <span>Διαγραφή Δεδομένων</span>
          </h2>
          <button onClick={onClose} className="text-[#374151] hover:text-white p-2">{icons.x}</button>
        </div>

        {step === 1 && (
          <>
            <div className="p-3 bg-red-950 border-b border-red-800">
              <p className="text-red-200 text-sm">
                ⚠️ <strong>ΠΡΟΣΟΧΗ:</strong> Η διαγραφή είναι <strong>ΟΡΙΣΤΙΚΗ</strong>!
              </p>
            </div>

            <div className="flex-grow overflow-y-auto p-4">
              <div className="space-y-3">
                {dataTypes.map(dataType => {
                  const item = selectedItems[dataType.key];
                  const isExpanded = expandedItem === dataType.key;
                  
                  return (
                    <div key={dataType.key} className={`rounded-lg border-2 transition-all ${
                      item.enabled ? 'bg-red-900 border-red-500' : 'bg-[#f3f4f6] border-[#d1d5db]'
                    }`}>
                      {/* Header */}
                      <button
                        onClick={() => toggleItem(dataType.key)}
                        className="w-full p-3 flex items-center gap-3 text-left"
                      >
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          item.enabled ? 'bg-red-500 border-red-500 text-white' : 'border-gray-500'
                        }`}>
                          {item.enabled && '✓'}
                        </div>
                        <span className="text-2xl">{dataType.icon}</span>
                        <span className={`font-bold ${item.enabled ? 'text-white' : 'text-[#374151]'}`}>
                          {dataType.label}
                        </span>
                      </button>

                      {/* Options - only show if enabled and has boats */}
                      {item.enabled && dataType.hasBoats && (
                        <div className="px-3 pb-3 border-t border-red-700">
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => setMode(dataType.key, 'all')}
                              className={`flex-1 py-2 px-3 rounded text-sm font-bold ${
                                item.mode === 'all' 
                                  ? 'bg-red-600 text-white' 
                                  : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                              }`}
                            >
                              ΟΛΑ
                            </button>
                            <button
                              onClick={() => {
                                setMode(dataType.key, 'selective');
                                setExpandedItem(isExpanded ? null : dataType.key);
                              }}
                              className={`flex-1 py-2 px-3 rounded text-sm font-bold ${
                                item.mode === 'selective' 
                                  ? 'bg-orange-600 text-white' 
                                  : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                              }`}
                            >
                              ΕΠΙΛΕΚΤΙΚΑ
                            </button>
                          </div>

                          {/* Boat selection */}
                          {item.mode === 'selective' && (
                            <div className="mt-3 p-2 bg-white rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-[#6b7280]">Επιλέξτε σκάφη:</span>
                                <button
                                  onClick={() => selectAllBoatsForItem(dataType.key)}
                                  className="text-xs text-[#1e40af] hover:text-[#1e40af]"
                                >
                                  {item.boats.length === boats.length ? 'Κανένα' : 'Όλα'}
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-1">
                                {boats.map(boat => (
                                  <button
                                    key={boat.id}
                                    onClick={() => toggleBoatForItem(dataType.key, boat.id)}
                                    className={`p-2 rounded text-xs font-bold ${
                                      item.boats.includes(boat.id)
                                        ? 'bg-orange-600 text-white'
                                        : 'bg-[#f9fafb] text-[#6b7280] hover:bg-gray-100'
                                    }`}
                                  >
                                    {boat.name || boat.id}
                                  </button>
                                ))}
                              </div>
                              {item.boats.length === 0 && (
                                <p className="text-xs text-red-400 mt-2">⚠️ Επιλέξτε τουλάχιστον ένα σκάφος</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* 🔥 NEW: Options for page1Charters (uses bookings instead of boats) */}
                      {item.enabled && dataType.hasBookings && (
                        <div className="px-3 pb-3 border-t border-red-700">
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => setMode(dataType.key, 'all')}
                              className={`flex-1 py-2 px-3 rounded text-sm font-bold ${
                                item.mode === 'all'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                              }`}
                            >
                              ΟΛΑ
                            </button>
                            <button
                              onClick={() => {
                                setMode(dataType.key, 'selective');
                                if (apiBookings.length === 0) {
                                  fetchBookingsFromAPI();
                                }
                              }}
                              className={`flex-1 py-2 px-3 rounded text-sm font-bold ${
                                item.mode === 'selective'
                                  ? 'bg-orange-600 text-white'
                                  : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                              }`}
                            >
                              ΕΠΙΛΕΚΤΙΚΑ
                            </button>
                          </div>

                          {/* Booking selection */}
                          {item.mode === 'selective' && (
                            <div className="mt-3 p-2 bg-white rounded-lg max-h-60 overflow-y-auto">
                              {loadingBookings ? (
                                <div className="text-center py-4">
                                  <span className="text-[#6b7280]">⏳ Φόρτωση κρατήσεων...</span>
                                </div>
                              ) : apiBookings.length === 0 ? (
                                <div className="text-center py-4">
                                  <span className="text-[#6b7280]">Δεν υπάρχουν κρατήσεις</span>
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs text-[#6b7280]">
                                      Επιλέξτε κρατήσεις ({selectedItems.page1Charters.bookings?.length || 0}/{apiBookings.length}):
                                    </span>
                                    <button
                                      onClick={selectAllBookings}
                                      className="text-xs text-[#1e40af] hover:text-[#1e40af]"
                                    >
                                      {selectedItems.page1Charters.bookings?.length === apiBookings.length ? 'Κανένα' : 'Όλα'}
                                    </button>
                                  </div>
                                  <div className="space-y-1">
                                    {apiBookings.map(booking => {
                                      const bookingCode = booking.code || booking.bookingCode || booking.booking_number || booking.id;
                                      const isSelected = selectedItems.page1Charters.bookings?.includes(bookingCode);
                                      return (
                                        <button
                                          key={bookingCode}
                                          onClick={() => toggleBookingSelection(bookingCode)}
                                          className={`w-full p-2 rounded text-left text-xs ${
                                            isSelected
                                              ? 'bg-orange-600 text-white'
                                              : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                                          }`}
                                        >
                                          <div className="flex justify-between items-center">
                                            <span className="font-bold">{bookingCode}</span>
                                            <span className={isSelected ? 'text-white' : 'text-[#6b7280]'}>
                                              {isSelected ? '✓' : ''}
                                            </span>
                                          </div>
                                          <div className="text-xs opacity-75 mt-1">
                                            {booking.vesselName || booking.vessel_name || booking.vessel || '-'} | {
                                              booking.startDate || booking.start_date || booking.checkInDate || '-'
                                            }
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                              {item.mode === 'selective' && selectedItems.page1Charters.bookings?.length === 0 && !loadingBookings && apiBookings.length > 0 && (
                                <p className="text-xs text-red-400 mt-2">⚠️ Επιλέξτε τουλάχιστον μία κράτηση</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="mx-4 mb-2 p-3 bg-red-900 border border-red-600 rounded text-red-200 text-sm">
                {error}
              </div>
            )}

            <div className="p-4 border-t border-[#d1d5db] space-y-2">
              <div className="text-center text-sm text-[#6b7280] mb-2">
                {getSelectedCount()} κατηγορίες επιλεγμένες
              </div>
              <button
                onClick={handleProceed}
                disabled={!isValidSelection()}
                className={`w-full font-bold py-3 px-4 rounded-lg ${
                  isValidSelection()
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-[#f9fafb] text-gray-500 cursor-not-allowed'
                }`}
              >
                Συνέχεια →
              </button>
              <button
                onClick={onClose}
                className="w-full bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold py-3 px-4 rounded-lg"
              >
                Ακύρωση
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="p-4 bg-red-950 border-b border-red-800">
              <p className="text-red-200 text-sm text-center">
                🔐 <strong>Εισάγετε τον κωδικό ADMIN για επιβεβαίωση</strong>
              </p>
            </div>

            <div className="flex-grow overflow-y-auto p-4">
              <div className="bg-[#f3f4f6] p-4 rounded-lg border border-[#d1d5db] mb-4">
                <h4 className="font-bold text-red-400 mb-3">Θα διαγραφούν:</h4>
                <div className="space-y-2">
                  {getSummary().map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-red-400">✗</span>
                      <span>{item.icon}</span>
                      <span className="text-white font-bold">{item.label}</span>
                      <span className="text-[#6b7280]">→ {item.detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Κωδικός Admin:
                </label>
                <input
                  type="password"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  placeholder="Εισάγετε τον κωδικό ADMIN"
                  className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-red-500 text-center text-lg"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900 border border-red-600 rounded text-red-200 text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#d1d5db] space-y-2">
              <button
                onClick={handleConfirmDelete}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg"
              >
                🗑️ ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ
              </button>
              <button
                onClick={() => { setStep(1); setError(''); setAdminCode(''); }}
                className="w-full bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold py-3 px-4 rounded-lg"
              >
                ← Πίσω
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================================================
// ACTIVITY LOG MODAL
// =====================================================
function ActivityLogModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    const activityLogs = authService.getAllActivityLogs();
    setLogs(activityLogs.reverse()); // Newest first
  };

  const getActionColor = (action) => {
    if (action.includes('login')) return 'text-green-400';
    if (action.includes('logout')) return 'text-yellow-400';
    if (action.includes('delete') || action.includes('clear')) return 'text-red-400';
    if (action.includes('add') || action.includes('create')) return 'text-blue-400';
    if (action.includes('edit') || action.includes('update')) return 'text-purple-400';
    if (action.includes('view')) return 'text-[#6b7280]';
    return 'text-white';
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return '🔓';
    if (action.includes('logout')) return '🔒';
    if (action.includes('delete') || action.includes('clear')) return '🗑️';
    if (action.includes('add') || action.includes('create')) return '➕';
    if (action.includes('edit') || action.includes('update')) return '✏️';
    if (action.includes('view')) return '👁️';
    if (action.includes('charter') || action.includes('booking')) return '⚓';
    if (action.includes('task')) return '📋';
    return '📝';
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} - ${day}/${month}/${year}`;
  };

  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(log => log.action.includes(filter));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-[#d1d5db] bg-blue-900">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            📊
            <span>Activity Log</span>
          </h2>
          <button onClick={onClose} className="text-[#374151] hover:text-white p-2">{icons.x}</button>
        </div>

        {/* Filter */}
        <div className="p-3 border-b border-[#d1d5db] bg-[#f3f4f6]">
          <div className="flex gap-2 flex-wrap">
            {['all', 'login', 'add', 'edit', 'delete', 'view'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded text-xs font-bold ${
                  filter === f ? 'bg-blue-600 text-white' : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
                }`}
              >
                {f === 'all' ? 'Όλα' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Logs List */}
        <div className="flex-grow overflow-y-auto p-3">
          {filteredLogs.length > 0 ? (
            <div className="space-y-2">
              {filteredLogs.map((log, idx) => (
                <div key={idx} className="bg-[#f3f4f6] p-3 rounded-lg border border-[#d1d5db]">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{getActionIcon(log.action)}</span>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-bold text-[#1e40af] text-base">
                            {log.employeeName || log.user || 'Unknown'}
                          </span>
                          <span className="text-gray-500 text-xs ml-2">
                            ({log.employeeCode || log.code || ''})
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                      <div className={`text-sm mt-1 ${getActionColor(log.action)}`}>
                        {log.action}
                      </div>
                      {(log.details || log.vesselId || log.bookingCode) && (
                        <div className="text-xs text-gray-500 mt-1">
                          {log.vesselId && <span className="mr-2">🚤 {log.vesselId}</span>}
                          {log.bookingCode && <span className="mr-2">📋 {log.bookingCode}</span>}
                          {log.details && <span>• {log.details}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="text-4xl">📭</span>
              <p className="text-[#6b7280] mt-2">Δεν υπάρχουν εγγραφές</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#d1d5db]">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{filteredLogs.length} εγγραφές</span>
            <button
              onClick={onClose}
              className="bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold py-2 px-6 rounded-lg"
            >
              Κλείσιμο
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// FINANCIALS SUMMARY MODAL - Συγκεντρωτικά Οικονομικά
// =====================================================
function FinancialsSummaryModal({ onClose, financialsData, boats }) {
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter boats based on search (case-insensitive)
  const filteredBoats = financialsData.boats.filter(boat => {
    if (!searchTerm.trim()) return true;
    const fullBoat = boats.find(b => b.id === boat.id);
    const boatText = `${boat.id} ${boat.name || ''} ${fullBoat?.type || ''} ${fullBoat?.model || ''}`;
    return textMatches(boatText, searchTerm);
  });

  // 🔥 API-first: Load boat details from API, with localStorage fallback
  const loadBoatDetails = async (boatId) => {
    setSelectedBoat(boatId);

    // Load charters from API ONLY
    let charters = [];
    try {
      charters = await getBookingsByVessel(boatId);
      console.log(`✅ Loaded ${charters.length} charters for boat ${boatId} from API`);
    } catch (e) {
      console.error(`❌ API failed for boat ${boatId}, no charters loaded`);
      charters = [];
    }

    // 🔥 Load invoices from API first, fallback to localStorage
    let invoices = [];
    try {
      invoices = await getInvoicesByVessel(boatId);
      console.log(`✅ Loaded ${invoices.length} invoices for boat ${boatId} from API`);
    } catch (e) {
      console.error(`❌ API failed for invoices, falling back to localStorage`);
      const invoicesKey = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ`;
      const invoicesStored = localStorage.getItem(invoicesKey);
      invoices = invoicesStored ? JSON.parse(invoicesStored) : [];
    }

    setDetailedData({ charters, invoices });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col z-50">
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-900 to-green-800 border-b border-green-700">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          💰
          <span>Συγκεντρωτικά Οικονομικά</span>
        </h2>
        <button 
          onClick={onClose} 
          className="bg-[#f9fafb] hover:bg-gray-100 text-[#374151] px-4 py-2 rounded-lg font-bold"
        >
          ✕ Κλείσιμο
        </button>
      </div>

      {/* Totals Summary */}
      <div className="p-4 bg-[#f3f4f6] border-b border-[#d1d5db]">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-green-900 p-4 rounded-lg">
            <div className="text-sm text-green-300 mb-1">ΣΥΝΟΛΙΚΑ ΕΣΟΔΑ</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">
              {formatCurrency(financialsData.totals.income)}
            </div>
          </div>
          <div className="bg-red-900 p-4 rounded-lg">
            <div className="text-sm text-red-300 mb-1">ΣΥΝΟΛΙΚΑ ΕΞΟΔΑ</div>
            <div className="text-2xl sm:text-3xl font-bold text-white">
              {formatCurrency(financialsData.totals.expenses)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-[#d1d5db]">
            <div className="text-sm text-[#374151] mb-1">ΚΑΘΑΡΟ ΑΠΟΤΕΛΕΣΜΑ</div>
            <div className={`text-2xl sm:text-3xl font-bold ${financialsData.totals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(financialsData.totals.net)}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow flex overflow-hidden">
        {/* Boats List - Left Side */}
        <div className="w-1/3 sm:w-1/4 border-r border-[#d1d5db] overflow-y-auto bg-[#f3f4f6] flex flex-col">
          <div className="p-2 border-b border-[#d1d5db] bg-white">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Αναζήτηση..."
              className="w-full px-2 py-1 bg-[#f9fafb] text-[#374151] rounded border border-[#d1d5db] focus:outline-none focus:ring-1 focus:ring-[#1e40af] text-xs"
            />
          </div>
          <div className="p-2 border-b border-[#d1d5db] bg-white">
            <h3 className="font-bold text-[#1e40af] text-sm">
              Σκάφη ({filteredBoats.length}{searchTerm ? ` / ${boats.length}` : ''})
            </h3>
          </div>
          <div className="space-y-1 p-2 flex-grow overflow-y-auto">
            {filteredBoats.map(boat => {
              const hasPage1Bookings = boat.draftCount > 0;

              return (
                <button
                  key={boat.id}
                  onClick={() => loadBoatDetails(boat.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors border-2 ${
                    selectedBoat === boat.id
                      ? 'bg-[#1e40af] border-[#1e40af]'
                      : hasPage1Bookings
                        ? 'bg-blue-900/50 border-blue-500 hover:bg-blue-800/50'
                        : 'bg-white hover:bg-[#f9fafb] border-[#d1d5db]'
                  }`}
                >
                  {/* 📝 Page 1 booking badge - prominent at top */}
                  {hasPage1Bookings && (
                    <div className="mb-2 px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded inline-flex items-center gap-1">
                      📝 {boat.draftCount === 1 ? 'Νέο ναύλο από Check-in' : `${boat.draftCount} νέα ναύλα από Check-in`}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-sm">{boat.name || boat.id}</span>
                  </div>
                  <div className={`text-lg font-bold ${boat.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(boat.net)}
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-green-400">{formatCurrency(boat.income)}</span>
                    <span className="text-red-400">{formatCurrency(boat.expenses)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Details - Right Side */}
        <div className="flex-grow overflow-y-auto bg-gray-100 p-4">
          {selectedBoat && detailedData ? (
            <>
              <h3 className="text-xl font-bold text-[#1e40af] mb-4">
                📊 {selectedBoat} - Αναλυτικά
              </h3>

              {/* Charters */}
              <div className="mb-6">
                <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2">
                  ⚓ Ναύλοι ({detailedData.charters.length})
                </h4>
                {detailedData.charters.length > 0 ? (
                  <div className="space-y-2">
                    {detailedData.charters.map((charter, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-[#d1d5db]">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-bold text-white">{charter.code || charter.charterCode || `#${idx + 1}`}</span>
                            <span className="text-gray-500 text-xs ml-2">
                              {charter.startDate ? charter.startDate.split('-').reverse().join('/') : ''} - {charter.endDate ? charter.endDate.split('-').reverse().join('/') : ''}
                            </span>
                          </div>
                          <span className="text-green-400 font-bold">
                            {formatCurrency(charter.amount || charter.charterAmount)}
                          </span>
                        </div>
                        <div className="text-xs text-[#6b7280] mt-1">
                          {charter.clientName || charter.charterer || 'N/A'}
                          {charter.status && (
                            <span className={`ml-2 px-2 py-0.5 rounded ${
                              charter.status === 'CONFIRMED' || charter.status === 'confirmed' || charter.status === 'Confirmed' ? 'bg-green-500 text-white' :
                              charter.status === 'CANCELLED' || charter.status === 'cancelled' || charter.status === 'Cancelled' || charter.status === 'Canceled' ? 'bg-red-500 text-white' :
                              'bg-yellow-400 text-black'
                            }`}>
                              {charter.status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Δεν υπάρχουν ναύλοι</p>
                )}
              </div>

              {/* Invoices/Expenses */}
              <div>
                <h4 className="font-bold text-red-400 mb-2 flex items-center gap-2">
                  📄 Τιμολόγια/Έξοδα ({detailedData.invoices.length})
                </h4>
                {detailedData.invoices.length > 0 ? (
                  <div className="space-y-2">
                    {detailedData.invoices.map((invoice, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-[#d1d5db]">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-bold text-white">{invoice.description || invoice.title || `#${idx + 1}`}</span>
                            <span className="text-gray-500 text-xs ml-2">{invoice.date}</span>
                          </div>
                          <span className="text-red-400 font-bold">
                            -{formatCurrency(invoice.amount)}
                          </span>
                        </div>
                        {invoice.category && (
                          <div className="text-xs text-[#6b7280] mt-1">
                            {invoice.category}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Δεν υπάρχουν τιμολόγια/έξοδα</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <span className="text-6xl mb-4">👈</span>
              <p>Επιλέξτε σκάφος για αναλυτικά στοιχεία</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================
// END OF PART 2 - Continue with Part 3 (ΤΑ ΥΠΟΛΟΙΠΑ ΜΕΝΟΥΝ ΙΔΙΑ)
// =====================================================
// =====================================================
// PART 3/4: Dashboard + Fleet Summary + Booking Sheet + Documents
// =====================================================

function DashboardPage({ boat, onSelectCategory, navigate, ownerCode }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const user = authService.getCurrentUser();
  const reactNavigate = useNavigate();
  const isOwnerUser = authService.isOwner();

  // Home button handler - logout and navigate to home page
  const handleHome = () => {
    authService.logout();
    reactNavigate('/');
  };

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  const allCategories = [
    { name: 'ΕΓΓΡΑΦΑ & ΣΤΟΙΧΕΙΑ', icon: icons.fileText, description: 'Στοιχεία σκάφους και έγγραφα' },
    { name: 'ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ', icon: icons.user, description: 'Στοιχεία ιδιοκτήτη σκάφους' },  // 🔥 FIX 37
    { name: 'ΦΩΤΟ & ΒΙΝΤΕΟ', icon: icons.media, description: 'Πολυμέσα σκάφους' },
    { name: 'ΕΡΓΑΣΙΕΣ', icon: icons.tasks, description: 'Τεχνικές εργασίες και συντήρηση' },
    { name: 'ΝΑΥΛΑ', icon: icons.charter, description: 'Διαχείριση ναύλων' },
    { name: 'BOOKING SHEET', icon: icons.bookingSheet, description: 'Πρόγραμμα κρατήσεων' },
    { name: 'ΟΙΚΟΝΟΜΙΚΑ', icon: icons.financials, description: 'Οικονομικά στοιχεία' },
    { name: 'ΑΠΟΣΤΟΛΗ E-MAIL', icon: icons.email, description: 'Επικοινωνία με εταιρία' },
  ];
  
  const isTechnicalUser = authService.isTechnical();
  
  const visibleCategories = isOwnerUser
    ? allCategories
    : allCategories.filter(cat => {
        if (cat.name === 'ΕΓΓΡΑΦΑ & ΣΤΟΙΧΕΙΑ' || cat.name === 'ΑΠΟΣΤΟΛΗ E-MAIL') return true;
        // 🔥 FIX 37: ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ - μόνο για admin που μπορεί να διαχειριστεί στόλο
        if (cat.name === 'ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ') return authService.canManageFleet();
        if (cat.name === 'ΦΩΤΟ & ΒΙΝΤΕΟ') return authService.canEdit();
        if (cat.name === 'ΕΡΓΑΣΙΕΣ') return authService.canManageTasks();
        // TECHNICAL δεν βλέπει ΝΑΥΛΑ - μόνο BOOKING SHEET
        if (cat.name === 'ΝΑΥΛΑ') return authService.canDoCheckInOut() && !isTechnicalUser;
        if (cat.name === 'BOOKING SHEET') return authService.canDoCheckInOut();
        if (cat.name === 'ΟΙΚΟΝΟΜΙΚΑ') return authService.canViewFinancials();
        return false;
      });

  const handleBackNavigation = (pageName) => {
    // Αν πατήσει messages
    if (pageName === 'messages') {
      navigate('messages');
      return;
    }
    
    // Owner πάει στο owner-dashboard
    if (ownerCode) {
      reactNavigate('/owner-dashboard', { state: { ownerCode: ownerCode } });
      return;
    }
    
    // Employees (TECHNICAL, BOOKING, ACCOUNTING, ADMIN) πάνε στο adminDashboard
    navigate('dashboard');
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title={boat.name || 'Dashboard'} onBack={handleBackNavigation} onHome={handleHome} lightTheme={true} />

      <div className="p-4 bg-white border-b border-[#d1d5db] shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-2xl font-bold text-[#374151]">{boat.name || boat.id}</h2>
            <p className="text-sm text-[#6b7280]">{boat.id}</p>
          </div>
          {user && (
            <div className="text-right">
              <div className="text-sm text-[#6b7280]">Logged as</div>
              <div className="text-sm font-bold text-[#374151]">{user.name || user.code}</div>
              <div className="text-xs text-[#6b7280]">
                {isOwnerUser ? 'Owner (View Only)' : user.role}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1 px-2 py-1 bg-[#f3f4f6] border border-[#d1d5db] rounded">
            {isOwnerUser ? icons.eye : icons.shield}
            <span className="text-[#374151]">
              {visibleCategories.length} / {allCategories.length} modules available
              {isOwnerUser && ' (View Only)'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto pb-20">
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4 text-[#374151]">
            {isOwnerUser ? 'All Modules (View Only)' : 'Available Modules'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {visibleCategories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => {
                  authService.logActivity('select_category', `${boat.id}/${cat.name}`);
                  onSelectCategory(cat.name);
                }}
                className="bg-white p-4 h-28 rounded-lg shadow-md flex flex-col items-center justify-center text-center transition-all duration-300 hover:bg-gray-50 hover:shadow-xl hover:-translate-y-2 hover:scale-105 border border-[#d1d5db] hover:border-[#1e40af] transform-gpu"
                style={{ transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
                <div className="text-[#1e40af] mb-2">
                  {React.cloneElement(cat.icon, { width: 28, height: 28 })}
                </div>
                <span className="text-center text-sm font-semibold text-[#374151] leading-tight">
                  {cat.name}
                </span>
                {isOwnerUser && (
                  <span className="text-xs text-[#6b7280] mt-1">{icons.eye}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// 🔥 Fleet Booking Sheet for Owner - με ΚΕΙΜΕΝΟ αντί για emojis
function FleetBookingSheetOwner({ boatIds, allBoatsData }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // 🔥 BUG FIX: Helper function to get boat name from ID
  const getBoatName = (boatId) => {
    const boat = INITIAL_FLEET.find(b => b.id === boatId || b.id === Number(boatId));
    return boat?.name || `Boat ${boatId}`;
  };
  
  const changeMonth = (offset) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const monthName = currentDate.toLocaleString('el-GR', { month: 'long' });
  const year = currentDate.getFullYear();

  let firstSaturday = new Date(year, currentDate.getMonth(), 1);
  while (firstSaturday.getDay() !== 6) {
    firstSaturday.setDate(firstSaturday.getDate() + 1);
  }

  const weeks = [];
  let currentWeekStart = new Date(firstSaturday);
  if (firstSaturday.getDate() > 7) {
    currentWeekStart.setDate(currentWeekStart.getDate() - 7);
  }
  
  while (currentWeekStart.getMonth() === currentDate.getMonth()) {
    let weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weeks.push({
      start: new Date(currentWeekStart),
      end: weekEnd,
      startDateString: `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth()+1).padStart(2,'0')}-${String(currentWeekStart.getDate()).padStart(2,'0')}`
    });
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  const formatDate = (date) => date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });

  // 🔥 FIX: Charter card colors - YELLOW (#FBBF24), GREEN (#10B981), RED
  const getStatusStyle = (status) => {
    switch(status) {
      case 'Option':
      case 'option':
      case 'OPTION':
      case 'Pending':
      case 'pending':
      case 'PENDING':
      case 'Reservation':
      case 'reservation':
      case 'RESERVATION':
      case 'Option Accepted':
      case 'option_accepted':
      case 'OPTION_ACCEPTED':
      case 'Pending Final Confirmation':
      case 'pending_final_confirmation':
      case 'PENDING_FINAL_CONFIRMATION':
      case 'Accepted':
      case 'accepted':
      case 'ACCEPTED':
        return { bg: 'bg-yellow-400', text: 'text-black', status: 'text-black' }; // YELLOW #FBBF24
      case 'Confirmed':
      case 'confirmed':
      case 'CONFIRMED':
        return { bg: 'bg-green-500', text: 'text-white', status: 'text-white' }; // GREEN #10B981
      case 'Canceled':
      case 'Cancelled':
      case 'Rejected':
      case 'cancelled':
      case 'CANCELLED':
      case 'CANCELED':
      case 'REJECTED':
        return { bg: 'bg-red-500', text: 'text-white', status: 'text-white' }; // RED
      case 'Expired':
      case 'expired':
      case 'EXPIRED':
        return { bg: 'bg-[#f9fafb]', text: 'text-[#374151]', status: 'text-[#374151]' };
      default:
        // 🔥 FALLBACK: Any unknown status defaults to YELLOW (pending)
        return { bg: 'bg-yellow-400', text: 'text-black', status: 'text-black' };
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
      <div className="p-2 border-b border-sky-300" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
        {/* 🔥 FIX: Legend colors - YELLOW (#FBBF24), GREEN (#10B981), RED */}
        <div className="flex flex-wrap justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-400"></div>
            <span className="text-yellow-600 font-semibold">Option</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-green-600 font-semibold">Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-red-600 font-semibold">Canceled</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center p-2 border-b border-sky-300" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
        <button onClick={() => changeMonth(-1)} className="text-blue-800 p-2 hover:bg-blue-300/50 rounded transition-colors text-2xl">{icons.chevronLeft}</button>
        <h2 className="text-2xl font-bold text-blue-900 capitalize">{monthName} {year}</h2>
        <button onClick={() => changeMonth(1)} className="text-blue-800 p-2 hover:bg-blue-300/50 rounded transition-colors text-2xl">{icons.chevronRight}</button>
      </div>

      <div className="flex-grow overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
              <th className="sticky left-0 p-3 border border-sky-300 text-left text-blue-900 text-base min-w-[100px] font-bold" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>Σκάφος</th>
              {weeks.map((week, index) => (
                <th key={index} className="p-2 border border-sky-300 text-sm text-blue-800 min-w-[120px]">
                  <div className="font-bold">ΕΒΔ. {index + 1}</div>
                  <div className="text-xs text-blue-700">{formatDate(week.start)} - {formatDate(week.end)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {boatIds.map((boatId) => {
              const boatData = allBoatsData[boatId];
              const charters = boatData?.charters || [];

              return (
                <tr key={boatId} className="hover:bg-sky-100">
                  {/* 🔥 BUG FIX: Display boat NAME instead of ID */}
                  <td className="sticky left-0 bg-sky-50 p-3 border border-sky-300 font-bold text-blue-800 text-base">{getBoatName(boatId)}</td>
                  
                  {weeks.map((week, index) => {
                    // 🔥 FIX 29: Charter displays ONLY in the week where check-in date falls (no spanning)
                    const charter = charters.find((c) => {
                      if (!c.startDate) return false;
                      const charterStart = new Date(c.startDate);
                      const charterEnd = c.endDate ? new Date(c.endDate) : new Date(charterStart.getTime() + 7*24*60*60*1000);
                      const weekStart = new Date(week.startDateString);
                      const weekEnd = new Date(weekStart.getTime() + 7*24*60*60*1000);

                      // Charter spans ALL overlapping weeks; back-to-back safe (end-of-week start belongs to next charter)
                      return charterStart.getTime() < weekEnd.getTime() && charterEnd.getTime() > weekStart.getTime();
                    });
                    const style = charter ? getStatusStyle(charter.status) : { bg: 'bg-sky-50', text: 'text-sky-400', status: 'text-sky-400' };
                    
                    // 🔥 FIXED: Payment status με ΚΕΙΜΕΝΟ
                    const paymentInfo = charter ? getPaymentStatusInfo(charter.paymentStatus) : null;
                    
                    return (
                      <td key={index} className={`p-2 border border-sky-300 text-center ${style.bg} relative`}>
                        {charter ? (
                          <div className={style.text}>
                            {/* 🔥 Red light - μόνο για ΑΝΕΞΟΦΛΗΤΟ, αναβοσβήνει */}
                            {paymentInfo?.showLight && (
                              <div className={`absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full shadow-lg shadow-red-500/50 ${paymentInfo.lightBlink ? 'animate-pulse' : ''}`}></div>
                            )}
                            <div className="font-bold text-sm">{charter.code}</div>
                            <div className="text-[#1e40af] text-sm">{charter.amount?.toFixed(0)}€</div>
                            {/* 🔥 Payment Status - ΚΕΙΜΕΝΟ αντί για emoji */}
                            <div className={`text-xs font-semibold ${paymentInfo?.color}`}>
                              {paymentInfo?.text}
                            </div>
                            <div className={`text-xs ${style.status}`}>{charter.status?.toUpperCase()}</div>
                          </div>
                        ) : (
                          <span className="text-sky-300">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FleetSummaryPage({ boatIds, ownerCode, navigate, showMessage }) {
  const reactNavigate = useNavigate();
  const [activeTab, setActiveTab] = useState('financials');
  const [allBoatsData, setAllBoatsData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const user = authService.getCurrentUser();

  // Get full boat info for filtering
  const allBoats = FleetService.getAllBoats();
  
  // Filter boatIds based on search (case-insensitive)
  const filteredBoatIds = boatIds.filter(boatId => {
    if (!searchTerm.trim()) return true;
    const boat = allBoats.find(b => b.id === boatId);
    const boatText = `${boatId} ${boat?.name || ''} ${boat?.type || ''} ${boat?.model || ''}`;
    return textMatches(boatText, searchTerm);
  });
  
  useEffect(() => {
    loadAllBoatsData();
  }, [boatIds]);

  // 🔥 FIX 16: Load boat data from API first, merge with localStorage
  const loadAllBoatsData = async () => {
    const data: { [key: string]: { charters: any[], invoices: any[], documents: any[] } } = {};

    // Load all boats in parallel for better performance
    await Promise.all(boatIds.map(async (boatId: any) => {
      // Load charters from API ONLY
      let charters = [];
      try {
        charters = await getBookingsByVessel(boatId);
        console.log(`✅ FleetSummary: Loaded ${charters.length} charters for boat ${boatId} from API`);
      } catch (e) {
        console.error(`❌ API failed for boat ${boatId}, no charters loaded`);
        charters = [];
      }

      // Load invoices (localStorage only for now)
      const invoicesKey = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ`;
      const invoicesStored = localStorage.getItem(invoicesKey);
      const invoices = invoicesStored ? JSON.parse(invoicesStored) : [];

      // Load documents (localStorage only)
      const docsKey = `fleet_${boatId}_documents`;
      const docsStored = localStorage.getItem(docsKey);
      const documents = docsStored ? JSON.parse(docsStored) : [];

      data[boatId] = { charters, invoices, documents };
    }));

    setAllBoatsData(data);
    console.log('✅ FleetSummary: All boat data loaded');
  };
  
  const calculateTotals = () => {
    let totalIncome = 0;
    let totalCharterExpenses = 0;
    let totalInvoiceExpenses = 0;
    
    Object.values(allBoatsData).forEach(boatData => {
      boatData.charters.forEach(c => {
        totalIncome += (c.amount || 0);
        totalCharterExpenses += (c.commission || 0) + (c.vat_on_commission || 0);
      });
      
      boatData.invoices.forEach(i => {
        totalInvoiceExpenses += (i.amount || 0);
      });
    });
    
    const totalExpenses = totalCharterExpenses + totalInvoiceExpenses;
    const netResult = totalIncome - totalExpenses;
    
    return { totalIncome, totalExpenses, netResult };
  };
  
  const totals = calculateTotals();
  
  const handleBackToOwnerDashboard = () => {
    authService.logActivity('back_to_owner_dashboard', ownerCode);
    reactNavigate('/owner-dashboard', { state: { ownerCode: ownerCode }, replace: true });
  };

  const handleHomeNavigation = (pageName) => {
    if (pageName === 'dashboard') {
      handleBackToOwnerDashboard();
    }
  };
  
  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title="ΣΥΓΚΕΝΤΡΩΤΙΚΑ ΣΤΟΙΧΕΙΑ" onBack={handleBackToOwnerDashboard} />
      
      <div className="p-4 bg-white border-b border-[#d1d5db] shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="text-2xl">👤</div>
            <div>
              <div className="text-sm text-[#6b7280]">Owner Code:</div>
              <div className="text-lg font-bold text-[#1e40af]">{ownerCode}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-[#6b7280]">Vessels:</div>
            <div className="text-lg font-bold text-[#374151]">{boatIds.length}</div>
          </div>
        </div>
        {/* 🔥 BUG FIX: Display boat NAMES instead of IDs */}
        <div className="text-sm text-[#6b7280]">{boatIds.map(id => allBoats.find(b => b.id === id)?.name || id).join(', ')}</div>
      </div>
      
      <div className="flex border-b border-[#d1d5db] bg-white">
        <button onClick={() => setActiveTab('financials')} className={`flex-1 py-3 px-2 font-semibold text-sm ${activeTab === 'financials' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          💰 Οικονομικά
        </button>
        <button onClick={() => setActiveTab('bookingSheet')} className={`flex-1 py-3 px-2 font-semibold text-sm ${activeTab === 'bookingSheet' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          📅 Booking
        </button>
        <button onClick={() => setActiveTab('charters')} className={`flex-1 py-3 px-2 font-semibold text-sm ${activeTab === 'charters' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          ⚓ Ναύλοι
        </button>
        <button onClick={() => setActiveTab('documents')} className={`flex-1 py-3 px-2 font-semibold text-sm ${activeTab === 'documents' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          📄 Έγγραφα
        </button>
      </div>
      
      <div className="flex-grow p-4 overflow-y-auto pb-20">
        {/* Search Box */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Αναζήτηση σκάφους (όνομα, τύπος, μοντέλο...)"
              className="w-full px-4 py-2 bg-white text-[#374151] rounded-lg border border-[#d1d5db] focus:outline-none focus:ring-2 focus:ring-[#1e40af] text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="text-center text-xs text-[#6b7280] mt-1">
              Βρέθηκαν {filteredBoatIds.length} από {boatIds.length} σκάφη
            </div>
          )}
        </div>

        {activeTab === 'financials' && (
          <div>
            <div className="grid grid-cols-3 gap-2 mb-6 text-center">
              <div className="bg-gradient-to-br from-green-700 to-green-800 p-3 rounded-lg shadow-lg">
                <div className="text-xs font-medium text-green-200">ΣΥΝ. ΕΣΟΔΑ</div>
                <div className="text-lg font-bold text-white">{totals.totalIncome.toFixed(2)}€</div>
              </div>
              <div className="bg-gradient-to-br from-red-700 to-red-800 p-3 rounded-lg shadow-lg">
                <div className="text-xs font-medium text-red-200">ΣΥΝ. ΕΞΟΔΑ</div>
                <div className="text-lg font-bold text-white">{totals.totalExpenses.toFixed(2)}€</div>
              </div>
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-3 rounded-lg shadow-lg">
                <div className="text-xs font-medium text-[#374151]">ΚΑΘΑΡΟ</div>
                <div className={`text-lg font-bold ${totals.netResult >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.netResult.toFixed(2)}€
                </div>
              </div>
            </div>
            
            {filteredBoatIds.map(boatId => {
              const boatData = allBoatsData[boatId];
              if (!boatData) return null;
              
              const boatIncome = boatData.charters.reduce((sum, c) => sum + (c.amount || 0), 0);
              const boatCharterExpenses = boatData.charters.reduce((sum, c) => sum + (c.commission || 0) + (c.vat_on_commission || 0), 0);
              const boatInvoiceExpenses = boatData.invoices.reduce((sum, i) => sum + (i.amount || 0), 0);
              const boatTotal = boatIncome - boatCharterExpenses - boatInvoiceExpenses;
              
              return (
                <div key={boatId} className="bg-white p-4 rounded-lg mb-3 border border-[#d1d5db]">
                  {/* 🔥 BUG FIX: Display boat NAME instead of ID */}
                  <h3 className="text-lg font-bold text-[#1e40af] mb-3">{allBoats.find(b => b.id === boatId)?.name || boatId}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#6b7280]">Έσοδα:</span>
                      <span className="text-green-400 font-semibold">{boatIncome.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6b7280]">Έξοδα (Ναύλων):</span>
                      <span className="text-red-400 font-semibold">{boatCharterExpenses.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6b7280]">Έξοδα (Τιμολ.):</span>
                      <span className="text-red-400 font-semibold">{boatInvoiceExpenses.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-[#d1d5db]">
                      <span className="text-[#374151] font-semibold">Καθαρό:</span>
                      <span className={`font-bold ${boatTotal >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {boatTotal.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {activeTab === 'bookingSheet' && (
          <div className="fixed inset-0 z-50 bg-[#f3f4f6] flex flex-col">
            <div className="bg-white p-2 flex items-center justify-between border-b border-[#d1d5db]">
              <button onClick={() => setActiveTab('financials')} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded-lg">
                {icons.chevronLeft}
              </button>
              <h1 className="text-lg font-bold text-white">Booking Sheet - {ownerCode}</h1>
              <div className="w-10"></div>
            </div>
            
            <div className="flex-grow overflow-hidden">
              <FleetBookingSheetOwner boatIds={boatIds} allBoatsData={allBoatsData} />
            </div>
          </div>
        )}
        
        {activeTab === 'charters' && (
          <div>
            {filteredBoatIds.map(boatId => {
              const boatData = allBoatsData[boatId];
              if (!boatData || boatData.charters.length === 0) return null;
              
              return (
                <div key={boatId} className="mb-6">
                  {/* 🔥 BUG FIX: Display boat NAME instead of ID */}
                  <h3 className="text-lg font-bold text-[#1e40af] mb-3 flex items-center gap-2">
                    <span>⚓</span>
                    <span>{allBoats.find(b => b.id === boatId)?.name || boatId}</span>
                    <span className="text-sm text-[#6b7280]">({boatData.charters.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {boatData.charters.map(charter => (
                      <div key={charter.id} className="bg-white p-3 rounded-lg border border-[#d1d5db]">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-bold text-white">{charter.code}</div>
                            <div className="text-xs text-[#6b7280]">{charter.startDate ? charter.startDate.split('-').reverse().join('/') : ''} - {charter.endDate ? charter.endDate.split('-').reverse().join('/') : ''}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-green-400">{charter.amount?.toFixed(2)}€</div>
                            <div className="text-xs text-[#6b7280]">{charter.status}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {activeTab === 'documents' && (
          <div>
            {filteredBoatIds.map(boatId => {
              const boatData = allBoatsData[boatId];
              if (!boatData || boatData.documents.length === 0) return null;
              
              return (
                <div key={boatId} className="mb-6">
                  {/* 🔥 BUG FIX: Display boat NAME instead of ID */}
                  <h3 className="text-lg font-bold text-[#1e40af] mb-3 flex items-center gap-2">
                    <span>📄</span>
                    <span>{allBoats.find(b => b.id === boatId)?.name || boatId}</span>
                    <span className="text-sm text-[#6b7280]">({boatData.documents.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {boatData.documents.map(doc => (
                      <div key={doc.id} className="bg-white p-3 rounded-lg border border-[#d1d5db]">
                        <div className="font-bold text-white">{doc.title}</div>
                        <div className="text-xs text-[#6b7280]">{doc.fileName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <BottomNav activePage={'dashboard'} onNavigate={handleHomeNavigation} />
    </div>
  );
}

// 🔥 BookingSheetPage - με ΚΕΙΜΕΝΟ αντί για emojis
function BookingSheetPage({ boat, navigate, showMessage }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  // 🔥 FIX 7: Add vessels state for API data
  const [vessels, setVessels] = useState([]);
  // 🔥 Auto-refresh: Track last update time
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  // 🔥 Charter details popup state
  const [selectedBookingForPopup, setSelectedBookingForPopup] = useState<any>(null);

  const isOwnerUser = authService.isOwner();
  const isTechnicalUser = authService.isTechnical();
  const canViewBookings = true;
  const canViewFinancials = !isTechnicalUser; // TECHNICAL δεν βλέπει οικονομικά
  const canEditBookings = (authService.isAdmin() || authService.isBooking()) && !isOwnerUser;

  // 🔥 Auto-refresh: Memoized loadBookings function - API-only
  const loadBookings = useCallback(async () => {
    if (!boat) return;
    try {
      // 🔥 API-only: Load bookings from API
      const apiBookings = await getBookingsByVessel(boat.id);
      setBookings(apiBookings || []);
      setLoading(false);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Error loading bookings from API:', e);
      setBookings([]); // No localStorage fallback
      setLoading(false);
    }
  }, [boat]);

  // 🔥 REMOVED: Local 5-minute refresh - now using global 3-minute refresh from App.tsx
  // const { isRefreshing } = useAutoRefresh(loadBookings, 5);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 🔥 NEW: Listen for global refresh events from App.tsx (every 3 minutes)
  useEffect(() => {
    const handleGlobalRefresh = () => {
      console.log('🔄 FleetManagement: Global refresh received, reloading bookings...');
      setIsRefreshing(true);
      loadBookings().finally(() => {
        setTimeout(() => setIsRefreshing(false), 1000);
      });
    };

    window.addEventListener('globalBookingsRefreshed', handleGlobalRefresh);
    return () => window.removeEventListener('globalBookingsRefreshed', handleGlobalRefresh);
  }, [loadBookings]);

  // 🔥 FIX 4: Use optional chaining in dependencies
  useEffect(() => {
    if (boat) {
      loadBookings();
    }
  }, [boat?.id, loadBookings]);

  // 🔥 FIX 7: Load vessels from API
  useEffect(() => {
    const loadVessels = async () => {
      try {
        const apiVessels = await getVessels();
        setVessels(apiVessels);
        console.log('📦 Vessels loaded for booking sheet:', apiVessels.length);
      } catch (error) {
        console.error('Error loading vessels:', error);
      }
    };
    loadVessels();
  }, []);

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  const cycleBookingStatus = async (booking) => {
    if (!canEditBookings) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    let newStatus;
    switch(booking.status) {
      case 'Option':
      case 'Pending':
        newStatus = 'Confirmed';
        break;
      case 'Confirmed':
        newStatus = 'Canceled';
        break;
      case 'Canceled':
        newStatus = 'Option';
        break;
      default:
        newStatus = 'Option';
    }

    // 🔥 API-only: Update status via API
    try {
      const bookingNumber = booking.bookingNumber || booking.code || booking.id;
      const updatedBooking = {
        ...booking,
        status: newStatus,
        updatedBy: authService.getCurrentUser()?.name,
        updatedAt: new Date().toISOString()
      };
      await saveBooking(bookingNumber, { bookingData: updatedBooking });

      // Update local state
      const updated = bookings.map((b: any) =>
        b.id === booking.id ? updatedBooking : b
      );
      setBookings(updated);
      showMessage(`✅ Status άλλαξε σε ${newStatus}`, 'success');
    } catch (e) {
      console.error('Error updating status via API:', e);
      showMessage('❌ Σφάλμα αποθήκευσης status', 'error');
    }
  };

  const changeMonth = (offset) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const monthName = currentDate.toLocaleString('el-GR', { month: 'long' });
  const year = currentDate.getFullYear();

  let firstSaturday = new Date(year, currentDate.getMonth(), 1);
  while (firstSaturday.getDay() !== 6) {
    firstSaturday.setDate(firstSaturday.getDate() + 1);
  }

  const weeks = [];
  let currentWeekStart = new Date(firstSaturday);
  if (firstSaturday.getDate() > 7) {
    currentWeekStart.setDate(currentWeekStart.getDate() - 7);
  }
  
  while (currentWeekStart.getMonth() === currentDate.getMonth()) {
    let weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    
    // 🔥 FIX 29: Charter displays ONLY in the week where check-in date falls (no spanning)
    const booking = bookings.find((b) => {
      if (!b.startDate) return false;
      const csP = String(b.startDate).slice(0,10).split('-').map(Number);
      const charterStart = new Date(csP[0], csP[1]-1, csP[2]);
      const ceP = b.endDate ? String(b.endDate).slice(0,10).split('-').map(Number) : null;
      const charterEnd = ceP ? new Date(ceP[0], ceP[1]-1, ceP[2]) : new Date(charterStart.getTime() + 7*24*60*60*1000);
      const weekStartTime = currentWeekStart.getTime();
      const weekEndTime = weekEnd.getTime();

      // Charter spans ALL overlapping weeks; back-to-back safe (end-of-week start belongs to next charter)
      return charterStart.getTime() < weekEndTime && charterEnd.getTime() > weekStartTime;
    });

    weeks.push({ start: new Date(currentWeekStart), end: weekEnd, booking: booking || null });
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  const formatDate = (date) => date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });

  // 🔥 FIX: Charter card colors - YELLOW (#FBBF24), GREEN (#10B981), RED
  const getStatusColor = (status) => {
    switch(status) {
      case 'Option':
      case 'option':
      case 'OPTION':
      case 'Pending':
      case 'pending':
      case 'PENDING':
      case 'Reservation':
      case 'reservation':
      case 'RESERVATION':
      case 'Accepted':
      case 'accepted':
      case 'ACCEPTED':
      case 'Option Accepted':
      case 'option_accepted':
      case 'OPTION_ACCEPTED':
      case 'Pending Final Confirmation':
      case 'pending_final_confirmation':
      case 'PENDING_FINAL_CONFIRMATION':
        return 'bg-yellow-400 border-yellow-300'; // YELLOW #FBBF24
      case 'Confirmed':
      case 'confirmed':
      case 'CONFIRMED':
        return 'bg-green-500 border-green-400'; // GREEN #10B981
      case 'Canceled':
      case 'Cancelled':
      case 'Rejected':
      case 'cancelled':
      case 'CANCELLED':
      case 'CANCELED':
      case 'REJECTED':
        return 'bg-red-500 border-red-400'; // RED
      case 'Expired':
      case 'expired':
      case 'EXPIRED':
        return 'bg-gray-500 border-gray-400';
      default:
        // 🔥 FALLBACK: Any unknown status defaults to YELLOW (pending)
        return 'bg-yellow-400 border-yellow-300';
    }
  };

  // 🔥 FIX: Charter card colors - YELLOW (#FBBF24), GREEN (#10B981), RED
  const getStatusText = (status) => {
    switch(status) {
      case 'Option':
      case 'option':
      case 'OPTION':
      case 'Pending':
      case 'pending':
      case 'PENDING':
        return { text: 'OPTION', color: 'text-black', bg: 'bg-yellow-400' }; // YELLOW #FBBF24
      case 'Reservation':
      case 'reservation':
      case 'RESERVATION':
        return { text: 'RESERVATION', color: 'text-black', bg: 'bg-yellow-400' }; // YELLOW #FBBF24
      case 'Option Accepted':
      case 'option_accepted':
      case 'OPTION_ACCEPTED':
      case 'Accepted':
      case 'accepted':
      case 'ACCEPTED':
        return { text: 'OPTION ACCEPTED', color: 'text-black', bg: 'bg-yellow-400' }; // YELLOW #FBBF24
      case 'Pending Final Confirmation':
      case 'pending_final_confirmation':
      case 'PENDING_FINAL_CONFIRMATION':
        return { text: 'ΑΝΑΜΟΝΗ ΤΕΛΙΚΗΣ ΕΠΙΒΕΒΑΙΩΣΗΣ', color: 'text-black', bg: 'bg-yellow-400' }; // YELLOW #FBBF24
      case 'Confirmed':
      case 'confirmed':
      case 'CONFIRMED':
        return { text: 'CONFIRMED', color: 'text-white', bg: 'bg-green-500' }; // GREEN #10B981
      case 'Cancelled':
      case 'Canceled':
      case 'cancelled':
      case 'CANCELLED':
      case 'CANCELED':
        return { text: 'CANCELLED', color: 'text-white', bg: 'bg-red-500' }; // RED
      case 'Rejected':
      case 'REJECTED':
        return { text: 'REJECTED', color: 'text-white', bg: 'bg-red-500' }; // RED
      case 'Expired':
      case 'expired':
      case 'EXPIRED':
        return { text: 'EXPIRED', color: 'text-white', bg: 'bg-gray-500' };
      default:
        // 🔥 FALLBACK: Any unknown status defaults to YELLOW (pending)
        return { text: status?.toUpperCase() || 'PENDING', color: 'text-black', bg: 'bg-yellow-400' };
    }
  };

  if (loading) return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title="Booking Sheet" onBack={() => navigate('boatDashboard')} />
      <FullScreenLoader />
      <BottomNav activePage={null} onNavigate={navigate} />
    </div>
  );

  return (
    <div className="flex flex-col h-screen w-screen bg-[#f3f4f6] fixed inset-0 z-50">
      <div className="bg-white p-2 flex items-center justify-between border-b border-[#d1d5db]">
        <button onClick={() => navigate('boatDashboard')} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded-lg">
          {icons.chevronLeft}
        </button>
        <div className="flex flex-col items-center">
          <h1 className="text-lg font-bold text-white">Booking Sheet - {boat.name || boat.id}</h1>
          <div className="flex items-center">
            <span className="text-xs text-[#6b7280]">Last updated: {lastUpdated.toLocaleTimeString()}</span>
            {isRefreshing && (
              <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full animate-pulse">
                🔄 Updating...
              </span>
            )}
          </div>
        </div>
        <div className="w-10"></div>
      </div>

      {isOwnerUser && (
        <div className="p-2 bg-blue-900 border-b border-blue-700 text-center">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">
            {icons.eye}
            <span>View Only Mode</span>
          </div>
        </div>
      )}

      {isTechnicalUser && (
        <div className="p-2 bg-purple-900 border-b border-purple-700 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-200 text-sm">
            {icons.eye}
            <span>Technical View - Μόνο ημερομηνίες & status</span>
          </div>
        </div>
      )}

      <div className="p-2 bg-white border-b border-[#d1d5db]">
        {/* 🔥 FIX: Legend colors - YELLOW (#FBBF24), GREEN (#10B981), RED */}
        <div className="flex flex-wrap justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-400"></div>
            <span className="text-yellow-400">Option</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-green-400">Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-red-400">Canceled</span>
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center p-2 bg-white border-b border-[#d1d5db]">
        <button onClick={() => changeMonth(-1)} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded transition-colors text-2xl">{icons.chevronLeft}</button>
        <h2 className="text-2xl font-bold text-[#1e40af] capitalize">{monthName} {year}</h2>
        <button onClick={() => changeMonth(1)} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded transition-colors text-2xl">{icons.chevronRight}</button>
      </div>

      <div className="flex-grow overflow-y-auto p-2">
        <div className="space-y-2">
          {weeks.map((week, index) => {
            const isBooked = !!week.booking;
            const status = week.booking?.status || 'Pending';
            const statusInfo = getStatusText(status);
            const colorClass = isBooked ? getStatusColor(status) : 'bg-white border-[#d1d5db]';
            
            // 🔥 FIXED: Payment status με ΚΕΙΜΕΝΟ
            const paymentInfo = isBooked ? getPaymentStatusInfo(week.booking.paymentStatus) : null;
            
            return (
              <button
                key={index}
                disabled={!isBooked}
                onClick={() => isBooked && setSelectedBookingForPopup(week.booking)}
                className={`w-full p-4 rounded-lg shadow-lg border-2 ${colorClass} ${isBooked ? 'hover:opacity-80 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] cursor-pointer transform-gpu' : 'cursor-default'} transition-all duration-300`}
                style={{ transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
                <div className="flex justify-between items-center">
                  <div className="text-left">
                    <p className="text-base font-semibold text-[#374151]">{`Εβδομάδα ${index + 1}`}</p>
                    <p className="text-sm text-[#6b7280]">{`${formatDate(week.start)} - ${formatDate(week.end)}`}</p>
                  </div>
                  <div className="text-right">
                    {isBooked ? (
                      <div className="relative">
                        {/* 🔥 Red light - μόνο αν βλέπει οικονομικά και είναι ΑΝΕΞΟΦΛΗΤΟ */}
                        {canViewFinancials && paymentInfo?.showLight && (
                          <div className={`absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full shadow-lg shadow-red-500/50 ${paymentInfo.lightBlink ? 'animate-pulse' : ''}`}></div>
                        )}
                        <p className="font-bold text-white text-xl">{week.booking.code}</p>
                        {/* Οικονομικά μόνο αν δεν είναι TECHNICAL */}
                        {canViewFinancials && (
                          <>
                            <p className="text-base font-semibold text-[#1e40af]">{week.booking.amount?.toFixed(2)}€</p>
                            <p className={`text-xs font-semibold ${paymentInfo?.color}`}>
                              {paymentInfo?.text}
                            </p>
                          </>
                        )}
                        <p className={`text-sm font-bold ${statusInfo.color}`}>{statusInfo.text}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 italic text-lg">Available</p>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Charter Detail Popup */}
      {selectedBookingForPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedBookingForPopup(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto text-[#374151]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Charter Details</h3>
              <button onClick={() => setSelectedBookingForPopup(null)} className="text-[#6b7280] hover:text-white text-2xl">&times;</button>
            </div>

            <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 space-y-2 border border-[#d1d5db]">
              <div className="flex justify-between"><span className="text-[#374151]">CODE:</span><span className="font-bold">{selectedBookingForPopup.code}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">YACHT:</span><span className="font-bold">{boat.name || boat.id}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">FROM:</span><span className="font-bold">{selectedBookingForPopup.startTime && `${selectedBookingForPopup.startTime} - `}{selectedBookingForPopup.startDate ? new Date(selectedBookingForPopup.startDate).toLocaleDateString('en-GB') : ''}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">TO:</span><span className="font-bold">{selectedBookingForPopup.endTime && `${selectedBookingForPopup.endTime} - `}{selectedBookingForPopup.endDate ? new Date(selectedBookingForPopup.endDate).toLocaleDateString('en-GB') : ''}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">DEPARTURE:</span><span className="font-bold">{selectedBookingForPopup.departure || 'ALIMOS MARINA'}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">ARRIVAL:</span><span className="font-bold">{selectedBookingForPopup.arrival || 'ALIMOS MARINA'}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">STATUS:</span><span className="font-bold">{selectedBookingForPopup.status?.toUpperCase()}</span></div>
            </div>

            {/* Skipper info */}
            {(selectedBookingForPopup.skipperFirstName || selectedBookingForPopup.skipperLastName) && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">SKIPPER:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-[#374151]">Name:</span><span className="font-bold">{selectedBookingForPopup.skipperFirstName} {selectedBookingForPopup.skipperLastName}</span></div>
                  {selectedBookingForPopup.skipperEmail && <div className="flex justify-between"><span className="text-[#374151]">Email:</span><span>{selectedBookingForPopup.skipperEmail}</span></div>}
                  {selectedBookingForPopup.skipperPhone && <div className="flex justify-between"><span className="text-[#374151]">Phone:</span><span>{selectedBookingForPopup.skipperPhone}</span></div>}
                  {selectedBookingForPopup.skipperAddress && <div className="flex justify-between"><span className="text-[#374151]">Address:</span><span>{selectedBookingForPopup.skipperAddress}</span></div>}
                </div>
              </div>
            )}

            {/* EXTRAS section */}
            {selectedBookingForPopup.extras && selectedBookingForPopup.extras.length > 0 && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">EXTRAS:</h4>
                <div className="space-y-1">
                  {selectedBookingForPopup.extras.map((extra: string, idx: number) => (
                    <div key={idx} className="flex items-center text-green-400">
                      <span className="mr-2">✓</span>
                      <span>{extra}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NOTES section */}
            {selectedBookingForPopup.notes && selectedBookingForPopup.notes.trim() && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2 text-blue-400">ΣΗΜΕΙΩΣΕΙΣ:</h4>
                <p className="text-[#374151] whitespace-pre-wrap">{selectedBookingForPopup.notes}</p>
              </div>
            )}

            {/* Financial info - only for non-technical users */}
            {canViewFinancials && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">FINANCIAL TERMS:</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-green-400"><span>Charter Fee:</span><span className="font-bold">{selectedBookingForPopup.amount?.toFixed(2)}€</span></div>
                  <div className="flex justify-between text-red-400"><span>Commission:</span><span className="font-bold">-{selectedBookingForPopup.commission?.toFixed(2)}€</span></div>
                  <div className="flex justify-between text-red-400"><span>VAT (24%):</span><span className="font-bold">-{selectedBookingForPopup.vat_on_commission?.toFixed(2)}€</span></div>
                  <hr className="border-[#d1d5db]" />
                  <div className="flex justify-between text-xl font-bold"><span>NET INCOME:</span><span className="text-[#1e40af]">{((selectedBookingForPopup.amount || 0) - (selectedBookingForPopup.commission || 0) - (selectedBookingForPopup.vat_on_commission || 0)).toFixed(2)}€</span></div>
                </div>
              </div>
            )}

            <button onClick={() => setSelectedBookingForPopup(null)} className="w-full bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-4 rounded-lg">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ΑΑΔΕ lookups (authorities + ports) — shared loader + cache
// ============================================================
const AADE_AUTH_LABEL = 'Λιμενική Αρχή ΑΑΔΕ';
let _aadeLookupsCache: any = null;
function useAadeLookups() {
  const [data, setData] = useState<any>(_aadeLookupsCache || { authorities: [], ports: [] });
  useEffect(() => {
    if (_aadeLookupsCache) { setData(_aadeLookupsCache); return; }
    let alive = true;
    fetch('https://yachtmanagementsuite.com/api/aade-lookups.php')
      .then(r => r.json())
      .then(d => {
        if (!d || (!Array.isArray(d.authorities) && !Array.isArray(d.ports))) return;
        _aadeLookupsCache = { authorities: d.authorities || [], ports: d.ports || [] };
        if (alive) setData(_aadeLookupsCache);
      })
      .catch(() => { /* dropdowns degrade to free-text */ });
    return () => { alive = false; };
  }, []);
  return data;
}

// Searchable dropdown. Stores a code (getKey), displays a label (getLabel).
// Legacy free-text values that match no option are shown as-is (not lost).
function SearchableSelect({ value, options, getKey, getLabel, getHint, getSearchText, onChange, disabled, placeholder, inputClassName }: any) {
  const hintOf = (o: any) => (getHint ? getHint(o) : getKey(o));
  const searchOf = (o: any) => (getSearchText ? getSearchText(o) : `${getLabel(o) || ''} ${hintOf(o) || ''}`);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o: any) => getKey(o) === value);
  const displayText = selected ? getLabel(selected) : (value || '');

  const q = query.trim().toLowerCase();
  const filtered = !q ? options : options.filter((o: any) =>
    String(searchOf(o) || '').toLowerCase().includes(q)
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        disabled={disabled}
        value={open ? query : displayText}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        className={inputClassName}
      />
      {open && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-[#d1d5db] rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#6b7280]">Κανένα αποτέλεσμα</div>
          ) : filtered.slice(0, 300).map((o: any) => (
            <div
              key={hintOf(o)}
              onMouseDown={(e) => { e.preventDefault(); onChange(getKey(o)); setOpen(false); setQuery(''); }}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 text-[#374151] ${getKey(o) === value ? 'bg-blue-100 font-semibold' : ''}`}
            >
              {getLabel(o)} <span className="text-xs text-[#9ca3af]">({hintOf(o)})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsAndDetailsPage({ boat, navigate, showMessage }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [activeTab, setActiveTab] = useState('details');
  const [boatDetails, setBoatDetails] = useState({});
  const aadeLookups = useAadeLookups();
  const [documents, setDocuments] = useState([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isSavingDoc, setIsSavingDoc] = useState(false);
  const fileInputRef = useRef(null);

  const isOwnerUser = authService.isOwner();
  const canEdit = authService.canEdit() && !isOwnerUser;
  const canView = true;

  const loadVesselDetails = async () => {
    try {
      // Fetch from API
      const response = await fetch(`https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(boat.name)}`);
      const result = await response.json();

      if (result.success) {
        const apiData = result.data || result;
        const customFields = typeof apiData.custom_fields === 'string'
          ? JSON.parse(apiData.custom_fields)
          : (apiData.custom_fields || {});

        // Merge API data with defaults
        const details = {
          'Όνομα Ιδιοκτήτη': (apiData.owner_first_name || '') + ' ' + (apiData.owner_last_name || ''),
          'Email Ιδιοκτήτη': apiData.owner_email || '',
          'Εταιρεία': apiData.company_name || '',
          'ΑΦΜ': apiData.vat_number || '',
          'Τηλέφωνο Ιδιοκτήτη': apiData.phone || '',
          'Διεύθυνση Ιδιοκτήτη': (apiData.street || '') + ' ' + (apiData.street_number || ''),
          'Flag': customFields['Flag'] || 'Greek',
          'Port of Registry': customFields['Port of Registry'] || 'Piraeus',
          'Builder/Year': customFields['Builder/Year'] || '',
          'LOA (Length)': customFields['LOA (Length)'] || '',
          'Beam (Width)': customFields['Beam (Width)'] || '',
          'Draft': customFields['Draft'] || '',
          'Engines': customFields['Engines'] || '',
          'Fuel Capacity': customFields['Fuel Capacity'] || '',
          'Water Capacity': customFields['Water Capacity'] || '',
          'Register No / Αριθμός Νηολογίου': customFields['register_no'] || '',
          'Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο': customFields['professional_license'] || '',
          'Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)': customFields['amepa'] || '',
          'CALL SIGN': customFields['call_sign'] || '',
          'Security Deposit / Εγγύηση': customFields['security_deposit'] || '',
          [AADE_AUTH_LABEL]: customFields['aade_authority'] || ''
        };
        setBoatDetails(details);
        console.log('✅ Loaded boat details from API:', details);
      } else {
        // Fallback to defaults
        const defaultDetails = {
          'Όνομα Ιδιοκτήτη': '',
          'Email Ιδιοκτήτη': '',
          'Εταιρεία': '',
          'ΑΦΜ': '',
          'Τηλέφωνο Ιδιοκτήτη': '',
          'Διεύθυνση Ιδιοκτήτη': '',
          'Flag': 'Greek',
          'Port of Registry': 'Piraeus',
          'Builder/Year': '',
          'LOA (Length)': '',
          'Beam (Width)': '',
          'Draft': '',
          'Engines': '',
          'Fuel Capacity': '',
          'Water Capacity': '',
          'Register No / Αριθμός Νηολογίου': '',
          'Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο': '',
          'Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)': '',
          'CALL SIGN': '',
          'Security Deposit / Εγγύηση': '',
          [AADE_AUTH_LABEL]: ''
        };
        setBoatDetails(defaultDetails);
      }
    } catch (e) {
      console.error('Error loading boat details:', e);
      // Fallback to defaults on error
      setBoatDetails({
        'Όνομα Ιδιοκτήτη': '',
        'Email Ιδιοκτήτη': '',
        'Εταιρεία': '',
        'ΑΦΜ': '',
        'Τηλέφωνο Ιδιοκτήτη': '',
        'Διεύθυνση Ιδιοκτήτη': '',
        'Flag': 'Greek',
        'Port of Registry': 'Piraeus',
        'Builder/Year': '',
        'LOA (Length)': '',
        'Beam (Width)': '',
        'Draft': '',
        'Engines': '',
        'Fuel Capacity': '',
        'Water Capacity': '',
        'Register No / Αριθμός Νηολογίου': '',
        'Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο': '',
        'Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)': '',
        'CALL SIGN': '',
        'Security Deposit / Εγγύηση': '',
        [AADE_AUTH_LABEL]: ''
      });
    }
  };

  // 🔥 FIX 4: Use optional chaining in dependencies
  useEffect(() => {
    if (boat) {
      loadVesselDetails();
      loadDocumentsFromAPI();
      // Auto-fill owner fields from API if empty
      (async () => {
        try {
          const res = await fetch(`https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(boat.name || '')}`);
          if (!res.ok) return;
          const raw = await res.json();
          const od = raw?.data || raw;
          if (!od || (!od.owner_first_name && !od.owner_email && !od.company_name)) return;
          setBoatDetails(prev => {
            const updated = { ...prev };
            // Remove old Insurance fields if present
            delete updated['Insurance Company'];
            delete updated['Insurance Policy Number'];
            // Owner fields from API
            if (!updated['Όνομα Ιδιοκτήτη']) updated['Όνομα Ιδιοκτήτη'] = [od.owner_first_name, od.owner_last_name].filter(Boolean).join(' ');
            if (!updated['Email Ιδιοκτήτη']) updated['Email Ιδιοκτήτη'] = od.owner_email || '';
            if (!updated['Εταιρεία']) updated['Εταιρεία'] = od.company_name || '';
            if (!updated['ΑΦΜ']) updated['ΑΦΜ'] = od.vat_number || '';
            if (!updated['Τηλέφωνο Ιδιοκτήτη']) updated['Τηλέφωνο Ιδιοκτήτη'] = od.phone || '';
            if (!updated['Διεύθυνση Ιδιοκτήτη']) updated['Διεύθυνση Ιδιοκτήτη'] = [od.street, od.street_number, od.postal_code, od.city].filter(Boolean).join(', ');
            // Custom fields from API (new vessel fields)
            if (od.custom_fields) {
              try {
                const cf = typeof od.custom_fields === 'string' ? JSON.parse(od.custom_fields) : od.custom_fields;
                const fieldMap = {
                  'register_no': 'Register No / Αριθμός Νηολογίου',
                  'professional_license': 'Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο',
                  'amepa': 'Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)',
                  'call_sign': 'CALL SIGN',
                  'security_deposit': 'Security Deposit / Εγγύηση',
                  'aade_authority': AADE_AUTH_LABEL
                };
                for (const [apiKey, uiKey] of Object.entries(fieldMap)) {
                  if (cf[apiKey]) updated[uiKey] = cf[apiKey];
                }
              } catch (e) { /* ignore parse errors */ }
            }
            return updated;
          });
        } catch (e) {
          console.log('Owner auto-fill error:', e);
        }
      })();
    }
  }, [boat?.id]);

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  const saveBoatDetails = (newDetails, explicitCustomFields?) => {
    if (!canEdit) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    try {
      // Save to localStorage
      const key = `fleet_${boat.id}_details`;
      localStorage.setItem(key, JSON.stringify(newDetails));
      setBoatDetails(newDetails);
      showMessage('✅ Τα στοιχεία αποθηκεύτηκαν!', 'success');

      // Build custom fields for API
      const skipFields = new Set([
        'Όνομα Ιδιοκτήτη', 'Email Ιδιοκτήτη', 'Εταιρεία', 'ΑΦΜ',
        'Τηλέφωνο Ιδιοκτήτη', 'Διεύθυνση Ιδιοκτήτη'
      ]);
      const cfKeyMap = {
        'Register No / Αριθμός Νηολογίου': 'register_no',
        'Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο': 'professional_license',
        'Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)': 'amepa',
        'CALL SIGN': 'call_sign',
        'Security Deposit / Εγγύηση': 'security_deposit',
        [AADE_AUTH_LABEL]: 'aade_authority'
      };
      const customFields = {};
      for (const [field, value] of Object.entries(newDetails)) {
        if (skipFields.has(field)) continue;
        const apiKey = cfKeyMap[field] || field;
        customFields[apiKey] = value;
      }

      // Merge explicit custom fields passed from the button (always overrides)
      if (explicitCustomFields) {
        Object.assign(customFields, explicitCustomFields);
      }

      // POST custom_fields to vessel-owners API
      const vesselName = boat.name || '';
      const payload = {
        vessel_name: vesselName,
        custom_fields: JSON.stringify(customFields)
      };
      console.log('📋 saveBoatDetails: POSTing to vessel-owners.php', payload);

      fetch('https://yachtmanagementsuite.com/api/vessel-owners.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(res => {
          console.log('📋 saveBoatDetails: API response status', res.status);
          if (!res.ok) {
            console.error('📋 saveBoatDetails: API error', res.status);
          }
          return res.json().catch(() => ({}));
        })
        .then(data => {
          console.log('📋 saveBoatDetails: API response data', data);
        })
        .catch(err => {
          console.error('📋 saveBoatDetails: fetch error', err);
        });
    } catch (e) {
      console.error('Error saving boat details:', e);
      showMessage('❌ Σφάλμα αποθήκευσης!', 'error');
    }
  };

  const handleDetailChange = (field, value) => {
    if (!canEdit) return;
    const updated = { ...boatDetails, [field]: value };
    setBoatDetails(updated);
  };

  const handleAddField = () => {
    if (!canEdit) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }
    
    const fieldName = prompt('Όνομα νέου πεδίου:');
    if (fieldName && fieldName.trim()) {
      const updated = { ...boatDetails, [fieldName.trim()]: '' };
      saveBoatDetails(updated);
    }
  };

  const handleRemoveField = (field) => {
    if (!canEdit) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }
    
    if (window.confirm(`Διαγραφή πεδίου "${field}";`)) {
      const updated = { ...boatDetails };
      delete updated[field];
      saveBoatDetails(updated);
    }
  };

  // Load documents from API, with localStorage fallback
  const loadDocumentsFromAPI = async () => {
    setIsLoadingDocs(true);
    try {
      const vesselName = boat.name || boat.id;
      console.log('🔄 Loading documents from API for vessel:', vesselName);
      const apiUrl = `https://yachtmanagementsuite.com/api/vessel-documents.php?vessel_name=${encodeURIComponent(vesselName)}`;
      console.log('🔗 Documents API URL:', apiUrl);

      const response = await fetch(apiUrl);
      console.log('📡 Documents API Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Documents API Response data:', data);

        // Handle different response formats - array directly or nested in documents/data
        const docsArray = Array.isArray(data) ? data :
                         Array.isArray(data.documents) ? data.documents :
                         Array.isArray(data.data) ? data.data : null;

        if (docsArray && docsArray.length > 0) {
          // Map API fields to UI fields
          const mappedDocs = docsArray.map(doc => ({
            id: doc.id || doc.document_id || uid(),
            title: doc.title || doc.document_name || '',
            document_type: typeof doc.document_type === 'number' ? doc.document_type : (parseInt(doc.document_type) || 0),
            fileName: doc.fileName || doc.file_name || doc.document_name || '',
            fileType: doc.fileType || doc.file_type || 'application/pdf',
            fileData: doc.fileData || doc.file_data || '',
            uploadedAt: doc.uploadedAt || doc.uploaded_at || doc.created_at || new Date().toISOString(),
            uploadedBy: doc.uploadedBy || doc.uploaded_by || '',
            expiryDate: doc.expiryDate || doc.expiry_date || null
          }));
          console.log('✅ Mapped documents:', mappedDocs);
          setDocuments(mappedDocs);
          console.log('✅ Loaded', mappedDocs.length, 'documents from API for vessel:', vesselName);
          setIsLoadingDocs(false);
          return;
        } else {
          console.log('⚠️ API returned no documents for vessel:', vesselName);
        }
      } else {
        console.log('❌ Documents API request failed with status:', response.status);
      }

      // Fallback to localStorage
      console.log('🔍 Trying localStorage fallback for documents');
      const key = `fleet_${boat.id}_documents`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDocuments(parsed);
        console.log('✅ Loaded', parsed.length, 'documents from localStorage for boat:', boat.id);
      } else {
        console.log('⚠️ No documents found in localStorage for boat:', boat.id);
      }
    } catch (e) {
      console.error('❌ Error loading documents from API:', e);
      // Fallback to localStorage on error
      try {
        const key = `fleet_${boat.id}_documents`;
        const stored = localStorage.getItem(key);
        if (stored) {
          setDocuments(JSON.parse(stored));
          console.log('✅ Loaded documents from localStorage (API fallback) for boat:', boat.id);
        }
      } catch (localErr) {
        console.error('Error loading from localStorage:', localErr);
      }
    }
    setIsLoadingDocs(false);
  };

  // Save single document to API
  const saveDocumentToAPI = async (doc) => {
    setIsSavingDoc(true);
    const vesselName = boat.name || boat.id;

    // Calculate auto-incremented document_type number
    const maxDocType = documents.reduce((max, d) => {
      const num = typeof d.document_type === 'number' ? d.document_type : (parseInt(d.document_type) || 0);
      return num > max ? num : max;
    }, 0);
    const nextDocType = maxDocType + 1;

    // Store the document_type number on the doc object for state consistency
    doc.document_type = nextDocType;

    // Prepare the payload with correct field mapping
    const payload = {
      vessel_name: vesselName,
      document_id: doc.id,
      document_type: nextDocType,  // Auto-incremented number
      document_name: doc.title,  // The user-typed title (e.g. "kaykas")
      file_name: doc.fileName,
      file_type: doc.fileType,
      file_data: doc.fileData,
      uploaded_at: doc.uploadedAt,
      uploaded_by: doc.uploadedBy,
      expiry_date: doc.expiryDate || null
    };

    console.log('📤 Saving document to API:', {
      vessel_name: vesselName,
      document_id: doc.id,
      document_type: nextDocType,
      document_name: doc.title,
      file_type: doc.fileType,
      has_file_data: !!doc.fileData,
      file_data_length: doc.fileData ? doc.fileData.length : 0
    });

    try {
      const response = await fetch('https://yachtmanagementsuite.com/api/vessel-documents.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('📥 API response:', response.status, responseText);

      if (response.ok) {
        console.log('✅ Saved document to API for vessel:', vesselName);
        const newDocs = [...documents, doc];
        // Cache to localStorage (non-critical - API is the source of truth)
        try {
          const key = `fleet_${boat.id}_documents`;
          localStorage.setItem(key, JSON.stringify(newDocs));
        } catch (localErr) {
          console.warn('⚠️ localStorage cache failed (quota exceeded), API save succeeded:', localErr);
        }
        setDocuments(newDocs);
        setIsSavingDoc(false);
        return true;
      } else {
        throw new Error(`API save failed: ${response.status} - ${responseText}`);
      }
    } catch (e) {
      console.error('❌ Error saving document to API:', e);
      setIsSavingDoc(false);
      return false;
    }
  };

  // Delete document from API
  const deleteDocumentFromAPI = async (docId) => {
    const vesselName = boat.name || boat.id;

    console.log('🗑️ Deleting document from API:', { vessel_name: vesselName, document_id: docId });

    try {
      const response = await fetch('https://yachtmanagementsuite.com/api/vessel-documents.php', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vessel_name: vesselName,
          document_id: docId
        })
      });

      const responseText = await response.text();
      console.log('📥 Delete API response:', response.status, responseText);

      if (response.ok) {
        console.log('✅ Deleted document from API for vessel:', vesselName);
      } else {
        console.log('⚠️ Delete API request returned status:', response.status);
      }
    } catch (e) {
      console.error('❌ Error deleting document from API:', e);
    }

    // Always update state, cache to localStorage (non-critical)
    const newDocs = documents.filter(d => d.id !== docId);
    try {
      const key = `fleet_${boat.id}_documents`;
      localStorage.setItem(key, JSON.stringify(newDocs));
    } catch (localErr) {
      console.warn('⚠️ localStorage cache failed (quota exceeded), API delete succeeded:', localErr);
    }
    setDocuments(newDocs);
    console.log('✅ Removed document from local state');
  };

  // Legacy saveDocuments for compatibility (saves all docs to localStorage)
  const saveDocuments = (docs) => {
    try {
      const key = `fleet_${boat.id}_documents`;
      localStorage.setItem(key, JSON.stringify(docs));
      setDocuments(docs);
    } catch (e) {
      console.error('Error saving documents:', e);
    }
  };

  const handleFileUpload = (e) => {
    if (!canEdit) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (!newDocTitle.trim()) {
      showMessage('❌ Παρακαλώ βάλτε τίτλο!', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const doc = {
        id: uid(),
        title: newDocTitle.trim(),
        fileName: file.name,
        fileType: file.type,
        fileData: reader.result,
        uploadedAt: new Date().toISOString(),
        uploadedBy: authService.getCurrentUser()?.name || 'Unknown'
      };

      const success = await saveDocumentToAPI(doc);
      if (success) {
        authService.logActivity('upload_document', `${boat.id}/${doc.fileName}`);
        setNewDocTitle('');
        setShowAddDoc(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        showMessage('✅ Έγγραφο προστέθηκε!', 'success');
      } else {
        showMessage('❌ Σφάλμα αποθήκευσης!', 'error');
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePreviewDocument = (doc) => {
    try {
        const byteString = atob(doc.fileData.split(",")[1]);
        const mimeType = doc.fileData.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: mimeType });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    } catch (e) {
        console.error("Preview error:", e);
        showMessage("\u274c Δεν μπορεί να ανοίξει preview!", "error");
    }
  };

  const handleDownloadDocument = (doc) => {
    try {
      const link = document.createElement('a');
      link.href = doc.fileData;
      link.download = doc.fileName;
      link.click();
      authService.logActivity('download_document', `${boat.id}/${doc.fileName}`);
      showMessage('✅ Λήψη ξεκίνησε!', 'success');
    } catch (e) {
      console.error('Error downloading document:', e);
      showMessage('❌ Σφάλμα λήψης!', 'error');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!canEdit) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα διαγραφής', 'error');
      return;
    }

    if (window.confirm('Διαγραφή εγγράφου;')) {
      const doc = documents.find(d => d.id === docId);
      await deleteDocumentFromAPI(docId);
      authService.logActivity('delete_document', `${boat.id}/${doc?.fileName}`);
      showMessage('✅ Έγγραφο διαγράφηκε!', 'success');
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title="ΕΓΓΡΑΦΑ & ΣΤΟΙΧΕΙΑ" onBack={() => navigate('boatDashboard')} />

      {isOwnerUser && (
        <div className="p-2 bg-blue-900 border-b border-blue-700 text-center">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">
            {icons.eye}
            <span>View Only Mode - Μόνο προβολή και λήψη</span>
          </div>
        </div>
      )}

      <div className="flex border-b border-[#d1d5db] bg-white">
        <button onClick={() => { setActiveTab('details'); authService.logActivity('view_boat_details', boat.id); }} className={`flex-1 py-3 px-4 font-semibold ${activeTab === 'details' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          📋 Στοιχεία
        </button>
        <button onClick={() => { setActiveTab('documents'); authService.logActivity('view_boat_documents', boat.id); }} className={`flex-1 py-3 px-4 font-semibold ${activeTab === 'documents' ? 'text-[#1e40af] border-b-2 border-[#1e40af] bg-[#f3f4f6]' : 'text-[#6b7280]'}`}>
          📄 Έγγραφα
        </button>
      </div>

      <div className="flex-grow p-4 overflow-y-auto pb-20">
        {activeTab === 'details' && (
          <div>
            {canEdit && (
              <div className="mb-4 space-y-2">
                <button onClick={() => saveBoatDetails(boatDetails, {
                  register_no: boatDetails['Register No / Αριθμός Νηολογίου'] || '',
                  professional_license: boatDetails['Αριθμ. Πρωτ. Αδείας Επαγγελματικού Πλοίου Αναψυχής / E-μητρώο'] || '',
                  amepa: boatDetails['Μοναδικό Αριθμό Μητρώου Επαγγελματικού Πλοίου Αναψυχής (Α.Μ.Ε.Π.Α)'] || '',
                  call_sign: boatDetails['CALL SIGN'] || '',
                  security_deposit: boatDetails['Security Deposit / Εγγύηση'] || ''
                })} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-lg">
                  <span>💾</span><span>Αποθήκευση Στοιχείων</span>
                </button>
                <button onClick={handleAddField} className="w-full bg-white hover:bg-[#f9fafb] text-[#374151] font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 border border-[#d1d5db]">
                  <span>➕</span><span>Προσθήκη Νέου Πεδίου</span>
                </button>
              </div>
            )}

            {/* Technical Details - Owner details moved to ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ section */}
            <h4 className="text-md font-bold text-[#6b7280] mb-3">📋 Τεχνικά Στοιχεία</h4>
            <div className="space-y-4">
              {/* ΑΑΔΕ Authority — searchable dropdown (saves code, displays name) */}
              <div className="bg-white p-4 rounded-lg border border-[#d1d5db]">
                <label className="block text-sm font-semibold text-[#374151] mb-2">⚓ Λιμενική Αρχή ΑΑΔΕ</label>
                <SearchableSelect
                  value={boatDetails[AADE_AUTH_LABEL] || ''}
                  options={aadeLookups.authorities || []}
                  getKey={(o: any) => o.code}
                  getLabel={(o: any) => o.name_el}
                  onChange={(code: string) => handleDetailChange(AADE_AUTH_LABEL, code)}
                  disabled={!canEdit}
                  placeholder="Αναζήτηση αρχής (π.χ. Πειραιά)..."
                  inputClassName={`w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'focus:border-[#1e40af] focus:outline-none'}`}
                />
                <p className="text-xs text-[#6b7280] mt-1">Χρησιμοποιείται στο ΑΑΔΕ export αυτού του σκάφους (default: Κ.Λ. ΠΕΙΡΑΙΑ)</p>
              </div>
              {Object.entries(boatDetails)
                .filter(([field]) => field !== AADE_AUTH_LABEL)
                .map(([field, value]) => (
                <div key={field} className="bg-white p-4 rounded-lg border border-[#d1d5db]">
                  <div className="flex justify-between items-start mb-2">
                    <label className="text-sm font-semibold text-[#374151]">{field}</label>
                    {canEdit && (
                      <button onClick={() => handleRemoveField(field)} className="text-red-500 hover:text-red-400">{icons.x}</button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleDetailChange(field, e.target.value)}
                    disabled={!canEdit}
                    className={`w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'focus:border-[#1e40af] focus:outline-none'}`}
                    placeholder={canEdit ? 'Εισάγετε τιμή...' : 'Δεν υπάρχει τιμή'}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            {canEdit && (
              <div className="mb-4">
                <button onClick={() => setShowAddDoc(!showAddDoc)} className="w-full bg-white hover:bg-[#f9fafb] text-[#374151] font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 border border-[#d1d5db]">
                  {icons.plus}
                  <span>{showAddDoc ? 'Ακύρωση' : 'Προσθήκη Εγγράφου'}</span>
                </button>

                {showAddDoc && (
                  <div className="mt-4 p-4 bg-white rounded-lg space-y-3 border border-[#d1d5db]">
                    <input type="text" value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} placeholder="Τίτλος εγγράφου (π.χ. Boarding Pass)" className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
                    <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db]" />
                    <p className="text-xs text-[#6b7280]">Υποστηριζόμενα: PDF, Word (.doc, .docx)</p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {documents.length === 0 ? (
                <div className="bg-white p-8 rounded-lg text-center border border-[#d1d5db]">
                  <div className="text-5xl mb-3">📄</div>
                  <p className="text-[#6b7280]">Δεν υπάρχουν έγγραφα</p>
                </div>
              ) : (
                documents.map((doc) => (
                  <div key={doc.id} className="bg-white p-4 rounded-lg border border-[#d1d5db]">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#1e40af] cursor-pointer hover:underline" onClick={() => handlePreviewDocument(doc)}>{doc.title}</h3>
                        <p className="text-sm text-[#6b7280]">{doc.fileName}</p>
                        <p className="text-xs text-gray-500">📅 {new Date(doc.uploadedAt).toLocaleDateString('en-GB')} {doc.uploadedBy && ` • 👤 ${doc.uploadedBy}`}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleDownloadDocument(doc)} className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg" title="Download">{icons.download}</button>
                        {canEdit && (
                          <button onClick={() => handleDeleteDocument(doc.id)} className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg" title="Delete">{icons.x}</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <BottomNav activePage={null} onNavigate={navigate} />
    </div>
  );
}

// =====================================================
// 🔥 FIX 37: OWNER DETAILS PAGE - Dedicated page for owner information
// =====================================================
function OwnerDetailsPage({ boat, navigate, showMessage }) {
  const [ownerDetails, setOwnerDetails] = useState({
    'Όνομα': '',
    'Επώνυμο': '',
    'Email Ιδιοκτήτη': '',
    'Email Εταιρείας': '',
    'Εταιρεία': '',
    'ΑΦΜ': '',
    'Αριθμ. Ταυτότητας / Διαβατηρίου': '',
    'Αρμόδια ΔΟΥ': '',
    'Τηλέφωνο Ιδιοκτήτη': '',
    'Οδός': '',
    'Αριθμός': '',
    'Πόλη': '',
    'Τ.Κ.': ''
  });
  const [ownerCode, setOwnerCode] = useState(''); // 🔥 FIX 37B: Track owner code for sync
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [idPassportFile, setIdPassportFile] = useState<{name: string; dataUrl: string} | null>(null);
  const [isUploadingIdFile, setIsUploadingIdFile] = useState(false);
  const idPassportFileInputRef = React.useRef<HTMLInputElement>(null);

  // Ενεργές εταιρίες (financial_entities) → dropdown σύνδεσης σκάφους↔εταιρίας
  const [financialEntities, setFinancialEntities] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const ec = authService.getCurrentUser()?.code || '';
        const r = await fetch('/api/financial-entities.php?action=list', { headers: { 'X-Employee-Code': ec } });
        const j = await r.json();
        if (j?.success) setFinancialEntities(j.entities || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // Fixed fields that cannot be deleted
  const FIXED_FIELDS = ['Όνομα', 'Επώνυμο', 'Email Ιδιοκτήτη', 'Email Εταιρείας', 'Εταιρεία', 'ΑΦΜ', 'Αριθμ. Ταυτότητας / Διαβατηρίου', 'Αρμόδια ΔΟΥ', 'Τηλέφωνο Ιδιοκτήτη', 'Οδός', 'Αριθμός', 'Πόλη', 'Τ.Κ.'];

  const canEdit = authService.canManageFleet();

  useEffect(() => {
    if (boat) {
      loadOwnerDetailsFromAPI();
    }
  }, [boat?.id]);

  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  // Load owner details from API, with localStorage/authService fallback
  const loadOwnerDetailsFromAPI = async () => {
    setIsLoading(true);
    try {
      // PRIORITY 1: Load from API
      const vesselName = boat.name || boat.id;
      console.log('🔄 Loading owner details from API for vessel:', vesselName);
      const apiUrl = `https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(vesselName)}`;
      console.log('🔗 API URL:', apiUrl);

      const response = await fetch(apiUrl);
      console.log('📡 API Response status:', response.status, response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 API Response data:', data);

        // Check if we have valid data - the API might return the owner object directly or nested
        const ownerData = data?.data || data;
        const hasData = ownerData && (
          ownerData.vessel_name ||
          ownerData.owner_first_name ||
          ownerData.owner_email ||
          ownerData.company_name
        );

        if (hasData) {
          // Map API fields to UI fields
          const loadedDetails = {
            'Όνομα': ownerData.owner_first_name || '',
            'Επώνυμο': ownerData.owner_last_name || '',
            'Email Ιδιοκτήτη': ownerData.owner_email || '',
            'Email Εταιρείας': ownerData.company_email || '',
            'Εταιρεία': ownerData.company_name || '',
            'ΑΦΜ': ownerData.vat_number || '',
            'Αριθμ. Ταυτότητας / Διαβατηρίου': ownerData.id_passport_number || '',
            'Αρμόδια ΔΟΥ': ownerData.tax_office || '',
            'Τηλέφωνο Ιδιοκτήτη': ownerData.phone || '',
            'Οδός': ownerData.street || '',
            'Αριθμός': ownerData.street_number || '',
            'Πόλη': ownerData.city || '',
            'Τ.Κ.': ownerData.postal_code || ''
          };
          // Add custom fields if they exist
          if (ownerData.custom_fields) {
            try {
              const customFields = typeof ownerData.custom_fields === 'string'
                ? JSON.parse(ownerData.custom_fields)
                : ownerData.custom_fields;
              Object.entries(customFields).forEach(([key, value]) => {
                if (!FIXED_FIELDS.includes(key)) {
                  loadedDetails[key] = value;
                }
              });
            } catch (e) {
              console.error('Error parsing custom fields:', e);
            }
          }
          console.log('✅ Mapped owner details:', loadedDetails);
          setOwnerDetails(loadedDetails);
          // Load ID/passport file if it exists
          if (ownerData.id_passport_file) {
            setIdPassportFile({ name: ownerData.id_passport_file_name || 'ID/Passport', dataUrl: ownerData.id_passport_file });
          }
          console.log('✅ Loaded owner details from API for vessel:', vesselName);
          setIsLoading(false);
          return;
        } else {
          console.log('⚠️ API returned no owner data for vessel:', vesselName);
        }
      } else {
        console.log('❌ API request failed with status:', response.status);
      }

      // PRIORITY 2: Fallback to authService (Admin Panel source)
      console.log('🔍 Trying authService fallback for boat:', boat.id);
      const ownerFromAuth = getOwnerByBoatId(boat.id);
      console.log('📋 Owner from authService:', ownerFromAuth);
      if (ownerFromAuth) {
        setOwnerCode(ownerFromAuth.code || '');
        setOwnerDetails({
          'Όνομα': ownerFromAuth.ownerFirstName || '',
          'Επώνυμο': ownerFromAuth.ownerLastName || '',
          'Email Ιδιοκτήτη': ownerFromAuth.ownerEmail || '',
          'Email Εταιρείας': ownerFromAuth.ownerCompanyEmail || '',
          'Εταιρεία': ownerFromAuth.ownerCompany || '',
          'ΑΦΜ': ownerFromAuth.ownerTaxId || '',
          'Αριθμ. Ταυτότητας / Διαβατηρίου': ownerFromAuth.ownerIdPassportNumber || '',
          'Αρμόδια ΔΟΥ': ownerFromAuth.ownerTaxOffice || '',
          'Τηλέφωνο Ιδιοκτήτη': ownerFromAuth.ownerPhone || '',
          'Οδός': ownerFromAuth.ownerStreet || '',
          'Αριθμός': ownerFromAuth.ownerNumber || '',
          'Πόλη': ownerFromAuth.ownerCity || '',
          'Τ.Κ.': ownerFromAuth.ownerPostalCode || ''
        });
        console.log('✅ Loaded owner details from authService for boat:', boat.id);
        setIsLoading(false);
        return;
      }

      // PRIORITY 3: Fallback to localStorage (boat-specific storage)
      console.log('🔍 Trying localStorage fallback for boat:', boat.id);
      const key = `fleet_${boat.id}_ownerDetails`;
      const stored = localStorage.getItem(key);
      console.log('📋 localStorage data:', stored ? 'found' : 'not found');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Handle backwards compatibility - migrate old fields to new format
        if (parsed['Όνομα Ιδιοκτήτη'] && !parsed['Όνομα']) {
          parsed['Όνομα'] = '';
          parsed['Επώνυμο'] = '';
        }
        if (!parsed['Email Εταιρείας']) {
          parsed['Email Εταιρείας'] = '';
        }
        if (parsed['Διεύθυνση Ιδιοκτήτη'] && !parsed['Οδός']) {
          parsed['Οδός'] = '';
          parsed['Αριθμός'] = '';
          parsed['Πόλη'] = '';
          parsed['Τ.Κ.'] = '';
        }
        setOwnerDetails(parsed);
        console.log('✅ Loaded owner details from localStorage for boat:', boat.id);
      } else {
        console.log('⚠️ No owner data found in any source for boat:', boat.id);
      }
    } catch (e) {
      console.error('Error loading owner details from API:', e);
      // Fallback to localStorage on error
      try {
        const key = `fleet_${boat.id}_ownerDetails`;
        const stored = localStorage.getItem(key);
        if (stored) {
          setOwnerDetails(JSON.parse(stored));
          console.log('✅ Loaded owner details from localStorage (API fallback) for boat:', boat.id);
        }
      } catch (localErr) {
        console.error('Error loading from localStorage:', localErr);
      }
    }
    setIsLoading(false);
  };

  // Save owner details to API, with localStorage as fallback
  const saveOwnerDetails = async () => {
    if (!canEdit) {
      showMessage('❌ Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    setIsSaving(true);
    const vesselName = boat.name || boat.id;

    // Collect custom fields (non-fixed fields) from owner page
    const ownerCustomFields = {};
    Object.entries(ownerDetails).forEach(([key, value]) => {
      if (!FIXED_FIELDS.includes(key)) {
        ownerCustomFields[key] = value;
      }
    });

    // Fetch existing custom_fields from API to preserve boat-level fields (Register No, AMEPA, etc.)
    let existingCf: any = {};
    try {
      const cfRes = await fetch(`https://yachtmanagementsuite.com/api/vessel-owners.php?vessel_name=${encodeURIComponent(vesselName)}`);
      if (cfRes.ok) {
        const cfRaw = await cfRes.json();
        const cfData = cfRaw?.data || cfRaw;
        if (cfData?.custom_fields) {
          const parsed = typeof cfData.custom_fields === 'string' ? JSON.parse(cfData.custom_fields) : cfData.custom_fields;
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            existingCf = parsed;
          }
        }
      }
    } catch (e) { /* ignore, will use ownerCustomFields only */ }

    // Merge: existing boat-level custom fields + any owner custom fields
    const mergedCf = { ...existingCf, ...ownerCustomFields };

    // Prepare API payload
    const apiPayload = {
      vessel_name: vesselName,
      owner_first_name: ownerDetails['Όνομα'] || '',
      owner_last_name: ownerDetails['Επώνυμο'] || '',
      owner_email: ownerDetails['Email Ιδιοκτήτη'] || '',
      company_email: ownerDetails['Email Εταιρείας'] || '',
      company_name: ownerDetails['Εταιρεία'] || '',
      vat_number: ownerDetails['ΑΦΜ'] || '',
      id_passport_number: ownerDetails['Αριθμ. Ταυτότητας / Διαβατηρίου'] || '',
      id_passport_file: idPassportFile?.dataUrl || null,
      id_passport_file_name: idPassportFile?.name || null,
      tax_office: ownerDetails['Αρμόδια ΔΟΥ'] || '',
      phone: ownerDetails['Τηλέφωνο Ιδιοκτήτη'] || '',
      street: ownerDetails['Οδός'] || '',
      street_number: ownerDetails['Αριθμός'] || '',
      city: ownerDetails['Πόλη'] || '',
      postal_code: ownerDetails['Τ.Κ.'] || '',
      custom_fields: JSON.stringify(mergedCf)
    };

    try {
      // PRIORITY 1: Save to API
      const response = await fetch('https://yachtmanagementsuite.com/api/vessel-owners.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });

      if (response.ok) {
        console.log('✅ Saved owner details to API for vessel:', vesselName);

        // Also save to localStorage as backup
        const key = `fleet_${boat.id}_ownerDetails`;
        localStorage.setItem(key, JSON.stringify(ownerDetails));

        // Sync to authService if owner code exists
        if (ownerCode) {
          authService.updateOwnerCode(ownerCode, {
            ownerFirstName: ownerDetails['Όνομα'],
            ownerLastName: ownerDetails['Επώνυμο'],
            ownerEmail: ownerDetails['Email Ιδιοκτήτη'],
            ownerCompanyEmail: ownerDetails['Email Εταιρείας'],
            ownerCompany: ownerDetails['Εταιρεία'],
            ownerTaxId: ownerDetails['ΑΦΜ'],
            ownerIdPassportNumber: ownerDetails['Αριθμ. Ταυτότητας / Διαβατηρίου'],
            ownerTaxOffice: ownerDetails['Αρμόδια ΔΟΥ'],
            ownerPhone: ownerDetails['Τηλέφωνο Ιδιοκτήτη'],
            ownerStreet: ownerDetails['Οδός'],
            ownerNumber: ownerDetails['Αριθμός'],
            ownerCity: ownerDetails['Πόλη'],
            ownerPostalCode: ownerDetails['Τ.Κ.']
          });
        }

        authService.logActivity('update_owner_details', boat.id);
        showMessage('✅ Τα στοιχεία ιδιοκτήτη αποθηκεύτηκαν!', 'success');
      } else {
        throw new Error('API save failed');
      }
    } catch (e) {
      console.error('Error saving owner details to API:', e);

      // Fallback: Save to localStorage
      try {
        const key = `fleet_${boat.id}_ownerDetails`;
        localStorage.setItem(key, JSON.stringify(ownerDetails));

        if (ownerCode) {
          authService.updateOwnerCode(ownerCode, {
            ownerFirstName: ownerDetails['Όνομα'],
            ownerLastName: ownerDetails['Επώνυμο'],
            ownerEmail: ownerDetails['Email Ιδιοκτήτη'],
            ownerCompanyEmail: ownerDetails['Email Εταιρείας'],
            ownerCompany: ownerDetails['Εταιρεία'],
            ownerTaxId: ownerDetails['ΑΦΜ'],
            ownerIdPassportNumber: ownerDetails['Αριθμ. Ταυτότητας / Διαβατηρίου'],
            ownerTaxOffice: ownerDetails['Αρμόδια ΔΟΥ'],
            ownerPhone: ownerDetails['Τηλέφωνο Ιδιοκτήτη'],
            ownerStreet: ownerDetails['Οδός'],
            ownerNumber: ownerDetails['Αριθμός'],
            ownerCity: ownerDetails['Πόλη'],
            ownerPostalCode: ownerDetails['Τ.Κ.']
          });
        }

        authService.logActivity('update_owner_details', boat.id);
        showMessage('✅ Αποθηκεύτηκε τοπικά (API μη διαθέσιμο)', 'success');
        console.log('✅ Saved owner details to localStorage (API fallback) for boat:', boat.id);
      } catch (localErr) {
        console.error('Error saving to localStorage:', localErr);
        showMessage('❌ Σφάλμα αποθήκευσης!', 'error');
      }
    }
    setIsSaving(false);
  };

  const handleChange = (field, value) => {
    if (!canEdit) return;
    setOwnerDetails(prev => ({ ...prev, [field]: value }));
  };

  // Handle ID/Passport file upload
  const handleIdPassportFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) {
      showMessage('❌ Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setIsUploadingIdFile(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const vesselName = boat.name || boat.id;

      try {
        const response = await fetch('https://yachtmanagementsuite.com/api/vessel-owners.php?action=upload_id_file', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vessel_name: vesselName,
            file_name: file.name,
            file_type: file.type,
            file_data: dataUrl
          })
        });

        if (response.ok) {
          const result = await response.json();
          setIdPassportFile({ name: file.name, dataUrl: result.file_path || dataUrl });
          showMessage('✅ Αρχείο ταυτότητας/διαβατηρίου ανέβηκε!', 'success');
        } else {
          // Fallback: store in localStorage
          setIdPassportFile({ name: file.name, dataUrl });
          showMessage('✅ Αρχείο αποθηκεύτηκε τοπικά', 'success');
        }
      } catch (err) {
        // Fallback: store in localStorage
        setIdPassportFile({ name: file.name, dataUrl });
        showMessage('✅ Αρχείο αποθηκεύτηκε τοπικά', 'success');
      }
      setIsUploadingIdFile(false);
    };
    reader.onerror = () => {
      showMessage('❌ Σφάλμα ανάγνωσης αρχείου', 'error');
      setIsUploadingIdFile(false);
    };
    reader.readAsDataURL(file);
  };

  // View ID/Passport file
  const handleViewIdPassportFile = () => {
    if (!idPassportFile?.dataUrl) return;
    const newWindow = window.open();
    if (newWindow) {
      if (idPassportFile.dataUrl.startsWith('data:application/pdf') || idPassportFile.name.endsWith('.pdf')) {
        newWindow.document.write(`<iframe src="${idPassportFile.dataUrl}" width="100%" height="100%" style="border:none;"></iframe>`);
      } else {
        newWindow.document.write(`<img src="${idPassportFile.dataUrl}" style="max-width:100%;"/>`);
      }
    }
  };

  // Remove ID/Passport file
  const handleRemoveIdPassportFile = () => {
    if (!canEdit) return;
    if (window.confirm('Διαγραφή αρχείου ταυτότητας/διαβατηρίου;')) {
      setIdPassportFile(null);
    }
  };

  const handleAddField = () => {
    if (!canEdit) return;
    if (!newFieldName.trim()) {
      showMessage('❌ Εισάγετε όνομα πεδίου!', 'error');
      return;
    }
    if (ownerDetails.hasOwnProperty(newFieldName.trim())) {
      showMessage('❌ Το πεδίο υπάρχει ήδη!', 'error');
      return;
    }
    setOwnerDetails(prev => ({ ...prev, [newFieldName.trim()]: '' }));
    setNewFieldName('');
    setShowAddField(false);
    showMessage('✅ Το πεδίο προστέθηκε!', 'success');
  };

  const handleRemoveField = (field) => {
    if (!canEdit) return;
    if (FIXED_FIELDS.includes(field)) {
      showMessage('❌ Δεν μπορείτε να διαγράψετε αυτό το πεδίο!', 'error');
      return;
    }
    if (window.confirm(`Διαγραφή του πεδίου "${field}";`)) {
      setOwnerDetails(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
      showMessage('✅ Το πεδίο διαγράφηκε!', 'success');
    }
  };

  // Get custom fields (non-fixed fields)
  const customFields = Object.entries(ownerDetails).filter(([field]) => !FIXED_FIELDS.includes(field));

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title="ΣΤΟΙΧΕΙΑ ΙΔΙΟΚΤΗΤΗ" onBack={() => navigate('boatDashboard')} />

      <div className="flex-grow p-4 overflow-y-auto pb-20">
        {/* Header with boat info */}
        <div className="mb-4 p-3 bg-white rounded-lg border border-[#d1d5db]">
          <div className="text-lg font-bold text-[#1e40af]">{boat.name || boat.id}</div>
          <div className="text-sm text-[#6b7280]">{boat.type} {boat.model && `• ${boat.model}`}</div>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="mb-4 p-4 bg-blue-900/50 rounded-lg border border-blue-500 text-center">
            <div className="text-blue-400 animate-pulse">🔄 Φόρτωση στοιχείων...</div>
          </div>
        )}

        {/* Save Button */}
        {canEdit && (
          <button
            onClick={saveOwnerDetails}
            disabled={isSaving}
            className={`w-full mb-4 ${isSaving ? 'bg-gray-600' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-lg`}
          >
            <span>{isSaving ? '⏳' : '💾'}</span>
            <span>{isSaving ? 'Αποθήκευση...' : 'Αποθήκευση Στοιχείων'}</span>
          </button>
        )}

        {/* Owner Details Form */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border-2 border-[#1e40af]">
          <h3 className="text-lg font-bold text-[#1e40af] mb-4 flex items-center gap-2">
            👤 Στοιχεία Ιδιοκτήτη Σκάφους
          </h3>

          <div className="space-y-4">
            {/* Owner Name - Split into First and Last Name */}
            <div className="grid grid-cols-2 gap-3">
              {/* First Name */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-2">Όνομα</label>
                <input
                  type="text"
                  value={ownerDetails['Όνομα'] || ''}
                  onChange={(e) => handleChange('Όνομα', e.target.value)}
                  disabled={!canEdit}
                  className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                  placeholder="Όνομα"
                />
              </div>
              {/* Last Name */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-2">Επώνυμο</label>
                <input
                  type="text"
                  value={ownerDetails['Επώνυμο'] || ''}
                  onChange={(e) => handleChange('Επώνυμο', e.target.value)}
                  disabled={!canEdit}
                  className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                  placeholder="Επώνυμο"
                />
              </div>
            </div>

            {/* Owner Email */}
            <div>
              <label className="block text-sm font-semibold text-[#1e40af] mb-2">Email Ιδιοκτήτη ⭐</label>
              <input
                type="email"
                value={ownerDetails['Email Ιδιοκτήτη'] || ''}
                onChange={(e) => handleChange('Email Ιδιοκτήτη', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border-2 border-[#1e40af] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="owner@email.com"
              />
              <p className="text-xs text-[#1e40af] mt-1">Για αποστολή ειδοποιήσεων ναύλων</p>
            </div>

            {/* Company Email */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Email Εταιρείας</label>
              <input
                type="email"
                value={ownerDetails['Email Εταιρείας'] || ''}
                onChange={(e) => handleChange('Email Εταιρείας', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="company@email.com"
              />
            </div>

            {/* Company — dropdown από ενεργές financial_entities (bind σκάφος↔εταιρία).
                Επιλογή εταιρίας → γεμίζει Επωνυμία + ΑΦΜ (το ΑΦΜ οδηγεί το entity mapping). */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Εταιρεία</label>
              <select
                value={(financialEntities.find((en: any) => (en.vat && en.vat === ownerDetails['ΑΦΜ']) || en.name === ownerDetails['Εταιρεία'])?.code) || ''}
                onChange={(e) => {
                  const en = financialEntities.find((x: any) => x.code === e.target.value);
                  if (en) { handleChange('Εταιρεία', en.name); handleChange('ΑΦΜ', en.vat || ''); }
                }}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
              >
                <option value="">— επιλέξτε εταιρία —</option>
                {financialEntities.map((en: any) => (
                  <option key={en.code} value={en.code}>{en.name} ({en.code})</option>
                ))}
              </select>
              <input
                type="text"
                value={ownerDetails['Εταιρεία'] || ''}
                onChange={(e) => handleChange('Εταιρεία', e.target.value)}
                disabled={!canEdit}
                className={`w-full mt-2 px-4 py-2 bg-white text-[#6b7280] rounded-lg border border-dashed border-[#d1d5db] text-sm ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="…ή χειροκίνητα (αν δεν είναι στη λίστα)"
              />
            </div>

            {/* Tax ID */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">ΑΦΜ</label>
              <input
                type="text"
                value={ownerDetails['ΑΦΜ'] || ''}
                onChange={(e) => handleChange('ΑΦΜ', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="123456789"
              />
            </div>

            {/* ID / Passport Number + File Upload */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Αριθμ. Ταυτότητας / Διαβατηρίου</label>
              <input
                type="text"
                value={ownerDetails['Αριθμ. Ταυτότητας / Διαβατηρίου'] || ''}
                onChange={(e) => handleChange('Αριθμ. Ταυτότητας / Διαβατηρίου', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="π.χ. ΑΚ123456"
              />
              {/* File upload for scanned copy */}
              <div className="mt-2">
                {idPassportFile ? (
                  <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-[#d1d5db]">
                    <span className="text-green-600">📎</span>
                    <span className="text-sm text-[#374151] flex-1 truncate">{idPassportFile.name}</span>
                    <button
                      onClick={handleViewIdPassportFile}
                      className="bg-[#059669] text-white px-2 py-1 rounded text-xs hover:bg-green-700 transition"
                    >
                      👁 Προβολή
                    </button>
                    {canEdit && (
                      <button
                        onClick={handleRemoveIdPassportFile}
                        className="bg-[#dc2626] text-white px-2 py-1 rounded text-xs hover:bg-red-700 transition"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                ) : (
                  <label className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition ${canEdit ? 'bg-[#1e40af] text-white cursor-pointer hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                    {isUploadingIdFile ? '⏳ Ανέβασμα...' : '📤 Ανέβασμα αντιγράφου'}
                    <input
                      ref={idPassportFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,.pdf"
                      className="hidden"
                      disabled={!canEdit || isUploadingIdFile}
                      onChange={handleIdPassportFileUpload}
                    />
                  </label>
                )}
                <p className="text-xs text-[#6b7280] mt-1">Σαρωμένο αντίγραφο ταυτότητας ή διαβατηρίου</p>
              </div>
            </div>

            {/* Tax Office */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Αρμόδια ΔΟΥ</label>
              <input
                type="text"
                value={ownerDetails['Αρμόδια ΔΟΥ'] || ''}
                onChange={(e) => handleChange('Αρμόδια ΔΟΥ', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="π.χ. Δ' Αθηνών"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Τηλέφωνο</label>
              <input
                type="tel"
                value={ownerDetails['Τηλέφωνο Ιδιοκτήτη'] || ''}
                onChange={(e) => handleChange('Τηλέφωνο Ιδιοκτήτη', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                placeholder="+30 697 1234567"
              />
            </div>

            {/* Address - Split into 4 fields */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-[#374151] mb-2">Διεύθυνση</label>

              {/* Street */}
              <div>
                <label className="block text-xs text-[#6b7280] mb-1">Οδός</label>
                <input
                  type="text"
                  value={ownerDetails['Οδός'] || ''}
                  onChange={(e) => handleChange('Οδός', e.target.value)}
                  disabled={!canEdit}
                  className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                  placeholder="π.χ. Λεωφόρος Αλεξάνδρας"
                />
              </div>

              {/* Number and City in same row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Number */}
                <div>
                  <label className="block text-xs text-[#6b7280] mb-1">Αριθμός</label>
                  <input
                    type="text"
                    value={ownerDetails['Αριθμός'] || ''}
                    onChange={(e) => handleChange('Αριθμός', e.target.value)}
                    disabled={!canEdit}
                    className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                    placeholder="π.χ. 123"
                  />
                </div>

                {/* Postal Code */}
                <div>
                  <label className="block text-xs text-[#6b7280] mb-1">Τ.Κ.</label>
                  <input
                    type="text"
                    value={ownerDetails['Τ.Κ.'] || ''}
                    onChange={(e) => handleChange('Τ.Κ.', e.target.value)}
                    disabled={!canEdit}
                    className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                    placeholder="π.χ. 11523"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-xs text-[#6b7280] mb-1">Πόλη</label>
                <input
                  type="text"
                  value={ownerDetails['Πόλη'] || ''}
                  onChange={(e) => handleChange('Πόλη', e.target.value)}
                  disabled={!canEdit}
                  className={`w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] ${!canEdit ? 'opacity-60' : 'focus:border-[#1e40af] focus:outline-none'}`}
                  placeholder="π.χ. Αθήνα"
                />
              </div>
            </div>


            {/* Add New Field Button */}
            {canEdit && !showAddField && (
              <button
                onClick={() => setShowAddField(true)}
                className="w-full mt-4 bg-[#f9fafb] hover:bg-gray-100 text-[#1e40af] font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 border border-[#d1d5db]"
              >
                <span>➕</span><span>Προσθήκη Νέου Πεδίου</span>
              </button>
            )}

            {/* Add New Field Form */}
            {canEdit && showAddField && (
              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-[#1e40af]">
                <h4 className="text-sm font-bold text-[#1e40af] mb-3">➕ Νέο Πεδίο</h4>
                <input
                  type="text"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none mb-3"
                  placeholder="Όνομα πεδίου (π.χ. ΙΒΑΝ, Σημειώσεις)"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddField}
                    className="flex-1 bg-[#1e40af] hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-lg"
                  >
                    ✅ Προσθήκη
                  </button>
                  <button
                    onClick={() => { setShowAddField(false); setNewFieldName(''); }}
                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg"
                  >
                    ❌ Ακύρωση
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav activePage={null} onNavigate={navigate} />
    </div>
  );
}

function DetailsPage({ boat, category, navigate, showMessage, initialEditCharter, onConsumedEdit }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔥 FIX 4: Use optional chaining in dependencies
  useEffect(() => {
    if (boat) {
      loadItems();
    }
  }, [boat?.id, category]);

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  // 🔥 FIX 16: Load from API first for charters, merge with localStorage
  const loadItems = async () => {
    try {
      const key = `fleet_${boat.id}_${category}`;

      // For ΝΑΥΛΑ (charters): fetch from API first, merge with localStorage
      if (category === 'ΝΑΥΛΑ') {
        console.log(`🔄 Loading charters for vessel ${boat.id} from API...`);
        const charters = await getBookingsByVessel(boat.id);
        setItems(charters);
        setLoading(false);
        return;
      }

      // For other categories (ΕΡΓΑΣΙΕΣ, ΤΙΜΟΛΟΓΙΑ, etc.): use localStorage only
      const stored = localStorage.getItem(key);
      if (stored) {
        setItems(JSON.parse(stored));
      } else {
        setItems([]);
      }
      setLoading(false);
    } catch (e) {
      console.error('Error loading items:', e);
      // Fallback to localStorage on error
      try {
        const key = `fleet_${boat.id}_${category}`;
        const stored = localStorage.getItem(key);
        setItems(stored ? JSON.parse(stored) : []);
      } catch (fallbackError) {
        setItems([]);
      }
      setLoading(false);
    }
  };

  const saveItems = (newItems) => {
    try {
      const key = `fleet_${boat.id}_${category}`;
      localStorage.setItem(key, JSON.stringify(newItems));
      setItems(newItems);
    } catch (e) {
      console.error('Error saving items:', e);
    }
  };

  const renderCategoryContent = () => {
    if (loading) return <FullScreenLoader />;
    switch (category) {
      case 'ΕΡΓΑΣΙΕΣ':
        return <TaskPage boat={boat} showMessage={showMessage} />;
      case 'ΝΑΥΛΑ':
        return <CharterPage items={items} boat={boat} showMessage={showMessage} saveItems={saveItems} initialEditCharter={initialEditCharter} onConsumedEdit={onConsumedEdit} />;
      case 'ΦΩΤΟ & ΒΙΝΤΕΟ':
        return <MediaPage items={items} boatId={boat.id} showMessage={showMessage} />;
      default:
        return <p>Άγνωστη κατηγορία.</p>;
    }
  };

  // Build subtitle with vessel info
  const boatSubtitle = boat ? `${boat.name} • ${boat.type} • ${boat.model || ''}`.replace(/ • $/, '') : null;

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title={category} onBack={() => navigate('boatDashboard')} subtitle={boatSubtitle} />
      <div className="flex-grow p-4 overflow-y-auto pb-20">{renderCategoryContent()}</div>
      <BottomNav activePage={null} onNavigate={navigate} />
    </div>
  );
}

function MediaPage({ items, boatId, showMessage }) {
  const isOwnerUser = authService.isOwner();
  const canEdit = authService.canEdit() && !isOwnerUser;
  
  return (
    <div>
      {isOwnerUser && (
        <div className="mb-4 p-3 bg-blue-900 rounded-lg text-center border border-blue-700">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">
            {icons.eye}
            <span>View Only Mode</span>
          </div>
        </div>
      )}
      {canEdit && (
         <div className="mb-4">
            <button className="flex items-center justify-center w-full bg-white hover:bg-[#f9fafb] text-[#374151] font-bold py-3 px-4 rounded-lg transition duration-200 opacity-50 cursor-not-allowed border border-[#d1d5db]">
              {icons.upload} <span className="ml-2">(Σύντομα) Ανέβασμα Media</span>
            </button>
         </div>
      )}
      <div className="bg-white p-6 rounded-lg text-center border border-[#d1d5db]">
        <div className="text-5xl mb-3">📸</div>
        <p className="text-[#6b7280]">Η λειτουργία Φωτογραφιών & Βίντεο δεν είναι ακόμα διαθέσιμη.</p>
      </div>
    </div>
  );
}

// =====================================================
// END OF PART 3 - ΣΥΝΕΧΕΙΑ ΣΤΟ ΕΠΟΜΕΝΟ ΜΗΝΥΜΑ
// =====================================================
// =====================================================
// PART 4/4: Tasks + Charters + Financials + Messages + Email + FleetBookingPlan
// =====================================================

// Expandable Task Categories Types
interface TaskCategoryItem {
  id: string;
  name: string;
  status: 'OK' | '#' | '?';
  comment: string;
  lastUpdatedBy: string | null;
  lastUpdatedAt: string | null;
}

interface TaskCategory {
  id: string;
  name: string;
  shortName: string;
  icon: string;
  color: string;
  expanded: boolean;
  items: TaskCategoryItem[];
}

// Default task category definitions - Light Blue gradient styling
const DEFAULT_TASK_CATEGORIES: TaskCategory[] = [
  {
    id: 'engine',
    name: 'ΜΗΧΑΝΗ',
    shortName: 'Engine',
    icon: '⚙️',
    color: 'from-sky-400 to-cyan-400',
    expanded: false,
    items: [
      { id: 'eng_1', name: 'Έλεγχος λαδιού', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'eng_2', name: 'Φίλτρο καυσίμου', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'eng_3', name: 'Ψυκτικό υγρό', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'eng_4', name: 'Ιμάντες', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'eng_5', name: 'Αντλία νερού', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null }
    ]
  },
  {
    id: 'generator',
    name: 'ΓΕΝΝΗΤΡΙΑ',
    shortName: 'Generator',
    icon: '🔌',
    color: 'from-cyan-400 to-sky-500',
    expanded: false,
    items: [
      { id: 'gen_1', name: 'Λειτουργία', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'gen_2', name: 'Λάδι γεννήτριας', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'gen_3', name: 'Φίλτρο αέρα', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'gen_4', name: 'Ώρες λειτουργίας', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null }
    ]
  },
  {
    id: 'shaft',
    name: 'ΑΞΟΝΑΣ ΠΡΟΠΕΛΑ',
    shortName: 'Shaft',
    icon: '🔩',
    color: 'from-sky-500 to-blue-400',
    expanded: false,
    items: [
      { id: 'shaft_1', name: 'Τσιμούχα', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'shaft_2', name: 'Ανόδια', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'shaft_3', name: 'Προπέλα', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'shaft_4', name: 'Ευθυγράμμιση', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null }
    ]
  },
  {
    id: 'seavalves',
    name: 'ΒΑΝΕΣ ΘΑΛΑΣΣΗΣ',
    shortName: 'Sea Valves',
    icon: '🚿',
    color: 'from-blue-400 to-sky-400',
    expanded: false,
    items: [
      { id: 'valve_1', name: 'Βάνα κινητήρα', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'valve_2', name: 'Βάνα γεννήτριας', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'valve_3', name: 'Βάνα WC', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'valve_4', name: 'Βάνα νεροχύτη', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'valve_5', name: 'Βάνες air condition', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null }
    ]
  },
  {
    id: 'electrical',
    name: 'ΗΛΕΚΤΡΟΛΟΓΙΚΑ',
    shortName: 'Electrical',
    icon: '⚡',
    color: 'from-sky-400 to-cyan-400',
    expanded: false,
    items: [
      { id: 'elec_1', name: 'Καρβουνάκια εργάτη άγκυρας', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'elec_2', name: 'Μίζα μηχανής', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'elec_3', name: 'Blower (εξαερισμός)', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'elec_4', name: 'Φώτα ναυσιπλοΐας', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'elec_5', name: 'Φώτα σαλονιού', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null },
      { id: 'elec_6', name: 'Φώτα καμπινών', status: '#', comment: '', lastUpdatedBy: null, lastUpdatedAt: null }
    ]
  }
];

function TaskPage({ boat, showMessage }) {
  const [winterizationData, setWinterizationData] = useState<{
    completed: number;
    total: number;
    replacements: number;
    lastSaved: string | null;
  } | null>(null);

  // Expandable task categories state
  const [taskCategories, setTaskCategories] = useState<TaskCategory[]>([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddItemToCategory, setShowAddItemToCategory] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState('');

  const reactNavigate = useNavigate();

  const isOwnerUser = authService.isOwner();
  const canManageTasks = authService.canManageTasks() && !isOwnerUser;
  const canView = true;

  // Load winterization summary data
  useEffect(() => {
    if (!boat?.name) return;

    const vesselKey = boat.name.replace(/\s+/g, '_').toLowerCase();
    const savedData = localStorage.getItem(`winterization_${vesselKey}_data`);
    const savedCustom = localStorage.getItem(`winterization_${vesselKey}_custom_items`);

    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        let completed = 0;
        let total = 0;
        let replacements = 0;

        // Count from default items
        if (data.sections) {
          Object.values(data.sections).forEach((section: any) => {
            if (section.items) {
              section.items.forEach((item: any) => {
                total++;
                if (item.checked) completed++;
                if (item.replaceQty > 0) replacements += item.replaceQty;
              });
            }
          });
        }

        // Count custom items
        if (savedCustom) {
          const customData = JSON.parse(savedCustom);
          Object.values(customData).forEach((sectionItems: any) => {
            if (Array.isArray(sectionItems)) {
              sectionItems.forEach((item: any) => {
                total++;
                if (item.checked) completed++;
                if (item.replaceQty > 0) replacements += item.replaceQty;
              });
            }
          });
        }

        setWinterizationData({
          completed,
          total: total || 75, // Default 75 items if none saved
          replacements,
          lastSaved: data.lastSaved || null
        });
      } catch (e) {
        console.error('Error loading winterization data:', e);
        setWinterizationData(null);
      }
    } else {
      // No data saved yet - show empty state
      setWinterizationData({
        completed: 0,
        total: 75,
        replacements: 0,
        lastSaved: null
      });
    }
  }, [boat?.name]);

  // 🔥 API-only: Load task categories from API with migration
  const loadTaskCategories = useCallback(async () => {
    if (!boat?.id || !boat?.name) return;

    // First, migrate any existing localStorage data to API
    await migrateTasksFromLocalStorage(boat.id, boat.name);

    // Then load from API
    try {
      const apiTasks = await getTasksByVessel(boat.id);

      if (apiTasks && apiTasks.length > 0) {
        // Convert flat task list to categories format
        // For now, just use the default categories and merge API tasks
        const mergedCategories = JSON.parse(JSON.stringify(DEFAULT_TASK_CATEGORIES));
        // You can enhance this to properly map tasks to categories
        setTaskCategories(mergedCategories);
      } else {
        // Fallback: try localStorage for backwards compatibility during migration
        const vesselKey = boat.name.replace(/\s+/g, '_').toLowerCase();
        const savedCategories = localStorage.getItem(`task_categories_${vesselKey}`);
        if (savedCategories) {
          try {
            const parsed = JSON.parse(savedCategories);
            const mergedCategories = [...parsed];
            DEFAULT_TASK_CATEGORIES.forEach(defaultCat => {
              const exists = parsed.some((cat: any) => cat.id === defaultCat.id);
              if (!exists) {
                mergedCategories.push(JSON.parse(JSON.stringify(defaultCat)));
              }
            });
            setTaskCategories(mergedCategories);
          } catch (e) {
            setTaskCategories(JSON.parse(JSON.stringify(DEFAULT_TASK_CATEGORIES)));
          }
        } else {
          setTaskCategories(JSON.parse(JSON.stringify(DEFAULT_TASK_CATEGORIES)));
        }
      }
    } catch (e) {
      console.error('Error loading task categories from API:', e);
      setTaskCategories(JSON.parse(JSON.stringify(DEFAULT_TASK_CATEGORIES)));
    }
  }, [boat?.id, boat?.name]);

  useEffect(() => {
    loadTaskCategories();
  }, [loadTaskCategories]);

  // 🔥 NEW: Listen for global refresh events (every 3 minutes) - SYNC TASKS
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (boat?.id && boat?.name) {
        console.log('🔄 TaskPage: Global refresh received, syncing tasks...');
        loadTaskCategories();
      }
    };

    window.addEventListener('globalBookingsRefreshed', handleGlobalRefresh);
    return () => window.removeEventListener('globalBookingsRefreshed', handleGlobalRefresh);
  }, [boat?.id, boat?.name, loadTaskCategories]);

  // 🔥 API-only: Save task categories to API whenever they change
  useEffect(() => {
    if (!boat?.id || !boat?.name || taskCategories.length === 0) return;

    const saveTaskCategoriesToAPI = async () => {
      // Save each category as a task to API
      for (const category of taskCategories) {
        for (const item of category.items) {
          if (item.lastUpdatedAt) {
            // Only save items that have been updated
            await saveTask({
              taskCode: item.id,
              vesselId: boat.id,
              vesselName: boat.name,
              title: item.name,
              category: category.name,
              status: item.status === 'OK' ? 'completed' : item.status === '#' ? 'pending' : 'in_progress',
              notes: item.comment,
              createdBy: item.lastUpdatedBy || undefined
            });
          }
        }
      }
    };

    // Debounce the save to avoid too many API calls
    const timeoutId = setTimeout(saveTaskCategoriesToAPI, 2000);
    return () => clearTimeout(timeoutId);
  }, [taskCategories, boat?.id, boat?.name]);

  // Toggle category expansion
  const toggleCategoryExpand = (categoryId: string) => {
    setTaskCategories(prev => prev.map(cat =>
      cat.id === categoryId ? { ...cat, expanded: !cat.expanded } : cat
    ));
  };

  // Toggle item status within a category
  const toggleCategoryItemStatus = (categoryId: string, itemId: string) => {
    if (!canManageTasks) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }
    const statuses: Array<'OK' | '#' | '?'> = ['OK', '#', '?'];
    setTaskCategories(prev => prev.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          items: cat.items.map(item => {
            if (item.id === itemId) {
              const currentIndex = statuses.indexOf(item.status);
              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
              return {
                ...item,
                status: nextStatus,
                lastUpdatedBy: authService.getCurrentUser()?.name || null,
                lastUpdatedAt: new Date().toISOString()
              };
            }
            return item;
          })
        };
      }
      return cat;
    }));
  };

  // Update item comment within a category
  const updateCategoryItemComment = (categoryId: string, itemId: string, comment: string) => {
    if (!canManageTasks) return;
    setTaskCategories(prev => prev.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          items: cat.items.map(item => {
            if (item.id === itemId) {
              return {
                ...item,
                comment,
                lastUpdatedBy: authService.getCurrentUser()?.name || null,
                lastUpdatedAt: new Date().toISOString()
              };
            }
            return item;
          })
        };
      }
      return cat;
    }));
  };

  // Add new item to a category
  const addItemToCategory = (categoryId: string) => {
    if (!canManageTasks || !newItemName.trim()) return;
    const newItem: TaskCategoryItem = {
      id: `item_${Date.now()}`,
      name: newItemName.trim(),
      status: '#',
      comment: '',
      lastUpdatedBy: authService.getCurrentUser()?.name || null,
      lastUpdatedAt: new Date().toISOString()
    };
    setTaskCategories(prev => prev.map(cat =>
      cat.id === categoryId ? { ...cat, items: [...cat.items, newItem] } : cat
    ));
    setNewItemName('');
    setShowAddItemToCategory(null);
    showMessage('✅ Το είδος προστέθηκε.', 'success');
  };

  // Delete item from a category
  const deleteItemFromCategory = (categoryId: string, itemId: string) => {
    if (!canManageTasks) return;
    setTaskCategories(prev => prev.map(cat =>
      cat.id === categoryId ? { ...cat, items: cat.items.filter(item => item.id !== itemId) } : cat
    ));
  };

  // Add new category
  const addNewCategory = () => {
    if (!canManageTasks || !newCategoryName.trim()) return;
    const newCat: TaskCategory = {
      id: `cat_${Date.now()}`,
      name: newCategoryName.trim().toUpperCase(),
      shortName: newCategoryName.trim().substring(0, 4).toUpperCase(),
      icon: '📋',
      color: 'from-sky-400 to-cyan-400',
      expanded: false,
      items: []
    };
    setTaskCategories(prev => [...prev, newCat]);
    setNewCategoryName('');
    setShowAddCategory(false);
    showMessage('✅ Η κατηγορία προστέθηκε.', 'success');
  };

  // Delete category
  const deleteCategory = (categoryId: string) => {
    if (!canManageTasks) return;
    if (window.confirm('Διαγραφή κατηγορίας και όλων των εργασιών της;')) {
      setTaskCategories(prev => prev.filter(cat => cat.id !== categoryId));
      showMessage('✅ Η κατηγορία διαγράφηκε.', 'success');
    }
  };

  // Get category stats
  const getCategoryStats = (category: TaskCategory) => {
    const total = category.items.length;
    const completed = category.items.filter(i => i.status === 'OK').length;
    const repair = category.items.filter(i => i.status === '?').length;
    return { total, completed, repair };
  };

  const renderStatusIcon = (status) => {
    switch(status) {
      case 'OK':
        return <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">{icons.check}</div>;
      case '?':
        return <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">{icons.helpCircle}</div>;
      case '#':
      default:
        return <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">{icons.xCircle}</div>;
    }
  };

  return (
    <div>
      {isOwnerUser && (
        <div className="mb-4 p-3 bg-blue-900 rounded-lg text-center border border-blue-700">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">
            {icons.eye}
            <span>View Only Mode</span>
          </div>
        </div>
      )}

      {/* ❄️ Winterization Summary Card */}
      {winterizationData && (
        <button
          onClick={() => {
            // Find vessel ID from VESSELS list
            const vesselId = [
              { id: 1, name: "Maria 1" },
              { id: 2, name: "Maria 2" },
              { id: 3, name: "Valesia" },
              { id: 4, name: "Bar Bar" },
              { id: 5, name: "Kalispera" },
              { id: 6, name: "Infinity" },
              { id: 7, name: "Perla" },
              { id: 8, name: "Bob" },
            ].find(v => v.name.toLowerCase() === boat?.name?.toLowerCase())?.id;

            if (vesselId) {
              localStorage.setItem('winterization_last_vessel', String(vesselId));
            }
            reactNavigate('/winterization');
          }}
          className="w-full mb-4 p-4 bg-gradient-to-r from-sky-400 to-cyan-400 rounded-xl border border-sky-300 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-sky-400/40 hover:scale-[1.02] text-left"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">❄️</span>
              <div>
                <h4 className="font-bold text-white text-lg">INVENTORY WINTER</h4>
                <p className="text-xs text-sky-100">Χειμερινές Εργασίες</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-green-400">✅</span>
                <span className="font-bold text-white">{winterizationData.completed}/{winterizationData.total}</span>
              </div>
              {winterizationData.replacements > 0 && (
                <div className="flex items-center gap-2 text-red-400">
                  <span>🔴</span>
                  <span className="text-sm">{winterizationData.replacements} αντ/σεις</span>
                </div>
              )}
            </div>
          </div>
          {winterizationData.lastSaved && (
            <div className="mt-2 text-xs text-[#6b7280]">
              📅 Τελευταία ενημέρωση: {new Date(winterizationData.lastSaved).toLocaleDateString('en-GB')}
            </div>
          )}
          {!winterizationData.lastSaved && (
            <div className="mt-2 text-xs text-yellow-400">
              ⚠️ Δεν έχει ξεκινήσει - Πατήστε για να ξεκινήσετε
            </div>
          )}
        </button>
      )}

      {/* 🔧 Expandable Task Categories - Light Blue Theme */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center justify-between mb-2 p-2 rounded-lg" style={{ background: 'rgba(144, 202, 249, 0.3)' }}>
          <h3 className="text-sm font-bold text-gray-800">Κατηγορίες Εργασιών</h3>
          {canManageTasks && (
            <button
              onClick={() => setShowAddCategory(true)}
              className="text-xs px-3 py-1 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors font-medium shadow-sm"
            >
              + Κατηγορία
            </button>
          )}
        </div>

        {/* Add Category Modal */}
        {showAddCategory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="p-5 rounded-xl border border-sky-300 w-80 shadow-xl" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
              <h4 className="font-bold text-gray-800 mb-3 text-lg">Νέα Κατηγορία</h4>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Όνομα κατηγορίας..."
                className="w-full px-3 py-2 bg-white text-gray-800 rounded-lg border border-sky-300 mb-3 focus:ring-2 focus:ring-sky-400 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}
                  className="flex-1 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-medium"
                >
                  Ακύρωση
                </button>
                <button
                  onClick={addNewCategory}
                  className="flex-1 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-medium"
                >
                  Προσθήκη
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Item to Category Modal */}
        {showAddItemToCategory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="p-5 rounded-xl border border-sky-300 w-80 shadow-xl" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
              <h4 className="font-bold text-gray-800 mb-3 text-lg">Νέο Είδος</h4>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Όνομα είδους..."
                className="w-full px-3 py-2 bg-white text-gray-800 rounded-lg border border-sky-300 mb-3 focus:ring-2 focus:ring-sky-400 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAddItemToCategory(null); setNewItemName(''); }}
                  className="flex-1 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-medium"
                >
                  Ακύρωση
                </button>
                <button
                  onClick={() => addItemToCategory(showAddItemToCategory)}
                  className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium"
                >
                  Προσθήκη
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expandable Task Category Cards */}
        {taskCategories.map(category => {
          const catStats = getCategoryStats(category);
          return (
            <div key={category.id} className="rounded-xl border border-sky-300 overflow-hidden shadow-md hover:shadow-xl hover:scale-[1.01] transition-all duration-300">
              {/* Category Header */}
              <button
                onClick={() => toggleCategoryExpand(category.id)}
                className={`w-full p-3 bg-gradient-to-r ${category.color} text-left flex items-center justify-between hover:opacity-90 hover:scale-[1.01] transition-all duration-300 border-b border-sky-300`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h4 className="font-bold text-white drop-shadow-sm">{category.name}</h4>
                    <p className="text-xs text-sky-100">{category.shortName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className="text-green-100 text-sm font-medium">✅ {catStats.completed}/{catStats.total}</span>
                    </div>
                    {catStats.repair > 0 && (
                      <span className="text-orange-200 text-xs">🔧 {catStats.repair} επισκευή</span>
                    )}
                  </div>
                  <span className="text-white text-lg drop-shadow-sm">{category.expanded ? '▼' : '▶'}</span>
                </div>
              </button>

              {/* Category Items (Expanded) */}
              {category.expanded && (
                <div className="p-3 space-y-2" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
                  {category.items.map(item => (
                    <div key={item.id} className="p-2 rounded-lg border border-sky-300 shadow-sm hover:shadow-lg hover:scale-[1.01] transition-all duration-300" style={{ background: 'rgba(255, 255, 255, 0.7)' }}>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => toggleCategoryItemStatus(category.id, item.id)}
                          className={`flex-grow text-left flex items-center gap-2 ${canManageTasks ? 'cursor-pointer hover:bg-sky-100' : 'cursor-default'} p-1 rounded transition-colors`}
                          disabled={!canManageTasks}
                        >
                          {renderStatusIcon(item.status)}
                          <span className="text-gray-800 text-sm font-medium">{item.name}</span>
                        </button>
                        {canManageTasks && (
                          <button
                            onClick={() => deleteItemFromCategory(category.id, item.id)}
                            className="text-red-600 hover:text-red-500 p-1 hover:bg-red-100 rounded transition-colors text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {item.status === '?' && (
                        <div className="mt-2 pt-2 border-t border-sky-200">
                          <textarea
                            value={item.comment || ''}
                            onChange={(e) => updateCategoryItemComment(category.id, item.id, e.target.value)}
                            placeholder="Σχόλια επισκευής..."
                            className="w-full px-2 py-1 bg-white text-gray-800 rounded border border-sky-300 text-xs focus:ring-2 focus:ring-sky-400 focus:outline-none"
                            rows={2}
                            disabled={!canManageTasks}
                          />
                        </div>
                      )}
                      {item.lastUpdatedBy && (
                        <div className="mt-1 text-xs text-gray-600">
                          👤 {item.lastUpdatedBy} • {new Date(item.lastUpdatedAt!).toLocaleDateString('en-GB')}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Item Button */}
                  {canManageTasks && (
                    <button
                      onClick={() => setShowAddItemToCategory(category.id)}
                      className="w-full py-2 bg-white/80 hover:bg-white text-sky-700 border border-sky-300 rounded-lg text-sm transition-colors font-medium"
                    >
                      + Προσθήκη Είδους
                    </button>
                  )}

                  {/* Delete Category Button */}
                  {canManageTasks && (
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="w-full py-2 bg-red-100 hover:bg-red-200 text-red-600 border border-red-300 rounded-lg text-xs transition-colors"
                    >
                      🗑️ Διαγραφή Κατηγορίας
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 📦 Winter Maintenance Buttons - ΕΡΓΑΣΙΕΣ Section */}
      <div className="mb-4 space-y-2">
        <div className="p-2 rounded-lg mb-3" style={{ background: 'rgba(144, 202, 249, 0.3)' }}>
          <h3 className="text-sm font-bold text-gray-800">🔧 Εργαλεία Χειμερινής Συντήρησης</h3>
        </div>
        <div className="grid grid-cols-1 gap-2">
            {/* Χειμερινές Εργασίες Button */}
            <button
              onClick={() => reactNavigate('/winterization')}
              className="w-full p-3 bg-gradient-to-r from-sky-400 to-cyan-400 rounded-xl border border-sky-300 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-sky-400/40 hover:scale-[1.02] text-left flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">📦</span>
                <div>
                  <h5 className="font-bold text-white">Χειμερινές Εργασίες</h5>
                  <p className="text-xs text-sky-100">Κατάσταση εξοπλισμού όλων των σκαφών</p>
                </div>
              </div>
              <span className="text-white">→</span>
            </button>

            {/* TakeOver Button */}
            <button
              onClick={() => reactNavigate('/winter-takeover')}
              className="w-full p-3 bg-gradient-to-r from-sky-400 to-cyan-400 rounded-xl border border-sky-300 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-sky-400/40 hover:scale-[1.02] text-left flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">📋</span>
                <div>
                  <h5 className="font-bold text-white">Take Over</h5>
                  <p className="text-xs text-sky-100">Παρακολούθηση παραλαβών σκαφών</p>
                </div>
              </div>
              <span className="text-white">→</span>
            </button>

            {/* Safety Equipment Button */}
            <button
              onClick={() => reactNavigate('/winter-safety')}
              className="w-full p-3 bg-gradient-to-r from-sky-400 to-cyan-400 rounded-xl border border-sky-300 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-sky-400/40 hover:scale-[1.02] text-left flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🩹</span>
                <div>
                  <h5 className="font-bold text-white">Safety Equipment</h5>
                  <p className="text-xs text-sky-100">Φαρμακείο & Πυροτεχνικά με ημερομηνίες λήξης</p>
                </div>
              </div>
              <span className="text-white">→</span>
            </button>
        </div>
      </div>

    </div>
  );
}

function CharterPage({ items, boat, showMessage, saveItems, initialEditCharter, onConsumedEdit }) {
  console.log('🚀 CharterPage component mounted');
  const [selectedCharter, setSelectedCharter] = useState(null);
  const editConsumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialEditCharter) return;
    const key = initialEditCharter.code || initialEditCharter.id || JSON.stringify(initialEditCharter);
    if (editConsumedRef.current === key) return;
    editConsumedRef.current = key;
    if (onConsumedEdit) onConsumedEdit();
    setSelectedCharter(initialEditCharter);
  }, [initialEditCharter]);
  const [showAddForm, setShowAddForm] = useState(false);
  // 🔥 NEW: Edit mode for Draft/Page1 charters
  const [editingCharter, setEditingCharter] = useState<any>(null);
  // 🔥 NEW: Filter for Page 1 bookings
  const [charterFilter, setCharterFilter] = useState<'all' | 'page1' | 'confirmed'>('all');

  // 🔥 FIX 9: Added 5 skipper fields + broker field
  const [newCharter, setNewCharter] = useState({
    code: '', startDate: '', endDate: '', startTime: '17:00', endTime: '09:00', amount: '',
    commissionPercent: '20',
    hasForeignBroker: false,
    foreignBrokerPercent: '20',
    broker: '', // Broker / Agency name
    brokerEmailContact: '', // Broker / Agency contact email
    // Foreign broker extended fields (shown only when hasForeignBroker)
    brokerVat: '', brokerDoy: '', brokerCountry: '', brokerAddress: '', brokerPhone: '',
    showVat: false, // Show VAT breakdown in Charter Party document
    departure: 'ALIMOS MARINA', arrival: 'ALIMOS MARINA', status: 'Option',
    // Charterer fields
    chartererFirstName: '', chartererLastName: '', chartererAddress: '', chartererEmail: '', chartererPhone: '', chartererTaxNumber: '', chartererTaxOffice: '',
    chartererDob: '', chartererGender: '', chartererNationality: '',
    chartererIdType: 'id', chartererIdNumber: '',
    /* ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ: η εταιρεία δεν έχει φύλο/γέννηση/διαβατήριο και δεν
       επιβαίνει — βλ. utils/chartererType. */
    chartererIsCompany: false, chartererCompanyName: '', chartererCountry: '',
    sameAsSkipper: false,
    // Skipper fields
    skipperFirstName: '', skipperLastName: '', skipperAddress: '', skipperEmail: '', skipperPhone: '', skipperTaxNumber: '', skipperTaxOffice: '',
    skipperDob: '', skipperGender: '', skipperNationality: '',
    skipperIdType: 'id', skipperIdNumber: ''
  });
  const aadeLookups = useAadeLookups();

  // 🔥 NEW: Validation error states
  const [charterCodeError, setCharterCodeError] = useState('');
  const [doubleBookingError, setDoubleBookingError] = useState('');
  const [dateRangeError, setDateRangeError] = useState('');

  // 🔥 NEW: Extras states for charter form
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [customExtras, setCustomExtras] = useState<string[]>([]);
  // 🔥 NEW: Notes state for charter form
  const [charterNotes, setCharterNotes] = useState<string>('');

  // 🔥 NEW: Refs for validation scroll and highlight
  const charterCodeRef = useRef<HTMLDivElement>(null);
  const datesRef = useRef<HTMLDivElement>(null);
  const skipperInfoRef = useRef<HTMLDivElement>(null);

  const isOwnerUser = authService.isOwner();
  const canViewCharters = true;
  const canEditCharters = (authService.isAdmin() || authService.isBooking()) && !isOwnerUser;
  const canViewFinancials = authService.canViewFinancials() || isOwnerUser;
  const canAcceptCharter = isOwnerUser || authService.isAdmin();

  // 🔥 Filter charters based on selected filter
  const filteredItems = useMemo(() => {
    switch (charterFilter) {
      case 'page1':
        return items.filter(c => c.source === 'page1' || c.status === 'Draft');
      case 'confirmed':
        return items.filter(c => c.status === 'Confirmed');
      default:
        return items;
    }
  }, [items, charterFilter]);

  // 🔥 Count Page 1 bookings for badge
  const page1Count = useMemo(() =>
    items.filter(c => c.source === 'page1' || c.status === 'Draft').length,
    [items]
  );

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    console.log('📝 handleFormChange:', { name, value, type });

    if (type === 'number') {
      setNewCharter(prev => ({ ...prev, [name]: value === '' ? '' : value }));
    } else if (name === 'startDate' && value) {
      setNewCharter(prev => ({ ...prev, startDate: value, endDate: value }));
      setDateRangeError('');
      setDoubleBookingError('');
    } else if (name === 'endDate' && value) {
      // Simple date range validation
      if (newCharter.startDate && value < newCharter.startDate) {
        setDateRangeError('Η ημερομηνία λήξης δεν μπορεί να είναι πριν από την ημερομηνία έναρξης! / End date cannot be before start date!');
      } else {
        setDateRangeError('');
      }
      setNewCharter(prev => ({ ...prev, [name]: value }));
      setDoubleBookingError(''); // Clear double booking error when dates change
    } else if (name === 'code') {
      // 🔥 SIMPLIFIED: Just update state, validate only on blur and before save
      setNewCharter(prev => ({ ...prev, [name]: value }));
      setCharterCodeError(''); // Clear error when typing
    } else {
      setNewCharter(prev => ({ ...prev, [name]: value }));
    }
  };

  // 🔥 NEW: Highlight element in red for 3 seconds
  const highlightElement = (ref) => {
    if (ref && ref.current) {
      ref.current.style.border = '2px solid red';
      ref.current.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.border = '';
          ref.current.style.backgroundColor = '';
        }
      }, 3000);
    }
  };

  // 🔥 NEW: Validate required fields and scroll to missing field
  const validateAndScrollCharter = () => {
    // Check charter code
    if (!newCharter.code || !newCharter.code.trim()) {
      charterCodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(charterCodeRef);
      alert('Παρακαλώ συμπληρώστε τον κωδικό ναύλου! / Please fill in the charter code!');
      return false;
    }

    // Check if charter code has error (duplicate)
    if (charterCodeError) {
      charterCodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(charterCodeRef);
      alert(charterCodeError);
      return false;
    }

    // Check dates
    if (!newCharter.startDate || !newCharter.endDate) {
      datesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(datesRef);
      alert('Παρακαλώ συμπληρώστε τις ημερομηνίες! / Please fill in the dates!');
      return false;
    }

    // Check date range error
    if (dateRangeError) {
      datesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(datesRef);
      alert(dateRangeError);
      return false;
    }

    // Check double booking error
    if (doubleBookingError) {
      datesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(datesRef);
      alert(doubleBookingError);
      return false;
    }

    return true; // All validations passed
  };

  // 🔥 NEW: Check for duplicate charter code (returns true if duplicate found)
  // Uses EXACT string match (case-insensitive) — no partial/leading-zero stripping
  const checkDuplicateOnEnter = async (code: string): Promise<boolean> => {
    if (!code || !code.trim()) return false;
    const numToCheck = code.trim().toUpperCase().replace(/[^0-9]/g, '');
    if (!numToCheck) return false;

    const excludeId = editingCharter?.booking_number || editingCharter?.bookingCode || editingCharter?.code || editingCharter?.id || editingCharter?.charterCode;
    const excludeNum = excludeId ? excludeId.toString().replace(/[^0-9]/g, '') : '';

    console.log('GLOBAL duplicate check for:', numToCheck, 'excluding:', excludeNum);

    try {
      const resp = await fetch('/api/bookings.php');
      if (!resp.ok) return false;
      const result = await resp.json();
      const allBookings = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
      console.log('Loaded', allBookings.length, 'bookings');

      const dup = allBookings.some((b: any) => {
        const bNum = (b.booking_number || b.code || '').toString().replace(/[^0-9]/g, '');
        if (!bNum) return false;
        if (excludeNum && bNum === excludeNum) return false;
        if (bNum === numToCheck) { console.log('DUPLICATE:', b.booking_number); return true; }
        return false;
      });

      if (dup) return true;
    } catch (e) {
      console.log('Check error:', e);
    }

    console.log('No duplicate found');
    return false;
  };

  // Ο ναύλος που επεξεργαζόμαστε — πάει στον server για self-exclusion (με αριθμό).
  const editingCharterRef = () =>
    editingCharter?.booking_number || editingCharter?.bookingCode || editingCharter?.code
    || editingCharter?.charterCode || editingCharter?.id || undefined;

  // Το μήνυμα το φτιάχνει η απάντηση του server — δείχνουμε ΠΟΙΟΣ ναύλος συγκρούεται.
  const overlapMessage = (result: any): string => {
    if (result?.error) return result.error;
    const c = result?.overlappingBooking;
    if (!c) return 'Το σκάφος έχει ήδη κράτηση για αυτές τις ημερομηνίες';
    return `Το σκάφος έχει ήδη κράτηση: ${c.bookingNumber || c.code} (${c.startDate} → ${c.endDate})`;
  };

  // 🔥 Έλεγχος διπλοκράτησης — ΤΟΝ ΚΑΝΕΙ Ο SERVER (bookings.php?action=check_overlap).
  // Δεν συγκρίνουμε πια τοπικά το `items`: εκείνο ήταν ό,τι είχε φορτώσει ο κάθε
  // browser για το vessel id που κρατούσε στο localStorage, γι' αυτό ο ίδιος ναύλος
  // περνούσε από laptop και μπλοκαριζόταν από PC.
  const checkDoubleBookingOnEnter = async (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !boat) return { hasOverlap: false };
    return await checkDateOverlap(boat.id, startDate, endDate, editingCharterRef(), boat.name);
  };

  // 🔥 NEW: Handle Enter key to move to next input field
  const handleFormKeyDown = async (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>): Promise<void> => {
    if (e.key === 'Enter') {
      e.preventDefault();
      console.log('⌨️ Enter pressed on field:', e.currentTarget.name);
      console.log('📋 Current newCharter:', newCharter);

      // 🔥 SPECIAL: If this is the charter code field, check for duplicates FIRST
      if (e.currentTarget.name === 'code' && newCharter.code && !editingCharter) {
        console.log('🔍 Checking charter code duplicate...');
        const isDuplicate = await checkDuplicateOnEnter(newCharter.code);
        console.log('🔍 isDuplicate:', isDuplicate);
        if (isDuplicate) {
          // Show error immediately
          console.log('❌ DUPLICATE! Showing alert...');
          setCharterCodeError('Αυτός ο αριθμός ναύλου υπάρχει ήδη');
          // 🔥 Apply INTENSE red highlight with glow
          if (charterCodeRef.current) {
            charterCodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            charterCodeRef.current.style.border = '4px solid #ff0000';
            charterCodeRef.current.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
            charterCodeRef.current.style.borderRadius = '12px';
            charterCodeRef.current.style.padding = '10px';
            charterCodeRef.current.style.boxShadow = '0 0 20px 5px rgba(255, 0, 0, 0.7)';
            const inputInside = charterCodeRef.current.querySelector('input');
            if (inputInside) {
              inputInside.style.border = '4px solid #ff0000';
              inputInside.style.backgroundColor = 'rgba(255, 0, 0, 0.4)';
              inputInside.style.boxShadow = '0 0 15px 3px rgba(255, 0, 0, 0.6)';
            }
            setTimeout(() => {
              if (charterCodeRef.current) {
                charterCodeRef.current.style.border = '';
                charterCodeRef.current.style.backgroundColor = '';
                charterCodeRef.current.style.borderRadius = '';
                charterCodeRef.current.style.padding = '';
                charterCodeRef.current.style.boxShadow = '';
                const inputInside = charterCodeRef.current.querySelector('input');
                if (inputInside) {
                  inputInside.style.border = '';
                  inputInside.style.backgroundColor = '';
                  inputInside.style.boxShadow = '';
                }
              }
            }, 3000);
          }
          alert(`❌ ΣΤΑΜΑΤΑ!\n\nΑυτός ο αριθμός ναύλου υπάρχει ήδη: "${newCharter.code}"!\n\nΔΕΝ ΜΠΟΡΕΙΤΕ ΝΑ ΑΠΟΘΗΚΕΥΣΕΤΕ!`);
          return; // DON'T move to next field
        } else {
          setCharterCodeError(''); // Clear error if no duplicate
            // Move to next field - find startDate input
            setTimeout(() => {
              const nextInput = document.querySelector('input[name="startDate"]') as HTMLElement;
              if (nextInput) nextInput.focus();
            }, 50);
            return; // Skip bottom move-to-next logic (e.currentTarget is null after await)
            // Manually move to next field after async check
            const codeForm = savedTarget.closest('form');
            if (codeForm) {
              const codeInputs = Array.from(codeForm.querySelectorAll('input:not([type="hidden"]), select, textarea')) as HTMLElement[];
              const codeIdx = codeInputs.indexOf(savedTarget);
              if (codeIdx < codeInputs.length - 1) {
                codeInputs[codeIdx + 1].focus();
              }
            }
        }
      }

      // 🔥 SPECIAL: If this is the endDate field, check for double booking
      if (e.currentTarget.name === 'endDate' && newCharter.startDate && newCharter.endDate) {
        console.log('🔍 Checking date overlap...');
        const overlapRes: any = await checkDoubleBookingOnEnter(newCharter.startDate, newCharter.endDate);
        const hasOverlap = overlapRes.hasOverlap || !!overlapRes.error;
        console.log('🔍 hasOverlap:', hasOverlap, overlapRes);
        if (hasOverlap) {
          // Show error immediately
          console.log('❌ OVERLAP! Showing alert...');
          setDoubleBookingError(overlapMessage(overlapRes));
          // 🔥 Apply INTENSE red highlight with glow
          if (datesRef.current) {
            datesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            datesRef.current.style.border = '4px solid #ff0000';
            datesRef.current.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
            datesRef.current.style.borderRadius = '12px';
            datesRef.current.style.padding = '10px';
            datesRef.current.style.boxShadow = '0 0 20px 5px rgba(255, 0, 0, 0.7)';
            const inputs = datesRef.current.querySelectorAll('input');
            inputs.forEach((input: HTMLInputElement) => {
              input.style.border = '4px solid #ff0000';
              input.style.backgroundColor = 'rgba(255, 0, 0, 0.4)';
              input.style.boxShadow = '0 0 15px 3px rgba(255, 0, 0, 0.6)';
            });
            setTimeout(() => {
              if (datesRef.current) {
                datesRef.current.style.border = '';
                datesRef.current.style.backgroundColor = '';
                datesRef.current.style.borderRadius = '';
                datesRef.current.style.padding = '';
                datesRef.current.style.boxShadow = '';
                const inputs = datesRef.current.querySelectorAll('input');
                inputs.forEach((input: HTMLInputElement) => {
                  input.style.border = '';
                  input.style.backgroundColor = '';
                  input.style.boxShadow = '';
                });
              }
            }, 3000);
          }
          alert(`❌ ΣΤΑΜΑΤΑ!\n\n${overlapMessage(overlapRes)}\n\nΔΕΝ ΜΠΟΡΕΙΤΕ ΝΑ ΑΠΟΘΗΚΕΥΣΕΤΕ!`);
          return; // DON'T move to next field
        } else {
          setDoubleBookingError(''); // Clear error if no overlap
        }
      }

      // Get all form inputs and selects
      const form = e.currentTarget.form;
      if (!form) return;

      const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]), select, textarea')) as HTMLElement[];
      const currentIndex = inputs.indexOf(e.currentTarget);

      if (currentIndex < inputs.length - 1) {
        // Focus next input
        inputs[currentIndex + 1].focus();
      } else {
        // Last input - trigger save button
        const saveButton = form.querySelector('button[type="button"]') as HTMLElement;
        if (saveButton) saveButton.click();
      }
    }
  };

  // 🔥 API VALIDATION: Check if charter code already exists (uses imported checkDuplicateCharterCode)
  const validateCharterCodeOnBlur = async (code: string, excludeId?: string) => {
    console.log('🔍 validateCharterCodeOnBlur called with code:', code);
    if (editingCharter) {
      setCharterCodeError('');
      return;
    }
    if (!code || !code.trim()) {
      setCharterCodeError('');
      return;
    }

    try {
      const result = await checkDuplicateCharterCode(code, excludeId);
      if (result.isDuplicate) {
        console.log('🚫 Duplicate found in API!');
        setCharterCodeError('Αυτός ο αριθμός ναύλου υπάρχει ήδη');
      } else {
        console.log('✅ Code is available');
        setCharterCodeError('');
      }
    } catch (error) {
      console.error('❌ Error checking duplicate charter code:', error);
    }
  };

  // 🔥 SERVER VALIDATION: ο έλεγχος γίνεται στο bookings.php?action=check_overlap.
  // Το excludeId αγνοείται σκόπιμα: ο ναύλος που επεξεργαζόμαστε προκύπτει από το
  // editingCharter (editingCharterRef), όχι από ό,τι περνούσε το κάθε onBlur —
  // παλιά το blur έστελνε editingCharter?.id και το save booking_number, οπότε η
  // εξαίρεση του ίδιου ναύλου δούλευε άλλοτε ναι κι άλλοτε όχι.
  const validateDatesOnBlur = async (startDate: string, endDate: string, _excludeId?: string) => {
    if (!boat || !startDate || !endDate) {
      setDoubleBookingError('');
      return;
    }

    const result = await checkDateOverlap(boat.id, startDate, endDate, editingCharterRef(), boat.name);
    if (result.hasOverlap || result.error) {
      console.log('🚫 Date overlap / ανολοκλήρωτος έλεγχος:', result);
      setDoubleBookingError(overlapMessage(result));
    } else {
      console.log('✅ Dates are available');
      setDoubleBookingError('');
    }
  };

  const calculateFinancials = () => {
    const amount = parseFloat(newCharter.amount) || 0;
    const netFare = amount / 1.12; // Remove 12% VAT from gross fare
    const hasForeignBroker = newCharter.hasForeignBroker;
    // Κενό πεδίο → default. Ρητό 0 → 0. Σκουπίδια → default.
    // Το παλιό `parseFloat(x) || default` έσπαγε στο 0: στη JS το 0 είναι falsy, οπότε
    // «προμήθεια 0%» γινόταν σιωπηλά 20% και ο χρήστης έβλεπε χρέωση που δεν ζήτησε.
    const pct = (raw: any, dflt: number) => {
      const str = String(raw ?? '').trim();
      if (str === '') return dflt;
      const n = parseFloat(str);
      return Number.isFinite(n) ? n : dflt;
    };
    const foreignBrokerPercent = pct(newCharter.foreignBrokerPercent, 20);
    const commissionPercent = pct(newCharter.commissionPercent, hasForeignBroker ? 10 : 20);

    let foreignCommission = 0;
    let greekCommissionBase = netFare;

    if (hasForeignBroker) {
      foreignCommission = netFare * (foreignBrokerPercent / 100);
      greekCommissionBase = netFare - foreignCommission;
    }

    const greekCommission = greekCommissionBase * (commissionPercent / 100);
    const vatOnGreekCommission = greekCommission * 0.24;
    const totalExpense = foreignCommission + greekCommission + vatOnGreekCommission;
    const netIncome = amount - totalExpense;

    return {
      amount,
      netFare,
      foreignCommission,
      greekCommission,
      vatOnGreekCommission,
      totalExpense,
      netIncome,
      hasForeignBroker,
      // Τα ποσοστά που ΟΝΤΩΣ μπήκαν στον τύπο — αποθηκεύονται αυτά, ώστε το
      // commissionPercent της βάσης να συμφωνεί πάντα με το commission δίπλα του.
      commissionPercent,
      foreignBrokerPercent
    };
  };

  const financials = calculateFinancials();
  const isFormValid = newCharter.code.trim() !== '' && newCharter.startDate !== '' && newCharter.endDate !== '' && parseFloat(newCharter.amount) > 0;
  const hasValidationErrors = !!(charterCodeError || dateRangeError || doubleBookingError);

  // 🔥 DEBUG: Log form validity
  console.log('📊 Form Status:', {
    code: newCharter.code,
    startDate: newCharter.startDate,
    endDate: newCharter.endDate,
    amount: newCharter.amount,
    isFormValid,
    hasValidationErrors,
    charterCodeError,
    dateRangeError,
    doubleBookingError
  });

  // 🔥 FIX 6: Add saveBooking API sync
  // 🔥 NEW: Handles both ADD and EDIT modes
  const handleAddCharter = async () => {
    // Check if code has at least one number
    const hasNumber = /\d/.test(newCharter.code || '');
    if (!hasNumber) {
      alert('Παρακαλώ εισάγετε αριθμό ναύλου (π.χ. CHARTER PARTY NO 65)');
      return;
    }

    const isEditMode = !!editingCharter;
    console.log('🔥 handleAddCharter called - MODE:', isEditMode ? 'EDIT' : 'ADD');
    console.log('canEditCharters:', canEditCharters);
    console.log('isFormValid:', isFormValid);
    console.log('hasValidationErrors:', hasValidationErrors);
    console.log('newCharter:', newCharter);
    if (isEditMode) console.log('✏️ Editing charter:', editingCharter.code);

    if (!canEditCharters) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    // 🔥 NEW: Validate and scroll to missing/error fields
    if (!validateAndScrollCharter()) {
      return;
    }

    if (!isFormValid) {
      showMessage('❌ Παρακαλώ συμπληρώστε όλα τα πεδία.', 'error');
      return;
    }

    // 🔥 NEW: Check for validation errors before saving
    if (charterCodeError) {
      showMessage('❌ ' + charterCodeError, 'error');
      return;
    }

    if (dateRangeError) {
      showMessage('❌ ' + dateRangeError, 'error');
      return;
    }

    if (doubleBookingError) {
      showMessage('❌ ' + doubleBookingError, 'error');
      return;
    }

    // 🔥 CRITICAL: Final check for duplicate charter code before saving (using API)
    // 🔥 SKIP duplicate check if editing the SAME charter code
    const isCodeChanged = isEditMode && editingCharter.code !== newCharter.code;
    const shouldCheckDuplicate = !isEditMode || isCodeChanged;

    console.log('🛑🛑🛑 FINAL CHECK for duplicate code:', newCharter.code);
    console.log('🛑 isEditMode:', isEditMode, 'isCodeChanged:', isCodeChanged, 'shouldCheckDuplicate:', shouldCheckDuplicate);

    // 🔥 API VALIDATION: Check duplicate charter code in database
    let isDuplicateCode = false;
    if (shouldCheckDuplicate) {
      const excludeId = isEditMode ? editingCharter.booking_number || editingCharter.bookingCode || editingCharter.code || editingCharter.id || editingCharter.charterCode : undefined;
      const duplicateResult = await checkDuplicateCharterCode(newCharter.code, excludeId);
      isDuplicateCode = duplicateResult.isDuplicate;
      if (duplicateResult.existingBooking) {
        console.log('🛑 Found existing booking with same code:', duplicateResult.existingBooking.code);
      }
    }
    console.log('🛑 isDuplicateCode result:', isDuplicateCode);

    // 🔥🔥🔥 ABSOLUTE BLOCK - IF DUPLICATE FOUND, DO NOT CONTINUE
    if (isDuplicateCode === true) {
      console.log('🛑🛑🛑 BLOCKING SAVE - DUPLICATE DETECTED! STOPPING HERE!');
      setCharterCodeError('Αυτός ο αριθμός ναύλου υπάρχει ήδη');
      // 🔥 Apply INTENSE red highlight
      if (charterCodeRef.current) {
        charterCodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        charterCodeRef.current.style.border = '4px solid #ff0000';
        charterCodeRef.current.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
        charterCodeRef.current.style.borderRadius = '12px';
        charterCodeRef.current.style.padding = '10px';
        charterCodeRef.current.style.boxShadow = '0 0 20px 5px rgba(255, 0, 0, 0.7)';
        const inputInside = charterCodeRef.current.querySelector('input');
        if (inputInside) {
          inputInside.style.border = '4px solid #ff0000';
          inputInside.style.backgroundColor = 'rgba(255, 0, 0, 0.4)';
          inputInside.style.boxShadow = '0 0 15px 3px rgba(255, 0, 0, 0.6)';
        }
        setTimeout(() => {
          if (charterCodeRef.current) {
            charterCodeRef.current.style.border = '';
            charterCodeRef.current.style.backgroundColor = '';
            charterCodeRef.current.style.borderRadius = '';
            charterCodeRef.current.style.padding = '';
            charterCodeRef.current.style.boxShadow = '';
            const inputInside = charterCodeRef.current.querySelector('input');
            if (inputInside) {
              inputInside.style.border = '';
              inputInside.style.backgroundColor = '';
              inputInside.style.boxShadow = '';
            }
          }
        }, 3000);
      }
      alert(`❌ ΣΤΑΜΑΤΑ!\n\nΑυτός ο αριθμός ναύλου υπάρχει ήδη: "${newCharter.code}"!\n\nΔΕΝ ΜΠΟΡΕΙΤΕ ΝΑ ΑΠΟΘΗΚΕΥΣΕΤΕ!`);
      console.log('🛑🛑🛑 RETURNING NOW - SAVE BLOCKED');
      return; // 🛑 ABSOLUTE BLOCK
    }

    // 🔥 Double-check: If we somehow got here with a duplicate, block again
    if (isDuplicateCode) {
      console.log('🛑 SECOND CHECK CAUGHT DUPLICATE - BLOCKING');
      alert('❌ Duplicate detected - save blocked!');
      return;
    }

    // 🔥 CRITICAL: τελικός έλεγχος διπλοκράτησης — αποφασίζει Ο SERVER.
    // Ο ίδιος ο ναύλος εξαιρείται μέσω editingCharterRef() (αριθμητικό match στον server),
    // ώστε edit χωρίς αλλαγή ημερομηνιών να μη μπλοκάρεται από τον εαυτό του.
    console.log('🔍 FINAL CHECK for date overlap (server validation)');
    console.log('🔍 Dates:', newCharter.startDate, '-', newCharter.endDate);
    const overlapResult = await checkDateOverlap(
      boat.id, newCharter.startDate, newCharter.endDate, editingCharterRef(), boat.name
    );
    // Αν ο server δεν αποφάνθηκε (άγνωστο σκάφος / ασυμφωνία ταυτότητας / δίκτυο)
    // ΔΕΝ αποθηκεύουμε: ο browser δεν έχει δικαίωμα να κρίνει μόνος του.
    const hasDateOverlap = overlapResult.hasOverlap || !!overlapResult.error;
    if (overlapResult.overlappingBooking) {
      console.log('🛑 Found overlapping booking:', overlapResult.overlappingBooking.bookingNumber);
    }
    console.log('🔍 hasDateOverlap result:', hasDateOverlap, overlapResult);
    if (hasDateOverlap) {
      console.log('❌❌❌ BLOCKING SAVE - DATE OVERLAP DETECTED!');
      setDoubleBookingError(overlapMessage(overlapResult));
      // 🔥 Apply INTENSE red highlight
      if (datesRef.current) {
        datesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        datesRef.current.style.border = '4px solid #ff0000';
        datesRef.current.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
        datesRef.current.style.borderRadius = '12px';
        datesRef.current.style.padding = '10px';
        datesRef.current.style.boxShadow = '0 0 20px 5px rgba(255, 0, 0, 0.7)';
        const inputs = datesRef.current.querySelectorAll('input');
        inputs.forEach((input: HTMLInputElement) => {
          input.style.border = '4px solid #ff0000';
          input.style.backgroundColor = 'rgba(255, 0, 0, 0.4)';
          input.style.boxShadow = '0 0 15px 3px rgba(255, 0, 0, 0.6)';
        });
        setTimeout(() => {
          if (datesRef.current) {
            datesRef.current.style.border = '';
            datesRef.current.style.backgroundColor = '';
            datesRef.current.style.borderRadius = '';
            datesRef.current.style.padding = '';
            datesRef.current.style.boxShadow = '';
            const inputs = datesRef.current.querySelectorAll('input');
            inputs.forEach((input: HTMLInputElement) => {
              input.style.border = '';
              input.style.backgroundColor = '';
              input.style.boxShadow = '';
            });
          }
        }, 3000);
      }
      alert(`❌ ΣΤΑΜΑΤΑ!\n\n${boat?.name}: ${overlapMessage(overlapResult)}\n\nΔΕΝ ΜΠΟΡΕΙΤΕ ΝΑ ΑΠΟΘΗΚΕΥΣΕΤΕ!`);
      return; // BLOCK SAVE
    }

    console.log('✅✅✅ All checks passed - OK to save');

    // 🔥🔥🔥 FINAL SAFETY CHECK - One last verification before saving (skip for edit mode same code)
    if (shouldCheckDuplicate) {
      const finalDuplicateCheck = await checkDuplicateOnEnter(newCharter.code);
      if (finalDuplicateCheck) {
        console.log('🛑🛑🛑 FINAL SAFETY CHECK CAUGHT DUPLICATE - ABSOLUTELY BLOCKING SAVE!');
        alert('❌ ΣΤΑΜΑΤΑ! Βρέθηκε διπλότυπο charter party code στον τελικό έλεγχο!\n\nΔεν αποθηκεύτηκε!');
        return;
      }
    }

    // 🔥 CREATE or UPDATE charter object
    const charter = {
      // Keep original id when editing, generate new when adding
      id: isEditMode ? editingCharter.id : uid(),
      code: newCharter.code,
      startDate: newCharter.startDate,
      endDate: newCharter.endDate,
      startTime: newCharter.startTime,
      endTime: newCharter.endTime,
      departure: newCharter.departure,
      arrival: newCharter.arrival,
      boatName: boat.name,
      vesselName: boat.name,
      vesselId: boat.id, // 🔥 FIX 6: Add vesselId for API sync
      ownerCode: boat.ownerCode || '',
      amount: financials.amount,
      commissionPercent: financials.commissionPercent,
      hasForeignBroker: newCharter.hasForeignBroker,
      foreignBrokerPercent: financials.foreignBrokerPercent,
      broker: newCharter.broker || '', // Broker / Agency name
      brokerEmailContact: newCharter.brokerEmailContact || '', // Broker contact email
      // Foreign broker object (consumed by ΑΑΔΕ export parties)
      foreign_broker: {
        enabled: newCharter.hasForeignBroker,
        name: newCharter.broker || '',
        email: newCharter.brokerEmailContact || '',
        vat: newCharter.brokerVat || '',
        doy: newCharter.brokerDoy || '',
        country: newCharter.brokerCountry || '',
        address: newCharter.brokerAddress || '',
        phone: newCharter.brokerPhone || '',
        percent: financials.foreignBrokerPercent,
      },
      showVat: newCharter.showVat || false, // Show VAT breakdown in Charter Party
      foreignCommission: financials.foreignCommission || 0,
      commission: financials.greekCommission,
      vat_on_commission: financials.vatOnGreekCommission,
      status: newCharter.status,
      bookingStatus: newCharter.status,
      // 🔥 Keep existing payment data when editing, reset when adding
      paymentStatus: isEditMode ? (editingCharter.paymentStatus || 'Pending') : 'Pending',
      payments: isEditMode ? (editingCharter.payments || []) : [],
      // Charterer fields
      /* ΕΤΑΙΡΕΙΑ: η ΕΠΩΝΥΜΙΑ γράφεται και στα παλιά πεδία ονόματος, ώστε DOCX,
         emails, λίστες και ΑΑΔΕ να δείχνουν την εταιρεία χωρίς να ξέρουν το νέο
         πεδίο· τα ατομικά μένουν ΚΕΝΑ (δεν υπάρχουν για νομικό πρόσωπο). */
      chartererIsCompany: !!newCharter.chartererIsCompany,
      chartererCompanyName: newCharter.chartererIsCompany ? (newCharter.chartererCompanyName || '') : '',
      chartererCountry: newCharter.chartererCountry || '',
      chartererFirstName: newCharter.chartererIsCompany ? '' : newCharter.chartererFirstName,
      chartererLastName: newCharter.chartererIsCompany ? (newCharter.chartererCompanyName || '') : newCharter.chartererLastName,
      chartererAddress: newCharter.chartererAddress,
      chartererEmail: newCharter.chartererEmail,
      chartererPhone: newCharter.chartererPhone, chartererTaxNumber: newCharter.chartererTaxNumber, chartererTaxOffice: newCharter.chartererTaxOffice,
      chartererDob: newCharter.chartererIsCompany ? '' : (newCharter.chartererDob || ''),
      chartererGender: newCharter.chartererIsCompany ? '' : (newCharter.chartererGender || ''),
      chartererNationality: newCharter.chartererIsCompany ? '' : (newCharter.chartererNationality || ''),
      // ID/Passport ναυλωτή: canonical chartererIdNumber (το διαβάζει Agreement + derived skipper),
      // + split chartererPassport όταν είναι passport (το διαβάζει το DOCX CHARTERER_PASSPORT).
      chartererIdType: newCharter.chartererIsCompany ? '' : (newCharter.chartererIdType || 'id'),
      chartererIdNumber: newCharter.chartererIsCompany ? '' : (newCharter.chartererIdNumber || ''),
      chartererPassport: (!newCharter.chartererIsCompany && newCharter.chartererIdType === 'passport')
                           ? (newCharter.chartererIdNumber || '') : '',
      /* Εταιρεία δεν μπορεί να είναι σκίπερ — το τικ αγνοείται ρητά. */
      sameAsSkipper: newCharter.chartererIsCompany ? false : newCharter.sameAsSkipper,
      // 🔥 FIX 9: Skipper fields.
      // sameAsSkipper=true → ο ναυλωτής ΕΙΝΑΙ ο skipper: παράγουμε τα skipper* από τον charterer
      // ΠΑΝΤΑ (single source of truth), ανεξάρτητα από ό,τι κάθεται stale στο state (π.χ. broker
      // πληκτρολογημένος στα skipper*). Χωρίς αυτό, μια αλλαγή charterer μετά το toggle άφηνε
      // τα skipper* παγωμένα — γι' αυτό ο broker επιβίωνε ως «Πλοίαρχος».
      skipperFirstName: newCharter.sameAsSkipper ? newCharter.chartererFirstName : newCharter.skipperFirstName,
      skipperLastName: newCharter.sameAsSkipper ? newCharter.chartererLastName : newCharter.skipperLastName,
      skipperAddress: newCharter.sameAsSkipper ? newCharter.chartererAddress : newCharter.skipperAddress,
      skipperEmail: newCharter.sameAsSkipper ? newCharter.chartererEmail : newCharter.skipperEmail,
      skipperPhone: newCharter.sameAsSkipper ? newCharter.chartererPhone : newCharter.skipperPhone, skipperTaxNumber: newCharter.sameAsSkipper ? newCharter.chartererTaxNumber : newCharter.skipperTaxNumber, skipperTaxOffice: newCharter.sameAsSkipper ? newCharter.chartererTaxOffice : newCharter.skipperTaxOffice,
      skipperDob: (newCharter.sameAsSkipper ? newCharter.chartererDob : newCharter.skipperDob) || '', skipperGender: (newCharter.sameAsSkipper ? newCharter.chartererGender : newCharter.skipperGender) || '', skipperNationality: (newCharter.sameAsSkipper ? newCharter.chartererNationality : newCharter.skipperNationality) || '',
      // ID/Passport σκίπερ: sameAsSkipper → κληρονομεί από ναυλωτή. canonical skipperIdNumber (Agreement +
      // derived skipper) + split skipperPassport όταν passport (DOCX CREW1_PASSPORT).
      skipperIdType: newCharter.sameAsSkipper ? (newCharter.chartererIdType || 'id') : (newCharter.skipperIdType || 'id'),
      skipperIdNumber: newCharter.sameAsSkipper ? (newCharter.chartererIdNumber || '') : (newCharter.skipperIdNumber || ''),
      skipperPassport: (newCharter.sameAsSkipper ? (newCharter.chartererIdType === 'passport' ? newCharter.chartererIdNumber : '') : (newCharter.skipperIdType === 'passport' ? newCharter.skipperIdNumber : '')) || '',
      // 🔥 Keep original created data when editing
      createdBy: isEditMode ? editingCharter.createdBy : authService.getCurrentUser()?.name,
      createdAt: isEditMode ? editingCharter.createdAt : new Date().toISOString(),
      // 🔥 Add updated info
      updatedBy: authService.getCurrentUser()?.name,
      updatedAt: new Date().toISOString(),
      // 🔥 Keep source info when editing
      source: isEditMode ? editingCharter.source : undefined,
      // 🔥 NEW: Extras
      extras: selectedExtras,
      // 🔥 NEW: Notes
      notes: charterNotes
    };

    // 🔥 FIX 6: Debug logging
    console.log(isEditMode ? '✏️ Updating charter:' : '📝 Adding charter:', charter);

    // Save locally - UPDATE existing or ADD new
    if (isEditMode) {
      // Update: replace the existing charter
      const updatedItems = items.map((item: any) => item.id === editingCharter.id ? charter : item);
      saveItems(updatedItems);
    } else {
      // Add: append new charter
      saveItems([...items, charter]);
    }

    // 🔥 FIX 6: Sync to API - show error if fails
    try {
      // 🔥 Clean charter data before sending - remove nested 'booking' and 'success' fields
      const cleanCharter = { ...charter };
      delete cleanCharter.booking;
      delete cleanCharter.success;

      // 🔥 FIX: When editing and charter code changed, use ORIGINAL booking_number to update existing record
      const originalBookingNumber = isEditMode
        ? (editingCharter.booking_number || editingCharter.bookingCode || editingCharter.code || editingCharter.id)
        : null;
      const newBookingNumber = cleanCharter.code;
      const codeChanged = isEditMode && originalBookingNumber && originalBookingNumber !== newBookingNumber;

      if (codeChanged) {
        // Code changed during edit: atomic rename via PUT with new_booking_number
        console.log('🔄 Charter code changed from', originalBookingNumber, 'to', newBookingNumber);
        const apiResult = await renameBooking(originalBookingNumber, newBookingNumber, cleanCharter);
        console.log('✅ Charter renamed and saved:', apiResult);
      } else {
        // Normal save: same code or new charter
        const bookingNumber = isEditMode ? (originalBookingNumber || newBookingNumber) : newBookingNumber;
        const apiResult = await saveBooking(bookingNumber, { bookingData: cleanCharter });
        console.log('✅ Charter synced to API:', apiResult);
      }
    } catch (error) {
      console.error('❌ API sync error:', error);
      if (error.message && error.message.includes('already exists')) {
        showMessage('❌ Αυτός ο αριθμός ναύλου υπάρχει ήδη! Δεν αποθηκεύτηκε.', 'error');
        return;
      }
      showMessage('⚠ Το ναύλο αποθηκεύτηκε τοπικά αλλά ΟΧΙ στον server! Δοκιμάστε ξανά.', 'error');
    }

    // ─────────────────────────────────────────────────────────────────────────
    // v6.470 — ΑΜΦΙΔΡΟΜΟΣ ΚΑΘΡΕΦΤΗΣ (β): «Ενημέρωση Ναύλου» → Agreement (nested + crew).
    // ΓΙΑΤΙ: το saveBooking/renameBooking γράφει ΜΟΝΟ flat booking_data (PUT = shallow
    // merge), ενώ το CharterAgreementPage κάνει prefill με σειρά
    // charter_archives.chartererData → booking_data.chartererData → flat → crewMembers
    // (CharterAgreementPage.tsx:775-806). Το flat είναι ΤΕΛΕΥΤΑΙΟ, άρα αλλαγή σκίπερ/
    // ναυλωτή από το Fleet δεν έφτανε ποτέ στη φόρμα όταν υπήρχε ήδη nested/archive.
    // TRIGGER = το πάτημα του κουμπιού (ενέργεια χρήστη — ΟΧΙ on-change).
    // GUARDS: μόνο ΜΗ-ΚΕΝΑ πεδία (κενό δεν σβήνει καλά δεδομένα)· skipper ΜΟΝΟ όταν
    // skipperMode !== 'me'/'invite'. ΔΕΝ αγγίζουμε sameAsSkipper.
    // ΔΕΝ φτιάχνουμε γραμμή crewMembers[role=skipper] αν λείπει — αυτό παραμένει δουλειά
    // του write-through / on-load auto-sync v6.469· εδώ μόνο ΕΝΗΜΕΡΩΝΟΥΜΕ υπάρχουσα.
    try {
      const syncBn = charter.code;
      const hasIdentity = !!(charter.chartererFirstName || charter.chartererLastName
        || charter.skipperFirstName || charter.skipperLastName);
      if (hasIdentity) {
        const bRes = await fetch('/api/bookings.php?booking_number=' + encodeURIComponent(syncBn));
        const bJson = bRes.ok ? await bRes.json() : null;
        const freshBd: any = bJson?.booking_data || (Array.isArray(bJson) ? bJson[0]?.booking_data : null) || {};
        const aRes = await fetch('/api/charter-archive.php?booking_number=' + encodeURIComponent(syncBn));
        const aJson = aRes.ok ? await aRes.json() : null;
        const archive: any = aJson?.data || {};
        // Βάση: archive chartererData (authoritative για το Agreement) πάνω από το nested
        // του booking — ώστε να ΜΗ χαθούν υπογραφές/timestamps/consents.
        const baseCd: any = { ...(freshBd.chartererData || {}), ...(archive.chartererData || {}) };
        const put = (k: string, v: any) => {
          const s = typeof v === 'string' ? v.trim() : v;
          if (s !== undefined && s !== null && s !== '') baseCd[k] = s;
        };
        put('firstName', charter.chartererFirstName);
        /* ═══ ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ ══════════════════════════════════════════
           Επωνυμία στο όνομα, ΚΑΜΙΑ ατομική πληροφορία — και ό,τι είχε γραφτεί
           παλιότερα (πριν γίνει «Εταιρεία») φεύγει ρητά από το nested. */
        const isCo = !!charter.chartererIsCompany;
        if (isCo) {
          const coName = String(charter.chartererCompanyName || charter.chartererLastName || '').trim();
          Object.assign(baseCd, companyChartererPatch(coName, charter.chartererCountry));
          INDIVIDUAL_KEYS.forEach((k) => { delete (baseCd as any)[k]; });
        } else {
          baseCd.isCompany = false;
          delete baseCd.companyName;
          put('lastName', charter.chartererLastName);
          put('idType', charter.chartererIdType);
          put('idNumber', charter.chartererIdNumber);
          put('dob', charter.chartererDob);
          put('gender', charter.chartererGender);
          put('nationality', charter.chartererNationality);
        }
        put('address', charter.chartererAddress);
        put('tel', charter.chartererPhone);
        put('email', charter.chartererEmail);
        put('country', charter.chartererCountry);
        put('taxNumber', charter.chartererTaxNumber);
        put('taxOffice', charter.chartererTaxOffice);
        const skMode = String(freshBd.skipperMode || baseCd.skipperMode || '').toLowerCase();
        const skIsMe = skMode === 'me' || baseCd.isAlsoSkipper === true
          || freshBd.sameAsSkipper === true || charter.sameAsSkipper === true;
        let crewToSend: any[] | null = null;
        const curCrew: any[] = Array.isArray(freshBd.crewMembers) ? freshBd.crewMembers : [];
        const skName = [charter.skipperFirstName, charter.skipperLastName].filter(Boolean).join(' ').trim();
        // v6.473 — ΡΗΤΗ ΔΙΑΓΡΑΦΗ: ο χρήστης άδειασε ΟΛΟ το skipper block και πάτησε
        // «Ενημέρωση Ναύλου». Στέλνουμε ΠΡΟΘΕΣΗ (skipperCleared), ΟΧΙ κενές τιμές — έτσι
        // το «λείπει το κλειδί» (μην αγγίζεις) μένει διακριτό από το «ρητά κενό» (καθάρισε).
        // Η φόρμα edit φορτώνει τα skipper πεδία από flat→nested→invitation (:8139), άρα σε
        // ναύλο με στοιχεία σκίπερ το skEmptyAll ΔΕΝ γίνεται true κατά λάθος.
        const skEmptyAll = [
          charter.skipperFirstName, charter.skipperLastName, charter.skipperEmail,
          charter.skipperPhone, charter.skipperAddress, charter.skipperIdNumber,
          charter.skipperDob, charter.skipperGender, charter.skipperNationality,
          charter.skipperTaxNumber, charter.skipperTaxOffice,
        ].every((v: any) => String(v ?? '').trim() === '');
        const skipperCleared = !skIsMe && skMode !== 'invite' && skEmptyAll;
        if (skipperCleared) {
          // nested (+ archive, γράφεται από το ίδιο baseCd) — αφαίρεση, όχι κενές τιμές
          ['skipperFirstName','skipperLastName','skipperName','skipperEmail','skipperPhone',
           'skipperAddress','skipperIdNumber','skipperIdType','skipperDob','skipperGen',
           'skipperNationality','skipperTaxNumber','skipperTaxOffice'
          ].forEach((k) => { delete baseCd[k]; });
          // και η γραμμή crew[role=skipper] — αλλιώς ο server-mirror την ξαναδιαβάζει
          crewToSend = curCrew.filter((m: any) => String(m?.role || '').toLowerCase() !== 'skipper');
        } else if (!skIsMe && skMode !== 'invite') {
          // nested: ΠΡΟΣΟΧΗ flat 'skipperGender' ↔ nested 'skipperGen'
          put('skipperFirstName', charter.skipperFirstName);
          put('skipperLastName', charter.skipperLastName);
          put('skipperName', skName);
          put('skipperAddress', charter.skipperAddress);
          put('skipperEmail', charter.skipperEmail);
          put('skipperPhone', charter.skipperPhone);
          put('skipperIdType', charter.skipperIdType);
          put('skipperIdNumber', charter.skipperIdNumber);
          put('skipperDob', charter.skipperDob);
          put('skipperGen', charter.skipperGender);
          put('skipperNationality', charter.skipperNationality);
          put('skipperTaxNumber', charter.skipperTaxNumber);
          put('skipperTaxOffice', charter.skipperTaxOffice);
          const si = curCrew.findIndex((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
          if (si >= 0) {
            const row: any = { ...curCrew[si] };
            const putM = (k: string, v: any) => {
              const s = typeof v === 'string' ? v.trim() : v;
              if (s !== undefined && s !== null && s !== '') row[k] = s;
            };
            putM('firstName', charter.skipperFirstName);
            putM('lastName', charter.skipperLastName);
            putM('name', skName);
            putM('email', charter.skipperEmail);
            putM('phone', charter.skipperPhone);
            putM('address', charter.skipperAddress);
            putM('passport', charter.skipperIdNumber);
            putM('dateOfBirth', charter.skipperDob);
            putM('gender', charter.skipperGender);
            putM('nationality', charter.skipperNationality);
            crewToSend = curCrew.map((m: any, i: number) => (i === si ? row : m));
          }
        }
        // Γραμμή ναυλωτή στο crew (isCharterer) — ίδια λογική, μόνο ΜΗ-ΚΕΝΑ.
        const crewBase: any[] = crewToSend || curCrew;
        /* ═══ Η ΕΤΑΙΡΕΙΑ ΔΕΝ ΤΑΞΙΔΕΥΕΙ (19/09/2026) ═══════════════════════════
           Με εταιρικό ναυλωτή η λίστα πληρώματος ΔΕΝ περιέχει τον ναυλωτή: ούτε
           νέα γραμμή, ούτε η παλιά που τυχόν είχε μπει όσο ήταν φυσικό πρόσωπο. */
        if (isCo) {
          const cleaned = crewWithoutCharterer(crewBase);
          if (cleaned.length !== crewBase.length) crewToSend = cleaned;
          else if (crewToSend) crewToSend = cleaned;
        } else {
        const ci = crewBase.findIndex((m: any) => m?.isCharterer === true);
        if (ci >= 0) {
          const row: any = { ...crewBase[ci] };
          const putC = (k: string, v: any) => {
            const s = typeof v === 'string' ? v.trim() : v;
            if (s !== undefined && s !== null && s !== '') row[k] = s;
          };
          putC('firstName', charter.chartererFirstName);
          putC('lastName', charter.chartererLastName);
          putC('name', [charter.chartererFirstName, charter.chartererLastName].filter(Boolean).join(' ').trim());
          putC('email', charter.chartererEmail);
          putC('phone', charter.chartererPhone);
          putC('address', charter.chartererAddress);
          putC('passport', charter.chartererIdNumber);
          putC('dateOfBirth', charter.chartererDob);
          putC('gender', charter.chartererGender);
          putC('nationality', charter.chartererNationality);
          crewToSend = crewBase.map((m: any, i: number) => (i === ci ? row : m));
        }
        }
        const empCode = localStorage.getItem('employeeCode') || '';
        // 1) charter_archives — ΠΡΩΤΟ στην αλυσίδα prefill. Στέλνουμε ΟΛΟ το archive_data
        //    (…archive) γιατί το POST αντικαθιστά top-level keys (αλλιώς χάνεται π.χ. customFields).
        await fetch('/api/charter-archive.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Employee-Code': empCode },
          body: JSON.stringify({ booking_number: syncBn, archive_data: { ...archive, chartererData: baseCd } }),
        });
        // 2) booking_data.chartererData (+ crew αν άλλαξε)· ο server καθρεφτίζει και πίσω
        //    στο flat (idempotent — ίδιες τιμές).
        await fetch('/api/update-charter-crew.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_number: syncBn, chartererData: baseCd, ...(crewToSend ? { crewMembers: crewToSend } : {}), ...(skipperCleared ? { skipperCleared: true } : {}) }),
        });
        console.log('✅ v6.470 Fleet → Agreement sync ok:', syncBn);
      }
    } catch (syncErr) {
      console.warn('⚠️ v6.470 Fleet → Agreement sync failed (ο ναύλος αποθηκεύτηκε κανονικά):', syncErr);
    }
    // ─────────────────────────────────────────────────────────────────────────

    // 🔥 Save Page 1 data so Page 4 floorplan can access vessel info
    try {
      const page1Data = {
        bookingNumber: charter.code,
        vesselCategory: boat.type || 'Catamaran',
        vesselName: boat.name,
        vesselId: boat.id,
        checkInDate: charter.startDate,
        checkInTime: charter.startTime || '17:00',
        checkOutDate: charter.endDate,
        checkOutTime: charter.endTime || '09:00',
        skipperFirstName: charter.skipperFirstName || '',
        skipperLastName: charter.skipperLastName || '',
        skipperAddress: charter.skipperAddress || '',
        skipperEmail: charter.skipperEmail || '',
        skipperPhone: charter.skipperPhone || '',
        departure: charter.departure || 'ALIMOS MARINA',
        arrival: charter.arrival || 'ALIMOS MARINA',
        source: 'fleetManagement' as const
      };
      await savePage1DataHybrid(charter.code, page1Data);
      console.log('✅ Page 1 data saved for floorplan:', page1Data);
    } catch (error) {
      console.error('❌ Page 1 data sync error:', error);
    }

    // 🔥 FIX 13 + FIX 38: Send email when new charter is created (skip email on edit unless status changed)
    // 🔥 FIX: Map status to correct email action
    if (!isEditMode || (isEditMode && editingCharter.status !== newCharter.status)) {
      let emailAction = 'new_charter'; // Default for Option status
      if (newCharter.status === 'Reservation' || newCharter.status === 'reservation') {
        emailAction = 'pending_final_confirmation';
        console.log('📧 Reservation status detected - sending pending_final_confirmation email');
      } else if (newCharter.status === 'Confirmed' || newCharter.status === 'confirmed') {
        emailAction = 'confirmed';
        console.log('📧 Confirmed status detected - sending confirmed email');
      }
      console.log('📧 Sending email with action:', emailAction, 'for status:', newCharter.status);
      await sendCharterEmail(charter, boat, emailAction);
    } else {
      console.log('📧 Skipping email - status unchanged during edit');
    }

    authService.logActivity(isEditMode ? 'edit_charter' : 'add_charter', `${boat.id}/${charter.code}`);

    // 🔥 Reset form and state
    setNewCharter({
      code: '', startDate: '', endDate: '', startTime: '17:00', endTime: '09:00', amount: '', commissionPercent: '20',
      hasForeignBroker: false, foreignBrokerPercent: '20', broker: '', brokerEmailContact: '',
      brokerVat: '', brokerDoy: '', brokerCountry: '', brokerAddress: '', brokerPhone: '',
      departure: 'ALIMOS MARINA', arrival: 'ALIMOS MARINA', status: 'Option',
      skipperFirstName: '', skipperLastName: '', skipperAddress: '', skipperEmail: '', skipperPhone: '', skipperTaxNumber: '', skipperTaxOffice: '',
      chartererDob: '', chartererGender: '', chartererNationality: '', skipperDob: '', skipperGender: '', skipperNationality: '',
      chartererIdType: 'id', chartererIdNumber: '', skipperIdType: 'id', skipperIdNumber: ''
    });
    setEditingCharter(null); // 🔥 Clear edit mode
    setSelectedExtras([]); // 🔥 Reset extras
    setCharterNotes(''); // 🔥 Reset notes
    setShowAddForm(false);
    showMessage(isEditMode ? '✅ Ο ναύλος ενημερώθηκε.' : '✅ Ο ναύλος προστέθηκε.', 'success');
  };

  // 🔥 FIX 19 + FIX 40: Fixed delete to work locally even when API fails
  // Deletes from ALL possible localStorage keys (by ID and by vessel name)
  const handleDeleteCharter = async (charterKey) => {
    if (!canEditCharters) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα διαγραφής', 'error');
      return;
    }

    // Find charter by code first (API charters), then by id (local charters)
    const charter = items.find(c => c.code === charterKey || c.id === charterKey);
    const bookingCode = charter?.code || charterKey;

    console.log(`🗑️ Deleting charter: key=${charterKey}, code=${bookingCode}, found=${!!charter}`);

    // 1. Try to delete from API (don't block on failure)
    if (bookingCode) {
      try {
        const response = await fetch(`https://yachtmanagementsuite.com/api/bookings.php?booking_number=${encodeURIComponent(bookingCode)}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
          console.warn(`API delete failed (status ${response.status}), continuing with local delete`);
        } else {
          console.log('✅ Charter deleted from API:', bookingCode);
        }
      } catch (error) {
        console.warn('⚠️ API delete error (will still delete locally):', error);
      }
    }

    // 2. 🔥 FIX 40: Delete from ALL possible localStorage keys
    // Page 1 saves to fleet_{vesselName}_ΝΑΥΛΑ, FleetManagement saves to fleet_{boatId}_ΝΑΥΛΑ
    const keysToCheck = [
      `fleet_${boat.id}_ΝΑΥΛΑ`,                           // By numeric ID (e.g., fleet_7_ΝΑΥΛΑ)
      `fleet_${boat.name}_ΝΑΥΛΑ`,                         // By name exact (e.g., fleet_Perla_ΝΑΥΛΑ)
      `fleet_${boat.name?.toUpperCase()}_ΝΑΥΛΑ`,          // By name UPPER (e.g., fleet_PERLA_ΝΑΥΛΑ)
      `fleet_${boat.name?.toLowerCase()}_ΝΑΥΛΑ`,          // By name lower (e.g., fleet_perla_ΝΑΥΛΑ)
    ];

    // Also check the charter's vesselId in case it's different
    if (charter?.vesselId && !keysToCheck.includes(`fleet_${charter.vesselId}_ΝΑΥΛΑ`)) {
      keysToCheck.push(`fleet_${charter.vesselId}_ΝΑΥΛΑ`);
    }

    console.log('🗑️ Checking localStorage keys:', keysToCheck);

    keysToCheck.forEach(key => {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const charters = JSON.parse(stored);
          const beforeCount = charters.length;
          const filtered = charters.filter((c: any) => c.code !== bookingCode && c.id !== charterKey);
          if (filtered.length < beforeCount) {
            localStorage.setItem(key, JSON.stringify(filtered));
            console.log(`   ✅ Removed from ${key} (${beforeCount} → ${filtered.length})`);
          }
        }
      } catch (e) {
        console.warn(`   ⚠️ Error cleaning key ${key}:`, e);
      }
    });

    // 3. Update React state
    saveItems(items.filter((item) => item.code !== charterKey && item.id !== charterKey));
    authService.logActivity('delete_charter', `${boat.id}/${bookingCode}`);
    setSelectedCharter(null);
    showMessage('✅ Ο ναύλος διαγράφηκε.', 'success');
  };

  const handleUpdateStatus = async (charter, newStatus) => {
    console.log('📋 handleUpdateStatus called:', { charterId: charter.id, code: charter.code, newStatus });

    // Update local state first
    const updated = items.map((item) => item.id === charter.id ? { ...item, status: newStatus, updatedBy: authService.getCurrentUser()?.name, updatedAt: new Date().toISOString() } : item);
    saveItems(updated);
    setSelectedCharter({ ...charter, status: newStatus });

    // 🔥 FIX 22: Save status to API for multi-device sync
    const bookingCode = charter.code || charter.id;
    try {
      await updateCharterStatus(bookingCode, newStatus);
      console.log('✅ Status synced to API:', bookingCode, newStatus);
      showMessage(`✅ Η κατάσταση άλλαξε σε ${newStatus}`, 'success');
    } catch (error) {
      console.error('❌ API sync failed for status:', error);
      showMessage(`⚠️ Κατάσταση αποθηκεύτηκε τοπικά (API error)`, 'warning');
    }

    authService.logActivity('BOOKING_UPDATED', `${boat.id}/${bookingCode}/${newStatus}`);
  };

  const handleUpdatePayments = async (charterId, newPayments) => {
    console.log('💰 handleUpdatePayments called:', { charterId, newPayments });

    const totalPaid = newPayments.reduce((sum, p) => sum + p.amount, 0);
    const charter = items.find((c) => c.id === charterId);
    console.log('💰 Found charter:', charter);

    const totalAmount = charter?.amount || 0;
    // 🔥 FIX: Use Greek payment status values
    let newPaymentStatus = "ΑΝΕΞΟΦΛΗΤΟ";  // unpaid - default
    if (totalPaid >= totalAmount - 0.05 && totalAmount > 0) newPaymentStatus = "ΕΞΟΦΛΗΜΕΝΟ";  // paid
    else if (totalPaid > 0) newPaymentStatus = "ΜΕΡΙΚΗ ΠΛΗΡΩΜΗ";  // partial

    console.log('💰 Payment calculation:', { totalPaid, totalAmount, newPaymentStatus });

    // Update local state first
    const updated = items.map((item) => item.id === charterId ? { ...item, payments: newPayments, paymentStatus: newPaymentStatus, updatedBy: authService.getCurrentUser()?.name, updatedAt: new Date().toISOString() } : item);
    saveItems(updated);
    setSelectedCharter((prev) => ({ ...prev, payments: newPayments, paymentStatus: newPaymentStatus }));
    console.log('💰 Local state updated');

    // 🔥 FIX: Save to API for multi-device sync
    const bookingCode = charter?.code || charterId;
    console.log('💰 Saving to API with bookingCode:', bookingCode);
    try {
      await updateCharterPayments(bookingCode, newPayments, newPaymentStatus);
      console.log('✅ Payments synced to API:', bookingCode);
      showMessage('✅ Οι πληρωμές αποθηκεύτηκαν!', 'success');
    } catch (error) {
      console.error('❌ API sync failed for payments:', error);
      showMessage('⚠️ Πληρωμές αποθηκεύτηκαν τοπικά (API error)', 'warning');
    }

    authService.logActivity('BOOKING_UPDATED', `${boat.id}/${bookingCode}`);
  };

  // 🔥 NEW: Handle editing a charter (Draft/Page1 charters get full edit capability)
  const handleEditCharter = (charter) => {
    console.log('✏️ EDIT CHARTER:', charter.code, 'Status:', charter.status, 'Source:', charter.source);

    // Populate the form with existing charter data
    setNewCharter({
      code: charter.code || '',
      startDate: charter.startDate || '',
      endDate: charter.endDate || '',
      startTime: charter.startTime || '',
      endTime: charter.endTime || '',
      amount: charter.amount?.toString() || '',
      commissionPercent: charter.commissionPercent?.toString() || '',
      hasForeignBroker: charter.hasForeignBroker || false,
      foreignBrokerPercent: charter.foreignBrokerPercent?.toString() || '20',
      broker: charter.broker || '', // Broker / Agency
      brokerEmailContact: charter.brokerEmailContact || '', // Broker contact email
      // Foreign broker extended fields: prefer foreign_broker object, fall back to flat fields
      brokerVat: charter.foreign_broker?.vat || '',
      brokerDoy: charter.foreign_broker?.doy || '',
      brokerCountry: charter.foreign_broker?.country || '',
      brokerAddress: charter.foreign_broker?.address || '',
      brokerPhone: charter.foreign_broker?.phone || '',
      showVat: charter.showVat || false,
      departure: charter.departure || 'ALIMOS MARINA',
      arrival: charter.arrival || 'ALIMOS MARINA',
      status: charter.status || 'Option',
      skipperFirstName: charter.skipperFirstName || '',
      skipperLastName: charter.skipperLastName || '',
      skipperAddress: charter.skipperAddress || '',
      skipperEmail: charter.skipperEmail || '',
      skipperPhone: charter.skipperPhone || '', skipperTaxNumber: charter.skipperTaxNumber || '', skipperTaxOffice: charter.skipperTaxOffice || '',
            chartererFirstName: charter.chartererFirstName || '',
            chartererLastName: charter.chartererLastName || '',
            chartererAddress: charter.chartererAddress || '',
            chartererEmail: charter.chartererEmail || '',
            chartererPhone: charter.chartererPhone || '', chartererTaxNumber: charter.chartererTaxNumber || '', chartererTaxOffice: charter.chartererTaxOffice || '',
            chartererDob: charter.chartererDob || charter.chartererData?.dob || '',
            chartererGender: charter.chartererGender || charter.chartererData?.gender || '',
            chartererNationality: charter.chartererNationality || charter.chartererData?.nationality || '',
            skipperDob: charter.skipperDob || charter.chartererData?.skipperDob || '',
            skipperGender: charter.skipperGender || charter.chartererData?.skipperGen || '',
            skipperNationality: charter.skipperNationality || charter.chartererData?.skipperNationality || '',
            // ID/Passport — flat → nested chartererData → passport fallback (infer type αν λείπει)
            chartererIdType: charter.chartererIdType || charter.chartererData?.idType || (charter.chartererPassport ? 'passport' : 'id'),
            chartererIdNumber: charter.chartererIdNumber || charter.chartererData?.idNumber || charter.chartererPassport || '',
            skipperIdType: charter.skipperIdType || charter.chartererData?.skipperIdType || (charter.skipperPassport ? 'passport' : 'id'),
            skipperIdNumber: charter.skipperIdNumber || charter.chartererData?.skipperIdNumber || charter.chartererData?.skipperPassport || charter.skipperPassport || '',
            sameAsSkipper: charter.sameAsSkipper || false,
            /* ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ: flat → nested. Χωρίς αυτό, η επεξεργασία ενός
               εταιρικού ναύλου ξανάνοιγε ως «φυσικό πρόσωπο» και η επόμενη
               αποθήκευση γύριζε πίσω τα ατομικά πεδία. */
            chartererIsCompany: isCompanyCharterer(charter) || isCompanyCharterer(charter.chartererData),
            chartererCompanyName: charter.chartererCompanyName || charter.chartererData?.companyName
                                  || ((isCompanyCharterer(charter) || isCompanyCharterer(charter.chartererData))
                                        ? (charter.chartererLastName || '') : ''),
            chartererCountry: charter.chartererCountry || charter.chartererData?.country || ''
    });

    // Set editing mode
    setEditingCharter(charter);

    // 🔥 Auto-fill skipper from online/invitation data when charterer ≠ skipper (fill-empty-only)
    if (!charter.sameAsSkipper) {
      (async () => {
        try {
          const code = charter.code || '';
          let onlineData: any = await loadChartererData(code).catch(() => null);
          try {
            const invR = await fetch('/api/crew-invitations.php?action=list&booking_number=' + encodeURIComponent(code));
            const invJ = await invR.json();
            if (invJ && invJ.success && Array.isArray(invJ.data)) {
              const skInv = invJ.data.find((x: any) => x.role === 'skipper' && x.status === 'submitted');
              if (skInv) {
                onlineData = onlineData || {};
                if (!onlineData.skipperFirstName) onlineData.skipperFirstName = skInv.submitted_first_name || '';
                if (!onlineData.skipperLastName) onlineData.skipperLastName = skInv.submitted_last_name || '';
                if (!onlineData.skipperAddress) onlineData.skipperAddress = skInv.submitted_address || '';
                if (!onlineData.skipperEmail) onlineData.skipperEmail = skInv.submitted_email || skInv.invite_email || '';
                if (!onlineData.skipperPhone) onlineData.skipperPhone = skInv.submitted_phone || '';
              }
            }
          } catch (eInv) { console.log('skipper invitation enrich skipped:', eInv); }
          if (onlineData) {
            setNewCharter(prev => ({
              ...prev,
              skipperFirstName: prev.skipperFirstName || onlineData.skipperFirstName || '',
              skipperLastName: prev.skipperLastName || onlineData.skipperLastName || '',
              skipperAddress: prev.skipperAddress || onlineData.skipperAddress || '',
              skipperEmail: prev.skipperEmail || onlineData.skipperEmail || '',
              skipperPhone: prev.skipperPhone || onlineData.skipperPhone || '',
            }));
          }
        } catch (eAuto) { console.log('skipper auto-fill skipped:', eAuto); }
      })();
    }

    // 🔥 Load extras when editing
    setSelectedExtras(charter.extras || []);
    // 🔥 Load notes when editing
    setCharterNotes(charter.notes || '');

    // Close detail modal and show form
    setSelectedCharter(null);
    setShowAddForm(true);

    showMessage('✏️ Επεξεργασία ναύλου: ' + charter.code, 'info');
    setTimeout(() => {
      try {
        if (charterCodeRef && charterCodeRef.current) {
          charterCodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (e) { window.scrollTo(0, 0); }
    }, 150);
  };

  // 🔥 FIX: Fetch latest status from API when opening charter modal
  const handleSelectCharter = async (charter) => {
    authService.logActivity('view_charter_details', `${boat.id}/${charter.code}`);

    // Try to fetch the latest status from API
    try {
      const bookingCode = charter.code || charter.id;
      const latestBooking = await getBooking(bookingCode);

      // 🔥 FIX: bookings.php single-GET returns { success, booking: { booking_number, booking_data } }.
      // The old code read latestBooking.bookingData (always undefined) → this whole branch was dead
      // and the modal always used stale local data (missing server-only crewMembers / foreign_broker).
      // Read from the correct path (support both shapes just in case).
      const serverData = latestBooking?.booking?.booking_data || latestBooking?.bookingData || null;

      if (serverData) {
        // Full merge: bring the complete server-side booking_data into the modal so
        // fields persisted only server-side (crewMembers, foreign_broker, export_*)
        // are present. DB wins for any key it holds; local-only identity fields
        // (code/id/bookingCode/charterCode) are preserved because the DB row may lack
        // the synthesized ones. status kept explicit as before.
        const updatedCharter = {
          ...charter,                 // local UI fields + synthesized identity keys
          ...serverData,              // authoritative server data
          status: serverData.status || charter.status,
          // Preserve identity keys the GET-all list synthesizes (raw booking_data may lack them)
          code: charter.code || serverData.code,
          id: charter.id || serverData.id,
          bookingCode: charter.bookingCode || serverData.bookingCode,
          charterCode: charter.charterCode || serverData.charterCode,
        };

        console.log('📋 Charter from API merged. Status:', serverData.status, 'Local:', charter.status);

        // Update local state if status changed
        if (serverData.status && serverData.status !== charter.status) {
          const updated = items.map((item) =>
            item.id === charter.id ? { ...item, status: serverData.status } : item
          );
          saveItems(updated);
          showMessage(`📋 Status updated from server: ${serverData.status}`, 'info');
        }

        setSelectedCharter(updatedCharter);
      } else {
        // API returned no data, use local charter
        setSelectedCharter(charter);
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch latest status from API:', error);
      // Fallback to local charter data
      setSelectedCharter(charter);
    }
  };

  // 🔥 NEW: Refresh selected charter from API (for auto-refresh)
  const refreshSelectedCharter = useCallback(async () => {
    if (!selectedCharter) return;

    try {
      const bookingCode = selectedCharter.code || selectedCharter.id;
      const latestBooking = await getBooking(bookingCode);

      // 🔥 FIX: same shape as handleSelectCharter — bookings.php returns
      // { success, booking: { booking_number, booking_data } }. latestBooking.bookingData
      // was always undefined (dead branch). Read the correct path (support both shapes).
      const serverData = latestBooking?.booking?.booking_data || latestBooking?.bookingData || null;

      if (serverData) {
        const newStatus = serverData.status;

        // Only update if status changed
        if (newStatus && newStatus !== selectedCharter.status) {
          console.log('🔄 Modal auto-refresh: Status changed from', selectedCharter.status, 'to', newStatus);

          // Update modal with full server data (same merge logic as handleSelectCharter)
          setSelectedCharter(prev => ({
            ...prev,                  // local UI fields + synthesized identity keys
            ...serverData,            // authoritative server data
            status: serverData.status || prev.status,
            // Preserve identity keys the GET-all list synthesizes (raw booking_data may lack them)
            code: prev.code || serverData.code,
            id: prev.id || serverData.id,
            bookingCode: prev.bookingCode || serverData.bookingCode,
            charterCode: prev.charterCode || serverData.charterCode,
          }));

          // Update list
          const updated = items.map((item) =>
            item.id === selectedCharter.id ? { ...item, status: newStatus } : item
          );
          saveItems(updated);

          showMessage(`📋 Status updated: ${newStatus}`, 'info');
        }
      }
    } catch (error) {
      console.warn('⚠️ Modal refresh error:', error);
    }
  }, [selectedCharter, items, saveItems, showMessage]);

  // 🔥 NEW: Auto-refresh modal every 30 seconds when open
  useEffect(() => {
    console.log('🔍 useEffect running, selectedCharter:', selectedCharter?.code);
    if (!selectedCharter) return;

    const interval = setInterval(() => {
      console.log('⏰ Modal 30-second refresh triggered');
      refreshSelectedCharter();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [selectedCharter?.id, refreshSelectedCharter]);

  // 🔥 NEW: Update modal when global refresh happens
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (selectedCharter) {
        console.log('🔄 CharterPage: Global refresh received, updating modal...');
        refreshSelectedCharter();
      }
    };

    window.addEventListener('globalBookingsRefreshed', handleGlobalRefresh);
    return () => window.removeEventListener('globalBookingsRefreshed', handleGlobalRefresh);
  }, [selectedCharter, refreshSelectedCharter]);

  return (
    <div>
      {isOwnerUser && (
        <div className="mb-4 p-3 bg-blue-900 rounded-lg text-center border border-blue-700">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">
            {icons.eye}
            <span>View Only - Μπορείτε να αποδεχτείτε/απορρίψετε ναύλους</span>
          </div>
        </div>
      )}

      {canEditCharters && (
        <div className="mb-4">
          <button onClick={() => {
            if (showAddForm) {
              // Cancel: clear form and editing state
              setShowAddForm(false);
              setEditingCharter(null);
              setNewCharter({
                code: '', startDate: '', endDate: '', startTime: '17:00', endTime: '09:00', amount: '', commissionPercent: '20',
                hasForeignBroker: false, foreignBrokerPercent: '20', broker: '', brokerEmailContact: '',
                departure: 'ALIMOS MARINA', arrival: 'ALIMOS MARINA', status: 'Option',
                skipperFirstName: '', skipperLastName: '', skipperAddress: '', skipperEmail: '', skipperPhone: '', skipperTaxNumber: '', skipperTaxOffice: '',
                chartererDob: '', chartererGender: '', chartererNationality: '', skipperDob: '', skipperGender: '', skipperNationality: '',
                chartererIdType: 'id', chartererIdNumber: '', skipperIdType: 'id', skipperIdNumber: ''
              });
            } else {
              setShowAddForm(true);
            }
          }} className={`flex items-center justify-center w-full ${editingCharter ? 'bg-blue-800 hover:bg-blue-700 border-blue-600 text-white' : 'bg-white hover:bg-[#f9fafb] border-[#d1d5db] text-[#374151]'} font-bold py-3 px-4 rounded-lg transition duration-200 border`}>
            {editingCharter ? icons.edit : icons.plus} <span className="ml-2">{showAddForm ? 'Ακύρωση' : 'Προσθήκη Νέου Ναύλου'}</span>
          </button>
          
          {showAddForm && (
            <form onSubmit={(e) => e.preventDefault()} className={`mt-4 p-5 rounded-lg border-2 space-y-4 ${editingCharter ? 'bg-blue-900 border-blue-600' : 'bg-white border-[#d1d5db]'}`}>
              {/* 🔥 Edit mode header banner */}
              {editingCharter && (
                <div className="bg-blue-600 text-white p-3 rounded-lg text-center font-bold text-lg mb-2 border-2 border-blue-400">
                  ✏️ ΕΠΕΞΕΡΓΑΣΙΑ ΝΑΥΛΟΥ: {editingCharter.code}
                </div>
              )}
              <div className="bg-[#f9fafb] p-4 rounded-lg border border-[#d1d5db]">
                <h3 className="text-lg font-bold text-[#1e40af] mb-3">
                  {editingCharter ? '✏️ ΕΠΕΞΕΡΓΑΣΙΑ ΝΑΥΛΟΥ' : 'CHARTERING INFORMATION'}
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <div ref={charterCodeRef}>
                    <label className="block text-sm font-medium text-[#374151] mb-2">Κωδικός Ναύλου *</label>
                    {/* 🔥 FIX 30: Added autoComplete="off" to fix Chrome typing issue */}
                    <input
                      type="text"
                      name="code"
                      value={newCharter.code}
                      onChange={handleFormChange}
                      onKeyDown={handleFormKeyDown}
                      onBlur={() => newCharter.code && validateCharterCodeOnBlur(newCharter.code, editingCharter?.booking_number || editingCharter?.bookingCode || editingCharter?.code || editingCharter?.id)}
                      placeholder="π.χ. NAY-002"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className={`w-full px-3 py-2 bg-gray-600 text-white rounded-lg border ${charterCodeError ? 'border-red-500' : 'border-gray-500'} focus:border-[#1e40af] focus:outline-none`}
                    />
                    {charterCodeError && (
                      <p className="mt-2 text-sm text-red-400 font-medium">{charterCodeError}</p>
                    )}
                  </div>
                  <div ref={datesRef}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">CHECK-IN DATE *</label>
                        <input
                          type="date"
                          name="startDate"
                          value={newCharter.startDate}
                          onChange={handleFormChange}
                          onKeyDown={handleFormKeyDown}
                          onBlur={() => newCharter.startDate && newCharter.endDate && validateDatesOnBlur(newCharter.startDate, newCharter.endDate, editingCharter?.id)}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 bg-gray-600 text-white rounded-lg border ${doubleBookingError ? 'border-red-500' : 'border-gray-500'} focus:border-[#1e40af] focus:outline-none`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">CHECK-IN TIME</label>
                        <input
                          type="time"
                          name="startTime"
                          value={newCharter.startTime}
                          onChange={handleFormChange}
                          onKeyDown={handleFormKeyDown}
                          className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-[#1e40af] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">CHECK-OUT DATE *</label>
                        <input
                          type="date"
                          name="endDate"
                          value={newCharter.endDate}
                          onChange={handleFormChange}
                          onKeyDown={handleFormKeyDown}
                          onBlur={() => newCharter.startDate && newCharter.endDate && validateDatesOnBlur(newCharter.startDate, newCharter.endDate, editingCharter?.id)}
                          min={newCharter.startDate || new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 bg-gray-600 text-white rounded-lg border ${dateRangeError || doubleBookingError ? 'border-red-500' : 'border-gray-500'} focus:border-[#1e40af] focus:outline-none`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#374151] mb-2">CHECK-OUT TIME</label>
                        <input
                          type="time"
                          name="endTime"
                          value={newCharter.endTime}
                          onChange={handleFormChange}
                          onKeyDown={handleFormKeyDown}
                          className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-[#1e40af] focus:outline-none"
                        />
                      </div>
                    </div>
                    {dateRangeError && (
                      <p className="mt-2 text-sm text-red-400 font-medium">{dateRangeError}</p>
                    )}
                    {doubleBookingError && (
                      <p className="mt-2 text-sm text-red-400 font-medium">{doubleBookingError}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">DEPARTURE</label>
                      <SearchableSelect
                        value={newCharter.departure}
                        options={aadeLookups.ports || []}
                        getKey={(o: any) => o.name_el || o.name_en}
                        getLabel={(o: any) => o.name_el || o.name_en}
                        getHint={(o: any) => o.un_code}
                        getSearchText={(o: any) => `${o.name_el || ''} ${o.name_en || ''} ${o.un_code || ''}`}
                        onChange={(name: string) => setNewCharter(prev => ({ ...prev, departure: name }))}
                        placeholder="Αναζήτηση λιμανιού (π.χ. Άλιμος / alimos)..."
                        inputClassName="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-[#1e40af] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">ARRIVAL</label>
                      <SearchableSelect
                        value={newCharter.arrival}
                        options={aadeLookups.ports || []}
                        getKey={(o: any) => o.name_el || o.name_en}
                        getLabel={(o: any) => o.name_el || o.name_en}
                        getHint={(o: any) => o.un_code}
                        getSearchText={(o: any) => `${o.name_el || ''} ${o.name_en || ''} ${o.un_code || ''}`}
                        onChange={(name: string) => setNewCharter(prev => ({ ...prev, arrival: name }))}
                        placeholder="Αναζήτηση λιμανιού (π.χ. Άλιμος / alimos)..."
                        inputClassName="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-[#1e40af] focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">STATUS</label>
                    <select name="status" value={newCharter.status} onChange={handleFormChange} onKeyDown={handleFormKeyDown} className="w-full px-3 py-3 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-[#1e40af] focus:outline-none font-bold">
                      <option value="Draft" className="bg-blue-400 text-white">📝 DRAFT (Από Check-in)</option>
                      <option value="Option" className="bg-yellow-400 text-black">🟡 OPTION (Αναμονή Owner)</option>
                      <option value="Reservation" className="bg-yellow-400 text-black">🟡 RESERVATION (Κράτηση)</option>
                      <option value="Pending Approval" className="bg-orange-400 text-black">🟠 PENDING APPROVAL (Προς Έγκριση)</option>
                      <option value="Confirmed" className="bg-green-500 text-white">🟢 CONFIRMED (Κλεισμένο)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* CHARTERER INFORMATION Section */}
              <div className="bg-[#f9fafb] p-4 rounded-lg border border-[#d1d5db]">
                <h3 className="text-lg font-bold text-green-400 mb-3">CHARTERER INFORMATION</h3>

                {/* ═══ ΦΥΣΙΚΟ ΠΡΟΣΩΠΟ Ή ΕΤΑΙΡΕΙΑ (19/09/2026) ══════════════════
                    Η εταιρεία δεν έχει φύλο, γέννηση, εθνικότητα ή διαβατήριο —
                    και ΔΕΝ επιβαίνει (δεν μπαίνει στο crew list). Βλ.
                    utils/chartererType: ένας κανόνας για φόρμα, Agreement, ΑΑΔΕ. */}
                <div className="flex items-center gap-4 mb-3 text-sm" data-yms-charterer-type>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="chartererIsCompany" aria-label="Φυσικό πρόσωπο"
                           checked={!newCharter.chartererIsCompany}
                           onChange={() => setNewCharter(prev => ({ ...prev, chartererIsCompany: false }))} />
                    <span>Φυσικό πρόσωπο</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="chartererIsCompany" aria-label="Εταιρεία"
                           checked={!!newCharter.chartererIsCompany}
                           onChange={() => setNewCharter(prev => ({
                             ...prev, chartererIsCompany: true, sameAsSkipper: false,
                             chartererCompanyName: prev.chartererCompanyName
                               || [prev.chartererFirstName, prev.chartererLastName].filter(Boolean).join(' ').trim(),
                           }))} />
                    <span>Εταιρεία</span>
                  </label>
                  {newCharter.chartererIsCompany && (
                    <em className="text-[11px] text-[#6b7280]">
                      Χωρίς φύλο/γέννηση/εθνικότητα/διαβατήριο — δεν μπαίνει στο crew list
                    </em>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {newCharter.chartererIsCompany ? (
                    <div data-yms-charterer-company>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Επωνυμία</label>
                      <input type="text" name="chartererCompanyName" aria-label="Επωνυμία"
                             value={newCharter.chartererCompanyName} onChange={handleFormChange} onKeyDown={handleFormKeyDown}
                             placeholder="π.χ. ACME MARITIME LTD"
                             className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                  ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">First Name</label>
                      <input type="text" name="chartererFirstName" aria-label="First Name" value={newCharter.chartererFirstName} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Όνομα" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Last Name</label>
                      <input type="text" name="chartererLastName" aria-label="Last Name" value={newCharter.chartererLastName} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Επώνυμο" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">Address</label>
                    <input type="text" name="chartererAddress" value={newCharter.chartererAddress} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Διεύθυνση" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Email</label>
                      <input type="email" name="chartererEmail" value={newCharter.chartererEmail} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="email@example.com" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Phone</label>
                      <input type="tel" name="chartererPhone" value={newCharter.chartererPhone} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="+30 69..." className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">
                        {newCharter.chartererIsCompany ? 'ΑΦΜ / VAT' : 'ΑΦΜ / Tax Number'}
                      </label>
                      <input type="text" name="chartererTaxNumber" aria-label="ΑΦΜ / VAT" value={newCharter.chartererTaxNumber} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="ΑΦΜ" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">ΔΟΥ / Tax Office</label>
                      <input type="text" name="chartererTaxOffice" value={newCharter.chartererTaxOffice} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="ΔΟΥ" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-400 focus:outline-none" />
                    </div>
                  </div>

                  {/* ΧΩΡΑ — η εταιρεία δεν έχει «εθνικότητα», έχει έδρα. */}
                  {newCharter.chartererIsCompany && (
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Χώρα</label>
                      <input type="text" name="chartererCountry" aria-label="Χώρα" value={newCharter.chartererCountry}
                             onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="π.χ. Ελλάδα / Germany"
                             className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                  )}

                  {/* ΑΤΟΜΙΚΑ ΣΤΟΙΧΕΙΑ — μόνο για φυσικό πρόσωπο. */}
                  {!newCharter.chartererIsCompany && (
                  <div data-yms-charterer-personal>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Date of Birth</label>
                      <input type="date" name="chartererDob" aria-label="Date of Birth" value={newCharter.chartererDob ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Gender</label>
                      <select name="chartererGender" aria-label="Gender" value={newCharter.chartererGender ?? ''} onChange={handleFormChange} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none">
                        <option value="">Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-[#374151] mb-2">Nationality</label>
                    <input type="text" name="chartererNationality" aria-label="Nationality" value={newCharter.chartererNationality ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Nationality" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                  </div>
                  {/* ID / Passport ναυλωτή — ίδιο μοντέλο με το Agreement (idType + idNumber) */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Τύπος εγγράφου</label>
                      <select name="chartererIdType" value={newCharter.chartererIdType ?? 'id'} onChange={handleFormChange} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none">
                        <option value="id">Ταυτότητα (ID)</option>
                        <option value="passport">Διαβατήριο (Passport)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">{newCharter.chartererIdType === 'passport' ? 'Passport No' : 'ID No'}</label>
                      <input type="text" name="chartererIdNumber" aria-label="ID/Passport" value={newCharter.chartererIdNumber ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder={newCharter.chartererIdType === 'passport' ? 'Αρ. Διαβατηρίου' : 'Αρ. Ταυτότητας'} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-green-500 focus:outline-none" />
                    </div>
                  </div>
                  </div>
                  )}
                  {/* Same as Skipper Checkbox — ΔΕΝ ισχύει για εταιρεία: νομικό
                      πρόσωπο δεν κυβερνά σκάφος. */}
                  {!newCharter.chartererIsCompany && (
                  <div className="mt-3 p-3 bg-gray-600 rounded-lg border-2 border-yellow-500">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCharter.sameAsSkipper}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          if (isChecked) {
                        // Ο ναυλωτής είναι η ΜΟΝΗ πηγή αλήθειας: αντιγράφουμε ΠΑΝΤΑ charterer→skipper.
                        // Το παλιό αντίστροφο κλαδί (skipper→charterer όταν charterer κενός) ήταν
                        // ο μηχανισμός που ΑΠΛΩΝΕ έναν broker πληκτρολογημένο στα skipper* πάνω
                        // στον ναυλωτή. Καταργείται — broker ΔΕΝ γίνεται ποτέ Πλοίαρχος/ναυλωτής.
                        setNewCharter(prev => ({ ...prev, sameAsSkipper: true, skipperFirstName: prev.chartererFirstName, skipperLastName: prev.chartererLastName, skipperAddress: prev.chartererAddress, skipperEmail: prev.chartererEmail, skipperPhone: prev.chartererPhone, skipperDob: prev.chartererDob, skipperGender: prev.chartererGender, skipperNationality: prev.chartererNationality, skipperIdType: prev.chartererIdType, skipperIdNumber: prev.chartererIdNumber }));
                          } else {
                            setNewCharter(prev => ({
                              ...prev,
                              sameAsSkipper: false,
                    chartererFirstName: '',
                    chartererLastName: '',
                    chartererAddress: '',
                    chartererEmail: '',
                    chartererPhone: ''
                            }));
                          }
                        }}
                        className="w-5 h-5 accent-yellow-500"
                      />
                      <span className="text-yellow-400 font-medium">Ο Charterer είναι και Skipper</span>
                    </label>
                    <p className="text-xs text-[#6b7280] mt-1 ml-8">Αν επιλεγεί, τα στοιχεία του Charterer αντιγράφονται στον Skipper</p>
                  </div>
                  )}
                </div>
              </div>

              {/* 🔥 FIX 9: Skipper Information Section */}
              <div ref={skipperInfoRef} className="bg-[#f9fafb] p-4 rounded-lg border border-[#d1d5db]">
                <h3 className="text-lg font-bold text-blue-400 mb-3">SKIPPER INFORMATION</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">First Name</label>
                      <input type="text" name="skipperFirstName" value={newCharter.skipperFirstName} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Όνομα" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Last Name</label>
                      <input type="text" name="skipperLastName" value={newCharter.skipperLastName} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Επώνυμο" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">Address</label>
                    <input type="text" name="skipperAddress" value={newCharter.skipperAddress} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Διεύθυνση" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Email</label>
                      <input type="email" name="skipperEmail" value={newCharter.skipperEmail} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="email@example.com" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Phone</label>
                      <input type="tel" name="skipperPhone" value={newCharter.skipperPhone} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="+30 69..." className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">ΑΦΜ / Tax Number</label>
                      <input type="text" name="skipperTaxNumber" value={newCharter.skipperTaxNumber} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="ΑΦΜ" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">ΔΟΥ / Tax Office</label>
                      <input type="text" name="skipperTaxOffice" value={newCharter.skipperTaxOffice} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="ΔΟΥ" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Date of Birth</label>
                      <input type="date" name="skipperDob" value={newCharter.skipperDob ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Gender</label>
                      <select name="skipperGender" value={newCharter.skipperGender ?? ''} onChange={handleFormChange} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none">
                        <option value="">Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">Nationality</label>
                    <input type="text" name="skipperNationality" value={newCharter.skipperNationality ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} placeholder="Nationality" className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                  {/* ID / Passport σκίπερ — ίδιο μοντέλο με το Agreement (idType + idNumber) */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">Τύπος εγγράφου</label>
                      <select name="skipperIdType" value={newCharter.skipperIdType ?? 'id'} onChange={handleFormChange} disabled={newCharter.sameAsSkipper} className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50">
                        <option value="id">Ταυτότητα (ID)</option>
                        <option value="passport">Διαβατήριο (Passport)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">{newCharter.skipperIdType === 'passport' ? 'Passport No' : 'ID No'}</label>
                      <input type="text" name="skipperIdNumber" value={newCharter.skipperIdNumber ?? ''} onChange={handleFormChange} onKeyDown={handleFormKeyDown} readOnly={newCharter.sameAsSkipper} placeholder={newCharter.skipperIdType === 'passport' ? 'Αρ. Διαβατηρίου' : 'Αρ. Ταυτότητας'} className={'w-full px-3 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none ' + (newCharter.sameAsSkipper ? 'opacity-50' : '')} />
                    </div>
                  </div>
                  {/* Broker / Agency Field */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-blue-400 mb-2">
                      Πράκτορας / Γραφείο (Broker / Agency)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="broker"
                        list="broker-list"
                        value={newCharter.broker}
                        onChange={handleFormChange}
                        onKeyDown={handleFormKeyDown}
                        placeholder="Επιλέξτε ή πληκτρολογήστε..."
                        className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-blue-500 focus:border-blue-400 focus:outline-none"
                      />
                      <datalist id="broker-list-skipper">
                        {BROKERS.map((broker, idx) => (
                          <option key={idx} value={broker} />
                        ))}
                      </datalist>
                    </div>
                    <p className="text-xs text-[#6b7280] mt-1">Προαιρετικό - Optional</p>
                  </div>
                  {/* Broker Email Field */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-blue-400 mb-2">
                      Email Πράκτορα (Broker Email)
                    </label>
                    <input
                      type="email"
                      name="brokerEmailContact"
                      value={newCharter.brokerEmailContact || ''}
                      onChange={handleFormChange}
                      onKeyDown={handleFormKeyDown}
                      placeholder="agency@example.com"
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-blue-500 focus:border-blue-400 focus:outline-none"
                    />
                    <p className="text-xs text-[#6b7280] mt-1">Προαιρετικό - Optional</p>
                  </div>
                </div>
              </div>

              {/* EXTRAS Section */}
              <div className="bg-[#f9fafb] p-4 rounded-lg border-2 border-orange-500 mb-4">
                <h3 className="text-lg font-bold text-orange-400 mb-3">EXTRAS:</h3>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {[
                    'Cabin Conversion', 'Crew Change', 'Cook/Hostess + provisions',
                    'Damage Waiver', 'Extra linen', 'Safety Net', 'SUP',
                    'Charter Pack 42', 'Charter Pack 43-45', 'Charter Pack 46-49',
                    'Charter Pack Cat 42', 'Charter Pack Cat 46', 'Charter Pack 50-55',
                    'Early Embarkation 14:00', 'Welcome Pack', 'Skipper + provisions',
                    'Outboard Engine', 'Pets Allowed 6kg', 'Transfer Service',
                    'Nespresso', 'One way Mykonos', 'One way Athens', 'Late check-in',
                    'Life Vest', 'Fishing Gear', 'Sea Scooter', 'Beach Towel', 'Wifi Router'
                  ].map((extra) => (
                    <label key={extra} className="flex items-center gap-2 text-sm text-[#374151] cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedExtras.includes(extra)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedExtras([...selectedExtras, extra]);
                          } else {
                            setSelectedExtras(selectedExtras.filter(x => x !== extra));
                          }
                        }}
                        className="w-4 h-4 accent-orange-500"
                      />
                      {extra}
                    </label>
                  ))}
                  {customExtras.map((extra) => (
                    <label key={extra} className="flex items-center gap-2 text-sm text-green-300 cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedExtras.includes(extra)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedExtras([...selectedExtras, extra]);
                          } else {
                            setSelectedExtras(selectedExtras.filter(x => x !== extra));
                          }
                        }}
                        className="w-4 h-4 accent-green-500"
                      />
                      {extra}
                      <button onClick={() => setCustomExtras(customExtras.filter(x => x !== extra))} className="text-red-400 ml-auto">✕</button>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    type="text"
                    placeholder="Προσθήκη νέου extra..."
                    className="flex-1 px-3 py-2 bg-gray-600 text-white rounded border border-gray-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        setCustomExtras([...customExtras, e.currentTarget.value.trim()]);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
              </div>

              {/* NOTES Section */}
              <div className="bg-[#f9fafb] p-4 rounded-lg border-2 border-blue-500">
                <h3 className="text-lg font-bold text-blue-400 mb-3">ΣΗΜΕΙΩΣΕΙΣ / NOTES:</h3>
                <textarea
                  value={charterNotes}
                  onChange={(e) => setCharterNotes(e.target.value)}
                  placeholder="Σημειώσεις για τη βάση..."
                  className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none min-h-[100px] resize-y"
                  rows={4}
                />
              </div>

              <div className="bg-[#f9fafb] p-4 rounded-lg border-2 border-[#1e40af]">
                <h3 className="text-lg font-bold text-[#1e40af] mb-3">FINANCIAL TERMS:</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-green-400 mb-2">Charter Fee (Income): *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="amount"
                      id="charter-fee-input"
                      value={newCharter.amount}
                      onChange={handleFormChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          document.getElementById('commission-percent-input')?.focus();
                        }
                      }}
                      placeholder="0.00"
                      className="w-full px-3 py-3 bg-gray-600 text-white text-lg font-bold rounded-lg border-2 border-green-500 focus:border-green-400 focus:outline-none"
                    />
                  </div>

                  {/* Show VAT in Charter Party toggle */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-blue-400 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCharter.showVat}
                        onChange={(e) => {
                          setNewCharter(prev => ({
                            ...prev,
                            showVat: e.target.checked
                          }));
                        }}
                        className="w-4 h-4 accent-blue-500"
                      />
                      Εμφάνιση ΦΠΑ στο Ναυλοσύμφωνο (Show VAT in Charter Party)
                    </label>
                    <p className="text-xs text-gray-400 ml-6">Αν επιλεγεί, εμφανίζεται ανάλυση ΦΠΑ 12% (ΚΑΘΑΡΟ + ΦΠΑ + ΣΥΝΟΛΟ)</p>
                  </div>

                  {/* Foreign Broker Section */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-orange-400 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCharter.hasForeignBroker}
                        onChange={(e) => {
                          setNewCharter(prev => ({
                            ...prev,
                            hasForeignBroker: e.target.checked,
                            // Default ΜΟΝΟ σε κενό πεδίο. Ό,τι έγραψε ο χρήστης (π.χ. 0 ή 5)
                            // δεν σβήνεται σιωπηλά επειδή τσέκαρε το checkbox.
                            commissionPercent: String(prev.commissionPercent ?? '').trim() === ''
                              ? (e.target.checked ? '10' : '20')
                              : prev.commissionPercent
                          }));
                        }}
                        className="w-4 h-4 accent-orange-500"
                      />
                      Με ξένο πράκτορα (Foreign Broker)
                    </label>

                    {newCharter.hasForeignBroker && (
                      <div className="ml-6 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-orange-300 mb-1">
                            Foreign Broker Commission (%)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            name="foreignBrokerPercent"
                            value={newCharter.foreignBrokerPercent}
                            onChange={handleFormChange}
                            placeholder="20"
                            className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                          />
                        </div>

                        {/* Extended foreign broker details (for ΑΑΔΕ broker party) */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-orange-300 mb-1">ΑΦΜ Πράκτορα (VAT)</label>
                            <input
                              type="text"
                              name="brokerVat"
                              value={newCharter.brokerVat}
                              onChange={handleFormChange}
                              placeholder="VAT / Tax ID"
                              className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-orange-300 mb-1">ΔΟΥ (προαιρετικό)</label>
                            <input
                              type="text"
                              name="brokerDoy"
                              value={newCharter.brokerDoy}
                              onChange={handleFormChange}
                              placeholder="Tax office"
                              className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-300 mb-1">Χώρα (Country)</label>
                          <input
                            type="text"
                            name="brokerCountry"
                            value={newCharter.brokerCountry}
                            onChange={handleFormChange}
                            placeholder="π.χ. Germany"
                            className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-300 mb-1">Διεύθυνση (Address)</label>
                          <input
                            type="text"
                            name="brokerAddress"
                            value={newCharter.brokerAddress}
                            onChange={handleFormChange}
                            placeholder="Street, number"
                            className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-300 mb-1">Τηλέφωνο (Phone)</label>
                          <input
                            type="tel"
                            name="brokerPhone"
                            value={newCharter.brokerPhone}
                            onChange={handleFormChange}
                            placeholder="+.."
                            className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg border-2 border-orange-500 focus:border-orange-400 focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-red-400 mb-2">
                      {newCharter.hasForeignBroker ? 'Tailwind Commission (%)' : 'Προμήθεια (%)'} *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="commissionPercent"
                      id="commission-percent-input"
                      value={newCharter.commissionPercent}
                      onChange={handleFormChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          document.getElementById('save-charter-btn')?.focus();
                        }
                      }}
                      placeholder="π.χ. 10"
                      className="w-full px-3 py-3 bg-gray-600 text-white text-lg font-bold rounded-lg border-2 border-red-500 focus:border-red-400 focus:outline-none"
                    />
                  </div>
                  <div className="pt-3 border-t-2 border-[#d1d5db] space-y-2">
                    {financials.hasForeignBroker && (
                      <div className="flex justify-between items-center">
                        <span className="text-orange-400 font-medium">Foreign Broker Commission:</span>
                        <span className="text-orange-400 font-bold text-lg">-{financials.foreignCommission.toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-red-400 font-medium">
                        {financials.hasForeignBroker ? 'Tailwind Commission:' : 'Commission (Expense):'}
                      </span>
                      <span className="text-red-400 font-bold text-lg">-{financials.greekCommission.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-red-400 font-medium">VAT on Commission (24%):</span>
                      <span className="text-red-400 font-bold text-lg">-{financials.vatOnGreekCommission.toFixed(2)}€</span>
                    </div>
                    <div className="pt-3 border-t-2 border-[#1e40af] flex justify-between items-center">
                      <span className="text-[#1e40af] font-bold text-xl">NET INCOME:</span>
                      <span className="text-[#1e40af] font-bold text-2xl">{financials.netIncome.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {!isFormValid && (
                <div className="mb-3 p-3 bg-yellow-900 border border-yellow-600 rounded-lg text-yellow-200 text-sm">
                  ⚠️ Συμπληρώστε όλα τα υποχρεωτικά πεδία (*):
                  {!newCharter.code && <div>• Κωδικός Ναύλου</div>}
                  {!newCharter.startDate && <div>• FROM (Ημερομηνία έναρξης)</div>}
                  {!newCharter.endDate && <div>• TO (Ημερομηνία λήξης)</div>}
                  {!(parseFloat(newCharter.amount) > 0) && <div>• Charter Fee (Ποσό {'>'} 0)</div>}
                </div>
              )}

              <button
                type="button"
                id="save-charter-btn"
                onClick={handleAddCharter}
                className={`w-full py-3 px-4 rounded-lg font-bold text-lg transition duration-200 ${isFormValid ? (editingCharter ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-[#1e40af] hover:bg-blue-800 text-white') : 'bg-gray-600 text-[#6b7280] cursor-not-allowed'}`}
              >
                {editingCharter ? '✏️ Ενημέρωση Ναύλου' : '💾 Αποθήκευση Ναύλου'} {!isFormValid && '(Συμπληρώστε τα υποχρεωτικά πεδία)'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* 🔥 Filter tabs for charter list */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setCharterFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            charterFilter === 'all'
              ? 'bg-[#1e40af] text-white'
              : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
          }`}
        >
          Όλα ({items.length})
        </button>
        <button
          onClick={() => setCharterFilter('page1')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            charterFilter === 'page1'
              ? 'bg-blue-600 text-white'
              : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
          }`}
        >
          📝 Από Check-in ({page1Count})
        </button>
        <button
          onClick={() => setCharterFilter('confirmed')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            charterFilter === 'confirmed'
              ? 'bg-green-600 text-white'
              : 'bg-[#f9fafb] text-[#374151] hover:bg-gray-100'
          }`}
        >
          ✅ Confirmed ({items.filter(c => c.status === 'Confirmed').length})
        </button>
      </div>

      {/* 🔥 CSS Animation for urgent payment attention */}
      <style>{`
        @keyframes blink-urgent {
          0%, 50% { background-color: rgba(239, 68, 68, 0.3); border-color: rgba(239, 68, 68, 0.8); }
          51%, 100% { background-color: rgba(31, 41, 55, 1); border-color: rgba(55, 65, 81, 1); }
        }
        .animate-blink-urgent {
          animation: blink-urgent 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="space-y-3">
        {filteredItems.map(charter => {
          const totalPaid = (charter.payments || []).reduce((sum, p) => sum + p.amount, 0);
          const paymentInfo = getPaymentStatusInfo(charter.paymentStatus);
          const urgentPayment = needsUrgentPaymentAttention(charter);

          // 🎨 Date-based bg — ΜΟΝΟ ημερομηνίες vs σήμερα (Europe/Athens). ΟΧΙ status/check-in flags.
          const startYmd = String(charter.startDate || '').slice(0, 10);
          const endYmd   = String(charter.endDate   || '').slice(0, 10);
          const athensToday = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens' }).format(new Date());
          const startsToday = startYmd !== '' && startYmd === athensToday;   // ξεκινά σήμερα
          const hasEnded    = endYmd   !== '' && endYmd   <  athensToday;    // έληξε (YYYY-MM-DD string compare)
          const dateGreen   = startsToday || hasEnded;
          const dateBg = startsToday
            ? 'bg-emerald-500 hover:bg-emerald-600'   // ΕΝΤΟΝΟ (start σήμερα προηγείται)
            : hasEnded
            ? 'bg-emerald-50 hover:bg-emerald-100'    // ΑΧΝΟ
            : 'bg-white hover:bg-[#f9fafb]';          // αλλιώς όπως τώρα

          return (
            <button
              key={charter.id}
              onClick={() => handleSelectCharter(charter)}
              className={`w-full text-left ${dateBg} p-4 rounded-lg transition duration-200 border border-[#d1d5db] hover:border-[#1e40af] relative ${urgentPayment && !dateGreen ? 'animate-blink-urgent' : ''}`}
            >
              {/* 🔥 Red light - μόνο για ΑΝΕΞΟΦΛΗΤΟ/ΜΕΡΙΚΗ ΠΛΗΡΩΜΗ */}
              {paymentInfo.showLight && (
                <div className={`absolute top-2 right-2 w-4 h-4 bg-red-500 rounded-full shadow-lg shadow-red-500/50 ${paymentInfo.lightBlink ? 'animate-pulse' : ''}`}></div>
              )}
              {/* 🔥 Urgent payment badge */}
              {urgentPayment && (
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded animate-pulse">
                  ⚠️ ΠΛΗΡΩΜΗ
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-[#1e40af]">{charter.code}</h3>
                  <p className="text-sm text-[#6b7280]">
                    {charter.startDate ? new Date(charter.startDate).toLocaleDateString('en-GB') : ''} - {charter.endDate ? new Date(charter.endDate).toLocaleDateString('en-GB') : ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                    Status: <span className={
                      charter.status === 'Confirmed' ? 'text-green-400' :
                      charter.status === 'Draft' ? 'text-blue-400' :
                      charter.status === 'Pending Approval' ? 'text-orange-400' :
                      (charter.status === 'Rejected' || charter.status === 'Cancelled' || charter.status === 'Canceled') ? 'text-red-400' :
                      'text-yellow-400'
                    }>{charter.status}</span>
                    {/* Badge if financial details are missing */}
                    {(!charter.amount || charter.amount === 0) && charter.status !== 'Cancelled' && charter.status !== 'Canceled' && (
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded border border-orange-500/50">
                        💰 Χρειάζεται Ποσό
                      </span>
                    )}
                    {/* 🔥 Badge for charters from Page 1 Check-in */}
                    {(charter.source === 'page1' || charter.status === 'Draft') && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded border border-blue-500/50">
                        📝 Από Check-in
                      </span>
                    )}
                  </p>
                  {/* 🔥 Payment Status - ΚΕΙΜΕΝΟ */}
                  <p className={`text-xs mt-1 font-semibold ${paymentInfo.color}`}>
                    {paymentInfo.text}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {canViewFinancials ? (
                    <span className="text-xl font-bold text-green-400">{charter.amount?.toFixed(2)}€</span>
                  ) : (
                    <span className="text-xl font-bold text-gray-500">🔒</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedCharter && (
        <CharterDetailModal
          key={selectedCharter.id || selectedCharter.code}
          charter={selectedCharter} boat={boat} canViewFinancials={canViewFinancials} canEditCharters={canEditCharters}
          canAcceptCharter={canAcceptCharter} isOwnerUser={isOwnerUser} onClose={() => setSelectedCharter(null)}
          onDelete={handleDeleteCharter} onUpdateStatus={handleUpdateStatus} onUpdatePayments={handleUpdatePayments} showMessage={showMessage}
          onEdit={handleEditCharter}
        />
      )}
    </div>
  );
}

function CharterDetailModal({ charter, boat, canViewFinancials, canEditCharters, canAcceptCharter, isOwnerUser, onClose, onDelete, onUpdateStatus, onUpdatePayments, showMessage, onEdit }) {
  const modalNavigate = useNavigate();

  // 📄 Άνοιγμα Charter Agreement με ΤΟΝ ΤΡΕΧΟΝΤΑ ναύλο ήδη φορτωμένο.
  // Ίδιο booking_number με Download PDF / ΑΑΔΕ / Εισαγωγή Crew.
  // Η CharterAgreementPage διαβάζει: sessionStorage 'charterAgreementAdminOverride' || location.state.bookingCode
  // → καθαρίζουμε τυχόν stale override ώστε να μην «κλέψει» άλλον ναύλο, και περνάμε state.
  const handleOpenCharterAgreement = () => {
    const bn = charter.code || charter.charterCode || charter.bookingNumber || '';
    if (!bn) { showMessage('❌ Ο ναύλος δεν έχει αριθμό booking.', 'error'); return; }
    try { sessionStorage.removeItem('charterAgreementAdminOverride'); } catch {}
    onClose && onClose();
    modalNavigate('/charter-agreement', { state: { bookingCode: bn } });
  };

  // 🔥 DEBUG: Log permissions for troubleshooting
  console.log('🔍 CharterDetailModal - Permissions:', {
    charterCode: charter.code,
    charterStatus: charter.status,
    isOwnerUser,
    canAcceptCharter,
    canEditCharters,
    showOwnerButtons: canAcceptCharter && charter.status === 'Pending Final Confirmation'
  });

  const totalExpense = (charter.commission || 0) + (charter.vat_on_commission || 0);
  const netIncome = (charter.amount || 0) - totalExpense;

  // 🔥 FIX: Use useRef to capture initial payments value ONCE - prevents reset on re-render
  const initialPaymentsRef = useRef<{charterId: string | number, payments: Array<{date: string, amount: number}>}>({
    charterId: charter.id,
    payments: charter.payments || []
  });
  const [payments, setPayments] = useState<Array<{date: string, amount: number}>>(initialPaymentsRef.current.payments);
  const [newPayDate, setNewPayDate] = useState('');
  const [newPayAmount, setNewPayAmount] = useState('');
  // ΕΠΑΝΑΦΟΡΑ check-in/out (admin): ένα κουμπί → παράθυρο (μηδένισε / επαναφορά από backup)
  const [ciResetOpen, setCiResetOpen] = useState(false);
  const [ciBackups, setCiBackups] = useState<any[]>([]);
  const [ciBusy, setCiBusy] = useState(false);
  const ciCode = () => (charter as any).code || (charter as any).charterCode || (charter as any).bookingNumber || (charter as any).id || '';
  const ciHdr = () => ({ 'Content-Type': 'application/json', 'X-Employee-Code': (authService.getCurrentUser() as any)?.code || '' });
  const ciLoadBackups = async () => { try { const r = await fetch('/api/reset-checkin.php?action=list&booking_number=' + encodeURIComponent(ciCode()), { headers: ciHdr() }); const d = await r.json(); setCiBackups(d?.backups || []); } catch { setCiBackups([]); } };
  const ciReset = async (mode: 'in'|'out') => { if (!window.confirm(`Να ΜΗΔΕΝΙΣΤΕΙ το ${mode==='out'?'check-out':'check-in'} του ${ciCode()} από την αρχή; Θα σβηστούν ώρες, υπογραφές, φωτο, items. (Κρατιέται backup.)`)) return; setCiBusy(true); try { const r = await fetch('/api/reset-checkin.php', { method:'POST', headers: ciHdr(), body: JSON.stringify({ action:'reset', booking_number: ciCode(), mode }) }); const d = await r.json(); if (showMessage) showMessage(d?.success?`✓ Μηδενίστηκε το ${mode==='out'?'check-out':'check-in'}`:'✗ '+(d?.error||'Απέτυχε'), d?.success?'success':'error'); await ciLoadBackups(); } catch(e){ alert('✗ '+((e as any)?.message||'Απέτυχε')); } finally { setCiBusy(false); } };
  const ciRestore = async (bk: any) => { if (!window.confirm(`Επαναφορά ${bk.mode==='out'?'check-out':'check-in'} από backup ${new Date(bk.created_at).toLocaleString('el-GR')}; Θα γυρίσουν ώρες/υπογραφές/φωτο/items.`)) return; setCiBusy(true); try { const r = await fetch('/api/reset-checkin.php', { method:'POST', headers: ciHdr(), body: JSON.stringify({ action:'restore', booking_number: ciCode(), backup_id: bk.id }) }); const d = await r.json(); if (showMessage) showMessage(d?.success?'✓ Επαναφέρθηκε από backup':'✗ '+(d?.error||'Απέτυχε'), d?.success?'success':'error'); } catch(e){ alert('✗ '+((e as any)?.message||'Απέτυχε')); } finally { setCiBusy(false); } };
  const [isProcessing, setIsProcessing] = useState(false);
  // 🔥 FIX 28: Inline status messages inside modal
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'loading' | 'success' | 'error' } | null>(null);

  // 👥 Crew editor (inline edit/delete in charter detail)
  const [crewEdit, setCrewEdit] = useState<any[]>(() => Array.isArray((charter as any)?.crewMembers) ? [...(charter as any).crewMembers] : []);
  const [crewSaving, setCrewSaving] = useState(false);
  const [editingSkipper, setEditingSkipper] = useState(false);
  const [skipperRowEdit, setSkipperRowEdit] = useState<any>({});
  const [skipperSaving, setSkipperSaving] = useState(false);

  // 📥 XLSX/PDF Crew Import (Booking Manager)
  const [xlsxModalOpen, setXlsxModalOpen] = useState(false);
  const [xlsxParsedCrew, setXlsxParsedCrew] = useState<any[]>([]);
  const [xlsxBookingNumber, setXlsxBookingNumber] = useState('');
  const [xlsxImporting, setXlsxImporting] = useState(false);
  const [xlsxParsing, setXlsxParsing] = useState(false);
  const [xlsxError, setXlsxError] = useState('');
  /* Άτομα που γύρισε το AI αλλά ΔΕΝ βρέθηκαν αυτούσια στο αρχείο (όνομα ή
     διαβατήριο). ΔΕΝ συμπληρώνονται αυτόματα — ο χρήστης τα βλέπει και τα
     πληκτρολογεί μόνος του αν ισχύουν. Ένα λάθος διαβατήριο σε crew list
     φτάνει στη λιμενική αρχή. */
  const [crewUnverified, setCrewUnverified] = useState<any[]>([]);
  const [xlsxSource, setXlsxSource] = useState<'excel' | 'excel-ai' | 'pdf' | 'word' | ''>('');
  // 6.480 — τι διάβασε/δεν διάβασε ο parser + το φύλλο σε κείμενο για το AI retry
  const [xlsxNotes, setXlsxNotes] = useState<any>(null);
  const [xlsxWorkbookText, setXlsxWorkbookText] = useState<string>('');
  const xlsxFileInputRef = useRef<HTMLInputElement>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const wordFileInputRef = useRef<HTMLInputElement>(null);

  // 📋 ΑΑΔΕ pre-export preview modal
  const [showAadePreview, setShowAadePreview] = useState(false);
  const [includeTailwind, setIncludeTailwind] = useState(false);
  const [tailwindInfo, setTailwindInfo] = useState<any>(null);
  const [aadeExporting, setAadeExporting] = useState(false);
  // Foreign broker controls (own checkbox + optional inline mini-form)
  const emptyFbForm = { name: '', vat: '', doy: '', country: '', address: '', email: '', phone: '' };
  const [includeForeignBroker, setIncludeForeignBroker] = useState(false);
  const [fbForm, setFbForm] = useState({ ...emptyFbForm });
  const [fbError, setFbError] = useState('');
  const setFbField = (k: string, v: string) => setFbForm(prev => ({ ...prev, [k]: v }));
  useEffect(() => {
    if (!showAadePreview || tailwindInfo) return;
    fetch('/api/company-info.php')
      .then(r => r.json())
      .then(d => { if (d && d.success) setTailwindInfo(d.company || null); })
      .catch(() => {});
  }, [showAadePreview, tailwindInfo]);

  // 📄 ΑΑΔΕ PDF (terms) upload modal
  const [showAadePdfModal, setShowAadePdfModal] = useState(false);
  const [aadePdfFile, setAadePdfFile] = useState<File | null>(null);
  const [aadePdfBusy, setAadePdfBusy] = useState(false);

  const formatXlsxDate = (v: any): string => {
    if (v == null || v === '') return '';
    if (typeof v === 'string') {
      const s = v.trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
      const m = s.match(/^(\d{1,2})[\/\.\-](\d{1,2})[\/\.\-](\d{2,4})$/);
      if (m) {
        let d = m[1], mo = m[2], y = m[3];
        if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
        return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      return s;
    }
    if (typeof v === 'number') {
      const date = new Date(Math.round((v - 25569) * 86400 * 1000));
      if (!isNaN(date.getTime())) return date.toISOString().substring(0, 10);
    }
    if (v instanceof Date) return v.toISOString().substring(0, 10);
    return String(v);
  };

  const handleXlsxOpen = () => {
    setXlsxBookingNumber(charter.code || charter.charterCode || charter.bookingNumber || '');
    setXlsxParsedCrew([]);
    setXlsxError('');
    setXlsxSource('');
    setXlsxModalOpen(true);
  };

  /* ═══ ΕΙΣΑΓΩΓΗ ΠΛΗΡΩΜΑΤΟΣ ΑΠΟ EXCEL (6.480) ═══
     Ο παλιός inline parser απέτυχε στη φόρμα GoFunSailing: σάρωνε μόνο 15
     γραμμές (header στη 19), τα headers ήταν exact-match λίστα («Passport No.
     / Valid till» δεν ταίριαζε) και οι ημερομηνίες έμεναν Excel serials.
     Μεταφέρθηκε σε src/utils/crewExcelParser.ts με fuzzy matching. */
  const handleXlsxFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setXlsxError('');
    setXlsxNotes(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const XLSX: any = await import('xlsx');
      const { parseCrewWorkbook, sheetToText } = await import('./utils/crewExcelParser');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: false });
      setXlsxWorkbookText(sheetToText(wb, XLSX));          // κρατιέται για «Ξαναδοκίμασε με AI»

      const res = parseCrewWorkbook(wb, XLSX);

      const ORDER: Record<string, number> = { skipper: 0, 'co-skipper': 1, crew: 2, passenger: 2 };
      const parsed = res.crew.map((c: any) => ({
        name: c.name, firstName: c.firstName, lastName: c.lastName,
        passport: c.passport, dateOfBirth: c.dateOfBirth,
        nationality: c.nationality, gender: c.gender, role: c.role,
        email: '', phone: '', _row: c._row,
      }));
      const sorted = parsed
        .map((m: any, i: number) => [m, i] as [any, number])
        .sort((a, b) => (ORDER[a[0].role] ?? 3) - (ORDER[b[0].role] ?? 3) || a[1] - b[1])
        .map(x => x[0]);

      setXlsxNotes({
        sheetName: res.sheetName, headerRow: res.headerRow,
        mappedColumns: res.mappedColumns, skipped: res.skipped,
        warnings: res.warnings, charterer: res.charterer,
        sameAsSkipper: res.sameAsSkipper,
      });

      if (sorted.length === 0) {
        // Ο parser δεν βρήκε άτομα — προτείνεται το AI (crew-extract.php).
        setXlsxError((res.warnings[0] || 'Δεν βρέθηκαν εγγραφές πληρώματος.')
                     + ' Δοκίμασε «Ξαναδοκίμασε με AI».');
        setXlsxParsedCrew([]);
        setXlsxSource('excel');
        return;
      }
      setXlsxParsedCrew(sorted);
      setXlsxSource('excel');
    } catch (err: any) {
      console.error('XLSX parse error:', err);
      setXlsxError('Σφάλμα ανάγνωσης αρχείου: ' + (err?.message || err));
    } finally {
      if (xlsxFileInputRef.current) xlsxFileInputRef.current.value = '';
    }
  };

  /* «Ξαναδοκίμασε με AI»: στέλνει το φύλλο ΩΣ ΚΕΙΜΕΝΟ στο crew-extract.php
     (τύπος 'xlsx_text'). Οι serials είναι ήδη DD/MM/YYYY από το sheetToText. */
  const retryXlsxWithAI = async () => {
    if (!xlsxWorkbookText) { setXlsxError('Δεν υπάρχει αρχείο Excel φορτωμένο.'); return; }
    setXlsxError('');
    setXlsxParsing(true);
    try {
      const resp = await fetch(`${API_BASE}/crew-extract.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Employee-Code': authService.getCurrentUser()?.code || '',
        },
        body: JSON.stringify({ type: 'xlsx_text', sheet_text: xlsxWorkbookText }),
      });
      const data = await resp.json();
      if (!data?.success) { setXlsxError(data?.error || 'Η AI εξαγωγή απέτυχε.'); return; }
      setCrewUnverified(Array.isArray(data.unverified) ? data.unverified : []);
      const list = Array.isArray(data.crew) ? data.crew : [];
      const ORDER: Record<string, number> = { skipper: 0, 'co-skipper': 1, crew: 2, passenger: 2 };
      // Το AI γυρίζει ημερομηνίες ISO (1982-02-18) και εθνικότητα ως έχει
      // ('Deutsch'). Περνάμε από τους ΙΔΙΟΥΣ μετασχηματιστές με τον parser,
      // ώστε Excel και Excel+AI να δίνουν ΤΑΥΤΟΣΗΜΟ σχήμα.
      const { excelDate, normalizeNationality } = await import('./utils/crewExcelParser');
      const mapped = list.map((m: any) => ({
        name: m.name || `${m.first_name || m.firstName || ''} ${m.last_name || m.lastName || ''}`.trim(),
        firstName: m.first_name || m.firstName || '',
        lastName:  m.last_name  || m.lastName  || '',
        passport:  m.passport || '',
        dateOfBirth: excelDate(m.dob || m.dateOfBirth || '', null),
        nationality: normalizeNationality(m.nationality || ''),
        gender: m.gender || '',
        role: m.role || 'passenger',
        email: m.email || '', phone: m.phone || '',
      }));
      const sorted = mapped
        .map((m: any, i: number) => [m, i] as [any, number])
        .sort((a, b) => (ORDER[a[0].role] ?? 3) - (ORDER[b[0].role] ?? 3) || a[1] - b[1])
        .map(x => x[0]);
      if (!sorted.length) { setXlsxError('Η AI δεν βρήκε άτομα στο φύλλο.'); return; }
      setXlsxParsedCrew(sorted);
      setXlsxSource('excel-ai');
    } catch (err: any) {
      setXlsxError('Σφάλμα AI: ' + (err?.message || err));
    } finally {
      setXlsxParsing(false);
    }
  };

  const handleWordFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setXlsxError('');
    const file = e.target.files?.[0];
    if (!file) return;
    setXlsxParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binStr = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.byteLength; i += CHUNK) {
        binStr += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
      }
      const b64 = btoa(binStr);

      // === Anthropic Vision extraction (word-extract.php) — server converts .docx -> PDF, then Vision. ===
      const empCode = localStorage.getItem('employeeCode') || '';
      const vr = await fetch('/api/word-extract.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': empCode },
        body: JSON.stringify({ docx_base64: b64, booking_number: xlsxBookingNumber }),
      });
      const vdata = await vr.json();
      if (vr.ok && vdata.success && Array.isArray(vdata.crew) && vdata.crew.length > 0) {
        const allowedRoles = ['skipper', 'co-skipper', 'crew', 'passenger'];
        const visionCrew = vdata.crew.map((m: any) => ({
          name: String(m.name || '').trim() || `${String(m.firstName||'').trim()} ${String(m.lastName||'').trim()}`.trim(),
          firstName: String(m.firstName || '').trim(),
          lastName: String(m.lastName || '').trim(),
          passport: String(m.passport || '').trim(),
          dateOfBirth: String(m.dateOfBirth || '').trim(),
          nationality: String(m.nationality || '').trim(),
          gender: m.gender === 'Male' ? 'Male' : m.gender === 'Female' ? 'Female' : '',
          role: allowedRoles.includes(String(m.role || '').toLowerCase()) ? String(m.role).toLowerCase() : 'passenger',
          email: String(m.email || '').trim(),
          phone: String(m.phone || '').trim(),
        })).filter((m: any) => m.name || m.passport);
        if (visionCrew.length > 0) {
          const ORDER: Record<string, number> = { skipper: 0, 'co-skipper': 1, crew: 2, passenger: 2 };
          const sortedCrew = visionCrew.map((m: any, i: number) => [m, i] as [any, number]).sort((a, b) => (ORDER[a[0].role] ?? 3) - (ORDER[b[0].role] ?? 3) || a[1] - b[1]).map(x => x[0]);
          setXlsxParsedCrew(sortedCrew);
          setXlsxSource('word');
          return;
        }
      }
      setXlsxError(vdata?.error ? ('Word: ' + vdata.error) : 'Δεν βρέθηκαν στοιχεία πληρώματος στο Word.');
    } catch (err: any) {
      console.error('Word parse error:', err);
      setXlsxError('Σφάλμα ανάγνωσης Word: ' + (err?.message || err));
    } finally {
      setXlsxParsing(false);
      if (wordFileInputRef.current) wordFileInputRef.current.value = '';
    }
  };

  const handlePdfFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setXlsxError('');
    const file = e.target.files?.[0];
    if (!file) return;
    setXlsxParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binStr = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.byteLength; i += CHUNK) {
        binStr += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
      }
      const b64 = btoa(binStr);

      // === PRIMARY: Anthropic Vision extraction (crew-extract.php) — works with ANY company
      // layout (Sea Time, Booking Manager, …), not just "Full Name + Passport". Falls back to
      // the legacy pdftotext column parsing below if it fails / returns nothing. ===
      try {
        const empCode = localStorage.getItem('employeeCode') || '';
        const vr = await fetch('/api/crew-extract.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Employee-Code': empCode },
          body: JSON.stringify({ pdf_base64: b64, booking_number: xlsxBookingNumber }),
        });
        const vdata = await vr.json();
        setCrewUnverified(Array.isArray(vdata?.unverified) ? vdata.unverified : []);
        if (vr.ok && vdata.success && Array.isArray(vdata.crew) && vdata.crew.length > 0) {
          const allowedRoles = ['skipper', 'co-skipper', 'crew', 'passenger'];
          const visionCrew = vdata.crew.map((m: any) => ({
            name: String(m.name || '').trim() || `${String(m.firstName||'').trim()} ${String(m.lastName||'').trim()}`.trim(),
            firstName: String(m.firstName || '').trim(),
            lastName: String(m.lastName || '').trim(),
            passport: String(m.passport || '').trim(),
            dateOfBirth: String(m.dateOfBirth || '').trim(),
            nationality: String(m.nationality || '').trim(),
            gender: m.gender === 'Male' ? 'Male' : m.gender === 'Female' ? 'Female' : '',
            role: allowedRoles.includes(String(m.role || '').toLowerCase()) ? String(m.role).toLowerCase() : 'passenger',
            email: String(m.email || '').trim(),
            phone: String(m.phone || '').trim(),
          })).filter((m: any) => m.name || m.passport);
          if (visionCrew.length > 0) {
            const ORDER: Record<string, number> = { skipper: 0, 'co-skipper': 1, crew: 2, passenger: 2 };
            const sortedCrew = visionCrew.map((m: any, i: number) => [m, i] as [any, number]).sort((a, b) => (ORDER[a[0].role] ?? 3) - (ORDER[b[0].role] ?? 3) || a[1] - b[1]).map(x => x[0]);
            setXlsxParsedCrew(sortedCrew);
            setXlsxSource('pdf');
            return; // success → skip legacy pdftotext fallback (finally still resets parsing/input)
          }
        }
        console.warn('Vision crew-extract returned no crew, falling back to pdftotext:', vdata?.error);
      } catch (vErr) {
        console.warn('Vision crew-extract failed, falling back to pdftotext:', vErr);
      }

      // === FALLBACK: legacy pdftotext column parsing (kept intact) ===
      const r = await fetch('/api/pdf-extract.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_base64: b64 }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || ('HTTP ' + r.status));
      const lines: string[] = Array.isArray(data.raw_lines) ? data.raw_lines : [];
      if (lines.length === 0) { setXlsxError('Άδειο κείμενο από PDF.'); return; }

      // find header line
      // Δέχεται και "Full Name" και ξεχωριστά "Name"/"Surname", και "Passp.nr."/"Passport"/"ID".
      const hasIdCol = (s: string) => /passport|passp\.?\s*nr|passp\.?\s*no|\bid\s*\/|\bid\s*(no|nr|number)\b/.test(s);
      const hasNameCol = (s: string) => /full name|surname|last name|first name|\bname\b/.test(s);
      let headerIdx = -1;
      for (let i = 0; i < lines.length; i++) {
        const lower = lines[i].toLowerCase();
        if (hasNameCol(lower) && hasIdCol(lower)) { headerIdx = i; break; }
      }
      if (headerIdx < 0) {
        setXlsxError('Δεν βρέθηκε header (Name/Surname/Full Name + Passport) στο PDF. Method: ' + (data.method || '?') + ', γραμμές: ' + lines.length);
        return;
      }
      const header = lines[headerIdx];
      const lowerHeader = header.toLowerCase();
      const findPos = (needle: string) => lowerHeader.indexOf(needle.toLowerCase());
      const findRe = (re: RegExp) => { const m = lowerHeader.match(re); return m && m.index != null ? m.index : -1; };
      const passPos = findPos('id / passport') >= 0 ? findPos('id / passport')
                    : findPos('id/passport') >= 0 ? findPos('id/passport')
                    : findPos('passport') >= 0 ? findPos('passport')
                    : findRe(/passp\.?\s*(nr|no)\.?/) >= 0 ? findRe(/passp\.?\s*(nr|no)\.?/)
                    : findRe(/\bid\b/);
      const fullPos = findPos('full name');
      const firstPos = fullPos >= 0 ? -1
                     : findPos('first name') >= 0 ? findPos('first name') : findRe(/\bname\b/);
      const lastPos  = fullPos >= 0 ? -1
                     : findPos('surname') >= 0 ? findPos('surname') : findPos('last name');
      const cols = [
        { key: 'birth',    pos: findPos('birth date') >= 0 ? findPos('birth date') : findPos('date of birth') },
        { key: 'name',     pos: fullPos },
        { key: 'first',    pos: firstPos },
        { key: 'last',     pos: lastPos },
        { key: 'address',  pos: findPos('address') },
        { key: 'passport', pos: passPos },
        { key: 'nat',      pos: findPos('nationality') },
        { key: 'cob',      pos: findPos('country of birth') },
        { key: 'gender',   pos: findPos('gender') },
        { key: 'arrival',  pos: findPos('estimated arrival') },
        { key: 'position', pos: findPos('position') },
      ].filter(c => c.pos >= 0).sort((a, b) => a.pos - b.pos);
      if (cols.length < 4) { setXlsxError('Δεν αναγνωρίστηκαν αρκετές στήλες στο header (' + cols.length + ').'); return; }

      const sliceCell = (line: string, start: number, end: number) => {
        if (start >= line.length) return '';
        const s = end === Infinity || end > line.length ? line.substring(start) : line.substring(start, end);
        return s.replace(/\s+/g, ' ').trim();
      };

      const parsed: any[] = [];
      for (let i = headerIdx + 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line || !line.trim()) continue;
        // skip another header repetition (multi-page tables)
        if (hasNameCol(line.toLowerCase()) && hasIdCol(line.toLowerCase())) continue;
        const cells: Record<string, string> = {};
        for (let c = 0; c < cols.length; c++) {
          const start = cols[c].pos;
          const end = c + 1 < cols.length ? cols[c + 1].pos : Infinity;
          cells[cols[c].key] = sliceCell(line, start, end);
        }
        const firstName = cells['first'] || '';
        const lastName  = cells['last'] || '';
        const name = cells['name'] || `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim();
        const passport = cells['passport'] || '';
        if (!name && !passport) continue;
        if (/^(total|σύνολο|page\s+\d|σελίδα\s+\d)/i.test(name)) continue;
        const genderRaw = (cells['gender'] || '').toLowerCase();
        const gender = genderRaw.startsWith('female') ? 'Female'
                     : genderRaw.startsWith('male') ? 'Male' : '';
        const posRaw = (cells['position'] || '').toUpperCase();
        let role = 'passenger';
        if (posRaw === 'SKIPPER') role = 'skipper';
        else if (posRaw === 'CO-SKIPPER' || posRaw === 'CO SKIPPER') role = 'co-skipper';
        else if (posRaw === 'CREW') role = 'crew';
        parsed.push({
          name,
          firstName,
          lastName,
          passport,
          dateOfBirth: formatXlsxDate(cells['birth'] || ''),
          nationality: cells['nat'] || cells['cob'] || '',
          gender,
          role,
          email: '',
          phone: '',
        });
      }
      if (parsed.length === 0) { setXlsxError('Δεν βρέθηκαν εγγραφές πληρώματος κάτω από το header (PDF). Method: ' + (data.method || '?')); return; }
      const ORDER: Record<string, number> = { skipper: 0, 'co-skipper': 1, crew: 2, passenger: 2 };
      const sortedParsed = parsed.map((m: any, i: number) => [m, i] as [any, number]).sort((a, b) => (ORDER[a[0].role] ?? 3) - (ORDER[b[0].role] ?? 3) || a[1] - b[1]).map(x => x[0]);
      setXlsxParsedCrew(sortedParsed);
      setXlsxSource('pdf');
    } catch (err: any) {
      console.error('PDF parse error:', err);
      setXlsxError('Σφάλμα ανάγνωσης PDF: ' + (err?.message || err));
    } finally {
      setXlsxParsing(false);
      if (pdfFileInputRef.current) pdfFileInputRef.current.value = '';
    }
  };

  // 👥 Crew editor handlers
  const updateCrewField = (idx: number, field: string, value: any) => {
    setCrewEdit(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };
  const removeCrewRow = (idx: number) => {
    setCrewEdit(prev => prev.filter((_, i) => i !== idx));
  };
  const handleSaveCrew = async () => {
    const bn = (charter as any).code || (charter as any).charterCode || (charter as any).bookingNumber || '';
    if (!bn) { showMessage('Λείπει booking number', 'error'); return; }
    setCrewSaving(true);
    try {
      // unifyCrew: όνομα/ID→λατινικά (ΑΑΔΕ/DOCX) + merge ίδιου ID → ΕΝΑ record ανά άτομο.
      const unified = unifyCrew(crewEdit);
      const r = await fetch('/api/update-charter-crew.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_number: bn, crewMembers: unified }),
      });
      const result = await r.json();
      if (!result.success) throw new Error(result.error || 'Save failed');
      // Reflect change on the in-memory charter so subsequent generators (Crew List/AADE) see it.
      (charter as any).crewMembers = unified;
      setCrewEdit(unified); // UI reflektάρει τα λατινικά/ενοποιημένα
      showMessage(`✅ Αποθηκεύτηκε (${unified.length} μέλη)`, 'success');
    } catch (err: any) {
      console.error('Crew save error:', err);
      showMessage('❌ Σφάλμα: ' + (err?.message || err), 'error');
    } finally {
      setCrewSaving(false);
    }
  };

  // Αποθήκευση επεξεργασμένου Πλοίαρχου (derived row). Γράφει στη ΣΩΣΤΗ πηγή κατά skipperMode:
  // 'me' → charterer πεδία (chartererData) · 'other' → skipper πεδία (chartererData.skipper*).
  // Γράφει στο charter_archives (πηγή AADE/agreement) + flat sync στο bookings.
  const handleSaveSkipperRow = async () => {
    const bn = (charter as any).code || (charter as any).charterCode || (charter as any).bookingNumber || '';
    if (!bn) { showMessage('Λείπει booking number', 'error'); return; }
    setSkipperSaving(true);
    try {
      const c: any = charter || {};
      const mode = String(c.skipperMode || '').toLowerCase();
      const isMe = mode === 'me' || c.isAlsoSkipper === true || c.isAlsoSkipper === 'true' || c.sameAsSkipper === true || c.sameAsSkipper === 'true';
      const e = skipperRowEdit || {};
      // Ξεκίνα από το υπάρχον archive chartererData ώστε να ΜΗΝ χαθεί τίποτα (charter-archive replace).
      const baseCd: any = { ...(c.chartererData || {}) };
      if (isMe) {
        baseCd.idNumber = e.passport ?? baseCd.idNumber ?? '';
        baseCd.dob = e.dateOfBirth ?? baseCd.dob ?? '';
        baseCd.gender = e.gender ?? baseCd.gender ?? '';
        baseCd.nationality = e.nationality ?? baseCd.nationality ?? '';
        // Καθρέφτισε και στα skipper* (BUG C: 'me' κληρονομεί) για συνέπεια στο export.
        baseCd.skipperDob = baseCd.dob;
        baseCd.skipperGen = baseCd.gender;
        baseCd.skipperNationality = baseCd.nationality;
      } else {
        baseCd.skipperIdNumber = e.passport ?? baseCd.skipperIdNumber ?? '';
        baseCd.skipperDob = e.dateOfBirth ?? baseCd.skipperDob ?? '';
        baseCd.skipperGen = e.gender ?? baseCd.skipperGen ?? '';
        baseCd.skipperNationality = e.nationality ?? baseCd.skipperNationality ?? '';
      }
      // ID→ομόγλυφα (ΑΡ→AP) στην πηγή, idempotent.
      if (isMe) baseCd.idNumber = latinId(baseCd.idNumber || '');
      else baseCd.skipperIdNumber = latinId(baseCd.skipperIdNumber || '');
      // UPSERT στη γραμμή crewMembers με κλειδί ID (sameIdentity) → το Fleet edit καταλήγει στο ΙΔΙΟ
      // crew record (όχι δεύτερο). v6.469: αν ΔΕΝ υπάρχει γραμμή skipper, ΤΩΡΑ τη ΦΤΙΑΧΝΟΥΜΕ (πριν:
      // no-op → ο σκίπερ έμενε μόνο στο chartererData και δεν φαινόταν στο Crew List, π.χ. ναύλος 726).
      // ΔΕΝ διπλοεγγράφεται στο DOCX: το generateCrewList (:828-832) εξαιρεί role='skipper'/'co-skipper'
      // από τα CREW2-12, και το deriveSkipper (:736) επιστρέφει null μόλις υπάρχει γραμμή skipper.
      const curCrew: any[] = Array.isArray(c.crewMembers) ? c.crewMembers : [];
      const editPerson: any = { passport: e.passport || '', dateOfBirth: e.dateOfBirth || '', gender: e.gender || '' };
      let crewToSend: any[] | null = null;
      let sIdx = curCrew.findIndex((m: any) => sameIdentity(m, editPerson));
      if (sIdx < 0) sIdx = curCrew.findIndex((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
      if (sIdx >= 0) {
        const updated = curCrew.map((m: any, i: number) => i === sIdx ? {
          ...m, role: 'skipper',
          passport: e.passport ?? m.passport ?? '',
          dateOfBirth: e.dateOfBirth ?? m.dateOfBirth ?? '',
          gender: e.gender ?? m.gender ?? '',
          nationality: e.nationality ?? m.nationality ?? '',
        } : m);
        crewToSend = unifyCrew(updated); // canon λατινικά + merge ίδιου ID
      } else if (!isMe && mode !== 'invite') {
        // Καμία γραμμή skipper → INSERT από το baseCd.skipper* (η πηγή που μόλις σώσαμε).
        // 'me' → ο σκίπερ είναι ο ναυλωτής (καλύπτεται από τη δική του γραμμή)· 'invite' → δεν ήρθε ακόμη.
        const nfn = String(baseCd.skipperFirstName || '').trim();
        const nln = String(baseCd.skipperLastName || '').trim();
        const nnm = `${nfn} ${nln}`.trim() || String(baseCd.skipperName || '').trim();
        if (nnm || String(e.passport || '').trim()) {
          crewToSend = unifyCrew([...curCrew, {
            name: nnm, firstName: nfn, lastName: nln, role: 'skipper',
            passport: e.passport ?? baseCd.skipperIdNumber ?? '',
            dateOfBirth: e.dateOfBirth ?? baseCd.skipperDob ?? '',
            gender: e.gender ?? baseCd.skipperGen ?? '',
            nationality: e.nationality ?? baseCd.skipperNationality ?? '',
            email: baseCd.skipperEmail || '',
            phone: baseCd.skipperPhone || '',
            address: baseCd.skipperAddress || '',
          }]);
        }
      }
      const empCode = localStorage.getItem('employeeCode') || '';
      // 1) charter_archives (authoritative για AADE/agreement)
      const ar = await fetch('/api/charter-archive.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': empCode },
        body: JSON.stringify({ booking_number: bn, archive_data: { chartererData: baseCd } }),
      });
      const arj = await ar.json().catch(() => ({}));
      if (arj && arj.error) throw new Error(arj.error);
      // 2) flat sync στο bookings (Fleet display + άλλοι consumers) + crew upsert στην ίδια κλήση
      await fetch('/api/update-charter-crew.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_number: bn, chartererData: baseCd, ...(crewToSend ? { crewMembers: crewToSend } : {}) }),
      });
      // reflect in-memory
      (charter as any).chartererData = baseCd;
      if (crewToSend) (charter as any).crewMembers = crewToSend;
      setEditingSkipper(false);
      showMessage('✅ Ο Πλοίαρχος αποθηκεύτηκε', 'success');
    } catch (err: any) {
      console.error('Skipper save error:', err);
      showMessage('❌ Σφάλμα: ' + (err?.message || err), 'error');
    } finally {
      setSkipperSaving(false);
    }
  };

  const handleXlsxImport = async () => {
    const bn = (xlsxBookingNumber || '').trim();
    if (!bn) { showMessage('Λείπει booking number', 'error'); return; }
    if (xlsxParsedCrew.length === 0) { showMessage('Δεν υπάρχουν εγγραφές προς εισαγωγή', 'error'); return; }
    setXlsxImporting(true);
    try {
      const r = await fetch('/api/bookings.php?booking_number=' + encodeURIComponent(bn));
      if (!r.ok) throw new Error('Booking not found (HTTP ' + r.status + ')');
      const existing = await r.json();
      const bd = existing?.booking_data || existing?.[0]?.booking_data || existing;
      const existingCrew: any[] = Array.isArray(bd?.crewMembers) ? bd.crewMembers : [];
      const norm = (p: any) => String(p || '').trim().toLowerCase();
      const byPass = new Map<string, any>();
      const noKey: any[] = [];
      existingCrew.forEach((m: any) => {
        const k = norm(m.passport);
        if (k) byPass.set(k, m); else noKey.push(m);
      });
      xlsxParsedCrew.forEach((m: any) => {
        const k = norm(m.passport);
        if (!k) { noKey.push(m); return; }
        const prev = byPass.get(k) || {};
        byPass.set(k, { ...prev, ...m });
      });
      const merged = [...Array.from(byPass.values()), ...noKey];
      const pr = await fetch('/api/update-charter-crew.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_number: bn, crewMembers: merged }),
      });
      const result = await pr.json();
      if (!result.success) throw new Error(result.error || 'Update failed');
      showMessage(`✅ Εισήχθησαν ${xlsxParsedCrew.length} εγγραφές (σύνολο πλήρωμα: ${merged.length})`, 'success');
      setXlsxModalOpen(false);
      setXlsxParsedCrew([]);
    } catch (err: any) {
      console.error('XLSX import error:', err);
      showMessage('Σφάλμα: ' + (err?.message || err), 'error');
    } finally {
      setXlsxImporting(false);
    }
  };

  // 🔥 FIX: Only sync payments when viewing a DIFFERENT charter
  useEffect(() => {
    if (charter.id !== initialPaymentsRef.current.charterId) {
      console.log('💳 Charter ID changed from:', initialPaymentsRef.current.charterId, 'to:', charter.id);
      console.log('💳 Loading payments for new charter:', charter.payments);
      initialPaymentsRef.current = {
        charterId: charter.id,
        payments: charter.payments || []
      };
      setPayments(charter.payments || []);
      // also sync crew editor to the new charter
      setCrewEdit(Array.isArray((charter as any).crewMembers) ? [...(charter as any).crewMembers] : []);
    }
  }, [charter.id, charter.payments]);

  // 🔥 DEBUG: Log when payments state changes
  useEffect(() => {
    console.log('💳 [useEffect] payments state changed:', payments, 'length:', payments.length);
  }, [payments]);

  // 🔥 FIX: Dedicated handler for adding payment
  const handleAddPaymentClick = (e: React.MouseEvent) => {
    console.log('🔴🔴🔴 handleAddPaymentClick FIRED! 🔴🔴🔴');
    e.preventDefault();
    e.stopPropagation();

    console.log('💳 ========== ADD PAYMENT CLICKED ==========');
    console.log('💳 Input values:', { newPayDate, newPayAmount });
    console.log('💳 Permissions:', { canEditCharters });
    console.log('💳 Current payments before add:', payments);

    if (!canEditCharters) {
      console.log('💳 ❌ BLOCKED: canEditCharters is false');
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    const amount = parseFloat(newPayAmount) || 0;
    console.log('💳 Parsed amount:', amount);

    if (!newPayDate) {
      console.log('💳 ❌ BLOCKED: No date selected');
      showMessage('❌ Επιλέξτε ημερομηνία πληρωμής', 'error');
      return;
    }
    if (amount <= 0) {
      console.log('💳 ❌ BLOCKED: Invalid amount (<=0)');
      showMessage('❌ Εισάγετε έγκυρο ποσό πληρωμής', 'error');
      return;
    }

    const newPayment = { date: newPayDate, amount: amount };
    const newPayments = [...payments, newPayment];
    console.log('💳 ✅ Adding new payment:', newPayment);
    console.log('💳 ✅ New payments array:', newPayments);

    setPayments(newPayments);
    setNewPayDate('');
    setNewPayAmount('');

    console.log('💳 ✅ State updated, payments should now show:', newPayments.length, 'items');
    showMessage('✅ Πληρωμή προστέθηκε - Πατήστε "Αποθήκευση" για να σωθεί', 'success');
  };

  // Keep the old function for backwards compatibility
  const addPayment = () => {
    console.log('💳 addPayment() called directly');
    handleAddPaymentClick({ preventDefault: () => {}, stopPropagation: () => {} } as any);
  };
  
  const removePayment = (index) => {
    if (!canEditCharters) { showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error'); return; }
    setPayments(payments.filter((_, i) => i !== index));
  };
  
  const savePayments = async () => {
    console.log('💰 savePayments called:', {
      charterId: charter.id,
      charterCode: charter.code,
      payments,
      charterAmount: charter.amount
    });

    if (!canEditCharters) {
      showMessage('❌ View Only - Δεν έχετε δικαίωμα επεξεργασίας', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      await onUpdatePayments(charter.id, payments);
      console.log('✅ savePayments completed successfully');
    } catch (error) {
      console.error('❌ Error saving payments:', error);
      showMessage('❌ Σφάλμα αποθήκευσης πληρωμών', 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balance = (charter.amount || 0) - totalPaid;

  // 🔥 FIX 28: Helper to clear status message after delay
  const clearStatusAfterDelay = () => {
    setTimeout(() => setStatusMessage(null), 3000);
  };

  // OWNER: Option → Option Accepted (accept) or Cancelled (reject)
  // 🔥 FIX 8 + FIX 28: Added inline status messages
  const handleOwnerAcceptOption = async () => {
    if (!canAcceptCharter) { showMessage('❌ Δεν έχετε δικαίωμα αποδοχής', 'error'); return; }
    setIsProcessing(true);
    setStatusMessage({ text: 'Αποστολή emails...', type: 'loading' });
    console.log('📝 Owner accepting option:', charter.code);
    const success = await sendCharterEmail(charter, boat, 'option_accepted');
    if (success) {
      onUpdateStatus(charter, 'Option Accepted');
      try {
        const updatedCharter = { ...charter, status: 'option_accepted', vesselId: boat.id };
        await saveBooking(charter.code, { bookingData: updatedCharter });
        console.log('✅ Charter acceptance synced to API');
      } catch (error) {
        console.error('❌ API sync error:', error);
      }
      setStatusMessage({ text: '✅ Charter επιβεβαιώθηκε!', type: 'success' });
      clearStatusAfterDelay();
    } else {
      setStatusMessage({ text: '❌ Σφάλμα', type: 'error' });
      clearStatusAfterDelay();
    }
    setIsProcessing(false);
  };

  // 🔥 FIX 8 + FIX 28: Added inline status messages
  const handleOwnerRejectOption = async () => {
    if (!canAcceptCharter) { showMessage('❌ Δεν έχετε δικαίωμα απόρριψης', 'error'); return; }
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να ΑΠΟΡΡΙΨΕΤΕ αυτόν τον ναύλο;')) return;
    setIsProcessing(true);
    setStatusMessage({ text: 'Αποστολή emails...', type: 'loading' });
    console.log('📝 Owner rejecting option:', charter.code);
    const success = await sendCharterEmail(charter, boat, 'cancelled');
    if (success) {
      onUpdateStatus(charter, 'Cancelled');
      try {
        const updatedCharter = { ...charter, status: 'Cancelled', vesselId: boat.id };
        await saveBooking(charter.code, { bookingData: updatedCharter });
        console.log('✅ Charter rejection synced to API');
      } catch (error) {
        console.error('❌ API sync error:', error);
      }
      setStatusMessage({ text: '❌ Ναύλος ακυρώθηκε!', type: 'success' });
      clearStatusAfterDelay();
    } else {
      setStatusMessage({ text: '❌ Σφάλμα', type: 'error' });
      clearStatusAfterDelay();
    }
    setIsProcessing(false);
  };

  // 🔥 FIX 29: OWNER: Pending Final Confirmation → Confirmed (ΤΕΛΙΚΗ ΕΠΙΒΕΒΑΙΩΣΗ)
  const handleOwnerFinalConfirmation = async () => {
    console.log('🔥 handleOwnerFinalConfirmation CALLED');
    console.log('🔥 canAcceptCharter:', canAcceptCharter);
    console.log('🔥 charter:', charter?.code, 'status:', charter?.status);
    console.log('🔥 boat:', boat?.id, boat?.name);

    if (!canAcceptCharter) {
      console.log('❌ canAcceptCharter is FALSE - blocking');
      showMessage('❌ Δεν έχετε δικαίωμα επιβεβαίωσης', 'error');
      return;
    }

    setIsProcessing(true);
    setStatusMessage({ text: 'Αποστολή emails...', type: 'loading' });

    console.log('📧 CALLING sendCharterEmail with action: confirmed');
    const success = await sendCharterEmail(charter, boat, 'confirmed');
    console.log('📧 sendCharterEmail RESULT:', success);

    if (success) {
      onUpdateStatus(charter, 'Confirmed');
      try {
        const updatedCharter = { ...charter, status: 'Confirmed', vesselId: boat.id };
        await saveBooking(charter.code, { bookingData: updatedCharter });
        console.log('✅ Final confirmation synced to API');
      } catch (error) {
        console.error('❌ API sync error:', error);
      }
      setStatusMessage({ text: '✅ Ναύλος επιβεβαιώθηκε τελικά!', type: 'success' });
      clearStatusAfterDelay();
    } else {
      console.log('❌ sendCharterEmail returned FALSE');
      setStatusMessage({ text: '❌ Σφάλμα αποστολής email', type: 'error' });
      clearStatusAfterDelay();
    }
    setIsProcessing(false);
  };

  // 🔥 FIX 29: ADMIN: Option Accepted → Pending Final Confirmation (ΑΠΟΣΤΟΛΗ ΓΙΑ ΤΕΛΙΚΗ ΕΠΙΒΕΒΑΙΩΣΗ)
  const handleAdminSendForFinalApproval = async () => {
    setIsProcessing(true);
    setStatusMessage({ text: 'Αποστολή emails...', type: 'loading' });
    console.log('📝 Admin sending for final approval:', charter.code);
    const success = await sendCharterEmail(charter, boat, 'pending_final_confirmation');
    if (success) {
      onUpdateStatus(charter, 'Pending Final Confirmation');
      try {
        const updatedCharter = { ...charter, status: 'Pending Final Confirmation', vesselId: boat.id };
        await saveBooking(charter.code, { bookingData: updatedCharter });
        console.log('✅ Sent for final approval - synced to API');
      } catch (error) {
        console.error('❌ API sync error:', error);
      }
      setStatusMessage({ text: '✅ Αποστάλθηκε για τελική επιβεβαίωση!', type: 'success' });
      clearStatusAfterDelay();
    } else {
      setStatusMessage({ text: '❌ Σφάλμα', type: 'error' });
      clearStatusAfterDelay();
    }
    setIsProcessing(false);
  };

  // 🔥 FIX 28: Added inline status messages
  const handleAdminCancelCharter = async () => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να ΑΚΥΡΩΣΕΤΕ αυτόν τον ναύλο;')) return;
    setIsProcessing(true);
    setStatusMessage({ text: 'Αποστολή emails...', type: 'loading' });
    const success = await sendCharterEmail(charter, boat, 'cancelled');
    if (success) {
      onDelete(charter.id);
      setStatusMessage({ text: '❌ Ναύλος ακυρώθηκε!', type: 'success' });
      clearStatusAfterDelay();
    } else {
      setStatusMessage({ text: '❌ Σφάλμα', type: 'error' });
      clearStatusAfterDelay();
    }
    setIsProcessing(false);
  };

  const handleDownloadSpecimen = () => { generateSpecimenPdf(charter, boat); };

  // 🔥 FIX 35: Professional PDF download handler
  const handleDownloadProfessionalPDF = async () => {
    try {
      showMessage('Generating PDF...', 'info');
      await generateOwnerCharterPDF(charter, boat, { company: boat.ownerCompany || boat.name || 'OWNER' });
      showMessage('PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showMessage('Error generating PDF', 'error');
    }
  };

  // 🔥 FIX 23: Charter Party DOCX download handler
  const handleDownloadCharterParty = () => { generateCharterParty(charter, boat, showMessage); };

  // 🔥 FIX 27: Crew List DOCX download handler
  const handleDownloadCrewList = () => {
    // Load boatDetails from localStorage
    const detailsKey = `fleet_${boat.id}_details`;
    const storedDetails = localStorage.getItem(detailsKey);
    const boatDetails = storedDetails ? JSON.parse(storedDetails) : {};
    generateCrewList(charter, boat, boatDetails, showMessage);
  };

  // 📋 ΑΑΔΕ JSON export handler — called from the pre-export preview modal.
  const handleExportAade = async () => {
    const code = charter.code || charter.charterCode || charter.bookingNumber || '';
    if (!code) { (showMessage ? showMessage('Λείπει booking code', 'error') : alert('Λείπει booking code')); return; }
    // Foreign broker: if opted-in, name is mandatory (from existing data or mini-form).
    let fbToSave: any = null;
    if (includeForeignBroker) {
      /* ═══ ΟΤΙ ΔΕΙΧΝΕΙ ΤΟ MODAL ΕΙΝΑΙ ΟΤΙ ΦΕΥΓΕΙ (19/09/2026) ══════════════
         Πριν: αν ο αποθηκευμένος πράκτορας είχε επωνυμία, το παράθυρο κρατούσε
         ΤΟ ΠΑΛΙΟ αντικείμενο («fbToSave = existing») και αγνοούσε ό,τι έγραφε ο
         υπάλληλος — με αποτέλεσμα ΑΦΜ/ΔΟΥ/διεύθυνση/email να φεύγουν κενά στο
         JSON. Τώρα πηγή είναι ΠΑΝΤΑ η φόρμα, με ό,τι υπήρχε ως βάση. */
      const existing = (charter as any)?.foreign_broker || null;
      const name = String(fbForm.name || existing?.name || '').trim();
      if (!name) {
        setFbError('Πρέπει να συμπληρωθεί τουλάχιστον η Επωνυμία');
        return;
      }
      const pick = (formVal: any, prevVal: any) => String(formVal ?? '').trim() || String(prevVal ?? '').trim();
      fbToSave = {
        ...(existing || {}),
        enabled: true,
        name,
        vat:     pick(fbForm.vat,     existing?.vat),
        doy:     pick(fbForm.doy,     existing?.doy),
        country: pick(fbForm.country, existing?.country),
        address: pick(fbForm.address, existing?.address),
        email:   pick(fbForm.email,   existing?.email),
        phone:   pick(fbForm.phone,   existing?.phone),
      };
    }
    setFbError('');
    try {
      setAadeExporting(true);
      // Persist broker opt-ins (both flags, always) so the export reflects the choices.
      try {
        const bookingData: any = {
          ...charter,
          export_include_tailwind: includeTailwind,
          export_include_foreign_broker: includeForeignBroker,
        };
        if (fbToSave) bookingData.foreign_broker = fbToSave;
        await saveBooking(code, { bookingData });
      } catch (saveErr) {
        console.error('Failed to persist broker opt-ins', saveErr);
        (showMessage ? showMessage('⚠ Δεν αποθηκεύτηκαν οι επιλογές ναυλομεσιτών', 'error') : alert('Σφάλμα αποθήκευσης επιλογών'));
        setAadeExporting(false);
        return;
      }
      const r = await fetch('/api/aade-export.php?booking_number=' + encodeURIComponent(code));
      const data = await r.json();
      if (data && data.error) { alert('Σφάλμα: ' + data.error); return; }
      if (data.blocked) {
        alert('⛔ Δεν μπορεί να γίνει export για ΑΑΔΕ:\n\n- ' + (data.blocking_errors || []).join('\n- ') + '\n\nΣυμπλήρωσε τα στοιχεία ONLINE (όχι μόνο PDF) και ξαναδοκίμασε.');
        return;
      }
      if (Array.isArray(data.warnings) && data.warnings.length > 0) {
        const ok = window.confirm('⚠️ Προειδοποιήσεις (' + data.warnings.length + '):\n\n- ' + data.warnings.join('\n- ') + '\n\nΝα κατεβάσω το JSON;');
        if (!ok) return;
      }
      const blob = new Blob([JSON.stringify(data.aade || [], null, 2)], { type: 'application/json' });
      const filename = 'aade-' + String(code || 'export').replace(/\s+/g, '_') + '.json';
      saveAs(blob, filename);
      if (showMessage) showMessage('✅ ΑΑΔΕ JSON κατέβηκε', 'success');
      setShowAadePreview(false);
    } catch (e: any) {
      console.error('AADE export failed', e);
      (showMessage ? showMessage('Σφάλμα export ΑΑΔΕ', 'error') : alert('Σφάλμα export ΑΑΔΕ: ' + (e?.message || e)));
    } finally {
      setAadeExporting(false);
    }
  };

  // Open the broker preview modal, seeding state from any saved choices.
  const openAadePreview = () => {
    setIncludeTailwind(!!(charter as any)?.export_include_tailwind);
    setIncludeForeignBroker(!!(charter as any)?.export_include_foreign_broker);
    const existingFb = (charter as any)?.foreign_broker || null;
    setFbForm(existingFb ? {
      name: existingFb.name || '', vat: existingFb.vat || '', doy: existingFb.doy || '',
      country: existingFb.country || '', address: existingFb.address || '',
      email: existingFb.email || '', phone: existingFb.phone || '',
    } : { ...emptyFbForm });
    setFbError('');
    setShowAadePreview(true);
  };

  // 📄 Upload a blank ΑΑΔΕ PDF → server fills the ΛΟΙΠΟΙ ΟΡΟΙ box → download.
  const handleFillAadePdf = async () => {
    if (!aadePdfFile) { alert('Επίλεξε πρώτα το κενό PDF ΑΑΔΕ.'); return; }
    const code = charter.code || charter.charterCode || charter.bookingNumber || '';
    setAadePdfBusy(true);
    try {
      const fd = new FormData();
      fd.append('pdf', aadePdfFile);
      if (code) fd.append('booking_number', code);
      const r = await fetch('/api/fill-aade-pdf.php', { method: 'POST', body: fd });
      if (!r.ok) {
        let msg = 'Αποτυχία επεξεργασίας PDF';
        try { const j = await r.json(); msg = j.error || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await r.blob();
      const filename = 'aade-terms-' + String(code || 'export').replace(/\s+/g, '_') + '.pdf';
      saveAs(blob, filename);
      if (showMessage) showMessage('✅ Το PDF ΑΑΔΕ συμπληρώθηκε', 'success');
      setShowAadePdfModal(false);
      setAadePdfFile(null);
    } catch (e: any) {
      console.error('fill-aade-pdf failed', e);
      (showMessage ? showMessage('Σφάλμα: ' + (e?.message || e), 'error') : alert('Σφάλμα: ' + (e?.message || e)));
    } finally {
      setAadePdfBusy(false);
    }
  };

  // Crew/passenger role tally for the preview. ΚΟΙΝΗ σύνθεση: ο skipper (Πλοίαρχος)
  // μπαίνει ΠΑΝΤΑ, ακόμη κι όταν είναι ο ναυλωτής και λείπει από τα crewMembers.
  const derivedSkipper = deriveSkipper(charter, crewEdit);
  // ΕΝΑΣ ΑΝΘΡΩΠΟΣ = ΜΙΑ ΓΡΑΜΜΗ. Ο derivedSkipper είναι ΣΥΝΘΕΤΙΚΗ γραμμή (ο σκίπερ που ζει
  // στα chartererData και δεν έχει δική του γραμμή crew). Αν ο ΙΔΙΟΣ άνθρωπος υπάρχει ήδη
  // ως γραμμή πληρώματος — τυπικά ο ναυλωτής-σκίπερ δηλωμένος ως «άλλο πρόσωπο» — τότε η
  // πραγματική γραμμή κερδίζει και η συνθετική παραλείπεται, αλλιώς μετριέται δύο φορές.
  // ΤΑΙΡΙΑΣΜΑ: ΟΝΟΜΑ **ΚΑΙ** ΤΑΥΤΟΤΗΤΑ μαζί — ποτέ μόνο όνομα.
  const aadeSamePersonAsDerived = (m: any): boolean => {
    if (!derivedSkipper || !m) return false;
    return samePersonStrict(
      normName(derivedSkipper.name) || normName(derivedSkipper.firstName, derivedSkipper.lastName),
      idKeys(derivedSkipper.passport, derivedSkipper.idNumber),
      normName(m.name) || normName(m.firstName, m.lastName),
      idKeys(m.passport, m.idNumber)
    );
  };
  const aadeCrewList: any[] = derivedSkipper
    ? [derivedSkipper, ...(Array.isArray(crewEdit) ? crewEdit : []).filter(m => !aadeSamePersonAsDerived(m))]
    : (Array.isArray(crewEdit) ? crewEdit : []);
  const aadeRoleCount = (pred: (r: string) => boolean) =>
    aadeCrewList.filter(m => pred(String(m?.role || 'passenger').toLowerCase())).length;
  const cntSkipper = aadeRoleCount(r => r === 'skipper');
  const cntCoSkipper = aadeRoleCount(r => r === 'co-skipper' || r === 'coskipper');
  const cntCrew = aadeRoleCount(r => r === 'crew');
  const cntPassenger = aadeRoleCount(r => !['skipper', 'co-skipper', 'coskipper', 'crew'].includes(r));
  const aadeTotalPeople = aadeCrewList.length;
  // "Πλοίαρχος:" ένδειξη — ΙΔΙΑ πηγή με το ΑΑΔΕ export (crewMembers[role=skipper] /
  // derivedSkipper), ΟΧΙ τα flat charter.skipper* που μένουν κενά σε mode 'other'/'crew'.
  // Πιάνει sameAsSkipper (derivedSkipper από chartererData), mode 'other' & mode 'crew'
  // (πραγματικό crew entry role=skipper).
  const aadeSkipperEntry = aadeCrewList.find(m => String(m?.role || '').toLowerCase() === 'skipper');
  const aadeSkipperName = (aadeSkipperEntry?.name
    || [aadeSkipperEntry?.firstName, aadeSkipperEntry?.lastName].filter(Boolean).join(' ')
    || [charter.skipperFirstName, charter.skipperLastName].filter(Boolean).join(' ')
    || '').replace(/\s+/g, ' ').trim() || '-';
  // Εθνικότητες που ΔΕΝ αναγνωρίζονται (→ XXX στο export). Ίδια λογική με τον server.
  const aadeUnknownNat = aadeCrewList.filter((m: any) => nationalityToIso3(m?.nationality) === 'XXX');
  const fbPreview = (charter as any)?.foreign_broker || null;
  // "Has existing saved data" → show read-only; otherwise show the mini-form.
  const fbEnabled = !!(fbPreview && String(fbPreview.name || '').trim() !== '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 overflow-y-auto max-h-[90vh] border border-[#d1d5db]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#1e40af]">CHARTER SPECIMEN</h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-white">{icons.x}</button>
        </div>

        {isOwnerUser && (
          <div className="mb-4 p-2 bg-blue-900 rounded-lg text-center border border-blue-700">
            <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">{icons.eye}<span>Owner Mode - Αποδοχή/Απόρριψη</span></div>
          </div>
        )}

        {/* 🔥 FIX 28: Inline status message display */}
        {statusMessage && (
          <div className={`mb-4 p-3 rounded-lg text-center font-semibold ${
            statusMessage.type === 'loading' ? 'bg-blue-600 text-white animate-pulse' :
            statusMessage.type === 'success' ? 'bg-green-600 text-white' :
            'bg-red-600 text-white'
          }`}>
            {statusMessage.text}
          </div>
        )}

        {/* 🔥 EDIT BUTTON - TOP OF MODAL - FORCED TO SHOW FOR TESTING */}
        {console.log('🔥 EDIT BUTTON RENDER - canEditCharters:', canEditCharters, 'onEdit:', !!onEdit, 'status:', charter?.status, 'source:', charter?.source)}

        {/* 🔥 TEST: Always show edit button if onEdit exists */}
        {onEdit && (
          <button
            onClick={() => {
              console.log('🔥 EDIT BUTTON CLICKED!', charter);
              onEdit(charter);
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-4 rounded-lg flex items-center justify-center mb-4 border-2 border-blue-400 shadow-xl text-lg"
            style={{ animation: 'pulse 2s infinite' }}
          >
            <span className="text-xl">✏️ ΕΠΕΞΕΡΓΑΣΙΑ ΝΑΥΛΟΥ</span>
          </button>
        )}

        {/* 🔥 FALLBACK: If onEdit is missing, show why */}
        {!onEdit && (
          <div className="w-full bg-red-600 text-white font-bold py-3 px-4 rounded-lg mb-4 text-center">
            ⚠️ onEdit prop is missing - Edit button disabled
          </div>
        )}

        <div className="text-center mb-4 pb-4 border-b border-[#d1d5db]">
          <h3 className="font-bold text-lg">{COMPANY_INFO.name}</h3>
          <p className="text-sm text-[#6b7280]">{COMPANY_INFO.address}</p>
          <p className="text-sm text-[#6b7280]">Tel: {COMPANY_INFO.phone}</p>
          <p className="text-sm text-[#6b7280]">{COMPANY_INFO.emails.info}</p>
        </div>

        <h3 className="text-center font-bold text-lg mb-4">CHARTERING INFORMATION - {charter.status === 'Confirmed' ? 'RESERVATION' : 'OPTION'} {charter.code}</h3>

        <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 space-y-2 border border-[#d1d5db]">
          <div className="flex justify-between"><span className="text-[#374151]">YACHT:</span><span className="font-bold">{boat.name || boat.id}</span></div>
          <div className="flex justify-between"><span className="text-[#374151]">FROM:</span><span className="font-bold">{charter.startDate ? charter.startDate.split('-').reverse().join('/') : ''}{charter.startTime && ` @ ${charter.startTime}`}</span></div>
          <div className="flex justify-between"><span className="text-[#374151]">TO:</span><span className="font-bold">{charter.endDate ? charter.endDate.split('-').reverse().join('/') : ''}{charter.endTime && ` @ ${charter.endTime}`}</span></div>
          <div className="flex justify-between"><span className="text-[#374151]">DEPARTURE:</span><span className="font-bold">{charter.departure || 'ALIMOS MARINA'}</span></div>
          <div className="flex justify-between"><span className="text-[#374151]">ARRIVAL:</span><span className="font-bold">{charter.arrival || 'ALIMOS MARINA'}</span></div>
          {/* Status badge for Draft bookings */}
          {charter.status === 'Draft' && (
            <div className="flex justify-between items-center pt-2 border-t border-[#d1d5db]">
              <span className="text-[#374151]">STATUS:</span>
              <span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded-lg text-sm font-bold border border-blue-500/50">
                📝 DRAFT - Από Check-in
              </span>
            </div>
          )}
          {/* Show skipper info if available */}
          {(charter.skipperFirstName || charter.skipperLastName) && (
            <div className="pt-2 border-t border-[#d1d5db]">
              <div className="flex justify-between"><span className="text-[#374151]">SKIPPER:</span><span className="font-bold">{charter.skipperFirstName} {charter.skipperLastName}</span></div>
              {charter.skipperEmail && <div className="flex justify-between"><span className="text-[#374151]">EMAIL:</span><span className="text-sm">{charter.skipperEmail}</span></div>}
              {charter.skipperPhone && <div className="flex justify-between"><span className="text-[#374151]">PHONE:</span><span className="text-sm">{charter.skipperPhone}</span></div>}
            </div>
          )}

          {/* Show broker info if available */}
          {charter.broker && (
            <div className="flex justify-between pt-2 border-t border-[#d1d5db]"><span className="text-[#374151]">BROKER:</span><span className="font-bold">{charter.broker}</span></div>
          )}
        </div>

        {/* EXTRAS section */}
        {charter.extras && charter.extras.length > 0 && (
          <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
            <h4 className="font-bold text-lg mb-3">EXTRAS:</h4>
            <div className="space-y-1">
              {charter.extras.map((extra: string, idx: number) => (
                <div key={idx} className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span>{extra}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTES section */}
        {charter.notes && charter.notes.trim() && (
          <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
            <h4 className="font-bold text-lg mb-3 text-blue-400">ΣΗΜΕΙΩΣΕΙΣ:</h4>
            <p className="text-[#374151] whitespace-pre-wrap">{charter.notes}</p>
          </div>
        )}

        {canViewFinancials && (
          <>
            <h4 className="font-bold text-lg mb-3">FINANCIAL TERMS:</h4>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-green-400"><span>Charter Fee (Income):</span><span className="font-bold">{charter.amount?.toFixed(2)}€</span></div>
              <div className="flex justify-between text-red-400"><span>Commission (Expense):</span><span className="font-bold">-{charter.commission?.toFixed(2)}€</span></div>
              <div className="flex justify-between text-red-400"><span>VAT on Commission (24%):</span><span className="font-bold">-{charter.vat_on_commission?.toFixed(2)}€</span></div>
              <hr className="border-[#d1d5db]" />
              <div className="flex justify-between text-xl font-bold"><span>NET INCOME:</span><span className="text-[#1e40af]">{netIncome.toFixed(2)}€</span></div>
            </div>
          </>
        )}

        <p className="text-center text-[#6b7280] text-sm mb-4">Please advise regarding the acceptance of the charter.<br/>Thank you,</p>

        {/* ADMIN: Option → Show waiting message */}
        {!isOwnerUser && (charter.status === 'Option' || charter.status === 'option' || charter.status === 'OPTION' || charter.status === 'Pending' || charter.status === 'pending') && (
          <div className="w-full bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg mb-3 flex flex-col items-center justify-center">
            <div className="flex items-center">🟡 <span className="ml-2">OPTION - Αναμονή αποδοχής Owner</span></div>
            <span className="text-xs font-normal mt-1">Ο Owner πρέπει να αποδεχτεί πρώτα</span>
          </div>
        )}

        {/* OWNER: Option → ΑΠΟΔΟΧΗ / ΜΗ ΑΠΟΔΟΧΗ */}
        {isOwnerUser && (charter.status === 'Option' || charter.status === 'option' || charter.status === 'OPTION' || charter.status === 'Pending' || charter.status === 'pending') && (
          <div className="space-y-2 mb-4">
            <div className="text-center text-sm mb-2 p-2 rounded-lg bg-yellow-400 text-black">
              ⏳ Αυτός ο ναύλος περιμένει την απόφασή σας
            </div>
            <button onClick={handleOwnerAcceptOption} disabled={isProcessing} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              {isProcessing ? 'Processing...' : <>{icons.checkCircle} <span className="ml-2">✅ ΑΠΟΔΟΧΗ</span></>}
            </button>
            <button onClick={handleOwnerRejectOption} disabled={isProcessing} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              {isProcessing ? 'Processing...' : <>{icons.xCircle} <span className="ml-2">❌ ΜΗ ΑΠΟΔΟΧΗ</span></>}
            </button>
          </div>
        )}

        {/* 🔥 FIX 29: ADMIN: Option Accepted → ΑΠΟΣΤΟΛΗ ΓΙΑ ΤΕΛΙΚΗ ΕΠΙΒΕΒΑΙΩΣΗ / ΑΚΥΡΟ */}
        {!isOwnerUser && (charter.status === 'Option Accepted' || charter.status === 'option_accepted' || charter.status === 'OPTION_ACCEPTED') && (
          <div className="space-y-2 mb-4">
            <div className="text-center text-sm mb-2 p-2 rounded-lg bg-yellow-400 text-black">
              ⏳ Owner αποδέχτηκε - Στείλτε για τελική επιβεβαίωση
            </div>
            {/* 🔥 FIX 29: Admin button sends to owner for final approval */}
            <button onClick={handleAdminSendForFinalApproval} disabled={isProcessing} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              {isProcessing ? 'Processing...' : <>{icons.checkCircle} <span className="ml-2">📤 ΑΠΟΣΤΟΛΗ ΓΙΑ ΤΕΛΙΚΗ ΕΠΙΒΕΒΑΙΩΣΗ</span></>}
            </button>
            <button onClick={handleAdminCancelCharter} disabled={isProcessing} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              {isProcessing ? 'Processing...' : <>{icons.xCircle} <span className="ml-2">❌ ΑΚΥΡΟ</span></>}
            </button>
          </div>
        )}

        {/* 🔥 FIX: OWNER ONLY: Pending Final Confirmation → BIG CLICKABLE YELLOW BUTTON */}
        {isOwnerUser && (charter.status === 'Pending Final Confirmation' || charter.status === 'pending_final_confirmation' || charter.status === 'PENDING_FINAL_CONFIRMATION') && (
          <div className="space-y-3 mb-4">
            {/* 🔥 BIG CLICKABLE YELLOW BUTTON - Primary action for owner */}
            <button
              onClick={handleOwnerFinalConfirmation}
              disabled={isProcessing}
              className="w-full bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-black font-bold py-5 px-4 rounded-lg flex flex-col items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-xl border-4 border-yellow-600 transform hover:scale-[1.02] transition-all duration-200 animate-pulse"
            >
              {isProcessing ? (
                <span className="text-xl">⏳ Processing...</span>
              ) : (
                <>
                  <span className="text-2xl mb-1">👆 ΚΛΙΚ ΓΙΑ ΤΕΛΙΚΗ ΕΠΙΒΕΒΑΙΩΣΗ</span>
                  <span className="text-sm font-normal">Πατήστε για να επιβεβαιώσετε τον ναύλο</span>
                </>
              )}
            </button>
            {/* 🔥 NO reject button here - only admin can cancel after option is accepted */}
            <div className="text-center text-xs text-gray-500">
              Για ακύρωση επικοινωνήστε με τον Admin
            </div>
          </div>
        )}

        {/* Status displays - NOT CLICKABLE, just informational */}
        {(charter.status === 'Option Accepted' || charter.status === 'option_accepted' || charter.status === 'OPTION_ACCEPTED') && isOwnerUser && (
          <div className="w-full bg-yellow-400/70 text-black font-bold py-3 px-4 rounded-lg mb-3 flex flex-col items-center justify-center cursor-not-allowed border-2 border-dashed border-yellow-600">
            <div className="flex items-center">{icons.checkCircle} <span className="ml-2">⏳ OPTION ACCEPTED</span></div>
            <span className="text-xs font-normal mt-1">Αναμονή ενέργειας από Admin...</span>
          </div>
        )}

        {/* 🔥 FIX 29: Show pending status to Admin - NOT CLICKABLE */}
        {(charter.status === 'Pending Final Confirmation' || charter.status === 'pending_final_confirmation' || charter.status === 'PENDING_FINAL_CONFIRMATION') && !isOwnerUser && (
          <div className="w-full bg-yellow-400/70 text-black font-bold py-3 px-4 rounded-lg mb-3 flex flex-col items-center justify-center cursor-not-allowed border-2 border-dashed border-yellow-600">
            <div className="flex items-center">{icons.checkCircle} <span className="ml-2">⏳ ΑΝΑΜΟΝΗ ΤΕΛΙΚΗΣ ΕΠΙΒΕΒΑΙΩΣΗΣ</span></div>
            <span className="text-xs font-normal mt-1">Αναμονή επιβεβαίωσης από Owner...</span>
          </div>
        )}

        {(charter.status === 'Confirmed' || charter.status === 'confirmed' || charter.status === 'CONFIRMED') && (
          <div className="w-full bg-green-500 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center">{icons.checkCircle} <span className="ml-2">✅ ΕΠΙΒΕΒΑΙΩΜΕΝΟΣ</span></div>
        )}

        {(charter.status === 'Cancelled' || charter.status === 'Canceled' || charter.status === 'Rejected' || charter.status === 'cancelled' || charter.status === 'canceled' || charter.status === 'rejected' || charter.status === 'CANCELLED' || charter.status === 'CANCELED' || charter.status === 'REJECTED') && (
          <div className="w-full bg-red-500 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center">{icons.xCircle} <span className="ml-2">❌ ΑΚΥΡΩΜΕΝΟΣ</span></div>
        )}

        {/* 🔥 FIX 35: Professional PDF Download Button */}
        <button onClick={handleDownloadProfessionalPDF} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-blue-400 shadow-lg">
          {icons.download} <span className="ml-2">📥 Download PDF (Professional)</span>
        </button>

        <button onClick={handleDownloadSpecimen} className="w-full bg-[#f9fafb] hover:bg-gray-100 text-[#374151] font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-[#d1d5db]">
          {icons.download} <span className="ml-2">Download Specimen</span>
        </button>

        {/* 🔥 FIX 23: Charter Party DOCX Button */}
        <button onClick={handleDownloadCharterParty} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-indigo-500">
          {icons.fileText} <span className="ml-2">📄 Charter Party (DOCX)</span>
        </button>

        {/* 🔥 FIX 27: Crew List DOCX Button */}
        <button onClick={handleDownloadCrewList} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-green-500">
          {icons.fileText} <span className="ml-2">👥 Crew List (DOCX)</span>
        </button>

        {/* 📋 ΑΑΔΕ JSON Export */}
        <button onClick={openAadePreview} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-amber-500">
          {icons.fileText} <span className="ml-2">📋 Export ΑΑΔΕ (JSON)</span>
        </button>

        {/* 📄 ΑΑΔΕ PDF — fill ΛΟΙΠΟΙ ΟΡΟΙ box with Terms & Conditions */}
        <button onClick={() => { setAadePdfFile(null); setShowAadePdfModal(true); }} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-teal-500">
          {icons.fileText} <span className="ml-2">📄 Συμπλήρωση PDF ΑΑΔΕ με Όρους</span>
        </button>

        {/* 📄 Επεξεργασία Charter Agreement — ανοίγει τη σελίδα ΜΕ τον τρέχοντα ναύλο φορτωμένο */}
        <button onClick={handleOpenCharterAgreement} className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-rose-500">
          {icons.fileText} <span className="ml-2">📄 Επεξεργασία Charter Agreement</span>
        </button>

        {/* 📥 Εισαγωγή Crew από Excel / PDF (Booking Manager) */}
        <button onClick={handleXlsxOpen} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg mb-3 flex items-center justify-center border border-purple-500">
          {icons.fileText} <span className="ml-2">📥 Εισαγωγή Crew από Excel ή PDF</span>
        </button>

        {/* 👥 Πλήρωμα — inline edit/delete */}
        <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold text-[#1e40af]">👥 Πλήρωμα & Επιβάτες ({crewEdit.length + (derivedSkipper ? 1 : 0)})</h3>
            <button
              onClick={handleSaveCrew}
              disabled={crewSaving || !canEditCharters}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-lg text-sm disabled:opacity-50"
            >{crewSaving ? 'Αποθήκευση...' : '💾 Αποθήκευση πληρώματος'}</button>
          </div>

          {(crewEdit.length === 0 && !derivedSkipper) ? (
            <p className="text-sm text-gray-500 italic">Δεν υπάρχει καταχωρημένο πλήρωμα. Χρησιμοποίησε την Εισαγωγή Excel/PDF παραπάνω ή προσθήκη μέσω online check-in.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-1 py-1 text-left">Όνομα</th>
                    <th className="px-1 py-1 text-left">Passport</th>
                    <th className="px-1 py-1 text-left">Γέννηση</th>
                    <th className="px-1 py-1 text-left">Φύλο</th>
                    <th className="px-1 py-1 text-left">Εθνικότητα</th>
                    <th className="px-1 py-1 text-left">Ρόλος</th>
                    <th className="px-1 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {derivedSkipper && (
                    <tr className="border-t border-gray-200 bg-blue-50">
                      <td className="px-1 py-1 font-semibold">{derivedSkipper.name || '—'}</td>
                      {editingSkipper ? (<>
                        <td className="px-1 py-1"><input type="text" value={skipperRowEdit.passport ?? ''} onChange={(e) => setSkipperRowEdit((p: any) => ({ ...p, passport: e.target.value }))} className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full font-mono" placeholder="Passport" /></td>
                        <td className="px-1 py-1"><input type="date" value={skipperRowEdit.dateOfBirth ?? ''} onChange={(e) => setSkipperRowEdit((p: any) => ({ ...p, dateOfBirth: e.target.value }))} className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full" /></td>
                        <td className="px-1 py-1"><select value={skipperRowEdit.gender ?? ''} onChange={(e) => setSkipperRowEdit((p: any) => ({ ...p, gender: e.target.value }))} className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full"><option value="">—</option><option value="Male">Male</option><option value="Female">Female</option></select></td>
                        <td className="px-1 py-1"><CountrySelect value={skipperRowEdit.nationality ?? ''} onChange={(v) => setSkipperRowEdit((p: any) => ({ ...p, nationality: v }))} placeholder="Nationality" className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full" /></td>
                      </>) : (<>
                        <td className="px-1 py-1 font-mono">{derivedSkipper.passport || '—'}</td>
                        <td className="px-1 py-1">{formatDateEU(derivedSkipper.dateOfBirth) || '—'}</td>
                        <td className="px-1 py-1">{derivedSkipper.gender || '—'}</td>
                        <td className="px-1 py-1">{derivedSkipper.nationality || '—'}</td>
                      </>)}
                      <td className="px-1 py-1"><span className="text-[#1e40af] font-semibold">Πλοίαρχος</span></td>
                      <td className="px-1 py-1 text-center whitespace-nowrap">
                        {editingSkipper ? (
                          <>
                            <button onClick={handleSaveSkipperRow} disabled={skipperSaving || !canEditCharters} className="text-green-600 hover:text-green-800 disabled:opacity-30 mr-1" title="Αποθήκευση">{skipperSaving ? '…' : '💾'}</button>
                            <button onClick={() => setEditingSkipper(false)} disabled={skipperSaving} className="text-gray-500 hover:text-gray-700" title="Άκυρο">✕</button>
                          </>
                        ) : (
                          <button onClick={() => { setSkipperRowEdit({ passport: derivedSkipper.passport || '', dateOfBirth: derivedSkipper.dateOfBirth || '', gender: derivedSkipper.gender || '', nationality: derivedSkipper.nationality || '' }); setEditingSkipper(true); }} disabled={!canEditCharters} className="text-blue-600 hover:text-blue-800 disabled:opacity-30" title="Επεξεργασία Πλοίαρχου">✏️</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {crewEdit.map((m: any, idx: number) => (
                    <tr key={idx} className="border-t border-gray-200">
                      <td className="px-1 py-1">{m?.name || `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || '—'}</td>
                      <td className="px-1 py-1 font-mono">{m?.passport || '—'}</td>
                      <td className="px-1 py-1">{formatDateEU(m?.dateOfBirth) || '—'}</td>
                      <td className="px-1 py-1">
                        <select
                          value={m?.gender || ''}
                          disabled={!canEditCharters}
                          onChange={(e) => updateCrewField(idx, 'gender', e.target.value)}
                          className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full disabled:opacity-50"
                        >
                          <option value="">—</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={m?.nationality || ''}
                          disabled={!canEditCharters}
                          onChange={(e) => updateCrewField(idx, 'nationality', e.target.value)}
                          className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full disabled:opacity-50"
                          placeholder="π.χ. German"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <select
                          value={String(m?.role || 'passenger').toLowerCase()}
                          disabled={!canEditCharters}
                          onChange={(e) => updateCrewField(idx, 'role', e.target.value)}
                          className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full disabled:opacity-50"
                        >
                          <option value="skipper">Πλοίαρχος</option>
                          <option value="co-skipper">Συγκυβερνήτης</option>
                          <option value="passenger">Επιβάτης</option>
                          <option value="crew">Πλήρωμα</option>
                        </select>
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button
                          onClick={() => { if (canEditCharters && window.confirm('Διαγραφή του μέλους "' + (m?.name || m?.passport || '') + '";')) removeCrewRow(idx); }}
                          disabled={!canEditCharters}
                          className="text-red-600 hover:text-red-800 disabled:opacity-30"
                          title="Διαγραφή"
                        >🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">⚠️ Οι αλλαγές αποθηκεύονται μόνο όταν πατήσεις «💾 Αποθήκευση πληρώματος».</p>
            </div>
          )}
        </div>

        {canEditCharters && canViewFinancials && (
          <div className="bg-[#f3f4f6] p-4 rounded-lg mb-4 border border-[#d1d5db]">
            <h3 className="text-xl font-bold text-yellow-400 mb-3">Διαχείριση Πληρωμών ({payments.length} καταχωρήσεις)</h3>
            <div className="space-y-2 text-base mb-4">
              <div className="flex justify-between"><span className="text-[#374151]">Σύνολο Πληρωμένο:</span><span className="font-bold text-green-400">{totalPaid.toFixed(2)}€</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">Υπόλοιπο:</span><span className="font-bold text-red-400">{balance.toFixed(2)}€</span></div>
            </div>
            {payments.length === 0 && (
              <div className="text-gray-500 text-sm mb-3 text-center italic">Δεν υπάρχουν καταχωρημένες πληρωμές</div>
            )}
            <div className="space-y-2 mb-3">
              {payments.map((p, index) => (
                <div key={index} className="flex justify-between items-center bg-[#f9fafb] p-2 rounded border border-[#d1d5db]">
                  <span className="text-sm">{new Date(p.date + 'T00:00:00').toLocaleDateString('en-GB')}</span>
                  <span className="text-sm font-semibold">{p.amount.toFixed(2)}€</span>
                  <button onClick={() => removePayment(index)} className="text-red-500 hover:text-red-400">{icons.x}</button>
                </div>
              ))}
            </div>
            <div className="flex space-x-2 mb-3">
              <input type="date" value={newPayDate} onChange={(e) => { console.log('📅 Date changed:', e.target.value); setNewPayDate(e.target.value); }} className="w-1/2 px-2 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
              <input type="number" step="0.01" value={newPayAmount} onChange={(e) => { console.log('💵 Amount changed:', e.target.value); setNewPayAmount(e.target.value); }} placeholder="Ποσό" className="w-1/2 px-2 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
            </div>
            <button
              type="button"
              onClick={() => {
                console.log('🔴 CLICK WORKS!');
                console.log('🔴 newPayDate:', newPayDate, 'newPayAmount:', newPayAmount);
                if (!newPayDate || !newPayAmount) {
                  console.log('🔴 Missing date or amount - NOT adding payment');
                  return;
                }
                const amt = parseFloat(newPayAmount);
                if (isNaN(amt) || amt <= 0) {
                  console.log('🔴 Invalid amount - NOT adding payment');
                  return;
                }
                const newPayment = { date: newPayDate, amount: amt };
                console.log('🔴 Creating new payment:', newPayment);
                // Use functional update to ensure we don't lose data
                setPayments(prevPayments => {
                  console.log('🔴 prevPayments:', prevPayments);
                  const updatedPayments = [...prevPayments, newPayment];
                  console.log('🔴 updatedPayments:', updatedPayments);
                  return updatedPayments;
                });
                // Clear inputs after adding
                setNewPayDate('');
                setNewPayAmount('');
                console.log('🔴 Payment added successfully!');
              }}
              style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 20px', cursor: 'pointer', width: '100%', borderRadius: '8px', fontWeight: 'bold', marginBottom: '12px', border: 'none' }}
            >
              Προσθήκη Πληρωμής
            </button>
            <button type="button" onClick={savePayments} disabled={isProcessing} className="w-full bg-[#1e40af] hover:bg-blue-800 disabled:bg-blue-300 text-white font-bold py-3 px-4 rounded-lg">
              {isProcessing ? '⏳ Αποθήκευση...' : 'Αποθήκευση Πληρωμών'}
            </button>
          </div>
        )}

        {/* Edit button moved to top of modal */}

        {canEditCharters && (
          <button onClick={async () => {
            const bookingCode = charter.code || charter.id;
            console.log('🗑️ DELETE BUTTON CLICKED - bookingCode:', bookingCode);
            if (!bookingCode) {
              console.error('❌ No booking code found!');
              return;
            }
            if (!window.confirm(`Οριστική διαγραφή του ναύλου ${bookingCode};`)) {
              console.log('❌ Delete cancelled by user');
              return;
            }
            try {
              console.log('🗑️ Calling deleteBooking API...');
              await deleteBooking(bookingCode);
              console.log('✅ API delete successful');
            } catch (error) {
              console.warn('⚠️ API delete failed:', error);
            }
            // Update local state via onDelete
            onDelete(bookingCode);
          }} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center">
            {icons.x} <span className="ml-2">🗑️ Οριστική Διαγραφή</span>
          </button>
        )}
        {/* ΕΠΑΝΑΦΟΡΑ CHECK-IN/OUT (10/10/2026) — ΜΟΝΟ admin, ΧΩΡΙΣ κωδικό. ΕΝΑ κουμπί → παράθυρο. */}
        {authService.isAdmin() && (
          <button type="button" onClick={() => { setCiResetOpen(true); ciLoadBackups(); }}
                  className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center">
            ↺ Επαναφορά check-in/out
          </button>
        )}
        {ciResetOpen && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4" onClick={() => setCiResetOpen(false)}>
            <div className="bg-white rounded-xl p-5 max-w-lg w-full max-h-[85vh] overflow-y-auto text-[#374151]" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold">Επαναφορά check-in/out — {ciCode()}</h3>
                <button onClick={() => setCiResetOpen(false)} className="text-gray-500 text-2xl">&times;</button>
              </div>
              <div className="mb-4">
                <div className="font-semibold mb-2 text-sm">Μηδένισε από την αρχή</div>
                <div className="flex gap-2">
                  <button disabled={ciBusy} onClick={() => ciReset('in')} className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold py-2 px-3 rounded-lg text-sm">↺ Check-in από την αρχή</button>
                  <button disabled={ciBusy} onClick={() => ciReset('out')} className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold py-2 px-3 rounded-lg text-sm">↺ Check-out από την αρχή</button>
                </div>
              </div>
              <div>
                <div className="font-semibold mb-2 text-sm">Επαναφορά από backup</div>
                {ciBackups.length === 0 ? (
                  <div className="text-gray-400 text-sm py-2">Κανένα backup ακόμη.</div>
                ) : (
                  <div className="space-y-2">
                    {ciBackups.map((bk) => (
                      <div key={bk.id} className="flex justify-between items-center border border-gray-200 rounded-lg p-2">
                        <div className="text-sm">
                          <span className="font-mono">{new Date(bk.created_at).toLocaleString('el-GR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                          <span className="text-gray-500"> · {bk.by_code || '—'} · {bk.mode === 'out' ? 'check-out' : 'check-in'}</span>
                        </div>
                        <button disabled={ciBusy} onClick={() => ciRestore(bk)} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-1 px-3 rounded text-sm">Επαναφορά</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ⚠️ ΑΑΔΕ pre-export preview modal (nested overlay) */}
      {showAadePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => !aadeExporting && setShowAadePreview(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 overflow-y-auto max-h-[90vh] border border-[#d1d5db]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-700">⚠️ Έλεγχος πριν την υποβολή στην ΑΑΔΕ</h2>
              <button onClick={() => !aadeExporting && setShowAadePreview(false)} className="text-[#6b7280] hover:text-black">{icons.x}</button>
            </div>

            {/* Section 1 — Στοιχεία ναύλου */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-gray-800">
              <h3 className="font-bold text-[#1e40af] mb-2">Στοιχεία ναύλου</h3>
              <div className="flex justify-between"><span>Ναύλος:</span><span className="font-semibold">{charter.code} - {boat?.name || ''}</span></div>
              <div className="flex justify-between"><span>Διάρκεια:</span><span className="font-semibold">{charter.startDate} {charter.startTime} → {charter.endDate} {charter.endTime}</span></div>
              <div className="flex justify-between"><span>Λιμάνια:</span><span className="font-semibold">{charter.departure || '-'} → {charter.arrival || '-'}</span></div>
              <div className="flex justify-between"><span>Πλοίαρχος:</span><span className="font-semibold">{aadeSkipperName}</span></div>
              <div className="flex justify-between"><span>Ναυλωτής:</span><span className="font-semibold">{[charter.chartererFirstName, charter.chartererLastName].filter(Boolean).join(' ') || '-'}</span></div>
            </div>

            {/* Section 2 — Πλήρωμα / Επιβάτες */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-gray-800">
              <h3 className="font-bold text-[#1e40af] mb-2">Πλήρωμα / Επιβάτες</h3>
              <div>✓ {cntSkipper} Πλοίαρχος(οι)</div>
              <div>✓ {cntCoSkipper} Συγκυβερνήτης(τες)</div>
              <div>✓ {cntPassenger} Επιβάτης(ες)</div>
              <div>✓ {cntCrew} Πλήρωμα</div>
              <div className="font-bold mt-1">Σύνολο: {aadeTotalPeople} άτομα</div>
              {aadeUnknownNat.length > 0 && (
                <div className="mt-2 bg-amber-50 border border-amber-300 text-amber-800 rounded p-2 text-xs">
                  ⚠ {aadeUnknownNat.length} μέλη με μη αναγνωρίσιμη/κενή εθνικότητα → θα σταλούν ως «XXX».
                  Διόρθωσε πριν το export: {aadeUnknownNat.map((m: any) => (m?.name || m?.full_name || '—') + ' (' + (String(m?.nationality || '').trim() || 'κενό') + ')').join(', ')}
                </div>
              )}
            </div>

            {/* Section 3 — Ναυλομεσίτες */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-gray-800">
              <h3 className="font-bold text-[#1e40af] mb-2">Ναυλομεσίτες</h3>

              {/* A.1 — TAILWIND Ι.Κ.Ε. */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeTailwind} onChange={(e) => setIncludeTailwind(e.target.checked)} className="w-4 h-4 accent-amber-600" />
                <span>Προσθήκη TAILWIND ΜΟΝΟΠΡΟΣΩΠΗ Ι.Κ.Ε. στο JSON</span>
              </label>
              {includeTailwind && (
                <div className="ml-6 mt-1 mb-2 text-xs text-gray-700 bg-white border border-gray-200 rounded p-2 space-y-0.5">
                  <div><span className="text-gray-500">Επωνυμία:</span> <span className="font-semibold">{tailwindInfo?.name || 'TAILWIND ΜΟΝΟΠΡΟΣΩΠΗ Ι.Κ.Ε.'}</span></div>
                  <div><span className="text-gray-500">ΑΦΜ:</span> {tailwindInfo?.vat || '-'}</div>
                  <div><span className="text-gray-500">ΔΟΥ:</span> {tailwindInfo?.doy || '-'}</div>
                  <div><span className="text-gray-500">Διεύθυνση:</span> {[tailwindInfo?.address, tailwindInfo?.city].filter(Boolean).join(', ') || '-'}</div>
                  <div><span className="text-gray-500">Email:</span> {tailwindInfo?.email_accounting || '-'}</div>
                  <div><span className="text-gray-500">Τηλέφωνο:</span> {tailwindInfo?.phone || '-'}</div>
                </div>
              )}

              {/* A.2 — Ξένος Πράκτορας */}
              <label className="flex items-center gap-2 cursor-pointer mt-2">
                <input type="checkbox" checked={includeForeignBroker} onChange={(e) => { setIncludeForeignBroker(e.target.checked); setFbError(''); }} className="w-4 h-4 accent-orange-600" />
                <span>Προσθήκη Ξένου Πράκτορα στο JSON</span>
              </label>
              {/* ═══ ΠΑΝΤΑ ΕΠΕΞΕΡΓΑΣΙΜΑ (19/09/2026) ═════════════════════════════
                  Όταν ο αποθηκευμένος πράκτορας είχε ΜΟΝΟ επωνυμία (έτσι έχουν
                  σωθεί οι περισσότεροι: name + percent, τα υπόλοιπα κενά), το
                  κουτί ήταν read-only: ο υπάλληλος έβλεπε «ΑΦΜ: -», δεν είχε πού
                  να το γράψει, και το JSON έπαιρνε τον πράκτορα ΧΩΡΙΣ στοιχεία.
                  Τώρα τα πεδία είναι πάντα ανοιχτά, προσυμπληρωμένα με ό,τι
                  υπάρχει· ό,τι φαίνεται εδώ είναι ό,τι θα μπει στο JSON. */}
              {includeForeignBroker && (
                  <div className="ml-6 mt-1 mb-1 space-y-2 bg-white border border-orange-200 rounded p-2">
                    <div className="text-xs text-orange-700">
                      {fbEnabled ? 'Στοιχεία ξένου πράκτορα (συμπλήρωσε ό,τι λείπει):'
                                 : 'Συμπλήρωσε τα στοιχεία ξένου πράκτορα:'}
                    </div>
                    <input value={fbForm.name} onChange={(e) => setFbField('name', e.target.value)} placeholder="Επωνυμία *" className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={fbForm.vat} onChange={(e) => setFbField('vat', e.target.value)} placeholder="ΑΦΜ" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                      <input value={fbForm.doy} onChange={(e) => setFbField('doy', e.target.value)} placeholder="ΔΟΥ" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={fbForm.country} onChange={(e) => setFbField('country', e.target.value)} placeholder="Χώρα" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                      <input value={fbForm.address} onChange={(e) => setFbField('address', e.target.value)} placeholder="Διεύθυνση" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={fbForm.email} onChange={(e) => setFbField('email', e.target.value)} placeholder="Email" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                      <input value={fbForm.phone} onChange={(e) => setFbField('phone', e.target.value)} placeholder="Τηλέφωνο" className="px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                    {fbEnabled && !String(fbForm.vat || '').trim() && (
                      <div className="text-[11px] text-amber-700">
                        Ο πράκτορας «{fbPreview?.name}» είναι αποθηκευμένος χωρίς ΑΦΜ — συμπλήρωσέ το για να μπει στο JSON.
                      </div>
                    )}
                  </div>
              )}
              {fbError && <div className="ml-6 mt-1 text-red-600 font-semibold">⛔ {fbError}</div>}

              {!includeTailwind && !includeForeignBroker && (
                <div className="mt-2 text-amber-700">⚠️ Κανείς ναυλομεσίτης δεν θα εμφανιστεί στο ΑΑΔΕ</div>
              )}
            </div>

            {/* Section 4 — Warning */}
            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-3 mb-4 text-sm text-orange-800">
              📌 <span className="font-bold">ΠΡΟΣΟΧΗ:</span> Μετά την Ολοκλήρωση στην ΑΑΔΕ:
              <ul className="list-disc ml-5 mt-1">
                <li>Δεν μπορείτε να αλλάξετε ρόλο μέλους</li>
                <li>Μπορείτε ΜΟΝΟ να αφαιρέσετε μέλος (όχι να προσθέσετε)</li>
                <li>Ελέγξτε ΤΩΡΑ: ρόλους πληρώματος, ναυλομεσίτες, στοιχεία</li>
              </ul>
            </div>

            {/* Section 5 — Buttons */}
            <div className="flex gap-3">
              <button onClick={() => !aadeExporting && setShowAadePreview(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded-lg">Άκυρο</button>
              <button onClick={handleExportAade} disabled={aadeExporting} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-bold py-3 px-4 rounded-lg">
                {aadeExporting ? '...' : '✓ Κατέβασμα JSON'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📄 ΑΑΔΕ PDF terms-fill modal (nested overlay) */}
      {showAadePdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => !aadePdfBusy && setShowAadePdfModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 border border-[#d1d5db]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-teal-700">📄 Συμπλήρωση PDF ΑΑΔΕ με Όρους</h2>
              <button onClick={() => !aadePdfBusy && setShowAadePdfModal(false)} className="text-[#6b7280] hover:text-black">{icons.x}</button>
            </div>
            <p className="text-sm text-gray-600 mb-3">Ανέβασε το κενό PDF ΑΑΔΕ. Το σύστημα γεμίζει αυτόματα το πλαίσιο «ΛΟΙΠΟΙ ΟΡΟΙ / SPECIAL CONDITIONS & TERMS» με τους όρους του Charter Party και επιστρέφει το συμπληρωμένο PDF.</p>
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setAadePdfFile(e.target.files?.[0] || null)} className="w-full mb-3 text-sm" />
            {aadePdfFile && <div className="text-xs text-gray-500 mb-3">Επιλέχθηκε: {aadePdfFile.name}</div>}
            <div className="flex gap-3">
              <button onClick={() => !aadePdfBusy && setShowAadePdfModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded-lg">Άκυρο</button>
              <button onClick={handleFillAadePdf} disabled={aadePdfBusy || !aadePdfFile} className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold py-3 px-4 rounded-lg">
                {aadePdfBusy ? '...' : '✓ Συμπλήρωση & Λήψη'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📥 XLSX Import Preview Modal (nested overlay) */}
      {xlsxModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => !xlsxImporting && setXlsxModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 overflow-y-auto max-h-[90vh] border border-[#d1d5db]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-purple-700">📥 Εισαγωγή Crew από Excel ή PDF</h2>
              <button onClick={() => !xlsxImporting && !xlsxParsing && setXlsxModalOpen(false)} className="text-[#6b7280] hover:text-black">{icons.x}</button>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-semibold text-[#374151] mb-1">Αριθμός Ναύλου (booking_number)</label>
              <input
                type="text"
                value={xlsxBookingNumber}
                onChange={(e) => setXlsxBookingNumber(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none font-mono"
                placeholder="π.χ. CHARTER PARTY NO 89"
              />
              <p className="text-xs text-gray-500 mt-1">Προσυμπληρωμένο από τον τρέχοντα ναύλο. Μπορείς να το αλλάξεις πριν τη μεταφορά.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1">📊 Excel (.xlsx)</label>
                <input
                  ref={xlsxFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={handleXlsxFile}
                  disabled={xlsxParsing || xlsxImporting}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1">📄 PDF (.pdf)</label>
                <input
                  ref={pdfFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfFile}
                  disabled={xlsxParsing || xlsxImporting}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">📄 Word</label>
                <input
                  ref={wordFileInputRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleWordFile}
                  disabled={xlsxParsing || xlsxImporting}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg disabled:opacity-50"
                />
              </div>
            </div>

            {xlsxParsing && (
              <div className="mb-3 p-3 rounded-lg bg-blue-100 text-blue-800 border border-blue-300 text-sm animate-pulse">⏳ Εξαγωγή κειμένου από PDF (μπορεί να πάρει λίγο αν χρειαστεί OCR)...</div>
            )}

            {(xlsxSource === 'pdf' || xlsxSource === 'word') && xlsxParsedCrew.length > 0 && (
              <div className="mb-3 p-3 rounded-lg bg-yellow-100 text-yellow-900 border border-yellow-400 text-sm font-semibold">⚠️ Τα στοιχεία από PDF μπορεί να χρειάζονται έλεγχο/διόρθωση. Επιβεβαίωσε ονόματα, passport, ημερομηνίες πριν τη μεταφορά.</div>
            )}

            {crewUnverified.length > 0 && (
              <div className="mb-3 p-3 rounded-lg bg-red-100 text-red-800 border border-red-300 text-sm">
                <b>{crewUnverified.length} άτομα ΔΕΝ επαληθεύτηκαν στο αρχείο και ΔΕΝ συμπληρώθηκαν:</b>
                <ul className="mt-1 ml-4 list-disc">
                  {crewUnverified.map((u: any, i: number) => (
                    <li key={i}>
                      {(u.name || `${u.firstName || ''} ${u.lastName || ''}`).trim() || '(χωρίς όνομα)'}
                      {u.passport ? ` · ${u.passport}` : ''} — {u.reason}
                    </li>
                  ))}
                </ul>
                <div className="mt-1 text-xs">Συμπλήρωσέ τα χειροκίνητα αφού τα ελέγξεις στο πρωτότυπο.</div>
              </div>
            )}

            {xlsxError && (
              <div className="mb-3 p-3 rounded-lg bg-red-100 text-red-800 border border-red-300 text-sm">{xlsxError}</div>
            )}

            {/* ═══ 6.480 ═══ Τι διάβασε ο parser ΚΑΙ τι ΔΕΝ διάβασε. */}
            {xlsxNotes && (
              <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-300 text-xs text-slate-700">
                <div className="font-bold text-slate-800 mb-1">Ανάγνωση αρχείου</div>
                <div>Φύλλο: <span className="font-mono">{xlsxNotes.sheetName}</span>
                     {xlsxNotes.headerRow ? <> · επικεφαλίδες: γραμμή <span className="font-mono">{xlsxNotes.headerRow}</span></> : null}</div>
                {xlsxNotes.mappedColumns && Object.keys(xlsxNotes.mappedColumns).length > 0 && (
                  <div className="mt-1">Στήλες: {Object.entries(xlsxNotes.mappedColumns)
                    .map(([k, v]: any) => `${k}→${v}`).join(' · ')}</div>
                )}
                {xlsxNotes.charterer && (
                  <div className="mt-1">Ναυλωτής: <span className="font-semibold">{xlsxNotes.charterer.name || '—'}</span>
                    {xlsxNotes.charterer.phone ? ` · ${xlsxNotes.charterer.phone}` : ''}
                    {xlsxNotes.sameAsSkipper ? ' · ίδιος με τον σκίπερ' : ''}</div>
                )}
                {Array.isArray(xlsxNotes.skipped) && xlsxNotes.skipped.length > 0 && (
                  <div className="mt-2 text-amber-800">
                    <span className="font-bold">Δεν διαβάστηκαν ({xlsxNotes.skipped.length}):</span>
                    <ul className="list-disc ml-5 mt-1">
                      {xlsxNotes.skipped.map((sk: any, i: number) => (
                        <li key={i}>γρ. {sk.row} — {sk.reason}{sk.raw ? `: «${sk.raw}»` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {Array.isArray(xlsxNotes.warnings) && xlsxNotes.warnings.map((w: string, i: number) => (
                  <div key={i} className="mt-1 text-amber-800">⚠ {w}</div>
                ))}
                {xlsxWorkbookText && (
                  <button
                    type="button"
                    onClick={retryXlsxWithAI}
                    disabled={xlsxParsing || xlsxImporting}
                    className="mt-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
                  >
                    {xlsxParsing ? 'Ανάλυση…' : 'Ξαναδοκίμασε με AI'}
                  </button>
                )}
              </div>
            )}

            {xlsxParsedCrew.length > 0 && (
              <div className="mb-3">
                <h3 className="font-bold text-[#1e40af] mb-2">Προεπισκόπηση ({xlsxParsedCrew.length} εγγραφές{xlsxSource ? ' — πηγή: ' + (xlsxSource === 'pdf' ? 'PDF' : xlsxSource === 'word' ? 'Word' : xlsxSource === 'excel-ai' ? 'Excel + AI' : 'Excel') : ''})</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 py-1 text-left">Όνομα</th>
                        <th className="px-2 py-1 text-left">Passport</th>
                        <th className="px-2 py-1 text-left">Γέννηση</th>
                        <th className="px-2 py-1 text-left">Φύλο</th>
                        <th className="px-2 py-1 text-left">Εθνικότητα</th>
                        <th className="px-2 py-1 text-left">Ρόλος</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xlsxParsedCrew.map((m: any, i: number) => (
                        <tr key={i} className="border-t border-gray-200">
                          <td className="px-2 py-1">{m.name}</td>
                          <td className={'px-2 py-1 font-mono ' + (!m.passport ? 'bg-red-100 text-red-700 font-semibold' : '')}>{m.passport || '— ΛΕΙΠΕΙ —'}</td>
                          <td className="px-2 py-1">{formatDateEU(m.dateOfBirth)}</td>
                          <td className={'px-2 py-1 ' + (!m.gender ? 'bg-red-100 text-red-700 font-semibold' : '')}>{m.gender || '— ΛΕΙΠΕΙ —'}</td>
                          <td className={'px-2 py-1 ' + (!m.nationality ? 'bg-red-100 text-red-700 font-semibold' : '')}>{m.nationality || '— ΛΕΙΠΕΙ —'}</td>
                          <td className="px-2 py-1">{m.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">⚠️ Κόκκινα κελιά = ελλιπή στοιχεία. Θα εισαχθούν ως έχουν, αλλά μπορούν να μπλοκάρουν το ΑΑΔΕ export.</p>
              </div>
            )}

            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => !xlsxImporting && !xlsxParsing && setXlsxModalOpen(false)}
                disabled={xlsxImporting || xlsxParsing}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-4 rounded-lg disabled:opacity-50"
              >Ακύρωση</button>
              <button
                onClick={handleXlsxImport}
                disabled={xlsxImporting || xlsxParsing || xlsxParsedCrew.length === 0 || !xlsxBookingNumber.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg disabled:opacity-50"
              >{xlsxImporting ? 'Μεταφορά...' : `📥 Μεταφορά στον ναύλο (${xlsxParsedCrew.length})`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FinancialsPage({ boat, navigate, setPage, setSelectedCategory, showMessage }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [charters, setCharters] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const isOwnerUser = authService.isOwner();
  const canViewFinancials = authService.canViewFinancials() || isOwnerUser;
  const canEditFinancials = authService.canEditFinancials() && !isOwnerUser;

  // 🔥 FIX 4: Use optional chaining in dependencies
  useEffect(() => {
    if (boat && canViewFinancials) {
      loadData();
    }
  }, [boat?.id, canViewFinancials]);

  // 🔥 NEW: Listen for global refresh events (every 3 minutes) - SYNC INVOICES
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (boat && canViewFinancials) {
        console.log('🔄 FinancialsPage: Global refresh received, syncing invoices...');
        loadData();
      }
    };

    window.addEventListener('globalBookingsRefreshed', handleGlobalRefresh);
    return () => window.removeEventListener('globalBookingsRefreshed', handleGlobalRefresh);
  }, [boat?.id, canViewFinancials]);

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  // 🔥 API-first: Load data from API, migrate localStorage on first load
  const loadData = async () => {
    try {
      // Load charters from API ONLY
      let chartersData: any[] = [];
      try {
        chartersData = await getBookingsByVessel(boat.id);
        console.log(`✅ FinancialsPage: Loaded ${chartersData.length} charters from API`);
      } catch (apiError) {
        console.error(`❌ API failed, no charters loaded`);
        chartersData = [];
      }

      // Sort charters by date
      chartersData.sort((a: any, b: any) => (b.startDate && a.startDate) ? new Date(b.startDate).getTime() - new Date(a.startDate).getTime() : 0);
      setCharters(chartersData);

      // 🔥 Migrate localStorage invoices to API (one-time migration)
      await migrateInvoicesFromLocalStorage(boat.id, boat.name || String(boat.id));

      // 🔥 Load invoices from API first
      let invoicesData: any[] = [];
      try {
        invoicesData = await getInvoicesByVessel(boat.id);
        console.log(`✅ FinancialsPage: Loaded ${invoicesData.length} invoices from API`);
      } catch (apiError) {
        console.error(`❌ API failed for invoices, falling back to localStorage`);
        // Fallback to localStorage if API fails
        const invoicesKey = `fleet_${boat.id}_ΤΙΜΟΛΟΓΙΑ`;
        const invoicesStored = localStorage.getItem(invoicesKey);
        if (invoicesStored) {
          invoicesData = JSON.parse(invoicesStored);
        }
      }

      // Also check for any pending_sync items in localStorage
      const pendingSyncKey = `fleet_${boat.id}_ΤΙΜΟΛΟΓΙΑ_pending`;
      const pendingStored = localStorage.getItem(pendingSyncKey);
      if (pendingStored) {
        try {
          const pendingItems = JSON.parse(pendingStored);
          // Merge pending items (they should be uploaded to API)
          for (const pendingInvoice of pendingItems) {
            const exists = invoicesData.some((inv: any) => inv.id === pendingInvoice.id || inv.invoiceCode === pendingInvoice.invoiceCode);
            if (!exists) {
              invoicesData.push(pendingInvoice);
              // Try to upload to API
              try {
                await saveInvoice({
                  invoiceCode: pendingInvoice.invoiceCode || pendingInvoice.id,
                  vesselId: boat.id,
                  vesselName: boat.name || String(boat.id),
                  invoiceNumber: pendingInvoice.code,
                  description: pendingInvoice.description,
                  amount: pendingInvoice.amount || 0,
                  invoiceDate: pendingInvoice.date,
                  createdBy: pendingInvoice.createdBy
                });
                console.log(`✅ Synced pending invoice: ${pendingInvoice.code}`);
              } catch (syncError) {
                console.warn(`⚠️ Failed to sync pending invoice: ${pendingInvoice.code}`);
              }
            }
          }
          // Clear pending after successful sync attempt
          localStorage.removeItem(pendingSyncKey);
        } catch (e) {
          console.error('Error processing pending invoices:', e);
        }
      }

      // Sort invoices by date
      invoicesData.sort((a: any, b: any) => {
        const dateA = a.date || a.invoiceDate || a.createdAt;
        const dateB = b.date || b.invoiceDate || b.createdAt;
        return (dateB && dateA) ? new Date(dateB).getTime() - new Date(dateA).getTime() : 0;
      });
      setInvoices(invoicesData);

      authService.logActivity('view_financials', boat.id);
      setLoading(false);
    } catch (e) {
      console.error('Error loading financials:', e);
      setLoading(false);
    }
  };

  const totalIncome = charters.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalCharterExpenses = charters.reduce((sum, c) => sum + (c.commission || 0) + (c.vat_on_commission || 0), 0);
  const totalInvoiceExpenses = invoices.reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalExpenses = totalCharterExpenses + totalInvoiceExpenses;
  const netResult = totalIncome - totalExpenses;

  if (loading) return (
    <div className="flex flex-col h-screen w-screen bg-[#f3f4f6] fixed inset-0 z-50">
      <div className="bg-white p-2 flex items-center justify-between border-b border-[#d1d5db]">
        <button onClick={() => navigate('boatDashboard')} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded-lg">{icons.chevronLeft}</button>
        <h1 className="text-lg font-bold text-white">Οικονομικά - {boat.id}</h1>
        <div className="w-10"></div>
      </div>
      <FullScreenLoader />
    </div>
  );

  return (
    <div className="flex flex-col h-screen w-screen bg-[#f3f4f6] fixed inset-0 z-50">
      <div className="bg-white p-2 flex items-center justify-between border-b border-[#d1d5db]">
        <button onClick={() => navigate('boatDashboard')} className="text-[#1e40af] p-2 hover:bg-[#f9fafb] rounded-lg">{icons.chevronLeft}</button>
        <h1 className="text-lg font-bold text-white">Οικονομικά - {boat.id}</h1>
        <div className="w-10"></div>
      </div>

      {isOwnerUser && (
        <div className="p-2 bg-blue-900 border-b border-blue-700 text-center">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm">{icons.eye}<span>View Only Mode</span></div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 p-2 bg-white border-b border-[#d1d5db]">
        <div className="bg-gradient-to-br from-green-700 to-green-800 p-3 rounded-lg text-center">
          <div className="text-xs text-green-200">ΕΣΟΔΑ</div>
          <div className="text-lg font-bold text-white">{totalIncome.toFixed(0)}€</div>
        </div>
        <div className="bg-gradient-to-br from-red-700 to-red-800 p-3 rounded-lg text-center">
          <div className="text-xs text-red-200">ΕΞΟΔΑ</div>
          <div className="text-lg font-bold text-white">{totalExpenses.toFixed(0)}€</div>
        </div>
        <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-3 rounded-lg text-center">
          <div className="text-xs text-[#374151]">ΚΑΘΑΡΟ</div>
          <div className={`text-lg font-bold ${netResult >= 0 ? 'text-green-400' : 'text-red-400'}`}>{netResult.toFixed(0)}€</div>
        </div>
      </div>
      
      <div className="flex-grow p-3 overflow-y-auto">
        <div className="mb-6">
           <h3 className="text-xl font-semibold mb-3 text-[#1e40af]">Έξοδα Σκάφους (Τιμολόγια)</h3>
           <InvoiceSection boatId={boat.id} canEditFinancials={canEditFinancials} showMessage={showMessage} invoices={invoices} setInvoices={setInvoices} isOwnerUser={isOwnerUser} />
        </div>

        {!isOwnerUser && canEditFinancials && (
          <div>
            <h3 className="text-xl font-semibold mb-3 text-[#1e40af]">Πληρωμές Ναύλων</h3>
            <div className="space-y-2">
              {charters.length > 0 ? charters.map((charter) => {
                const totalPaid = (charter.payments || []).reduce((sum, p) => sum + p.amount, 0);
                const balance = (charter.amount || 0) - totalPaid;
                const paymentInfo = getPaymentStatusInfo(charter.paymentStatus);
                
                return (
                  <button key={charter.id} onClick={() => { setSelectedCategory('ΝΑΥΛΑ'); setPage('details'); }} className="w-full text-left bg-white p-4 rounded-lg hover:bg-[#f9fafb] border border-[#d1d5db] hover:border-[#1e40af] transition-all relative">
                    {paymentInfo.showLight && (
                      <div className={`absolute top-2 right-2 w-4 h-4 bg-red-500 rounded-full shadow-lg shadow-red-500/50 ${paymentInfo.lightBlink ? 'animate-pulse' : ''}`}></div>
                    )}
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="font-bold text-[#374151]">{charter.code}</h4>
                      <span className="font-bold text-green-400">{charter.amount?.toFixed(0)}€</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className={`font-semibold ${paymentInfo.color}`}>{paymentInfo.text}</span>
                      {balance > 0 && <span className="text-red-400">Υπόλοιπο: {balance.toFixed(0)}€</span>}
                    </div>
                  </button>
                )
              }) : (
                 <div className="bg-white p-6 rounded-lg text-center border border-[#d1d5db]"><p className="text-[#6b7280]">Δεν υπάρχουν ναύλοι.</p></div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceSection({ boatId, canEditFinancials, showMessage, invoices, setInvoices, isOwnerUser }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newInvoice, setNewInvoice] = useState({ code: '', date: '', description: '', amount: 0 });
  const fileInputRef = useRef(null);

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    setNewInvoice(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) : value }));
  };

  const handleFileUpload = (e) => {
    if (!canEditFinancials) { showMessage('❌ View Only - Δεν έχετε δικαίωμα', 'error'); return; }
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setNewInvoice(prev => ({ ...prev, fileUrl: URL.createObjectURL(file), fileName: file.name, fileData: reader.result }));
      showMessage('✅ Αρχείο επιλέχθηκε: ' + file.name, 'success');
    };
    reader.readAsDataURL(file);
  };

  const handleAddInvoice = async () => {
    if (!canEditFinancials) { showMessage('❌ View Only - Δεν έχετε δικαίωμα', 'error'); return; }
    if (!newInvoice.code || !newInvoice.date || !newInvoice.description) {
      showMessage("❌ Παρακαλώ συμπληρώστε όλα τα πεδία.", "error");
      return;
    }

    const invoiceId = uid();
    const invoice = {
      id: invoiceId,
      invoiceCode: `INV-${boatId}-${invoiceId}`,
      ...newInvoice,
      createdAt: new Date().toISOString(),
      createdBy: authService.getCurrentUser()?.name
    };

    // 🔥 Step 1: Save to localStorage FIRST (immediate, never lost)
    const key = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ`;
    const updated = [...invoices, invoice];
    localStorage.setItem(key, JSON.stringify(updated));
    setInvoices(updated);

    // 🔥 Step 2: Try to POST to API
    try {
      const result = await saveInvoice({
        invoiceCode: invoice.invoiceCode,
        vesselId: boatId,
        vesselName: invoice.vesselName || String(boatId),
        invoiceNumber: invoice.code,
        description: invoice.description,
        amount: invoice.amount || 0,
        invoiceDate: invoice.date,
        notes: invoice.notes,
        createdBy: invoice.createdBy
      });

      if (result.success) {
        console.log(`✅ Invoice saved to API: ${invoice.code}`);
        showMessage("✅ Το τιμολόγιο αποθηκεύτηκε.", "success");
      } else {
        // API failed, mark as pending_sync
        const pendingKey = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ_pending`;
        const pendingStored = localStorage.getItem(pendingKey);
        const pendingItems = pendingStored ? JSON.parse(pendingStored) : [];
        pendingItems.push({ ...invoice, pending_sync: true });
        localStorage.setItem(pendingKey, JSON.stringify(pendingItems));
        console.warn(`⚠️ Invoice saved locally, pending sync: ${invoice.code}`);
        showMessage("✅ Αποθηκεύτηκε τοπικά (θα συγχρονιστεί).", "success");
      }
    } catch (apiError) {
      // 🔥 Step 3: If API fails (offline), mark as "pending_sync" in localStorage
      console.error('API save failed:', apiError);
      const pendingKey = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ_pending`;
      const pendingStored = localStorage.getItem(pendingKey);
      const pendingItems = pendingStored ? JSON.parse(pendingStored) : [];
      pendingItems.push({ ...invoice, pending_sync: true });
      localStorage.setItem(pendingKey, JSON.stringify(pendingItems));
      console.warn(`⚠️ Invoice saved locally, pending sync: ${invoice.code}`);
      showMessage("✅ Αποθηκεύτηκε τοπικά (offline mode).", "success");
    }

    authService.logActivity('add_invoice', `${boatId}/${invoice.code}`);
    setNewInvoice({ code: '', date: '', description: '', amount: 0 });
    setShowAddForm(false);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!canEditFinancials) { showMessage('❌ View Only - Δεν έχετε δικαίωμα', 'error'); return; }
    const invoice = invoices.find(inv => inv.id === invoiceId);

    // 🔥 Step 1: Delete from localStorage FIRST
    const updated = invoices.filter((inv) => inv.id !== invoiceId);
    const key = `fleet_${boatId}_ΤΙΜΟΛΟΓΙΑ`;
    localStorage.setItem(key, JSON.stringify(updated));
    setInvoices(updated);

    // 🔥 Step 2: Try to DELETE from API
    try {
      const invoiceCode = invoice?.invoiceCode || invoice?.id;
      if (invoiceCode) {
        const success = await deleteInvoice(invoiceCode);
        if (success) {
          console.log(`✅ Invoice deleted from API: ${invoice?.code}`);
        } else {
          console.warn(`⚠️ Invoice deleted locally only: ${invoice?.code}`);
        }
      }
    } catch (apiError) {
      console.error('API delete failed:', apiError);
      // Local delete already done, API will be cleaned on next sync
    }

    showMessage("✅ Το τιμολόγιο διαγράφηκε.", "success");
  };

  const handleDownloadInvoice = (invoice) => {
    if (invoice.fileData) {
      const link = document.createElement('a');
      link.href = invoice.fileData;
      link.download = invoice.fileName || `invoice_${invoice.code}.pdf`;
      link.click();
      authService.logActivity('download_invoice', `${boatId}/${invoice.code}`);
      showMessage('✅ Λήψη ξεκίνησε!', 'success');
    } else {
      showMessage("❌ Δεν υπάρχει αρχείο για λήψη.", "error");
    }
  };

  return (
    <div>
      {canEditFinancials && (
        <div className="mb-4">
          <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center justify-center w-full bg-white hover:bg-[#f9fafb] text-[#374151] font-bold py-3 px-4 rounded-lg transition duration-200 border border-[#d1d5db]">
            {icons.plus} <span className="ml-2">{showAddForm ? 'Ακύρωση' : 'Προσθήκη Νέου Εξόδου'}</span>
          </button>
          
          {showAddForm && (
            <div className="mt-4 p-4 bg-white rounded-lg space-y-3 border border-[#d1d5db]">
              {/* 🔥 FIX 30: Added autoComplete="off" to fix Chrome typing issue */}
              <input type="text" name="code" value={newInvoice.code} onChange={handleFormChange} placeholder="Κωδικός (π.χ. TIM-001)" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
              <input type="date" name="date" value={newInvoice.date} onChange={handleFormChange} className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
              <input type="text" name="description" value={newInvoice.description} onChange={handleFormChange} placeholder="Περιγραφή (π.χ. Αναλώσιμα)" className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
              <input type="number" step="0.01" name="amount" value={newInvoice.amount} onChange={handleFormChange} placeholder="Ποσό (€)" className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Ανέβασμα Αρχείου (PDF/Image)</label>
                <input type="file" ref={fileInputRef} accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="w-full px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-lg border border-[#d1d5db]" />
                {newInvoice.fileName && <p className="text-sm text-green-400 mt-2">✓ {newInvoice.fileName}</p>}
              </div>
              <button onClick={handleAddInvoice} className="w-full bg-[#1e40af] hover:bg-blue-800 text-white font-bold py-3 px-4 rounded-lg">Αποθήκευση Εξόδου</button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {invoices.length === 0 ? (
          <div className="bg-white p-6 rounded-lg text-center border border-[#d1d5db]"><div className="text-5xl mb-3">📄</div><p className="text-[#6b7280]">Δεν υπάρχουν τιμολόγια</p></div>
        ) : (
          invoices.map((invoice) => (
            <div key={invoice.id} className="bg-white p-4 rounded-lg border border-[#d1d5db]">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-[#1e40af]">{invoice.code}</h3>
                  <p className="text-sm text-[#374151]">{invoice.description}</p>
                  <p className="text-sm text-[#6b7280]">{invoice.date ? new Date(invoice.date).toLocaleDateString('en-GB') : ''}</p>
                  {invoice.fileName && <p className="text-xs text-gray-500 mt-1">📎 {invoice.fileName}</p>}
                </div>
                <span className="text-xl font-bold text-red-400">{invoice.amount?.toFixed(2)}€</span>
              </div>
              <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-[#d1d5db]">
                {invoice.fileData && (
                  <button onClick={() => handleDownloadInvoice(invoice)} className="text-blue-400 hover:text-blue-300 p-1 flex items-center gap-1 hover:bg-blue-900 rounded transition-colors">
                    {icons.download} <span className="text-sm">Download</span>
                  </button>
                )}
                {canEditFinancials && (
                  <button onClick={() => handleDeleteInvoice(invoice.id)} className="text-red-500 hover:text-red-400 p-1 hover:bg-red-900 rounded transition-colors">{icons.x}</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MessagesPage({ boat, currentUser, navigate, showMessage }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const user = authService.getCurrentUser();

  // 🔥 FIX 4: Use optional chaining in dependencies
  useEffect(() => {
    if (boat) {
      loadMessages();
    }
  }, [boat?.id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  const loadMessages = () => {
    try {
      const key = `fleet_${boat.id}_messages`;
      const stored = localStorage.getItem(key);
      if (stored) setMessages(JSON.parse(stored));
      authService.logActivity('view_messages', boat.id);
    } catch (e) { console.error('Error loading messages:', e); }
  };

  const saveMessages = (newMessages) => {
    try {
      const key = `fleet_${boat.id}_messages`;
      localStorage.setItem(key, JSON.stringify(newMessages));
      setMessages(newMessages);
    } catch (e) { console.error('Error saving messages:', e); }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    const message = { id: mid(), text: newMessage.trim(), senderId: user.code, senderName: user.name || user.code, senderRole: user.role, isFromAdmin: authService.isAdmin(), createdAt: new Date().toISOString() };
    saveMessages([...messages, message]);
    authService.logActivity('send_message', `${boat.id}/${message.text.substring(0, 50)}`);
    setNewMessage('');
  };

  const handleDeleteMessage = (msgId) => {
    if (!authService.isAdmin()) { showMessage('❌ Only Admin can delete messages', 'error'); return; }
    const msg = messages.find(m => m.id === msgId);
    saveMessages(messages.filter((msg) => msg.id !== msgId));
    authService.logActivity('delete_message', `${boat.id}/${msgId}`);
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title={`Συνομιλία - ${boat.id}`} onBack={() => navigate('boatDashboard')} />
      
      <div className="flex-grow p-4 overflow-y-auto space-y-4 pb-20">
        {messages.length === 0 && (
          <div className="text-center p-8"><div className="text-6xl mb-4">💬</div><p className="text-[#6b7280]">Δεν υπάρχουν μηνύματα.</p><p className="text-sm text-gray-500 mt-2">Γράψτε την παρατήρησή σας.</p></div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.isFromAdmin ? 'justify-end' : 'justify-start'}`}>
            <div className={`p-3 rounded-lg max-w-xs relative group ${msg.isFromAdmin ? 'bg-[#1e40af]' : 'bg-[#f9fafb]'} border ${msg.isFromAdmin ? 'border-[#1e40af]' : 'border-[#d1d5db]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs font-bold">{msg.senderName}</p>
                {msg.senderRole && <span className="text-xs px-1.5 py-0.5 bg-[#f3f4f6] rounded">{msg.senderRole}</span>}
              </div>
              <p className="text-base">{msg.text}</p>
              <p className="text-xs text-[#6b7280] mt-1">{new Date(msg.createdAt).toLocaleString('el-GR')}</p>
              {authService.isAdmin() && (
                <button onClick={() => handleDeleteMessage(msg.id)} className="absolute -top-2 -right-2 text-red-500 bg-[#f3f4f6] rounded-full p-0.5 opacity-0 group-hover:opacity-100 hover:bg-red-900 transition-all">{icons.x}</button>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="flex p-2 bg-white border-t border-[#d1d5db] sticky bottom-[56px] z-10">
        <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Γράψτε την παρατήρησή σας..." className="flex-grow px-3 py-2 bg-[#f9fafb] text-[#374151] rounded-l-lg border border-[#d1d5db] focus:outline-none focus:border-[#1e40af]" />
        <button type="submit" className="bg-[#1e40af] hover:bg-blue-800 text-white p-3 rounded-r-lg transition-colors">{icons.send}</button>
      </form>
      
      <BottomNav activePage={'messages'} onNavigate={navigate} />
    </div>
  );
}

function EmailPage({ boat, navigate }) {
  // 🔥 FIX 3-4: All hooks MUST come before any conditional returns
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const user = authService.getCurrentUser();

  // 🔥 FIX 3: Null check AFTER all hooks
  if (!boat) {
    return (
      <div className="flex flex-col h-full bg-[#f3f4f6] items-center justify-center">
        <div className="text-[#1e40af] text-xl">Loading...</div>
      </div>
    );
  }

  const handleSendEmail = () => {
    // ALL emails go to ONLY these 2 addresses
    const emailTo = 'info@tailwindyachting.com,charter@tailwindyachting.com';
    const mailtoLink = `mailto:${emailTo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`Σκάφος: ${boat.name || boat.id}\nΑπό: ${user?.name || user?.code} (${user?.role})\n\n${body}`)}`;
    authService.logActivity('send_email', `${boat.id}/${subject}`);
    window.location.href = mailtoLink;
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6]">
      <Header title="Αποστολή E-mail" onBack={() => navigate('boatDashboard')} />
      
      <div className="flex-grow p-4 overflow-y-auto pb-20 space-y-4">
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-[#374151] mb-2">Θέμα</label>
          <input type="text" id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="π.χ. Πρόβλημα με το WC" className="w-full px-4 py-3 bg-white text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
        </div>
        
        <div>
          <label htmlFor="body" className="block text-sm font-medium text-[#374151] mb-2">Μήνυμα</label>
          <textarea id="body" rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Περιγράψτε το αίτημά σας..." className="w-full px-4 py-3 bg-white text-[#374151] rounded-lg border border-[#d1d5db] focus:border-[#1e40af] focus:outline-none" />
        </div>
        
        <button onClick={handleSendEmail} className="w-full flex items-center justify-center bg-[#1e40af] hover:bg-blue-800 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors">
          <span className="mr-2">Άνοιγμα E-mail Client</span> {icons.send}
        </button>
        
        <div className="text-center text-[#6b7280] text-sm p-4 bg-white rounded-lg border border-[#d1d5db]">
          <p>Πατώντας "Αποστολή", θα ανοίξει η προεπιλεγμένη εφαρμογή email.</p>
        </div>
      </div>
      
      <BottomNav activePage={null} onNavigate={navigate} />
    </div>
  );
}

// 🔥 FleetBookingPlanPage - με ΚΕΙΜΕΝΟ αντί για emojis
function FleetBookingPlanPage({ navigate, showMessage }) {
  const [allBoats, setAllBoats] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [selectedBoatForPopup, setSelectedBoatForPopup] = useState<any>(null);
  const [cleaningBusy, setCleaningBusy] = useState(false); // 🧽 loading state για το POST

  const isTechnicalUser = authService.isTechnical();
  const canViewFinancials = !isTechnicalUser; // TECHNICAL δεν βλέπει οικονομικά

  // Filter boats based on search (case-insensitive)
  const filteredBoats = allBoats.filter(boat => {
    if (!searchTerm.trim()) return true;
    const boatText = `${boat.id} ${boat.name} ${boat.type} ${boat.model || ''}`;
    return textMatches(boatText, searchTerm);
  });
  
  useEffect(() => {
    // TECHNICAL, ADMIN, OWNER μπορούν να δουν
    if (authService.isAdmin() || authService.isOwner() || authService.isTechnical()) { loadAllData(); }
  }, []);

  // Μόνο αν ΔΕΝ είναι ADMIN, OWNER ή TECHNICAL -> Access Denied
  if (!authService.isAdmin() && !authService.isOwner() && !authService.isTechnical()) {
    return (
      <div className="flex flex-col h-full" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
        <Header title="Fleet Booking Plan" onBack={() => navigate('adminDashboard')} />
        <div className="flex-grow flex items-center justify-center p-8">
          <div className="text-center"><div className="text-8xl mb-6">🔒</div><h2 className="text-3xl font-bold text-red-500 mb-4">Access Denied</h2><p className="text-blue-700 mb-6">Only Administrators can view Fleet Booking Plan.</p></div>
        </div>
      </div>
    );
  }

  const loadAllData = async () => {
    try {
      const boats = FleetService.getAllBoats();
      setAllBoats(boats);

      // Load all bookings from API for all boats
      let allBookingsData: any[] = [];
      await Promise.all(boats.map(async (boat: any) => {
        try {
          const bookings = await getBookingsByVessel(boat.id);
          bookings.forEach((booking: any) => { allBookingsData.push({ ...booking, boatId: boat.id }); });
        } catch (e) {
          console.error(`❌ Failed to load bookings for boat ${boat.id}`);
        }
      }));
      setAllBookings(allBookingsData);
      setLoading(false);
    } catch (e) {
      console.error('Error loading fleet data:', e);
      showMessage('❌ Σφάλμα φόρτωσης πλάνου.', 'error');
      setLoading(false);
    }
  };

  const changeMonth = (offset) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const monthName = currentDate.toLocaleString('el-GR', { month: 'long' });
  const year = currentDate.getFullYear();

  let firstSaturday = new Date(year, currentDate.getMonth(), 1);
  while (firstSaturday.getDay() !== 6) { firstSaturday.setDate(firstSaturday.getDate() + 1); }

  const weeks = [];
  let currentWeekStart = new Date(firstSaturday);
  if (firstSaturday.getDate() > 7) { currentWeekStart.setDate(currentWeekStart.getDate() - 7); }
  
  while (currentWeekStart.getMonth() === currentDate.getMonth()) {
    let weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weeks.push({ start: new Date(currentWeekStart), end: weekEnd, startDateString: `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth()+1).padStart(2,'0')}-${String(currentWeekStart.getDate()).padStart(2,'0')}` });
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  const formatDate = (date) => date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });

  if (loading) return (
    <div className="flex flex-col h-screen" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
      <Header title="Fleet Booking Plan" onBack={() => navigate('adminDashboard')} />
      <FullScreenLoader />
    </div>
  );

  return (
    <div className="flex flex-col h-screen w-screen fixed inset-0 z-50" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
      <div className="p-2 flex items-center justify-between border-b border-sky-300" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
        <div className="w-12"></div>
        <h1 className="text-lg font-bold text-blue-900">Fleet Booking Plan</h1>
        <button onClick={() => navigate('adminDashboard')} className="text-blue-800 p-2 hover:bg-blue-300/50 rounded-lg transition-colors flex flex-col items-center" title="Πίσω (παραμένετε συνδεδεμένοι)">
          {icons.returnArrow}
          <span className="text-[10px] mt-0.5">Return</span>
        </button>
      </div>

      {isTechnicalUser && (
        <div className="p-2 border-b border-sky-300 text-center" style={{ background: 'linear-gradient(135deg, #bae6fd 0%, #7dd3fc 100%)' }}>
          <div className="flex items-center justify-center gap-2 text-blue-800 text-sm">
            {icons.eye}
            <span>Technical View - Μόνο ημερομηνίες & status</span>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center p-2 border-b border-sky-300" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
        <button onClick={() => changeMonth(-1)} className="text-blue-800 p-2 hover:bg-blue-300/50 rounded transition-colors text-2xl">{icons.chevronLeft}</button>
        <h2 className="text-2xl font-bold text-blue-900 capitalize">{monthName} {year}</h2>
        <button onClick={() => changeMonth(1)} className="text-blue-800 p-2 hover:bg-blue-300/50 rounded transition-colors text-2xl">{icons.chevronRight}</button>
      </div>

      <div className="p-2 border-b border-sky-300" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
        <div className="flex flex-wrap justify-center gap-4 text-xs">
          <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-yellow-400"></div><span className="text-yellow-600 font-semibold">Option</span></div>
          <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-green-500"></div><span className="text-green-600 font-semibold">Confirmed</span></div>
          {/* «Canceled» έξω (δεν εμφανίζονται στο πλάνο)· «Service» γκρι */}
          <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-gray-300"></div><span className="text-gray-600 font-semibold">Service</span></div>
        </div>
      </div>

      {/* Search Box */}
      <div className="p-2 border-b border-sky-300 bg-sky-50">
        <div className="relative max-w-md mx-auto">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="🔍 Αναζήτηση σκάφους (όνομα, τύπος, μοντέλο...)"
            className="w-full px-4 py-2 bg-white text-blue-900 rounded-lg border border-sky-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-800"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="text-center text-xs text-blue-600 mt-1">
            Βρέθηκαν {filteredBoats.length} από {allBoats.length} σκάφη
          </div>
        )}
      </div>

      <div className="flex-grow overflow-auto p-2">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>
              <th className="sticky left-0 p-3 border border-sky-300 text-left text-blue-900 text-base min-w-[100px] font-bold" style={{ background: 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)' }}>Σκάφος</th>
              {weeks.map((week, index) => (
                <th key={index} className="p-2 border border-sky-300 text-sm text-blue-800 min-w-[120px]">
                  <div className="font-bold">ΕΒΔ. {index + 1}</div>
                  <div className="text-xs text-blue-700">{formatDate(week.start)} - {formatDate(week.end)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredBoats.map((boat) => (
              <tr key={boat.id} className="hover:bg-sky-100">
                {/* 🔥 BUG FIX: Display boat NAME instead of ID */}
                <td className="sticky left-0 bg-sky-50 hover:bg-sky-100 p-3 border border-sky-300 font-bold text-blue-800 text-base">{boat.name}</td>
                
                {weeks.map((week, index) => {
                    // 🔥 FIX 29: Charter displays ONLY in the week where check-in date falls (no spanning)
                    const booking = allBookings.find((b) => {
                      if (b.boatId !== boat.id || !b.startDate) return false;
                      /* ΑΚΥΡΩΜΕΝΟΙ: ΔΕΝ πιάνουν θέση στο πλάνο — κενό κελί (μένουν στη
                         λίστα Ναύλοι/Ιστορικό/MMK). (12/10/2026) */
                      {
                        const cs = String(b.status || '').toLowerCase().replace(/[\s_-]+/g, '');
                        if (['cancelled', 'canceled', 'rejected'].includes(cs)) return false;
                      }
                      const charterStart = new Date(b.startDate);
                      const charterEnd = b.endDate ? new Date(b.endDate) : new Date(charterStart.getTime() + 7*24*60*60*1000);
                      const weekStart = new Date(week.startDateString);
                      const weekEnd = new Date(weekStart.getTime() + 7*24*60*60*1000);
                      // Charter spans ALL overlapping weeks; back-to-back safe (end-of-week start belongs to next charter)
                      return charterStart.getTime() < weekEnd.getTime() && charterEnd.getTime() > weekStart.getTime();
                    });
                    const isBooked = !!booking;
                    const status = booking?.status || 'Pending';

                    let bgColor = 'bg-sky-50 hover:bg-sky-100';
                    let textColor = 'text-sky-400';
                    let statusColor = 'text-sky-400';
                    
                    if (isBooked) {
                      switch(status) {
                        // 🔥 FIX: YELLOW for all pending statuses (all case variations)
                        case 'Option': case 'option': case 'OPTION':
                        case 'Pending': case 'pending': case 'PENDING':
                        case 'Reservation': case 'reservation': case 'RESERVATION':
                          bgColor = 'bg-yellow-400'; textColor = 'text-black'; statusColor = 'text-black'; break;
                        case 'Accepted': case 'accepted': case 'ACCEPTED':
                        case 'Option Accepted': case 'option_accepted': case 'OPTION_ACCEPTED':
                        case 'Pending Final Confirmation': case 'pending_final_confirmation': case 'PENDING_FINAL_CONFIRMATION':
                          bgColor = 'bg-yellow-400'; textColor = 'text-black'; statusColor = 'text-black'; break;
                        // 🔥 FIX: GREEN for confirmed (all case variations)
                        case 'Confirmed': case 'confirmed': case 'CONFIRMED':
                          bgColor = 'bg-green-500'; textColor = 'text-white'; statusColor = 'text-green-100'; break;
                        // ΜΠΛΟΚ / SERVICE → ΓΚΡΙ (δεν είναι ναύλος· χωρίς ποσό/ΑΝΕΞΟΦΛΗΤΟ)
                        case 'Service': case 'service': case 'SERVICE':
                        case 'Maintenance': case 'maintenance': case 'Block': case 'block':
                          bgColor = 'bg-gray-300'; textColor = 'text-gray-700'; statusColor = 'text-gray-700'; break;
                        // OPTION EXPIRED → ανοιχτό γκρι
                        case 'Option expired': case 'option_expired': case 'OPTION EXPIRED':
                          bgColor = 'bg-[#f9fafb]'; textColor = 'text-[#374151]'; statusColor = 'text-[#374151]'; break;
                        // 🔥 FIX: RED for cancelled (all case variations) — ΔΕΝ φτάνει εδώ (εξαιρούνται παραπάνω)
                        case 'Canceled': case 'Cancelled': case 'cancelled': case 'CANCELLED': case 'CANCELED':
                        case 'Rejected': case 'rejected': case 'REJECTED':
                          bgColor = 'bg-red-500'; textColor = 'text-white'; statusColor = 'text-red-100'; break;
                        // 🔥 FALLBACK: Default to YELLOW (pending)
                        default: bgColor = 'bg-yellow-400'; textColor = 'text-black'; statusColor = 'text-black';
                      }
                    }
                    
                    // 🔥 FIXED: Payment status με ΚΕΙΜΕΝΟ
                    const paymentInfo = isBooked ? getPaymentStatusInfo(booking.paymentStatus) : null;
                    /* ΑΝΕΝΕΡΓΟΣ (Service/μπλοκ/option_expired): μόνο κωδικός + SERVICE,
                       ΧΩΡΙΣ ποσό, ΑΝΕΞΟΦΛΗΤΟ, κόκκινη βούλα, πλήρωμα/σκίπερ. */
                    const inactive = isBooked && ['service', 'maintenance', 'block', 'blocked', 'optionexpired', 'expired']
                      .includes(String(status).toLowerCase().replace(/[\s_-]+/g, ''));

                    return (
                       <td key={index} className={`p-2 border border-sky-300 text-center ${bgColor} relative`}>
                         {isBooked ? (
                           <div
                             className={`${textColor} cursor-pointer hover:opacity-80`}
                             onClick={() => { setSelectedBooking(booking); setSelectedBoatForPopup(boat); }}
                           >
                             {/* 🔥 Red light - μόνο αν βλέπει οικονομικά και είναι ΑΝΕΞΟΦΛΗΤΟ· ΟΧΙ σε ανενεργό */}
                             {canViewFinancials && paymentInfo?.showLight && !inactive && (
                               <div className={`absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full shadow-lg shadow-red-500/50 ${paymentInfo.lightBlink ? 'animate-pulse' : ''}`}></div>
                             )}
                             <p className="font-bold text-sm">{booking.code}</p>
                             {/* Οικονομικά μόνο αν δεν είναι TECHNICAL ΚΑΙ είναι πραγματικός ναύλος */}
                             {canViewFinancials && !inactive && (
                               <>
                                 <p className="text-[#1e40af] text-sm">{booking.amount?.toFixed(0)}€</p>
                                 <p className={`text-xs font-semibold ${paymentInfo?.color}`}>{paymentInfo?.text}</p>
                               </>
                             )}
                             <p className={`text-xs ${statusColor}`}>{status.toUpperCase()}</p>
                             {!inactive && (
                               <>
                                 {/* ΑΤΟΜΑ: σκίπερ+επιβάτες (crew list) ή «—». */}
                                 <p className="text-xs font-semibold">{`👥 ${Array.isArray(booking.crewMembers) && booking.crewMembers.length ? booking.crewMembers.length : '—'}`}</p>
                                 {/* ΣΚΙΠΕΡ — ΜΟΝΟ εδώ (κάρτα) & στο pop-up. */}
                                 <p className="text-xs" title={skipperName(booking) || '—'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{skipperBadge(booking)}</p>
                               </>
                             )}
                           </div>
                         ) : (
                           <>
                             <span className="text-sky-300">-</span>
                             {/* ΚΑΘΑΡΙΣΜΟΣ ΧΩΡΙΣ ΝΑΥΛΟ (Σάββατο κενό): ίδιο api με το τικ. */}
                             {['ADMIN','BOOKING','TECHNICAL'].includes((authService.getCurrentUser()?.role || '').toUpperCase()) && (
                               <button type="button" title={`Καθαρισμός χωρίς ναύλο — ${boat.name} ${week.startDateString}`}
                                 onClick={async (e) => {
                                   e.stopPropagation();
                                   const map: any = { '1':'after_service', '2':'owner_use', '3':'before_next', '4':'other' };
                                   const pick = window.prompt('Αιτία καθαρισμού (χωρίς ναύλο):\n1 = Μετά από service\n2 = Χρήση ιδιοκτήτη\n3 = Πριν τον επόμενο ναύλο\n4 = Άλλο', '1');
                                   if (!pick) return;
                                   const reason = map[String(pick).trim()] || 'other';
                                   try {
                                     const code = authService.getCurrentUser()?.code || '';
                                     const res = await fetch('/api/cleaning-jobs.php', { method: 'POST',
                                       headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
                                       body: JSON.stringify({ vessel_name: boat.name, cleaned_date: week.startDateString, reason }) });
                                     const j = await res.json();
                                     if (j?.success) showMessage('🧹 Καθαρισμός χωρίς ναύλο προστέθηκε', 'success');
                                     else showMessage('❌ ' + (j?.error || 'Σφάλμα'), 'error');
                                   } catch (err) { showMessage('❌ Σφάλμα', 'error'); }
                                 }}
                                 style={{ position: 'absolute', top: 2, right: 3, border: 'none', background: 'transparent',
                                          cursor: 'pointer', opacity: 0.4, fontSize: 12 }}>🧹</button>
                             )}
                           </>
                         )}
                       </td>
                    )
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Charter Detail Popup for all users (Technical users see limited info) */}
      {selectedBooking && selectedBoatForPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedBooking(null); setSelectedBoatForPopup(null); }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto text-[#374151]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Charter Details</h3>
              <button onClick={() => { setSelectedBooking(null); setSelectedBoatForPopup(null); }} className="text-[#6b7280] hover:text-white text-2xl">&times;</button>
            </div>

            <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 space-y-2 border border-[#d1d5db]">
              <div className="flex justify-between"><span className="text-[#374151]">CODE:</span><span className="font-bold">{selectedBooking.code}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">YACHT:</span><span className="font-bold">{selectedBoatForPopup.name || selectedBoatForPopup.id}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">FROM:</span><span className="font-bold">{selectedBooking.startTime && `${selectedBooking.startTime} - `}{selectedBooking.startDate ? new Date(selectedBooking.startDate).toLocaleDateString('en-GB') : ''}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">TO:</span><span className="font-bold">{selectedBooking.endTime && `${selectedBooking.endTime} - `}{selectedBooking.endDate ? new Date(selectedBooking.endDate).toLocaleDateString('en-GB') : ''}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">DEPARTURE:</span><span className="font-bold">{selectedBooking.departure || 'ALIMOS MARINA'}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">ARRIVAL:</span><span className="font-bold">{selectedBooking.arrival || 'ALIMOS MARINA'}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">STATUS:</span><span className="font-bold">{selectedBooking.status?.toUpperCase()}</span></div>
              {/* ΑΤΟΜΑ + ΣΚΙΠΕΡ — ΜΟΝΟ στο Booking plan (κάρτα + αυτό το pop-up). */}
              <div className="flex justify-between"><span className="text-[#374151]">ΑΤΟΜΑ:</span><span className="font-bold">{`👥 ${Array.isArray(selectedBooking.crewMembers) && selectedBooking.crewMembers.length ? selectedBooking.crewMembers.length : '—'}`}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">ΣΚΙΠΕΡ:</span><span className="font-bold">{skipperBadge(selectedBooking)}</span></div>
            </div>

            {/* Skipper info */}
            {(selectedBooking.skipperFirstName || selectedBooking.skipperLastName) && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">SKIPPER:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-[#374151]">Name:</span><span className="font-bold">{selectedBooking.skipperFirstName} {selectedBooking.skipperLastName}</span></div>
                  {selectedBooking.skipperEmail && <div className="flex justify-between"><span className="text-[#374151]">Email:</span><span>{selectedBooking.skipperEmail}</span></div>}
                  {selectedBooking.skipperPhone && <div className="flex justify-between"><span className="text-[#374151]">Phone:</span><span>{selectedBooking.skipperPhone}</span></div>}
                  {selectedBooking.skipperAddress && <div className="flex justify-between"><span className="text-[#374151]">Address:</span><span>{selectedBooking.skipperAddress}</span></div>}
                </div>
              </div>
            )}

            {/* EXTRAS section */}
            {selectedBooking.extras && selectedBooking.extras.length > 0 && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">EXTRAS:</h4>
                <div className="space-y-1">
                  {selectedBooking.extras.map((extra: string, idx: number) => (
                    <div key={idx} className="flex items-center text-green-400">
                      <span className="mr-2">✓</span>
                      <span>{extra}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NOTES section */}
            {selectedBooking.notes && selectedBooking.notes.trim() && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2 text-blue-400">ΣΗΜΕΙΩΣΕΙΣ:</h4>
                <p className="text-[#374151] whitespace-pre-wrap">{selectedBooking.notes}</p>
              </div>
            )}

            {/* Financial info - only for non-technical users */}
            {canViewFinancials && (
              <div className="bg-[#f9fafb] p-4 rounded-lg mb-4 border border-[#d1d5db]">
                <h4 className="font-bold text-lg mb-2">FINANCIAL TERMS:</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-green-400"><span>Charter Fee:</span><span className="font-bold">{selectedBooking.amount?.toFixed(2)}€</span></div>
                  <div className="flex justify-between text-red-400"><span>Commission:</span><span className="font-bold">-{selectedBooking.commission?.toFixed(2)}€</span></div>
                  <div className="flex justify-between text-red-400"><span>VAT (24%):</span><span className="font-bold">-{selectedBooking.vat_on_commission?.toFixed(2)}€</span></div>
                  <hr className="border-[#d1d5db]" />
                  <div className="flex justify-between text-xl font-bold"><span>NET INCOME:</span><span className="text-[#1e40af]">{((selectedBooking.amount || 0) - (selectedBooking.commission || 0) - (selectedBooking.vat_on_commission || 0)).toFixed(2)}€</span></div>
                </div>
              </div>
            )}

            {/* 🧽 Cleaning — add this charter to the cleaning list (ADMIN/BOOKING/TECHNICAL only) */}
            {['ADMIN','BOOKING','TECHNICAL'].includes((authService.getCurrentUser()?.role || '').toUpperCase()) && (
              <button
                disabled={cleaningBusy}
                onClick={async () => {
                  if (cleaningBusy) return;
                  setCleaningBusy(true);
                  try {
                    const code = authService.getCurrentUser()?.code || '';
                    const res = await fetch('/api/cleaning-jobs.php', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
                      body: JSON.stringify({
                        booking_number: selectedBooking.code,
                        vessel_name: selectedBoatForPopup?.name || selectedBooking.vesselName || '',
                        cleaned_date: selectedBooking.startDate || ''
                      })
                    });
                    const j = await res.json();
                    if (j.duplicate) { showMessage('🧽 Ο ναύλος είναι ήδη στη λίστα καθαρισμού', 'error'); }
                    else if (j.success) { showMessage('🧽 Προστέθηκε στους καθαρισμούς', 'success'); }
                    else { showMessage('❌ Σφάλμα προσθήκης καθαρισμού', 'error'); }
                  } catch (e) { showMessage('❌ Σφάλμα δικτύου', 'error'); }
                  finally { setCleaningBusy(false); }
                }}
                className="w-full mb-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {cleaningBusy ? '🧽 …' : '🧽 Καθαρισμός'}
              </button>
            )}

            {/* ✓ Καθαρίστηκε — ΜΟΝΟ ένδειξη (πράσινο Plan + READY κάρτα). ΚΑΜΙΑ σχέση με πληρωμή/amount/ώρες. */}
            {['ADMIN','BOOKING','TECHNICAL'].includes((authService.getCurrentUser()?.role || '').toUpperCase()) && (
              <button
                disabled={cleaningBusy}
                onClick={async () => {
                  if (cleaningBusy) return;
                  // 🛡️ guard: μη μαρκάρεις "καθαρίστηκε" ναύλο με μελλοντική ημερομηνία (αύριο/μέλλον)
                  const _sd = String(selectedBooking.startDate || '').slice(0, 10);
                  if (_sd) {
                    const _n = new Date();
                    const _today = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
                    if (_sd > _today && !window.confirm(`Ο καθαρισμός είναι για μελλοντική ημερομηνία (${_sd.split('-').reverse().join('/')}). Σίγουρα καθαρίστηκε;`)) return;
                  }
                  setCleaningBusy(true);
                  try {
                    const code = authService.getCurrentUser()?.code || '';
                    const res = await fetch('/api/cleaning-jobs.php?action=mark-cleaned', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
                      body: JSON.stringify({
                        booking_number: selectedBooking.code,
                        vessel_name: selectedBoatForPopup?.name || selectedBooking.vesselName || '',
                        cleaned_date: selectedBooking.startDate || ''
                      })
                    });
                    const j = await res.json();
                    if (j.success) {
                      showMessage('✅ Σημειώθηκε ως καθαρισμένο', 'success');
                    } else { showMessage('❌ Σφάλμα', 'error'); }
                  } catch (e) { showMessage('❌ Σφάλμα δικτύου', 'error'); }
                  finally { setCleaningBusy(false); }
                }}
                className="w-full mb-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                ✓ Καθαρίστηκε
              </button>
            )}

            <button onClick={() => { setSelectedBooking(null); setSelectedBoatForPopup(null); }} className="w-full bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-4 rounded-lg">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== FLEET MANAGEMENT COMPLETE ==========